/**
 * Three.js renderer (WebGL2-first, mobile budget-aware). The sea uses the SHARED
 * Gerstner GLSL from @piratas/sim, so the rendered water surface is the exact
 * height field the server floats ships on. Ships/cannonballs are stylized
 * low-poly procedural meshes — no external art dependency (see ARCHITECTURE.md
 * §2 legal posture). Heavy reuse + instancing keeps the draw-call budget low.
 *
 * World mapping: sim (x, y) -> three (x, oceanHeight, y). sim-y is world-Z.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Subtle cinematic vignette — darkens the frame corners to draw the eye to the
// action. Runs in linear space before the tone map. Cheap full-screen multiply.
const VignetteShader = {
  uniforms: { tDiffuse: { value: null }, uAmount: { value: 0.26 } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: /* glsl */ `
    precision highp float; uniform sampler2D tDiffuse; uniform float uAmount; varying vec2 vUv;
    void main(){
      vec4 c = texture2D(tDiffuse, vUv);
      vec2 d = vUv - 0.5;
      float vig = smoothstep(0.75, 0.18, dot(d, d)); // 1 at centre -> 0 at corners
      c.rgb *= 1.0 - uAmount * (1.0 - vig);
      gl_FragColor = c;
    }`,
};
import { AURORA, AmmoType, BASE, CANNON, CAPITAL, CAPITAL_LAYOUT, CATSPAW, DAY_NIGHT_CYCLE_S, FISHING, ISLAND_ARCHETYPES, KRAKEN, KrakenPhase, LANDMARK, LANDMARK_WALL_W, NAME_BADGE, SHIP, SHIP_CLASS, ShipClassId, ShipFlag, SINK_FX, STORM, WORLD_SIZE, archetypeForIsland, cosmeticById, harborMouth, landmarkForOutpost, layoutForOutpost, type IslandArchetypeSet, type IslandDecoration, type KrakenState, type LandmarkPiece, type PortLayout, type WireBase, type WorldIsland, type WorldMsg } from '@piratas/protocol';
import { OCEAN_COAST_MAX, auroraBreakupAt, auroraIntensityAt, buildOceanCoastGLSL, buildOceanGLSL, buildOceanNormalGLSL, buildOceanSwellGLSL, chopGainAt, clamp, lerp, normAngle, oceanCoastFactor, oceanEnvUniforms, oceanHeightAt, oceanHullHeightAt, oceanSampleAt, oceanSampleInto, setOceanEnv, tradeLaneDirAt, type OceanSample } from '@piratas/sim';
import { KIT_URL, KIT_FOOT, KIT_TALL, SKULL_HARBOR_KINDS, HEAVY_LOD_KINDS, WINDOW_SPECS, LAMP_KINDS } from './portscene.js';
import { LightSystem, GLINT_MAX, buildLandmarkKindLights, presetForLightKind } from './lights.js';
import { OCEAN_FADE_R, OCEAN_SNAP, buildOceanClipmap } from './oceanmesh.js';
import { WakeSystem, type WakeStats } from './wake.js';
import { ContactShadows, SplashFx, type SeaFxStats } from './seafx.js';
import { StormFx, type StormEnv } from './storm.js';
import { BirdFlock, FlyingFish, buildGullGeometry, buildGullMaterial } from './birds.js';
import { WhaleBody, WHALE_ART, type WhaleFx } from './whale.js';
import { retryAsync, loadWithRetry, withDeadline } from './retry.js';
import { verticalFovForAspect } from './camfov.js';
import { toneMapping, waterQuality } from './config.js';
import { shouldRedressShip } from './redress.js';
import { SAIL_ART, SailState, bakeSailRig, makeSailShared, patchSailMaterial, sailEnv, type SailRig, type SailShared, type SailUniforms } from './sails.js';
import type { InterpShip, InterpBall } from './net.js';

/** Mirrors server BuildingType (apps/server/src/persistence/types.ts). */
// (farms-demand-first: type 3 was the removed SHIELD phantom — never a real building; sanitized off the wire.)
const BT = { HQ: 0, STORAGE: 1, TURRET: 2, FARM: 4, DOCK: 5, TAVERN: 6, FOUNDRY: 7, CROP: 8 } as const;

/** Onda Portos Livres — props the owner drops OUTSIDE a walkable port plaza (the CAPITAL and every VILLAGE) also
 *  render from the SEA, so a rampart / lighthouse / tree — OR a generated harbour building (wharf, citadel, …) —
 *  placed "outside the city" no longer vanishes when you sail up to it. A layout's exterior props are converted
 *  into landmark pieces in the outpost's LOCAL sea frame by `exteriorPiecesFor`: the port frame is embedded at
 *  the outpost's seaward heading (from `harborMouth`), pinning the plaza's seaward centre-line (x=SIZE/2, the
 *  spawn/gate/dock line) on the outpost centre and the dock edge (~port y 0) a fixed distance seaward. Interior
 *  props stay in the walkable view only; nothing here derives collision (render-only, like the landmark path).
 *  The capital's shipped seaward heading is phi = -PI/2 (frame yaw 0), so its transform is still the exact pure
 *  OFFSET (px-48, py-46) the Onda Entorno Livre wave used — reproduced by the general formula (verified). */
/** Port-local dock line → this many metres seaward of the outpost centre. Capital uses its harbour-mouth
 *  distance; a village has no mouth, so its dock pins a bit past the visible dock cylinder (~20 m). */
const VILLAGE_SEA_PIN_Y = 18;
/** Sea render size = the WALKABLE size, so a shared exterior prop does NOT jump scale between the two scenes (the
 *  "farol fica bugadão" report — a lighthouse was 22 m in the plaza but 30 m at sea). For the CLASSIC breakwater
 *  kit (below) the ratio = portscene's KIT_TALL/KIT_FOOT base size ÷ this sea LANDMARK.* base size (the sea uses
 *  its own LANDMARK.* normalization + this ratio). For ANY OTHER kit kind (wharf/citadel/houses/town-dressing)
 *  the sea normalizes DIRECTLY with the imported KIT_FOOT/KIT_TALL tables — the SAME numbers the walkable city
 *  uses — so there is no second table to drift (see `instanceKitSeaKind`). `wall` keeps the sea seawall's native
 *  breakwater proportions (non-uniform W×H×D — no single-scalar parity with the uniformly-scaled plaza wall). */
const EXT_SEA_SCALE: Record<string, number> = {
  lighthouse: KIT_TALL.lighthouse! / LANDMARK.LIGHTHOUSE_H,
  pine: KIT_TALL.pine! / LANDMARK.PINE_H,
  pine_tall: KIT_TALL.pine_tall! / LANDMARK.PINE_TALL_H,
  rock: KIT_FOOT.rock! / LANDMARK.ROCK_W,
  cliff: KIT_FOOT.cliff! / LANDMARK.CLIFF_W,
  wall: 1,
};

/** Seconds a cosmetic hull recoil (cannon-hit knockback nudge + tilt) takes to fully ease back. */
const RECOIL_DUR = 0.32;
// Remote-ship LOD: past ~300 m a hull's sail-furl mixer is frozen (the canvas ripple is imperceptible at
// that range) and its floating name sprite (a per-ship draw call) is hidden. Hysteresis (~20 m) restores
// detail on re-approach so a hull hovering at the boundary never flickers. Squared metres from the CAMERA.
const SHIP_LOD_FAR_D2 = 300 * 300;
const SHIP_LOD_NEAR_D2 = 280 * 280;

/** Day/night sky keyframes (phase 0=midnight · 0.25=dawn · 0.5=noon · 0.75=dusk · 1=midnight).
 *  The noon key reproduces the game's original daylight look. All cosmetic — lerped each frame. */
interface SkyKey { p: number; sky: THREE.Color; deep: THREE.Color; shallow: THREE.Color; sun: THREE.Color; sunI: number; hemiSky: THREE.Color; hemiGround: THREE.Color; hemiI: number; star: number; glow: THREE.Color; glowAmt: number; }
const skyKey = (p: number, sky: number, deep: number, shallow: number, sun: number, sunI: number, hemiSky: number, hemiGround: number, hemiI: number, star: number, glow: number, glowAmt: number): SkyKey =>
  ({ p, sky: new THREE.Color(sky), deep: new THREE.Color(deep), shallow: new THREE.Color(shallow), sun: new THREE.Color(sun), sunI, hemiSky: new THREE.Color(hemiSky), hemiGround: new THREE.Color(hemiGround), hemiI, star, glow: new THREE.Color(glow), glowAmt });
// glow = a horizon-glow colour painted along the skyline (Solana-tinted at the golden/sunset/twilight
// keys: gold → magenta → purple), glowAmt = its strength. The night keys carry no glow (the aurora owns the night).
// Player-tuned daylight balance: night (twilight→midnight→dawn) squeezed to ~19% of the cycle —
// playtesters hated long nights ("o mapa sempre tá de noite"). Day/golden/sunset own ~81%.
const SKY_KEYS: SkyKey[] = [
  skyKey(0.00, 0x1c2850, 0x0c1c38, 0x1a466c, 0x90a0d8, 0.22, 0x6e82ba, 0x1e2e4c, 0.95, 1.0,  0x24407e, 0.0),  // midnight (moonlit blue, NOT black)
  skyKey(0.12, 0x6a73a4, 0x123048, 0x32587a, 0xffb070, 0.60, 0x93a4cc, 0x33434f, 0.95, 0.30, 0xff7ea8, 0.78), // dawn (pink glow)
  skyKey(0.22, 0xa9c8e6, 0x0a3a54, 0x1f86ad, 0xfff0d0, 1.35, 0xbfe3ff, 0x274a5a, 1.05, 0.0,  0xcfe6ff, 0.18), // morning
  skyKey(0.50, 0x8ec5e6, 0x0a3a54, 0x1f86ad, 0xfff2d8, 1.55, 0xbfe3ff, 0x274a5a, 1.10, 0.0,  0xd6eeff, 0.12), // noon (original daylight)
  skyKey(0.72, 0x9fb2cc, 0x123a50, 0x3a7aa0, 0xffd9a0, 1.30, 0xdcc6ae, 0x3a3a4a, 0.95, 0.0,  0xffb265, 0.78), // golden hour (cool zenith, gold horizon)
  skyKey(0.84, 0x4a3a72, 0x24304f, 0x5a5a86, 0xff7a4a, 0.70, 0xcf8f8f, 0x342f42, 0.82, 0.20, 0xcf6a86, 0.9),  // sunset (purple zenith → muted rose horizon)
  skyKey(0.93, 0x2e2c5a, 0x142a4a, 0x2a4a7e, 0x9a8ac8, 0.42, 0x70669e, 0x1e2844, 0.92, 0.55, 0x8e5cff, 0.78), // twilight (deep-blue zenith → purple horizon)
  skyKey(1.00, 0x1c2850, 0x0c1c38, 0x1a466c, 0x90a0d8, 0.22, 0x6e82ba, 0x1e2e4c, 0.95, 1.0,  0x24407e, 0.0),  // → midnight (wrap)
];

/* ======================== 🎨 WATER PALETTE — A/B exploration rig (temporary) ========================
 * The sea's colour is not one number: it is a CHAIN — the body gradient (uDeep→uShallow, keyed per
 * time-of-day next to the sky), how much of the sky the surface mirrors (the fresnel weight), the
 * light that travels THROUGH a crest toward the eye (the subsurface term — the "stained glass"), the
 * white the foam mixes toward, and the colour the water reaches at the sand. Change one and the sea
 * does not change palette, it changes only mood; change all five together and it becomes a different
 * ocean. This block is the whole chain, per variant, in one place.
 *
 * SELECTED AT RUNTIME (`?palette=jade`, or `__cc.renderer.setWaterPalette('jade')`) so all four can be
 * photographed on the SAME FRAME — same wave phase, same camera, same sun — which is the only way an
 * A/B of colour is worth anything. Every variant is UNIFORM VALUES ONLY: one shader, one program, one
 * draw call, identical `stats()`. Nothing here touches the wire or the sim.
 *
 * ⚠️ TEMPORARY. This is an exploration rig for the owner to point at. Once a variant is chosen it gets
 * promoted into SKY_KEYS + the shader constants and this block is deleted — do not build on it.
 */
interface WaterPalette {
  /** Sea body colour [deep, shallow] at EACH SKY_KEYS index — authored per key, not hue-rotated, because
   *  the night keys need their own restraint (a saturated jade lerped into midnight prints radioactive
   *  lime; a jade authored AT midnight is a dark teal that still reads as night). */
  keys: [THREE.Color, THREE.Color][];
  /** How far the shoal colour is pulled to wet sand. Lower = the shallows keep more of the sea's own hue. */
  sandMix: number;
  /** Multiplier on the fresnel sky-reflection weight. 1 = classic (the sky is most of the surface at
   *  grazing angles); < 1 = the water asserts its OWN colour instead of mirroring the sky. */
  fresK: number;
  /** The subsurface crest glow, as a MULTIPLIER on the live shallow colour (tint x gain, premultiplied at
   *  module load). This is the "stained glass" knob — the single biggest lever between a Caribbean sea and
   *  a Sea-of-Thieves one. Deliberately a multiplier and NOT an absolute colour: the Draga overlay builds
   *  its own ocean material and hand-sets a near-black uShallow without ever running updateSky, so an
   *  absolute subsurface colour would have leaked this rig's daylight blue into the wreck dive. A
   *  multiplier of white is the identity for anyone who never opts in. */
  sssK: THREE.Color;
  /** The white the crest sheen and the steep-face whitening mix toward BY DAY (night keeps its cold blue,
   *  deliberately: a warm foam at midnight is what turns a green sea radioactive). */
  foam: THREE.Color;
  /** How hard a full gale drags the palette back to the common storm sea. A storm is a storm in every
   *  palette — the menace is the POINT, and a cheerful turquoise gale is not menacing. */
  stormConverge: number;
}
/** The one sea a full gale converges toward, whatever palette is flying: lead, with a green-grey cast. */
const STORM_SEA_DEEP = new THREE.Color(0x11242c);
/** The subsurface multiplier a gale converges to: plain uShallow, i.e. the shipped behaviour. */
const SSS_IDENTITY = new THREE.Color(0xffffff);
const STORM_SEA_SHALLOW = new THREE.Color(0x2c4a52);
/** Premultiply the subsurface tint by its gain once, at module load. The result is a plain rgb multiplier
 *  and may legitimately exceed 1 — a stained-glass crest is BRIGHTER than the water it is made of. */
const sssK = (tint: number, gain: number): THREE.Color => new THREE.Color(tint).multiplyScalar(gain);
const wpKeys = (...hex: number[]): [THREE.Color, THREE.Color][] => {
  const out: [THREE.Color, THREE.Color][] = [];
  for (let i = 0; i < hex.length; i += 2) out.push([new THREE.Color(hex[i]!), new THREE.Color(hex[i + 1]!)]);
  return out;
};
export type WaterPaletteName = 'classic' | 'jade' | 'tropical' | 'abyss';
const WATER_PALETTES: Record<WaterPaletteName, WaterPalette> = {
  // ---- the sea as shipped. Bit-identical to HEAD: the deep/shallow pairs ARE SKY_KEYS', fresK 1,
  //      the subsurface is plain uShallow at gain 1, and a gale converges nowhere because it is already
  //      the palette the storm grade was authored against. This is the control frame.
  classic: {
    // DERIVED from SKY_KEYS, never transcribed: the control frame cannot be allowed to drift from the
    // shipped sea because someone retuned a sky key and forgot this table existed.
    keys: SKY_KEYS.map((s) => [s.deep, s.shallow] as [THREE.Color, THREE.Color]),
    sandMix: 0.52, fresK: 1.0,
    sssK: sssK(0xffffff, 1.0),
    foam: new THREE.Color(0xebf5ff), stormConverge: 0.0,
  },
  // ---- JADE — the Sea-of-Thieves lean. Three moves, in order of how much they matter:
  //      (1) the subsurface goes GREEN and gets 2.4x the gain — the translucent stained-glass crest is
  //          the whole trick, and it is a LIGHT term, not a body colour, so it only fires where the sun
  //          is actually behind a wave face. That is why SoT water reads as lit glass and not as paint.
  //      (2) fresnel down to 0.62 — the sky stops owning the surface, so the sea can have a colour.
  //      (3) the body itself goes jade, and the shallows a vivid turquoise, with sandMix pulled back so
  //          the beach band stays luminous instead of grading straight to sand.
  //      Night is authored DARK TEAL, not dark jade, and the foam's cream is day-only — together those
  //      are what stop midnight going lime. A gale converges hard (0.72): jade is a fair-weather sea.
  jade: {
    keys: wpKeys(
      0x08201f, 0x143f48,  // midnight — dark teal, still reads as night
      0x0e3336, 0x2c6a6a,  // dawn
      0x06413a, 0x25b89a,  // morning — jade body, vivid turquoise shallow
      0x06413a, 0x25b89a,  // noon
      0x0d3f3e, 0x37a08f,  // golden hour — jade holding under warm light
      0x1c3a42, 0x4c7f80,  // sunset — muted, so it does not fight the purple sky
      0x102a36, 0x255e64,  // twilight
      0x08201f, 0x143f48), // → midnight (wrap)
    sandMix: 0.46, fresK: 0.62,
    sssK: sssK(0x8cffd0, 2.4),
    foam: new THREE.Color(0xfff4e0), stormConverge: 0.72,
  },
  // ---- TROPICAL — the middle road: our sea, more inviting. Caribbean turquoise body, the stained-glass
  //      crest at a moderate 1.55x (present, not the subject), fresnel only slightly off classic so the
  //      sky still lives in the water and the existing sky/storm art keeps working unchanged.
  tropical: {
    keys: wpKeys(
      0x0a2340, 0x184a63,  // midnight
      0x10334a, 0x2f6076,  // dawn
      0x074457, 0x1ea6b4,  // morning — caribbean turquoise
      0x074457, 0x1ea6b4,  // noon
      0x11414f, 0x3894a2,  // golden hour
      0x233250, 0x567f8c,  // sunset
      0x132c48, 0x2a5a78,  // twilight
      0x0a2340, 0x184a63), // → midnight (wrap)
    sandMix: 0.50, fresK: 0.85,
    sssK: sssK(0xc8ffe8, 1.55),
    foam: new THREE.Color(0xf4faff), stormConverge: 0.45,
  },
  // ---- ABYSS — the opposite pole, on purpose. Deep saturated ocean blue, a COLD crest (the subsurface
  //      tinted blue and pulled BELOW classic), and fresnel pushed ABOVE 1 so the sea is more mirror than
  //      body: a hard, cold, open-ocean look. Included so the owner can see both ends of the axis and
  //      point at a place on it, not just at a picture.
  abyss: {
    keys: wpKeys(
      0x050f2e, 0x102a5c,  // midnight
      0x0b1e42, 0x1f3f70,  // dawn
      0x041c4e, 0x0f4f9e,  // morning — deep saturated blue
      0x041c4e, 0x0f4f9e,  // noon
      0x0a2350, 0x1f5590,  // golden hour
      0x1a2350, 0x3d4a80,  // sunset
      0x0c1c44, 0x1c3c74,  // twilight
      0x050f2e, 0x102a5c), // → midnight (wrap)
    sandMix: 0.55, fresK: 1.12,
    sssK: sssK(0x9ec8ff, 0.8),
    foam: new THREE.Color(0xe6f0ff), stormConverge: 0.25,
  },
};
/** `?palette=jade` — exploration only; anything unknown (and the absent case) is the shipped sea. */
const resolveWaterPaletteName = (): WaterPaletteName => {
  try {
    const v = new URLSearchParams(location.search).get('palette');
    if (v && v in WATER_PALETTES) return v as WaterPaletteName;
  } catch { /* no location (tests/SSR) — classic */ }
  return 'classic';
};

const SKY = new THREE.Color(0x8ec5e6);
/** Legacy sea span — still the scale the scene fog and the Draga overlay's own flat sea are tuned to.
 *  The PLAYABLE sea is no longer a plane of this size: it is the camera-following clipmap in oceanmesh.ts. */
const OCEAN_SPAN = 1500;
/** Distance-cull radius (m) for static world groups (islands + outposts). On the 8 km "The World" map
 *  there are ~60 islands; hiding the far ones keeps draw calls + per-frame beacon work in budget (their
 *  minimap markers still show where they are). Well beyond the ocean's visible horizon, so it never pops. */
const WORLD_CULL_DIST = 1600;
const WORLD_CULL_D2 = WORLD_CULL_DIST * WORLD_CULL_DIST;
/** Onda Portos Livres — a tighter distance cull (m) for the HEAVY exterior kit meshes (a hero citadel is
 *  100-400k tris). Frustum culling already frees off-screen ports; this frees the on-screen-but-far case so a
 *  citadel near the horizon costs nothing. Just inside the port-group cull (1600 m), so the pop is imperceptible
 *  (the piece is a few px near the horizon) yet a 2 km citadel never draws. */
const KIT_EXT_CULL_DIST = 1500;
const KIT_EXT_CULL_D2 = KIT_EXT_CULL_DIST * KIT_EXT_CULL_DIST;
/** Hero-building LOD — the 5 heavy harbour dioramas (100-400k tris) the owner uses as port FAÇADES ship a
 *  decimated `.lod.glb` (~20% tris, silhouette-preserving). The sea view + the walkable port swap the LOD in past
 *  ~400 m and the full mesh within ~390 m; the 30 m hysteresis band (mirroring the ship LOD) stops a port hovering
 *  at the switch distance from flickering. Derived from KIT_URL by swapping `.opt.glb`→`.lod.glb`. */
const KIT_LOD_FAR_D2 = 420 * 420;   // beyond this the outpost draws its LOD meshes
const KIT_LOD_NEAR_D2 = 390 * 390;  // within this it draws the full meshes (30 m hysteresis in between)
/** Base-island footprint (== server ISLAND_R) + a margin, used to keep the camera out of the land. */
const BASE_ISLAND_R = BASE.GRID * BASE.CELL_SIZE * 0.62;
const CAM_ISLAND_MARGIN = 7;
/** Impact dolly-punch feel (all CALIBRABLE). RAMP/DECAY are per-second bases fed to Math.pow(base, dt):
 *  RAMP = how fast the applied kick chases the target (smaller = snappier rise; 0.00002 ≈ full in ~100 ms);
 *  DECAY = how fast the impulse fades (0.02 ≈ gone in ~0.5 s). MOBILE_SCALE trims the magnitude on phones. */
const CAM_PUNCH = { RAMP: 0.00002, DECAY: 0.02, MOBILE_SCALE: 0.8 } as const;
/** Treasure-islet render tuning (Onda de Arte — Ilhas de Tesouro). The Meshy islets are ~1:1 proportioned
 *  (a chunky mound + a tall palm), so they are scaled NON-UNIFORMLY: the footprint fills to ~the island
 *  radius (so the sand covers the chests, which the server scatters out to 50 m) while the height is pinned
 *  LOW so a loot islet reads like a flat sandbar seen from the sea, never a mountain (the retired procedural
 *  cone was 112 m wide × 9 m tall). Sunk a touch so wave crests wash the beach edge. */
const ISLE = {
  FOOT_MULT: 1.15,   // island footprint (max width) = treasure-island radius × this (~52 m) — a compact "minimal" islet
  SINK: -1.2,        // metres the base sits below the waterline (waves lap the beach; hides the sand/water seam)
  VS_COCONUT: 1.0,   // vertical scale factor — UNIFORM: the palm only reads right undistorted (any squash pancakes it)
  VS_SAND: 0.7,      // the bare sandbar flattens a little (no palm to protect) → a lower dune
  SAND_FRAC: 0.42,   // (legacy) sand-dome top as a fraction of the rendered island height — kept for reference
  // --- sand apron (Onda Ilhas-Tipo, frente 1) ---------------------------------------------------------
  // The server scatters each island's chests over a ~20..50 m ring (worldgen.ts) but the compact GLB islet
  // only covers ~26 m, so ~79% of chests floated on open water. A wide, low sand apron under the islet gives
  // real sand under every chest; the GLB islet mound + palm sits on top as the hero centrepiece. The apron
  // is render-only (sim/worldgen untouched); chests seat on it by a downward raycast, never a guessed slope.
  BEACH_MULT: 1.45,  // apron radius = island radius × this (~65 m) — comfortably past the ~50 m chest scatter
  BEACH_RISE: 3.4,   // apron height at the islet centre (m); tapers to the waterline out at the rim
  BEACH_SINK: 1.5,   // the apron rim sits this far BELOW the waterline so sand meets sea with no hard seam
  SEAT_CAP: 15,      // raycast ceiling (m) when seating a chest — catches the sand/mound but skips the tall palm
  SEAT_LIFT: 0.15,   // the chest base rests this far above the sampled sand (kills z-fighting on the slope)
  WATER_Y: -0.7,     // fallback only: a chest with no sand under it at all rests here, bobbing at the waterline
} as const;
const OCEAN_DISP_MAX = 6;   // max hulls that displace the water in the ocean shader (nearest to camera)
/**
 * Shores the sea shades at once (the N nearest islands). Four is not a compromise: the whole effect lives
 * inside ~45 m of a waterline, and there is nowhere on the map you can be that close to five islets. Each
 * costs one vec4 of FRAGMENT uniform budget, which is the budget the weak-device probe guards.
 */
const SHORE_MAX = 4;
/** Metres of sea, out from the waterline, that the shore treatment reaches (colour ramp + swash ring). */
const SHORE_REACH = 46;
/** Wet sand — the colour the shoaling water is graded toward. Deliberately the apron material's own tone
 *  pulled a touch green, so the last metre of sea and the first metre of beach are nearly the same pixel. */
const SHOAL_SAND = new THREE.Color(0xbcc9a8);

/**
 * The sea's value noise — declared ONCE and included in BOTH shader stages.
 *
 * It lives here rather than inline in the fragment because the cat's-paw field is now evaluated per
 * VERTEX (see `vPaw`): a 170 m noise field on a mesh whose finest cells are metres across is perfectly
 * represented by the rasteriser's own interpolation, and moving it off the ~1.4 M pixels of a 1600x900
 * frame onto the ~22 k vertices of the clipmap is a 60x reduction in the transcendentals it costs. The
 * two stages must sample the IDENTICAL field, so there is one copy of it.
 */
const OCEAN_NOISE_GLSL = /* glsl */ `
        // cheap value noise — the cat's paws, the storm's broken foam, the LOW path's foam break-up,
        // the shore swash and the rain dimples are all built from it (no textures, no lookups)
        //
        // PRECISION-SAFE CELL HASH. The classic fract(sin(·) * 43758.5) needs the product to keep a
        // fractional part; where the float spacing has grown past 1 (fp16 at ~44 000: spacing 32) the
        // fraction is IDENTICALLY ZERO and every cell hashes the same — the noise becomes a constant and
        // everything built on it flattens. Both stages here ask for highp, but 'precision highp float' is
        // a REQUEST: on a GLES2-class device without highp support it silently degrades to mediump, and
        // that is exactly the device the cheap sea exists for. This form keeps every intermediate inside
        // ~[0, 100) so it degrades gracefully instead of collapsing. Same hash the storm kit and the lens
        // spatter carry, for the same reason. Cost: 3 fract + 1 dot instead of 1 sin — a wash.
        float oHash2(vec2 p){
          vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
          q += dot(q, q.yzx + 33.33);
          return fract((q.x + q.y) * q.z);
        }
        float oNoise(vec2 p){
          vec2 i = floor(p), fr = fract(p);
          // QUINTIC interpolant, not the cubic smoothstep. Value noise sits on an axis-aligned lattice;
          // smoothstep leaves a second-derivative crease at every cell boundary, and a hard threshold on
          // top of that prints the lattice as a QUILT OF RECTANGLES — which is exactly what a full gale's
          // whitecaps did to the swell (rows of little white bricks). Quintic is C2 across the boundary,
          // so there is no crease left for a threshold to find. 4 extra ALU, and every consumer of this
          // noise (paws, foam, break-up tails, rain dimples) gets it.
          fr = fr * fr * fr * (fr * (fr * 6.0 - 15.0) + 10.0);
          float a = oHash2(i), b = oHash2(i + vec2(1.0, 0.0)), c = oHash2(i + vec2(0.0, 1.0)), dd = oHash2(i + vec2(1.0, 1.0));
          return mix(mix(a, b, fr.x), mix(c, dd, fr.x), fr.y);
        }
        /** Rotate a noise domain ~34 deg. Stacking octaves on ONE lattice makes every octave's grid line
         *  up; rotating between them decorrelates the lattices so no grid survives the sum. */
        vec2 oRot(vec2 p){ return vec2(p.x * 0.8253 + p.y * 0.5646, p.y * 0.8253 - p.x * 0.5646); }
`;

/**
 * 🌌 AURORA BOREALIS — the shared GLSL, compiled into BOTH the sky dome and the sea.
 *
 * ONE FUNCTION, TWO RAYS. The curtains overhead and the road on the water are the identical curtain
 * evaluated down the view ray and down the REFLECTED view ray. That is not a saving, it is the whole
 * trick: a sky effect with a hand-authored "reflection" drifts out of agreement the moment either side
 * is touched, and the eye catches it instantly on a moving surface. (Same argument as the moon: the
 * disc in the sky and the road on the water read the same vector, so no player can ever catch them
 * disagreeing.)
 *
 * THE ANATOMY, and why it is not a stripe.
 *   • Everything is expressed in a POLAR FRAME AROUND THE MAGNETIC ZENITH — a fixed WORLD direction,
 *     high in the north (AURORA.ZENITH_*). `rho` is the angle from it, `ph` the bearing around it.
 *   • The RAYS are a function of `ph` ALONE. Lines of constant bearing all point at the zenith, so the
 *     striations fan out from one place in the sky exactly the way the field lines they trace do. Put a
 *     single `rho` term in the ray function and they turn parallel — which IS the stripe.
 *   • The lower border is SHARP and the tail is LONG: `smoothstep` over a couple of degrees below it,
 *     a decaying exponential for tens of degrees above. That is the physics (the border is the altitude
 *     the electron beam stops at, the tail is that beam thinning into nothing) and it is also the single
 *     silhouette cue that separates "a curtain hanging above me" from "a band painted on a dome".
 *   • Three of them, at non-harmonic heights, scales and drift rates, sharing ONE slow domain warp, and
 *     broken into segments by one very low-frequency ride around the field. A curtain that runs
 *     unbroken all the way round the sky is a ring, and a ring seen from a deck is a stripe again.
 *
 * SEAMLESS BY CONSTRUCTION. Every noise here is sampled on a CIRCLE through the field — `auArc1/2` walk
 * `vec2(cos ph, sin ph) * R` — so the pattern is exactly 2π-periodic in the bearing and closes on
 * itself. Sampling noise at a linear `ph` tears along the ±π meridian: a straight bright line running
 * from the zenith to the horizon, which is the most amateur thing an aurora can do. The rays use
 * INTEGER harmonics of `ph` for the same reason.
 *
 * COST. ALU only, no textures, no draws, and both call sites sit behind a compare that is false all day
 * plus a screen-coherent northern-sky reach test. High sky path: 7 noise + 5 sines. Low sky path: 2
 * noise + 3 sines. Sea: 2 noise, band only.
 *
 * ------------------------------------------------------------------------------------------------
 * THE CALL SITES, and every decision in them. (The prose lives HERE and not in the shader strings on
 * purpose: a comment inside a template literal is shipped to every player in the bundle, a comment out
 * here is stripped by the minifier. The shader keeps one-line markers that point back at this block.)
 *
 * SKY — `buildSkyDome`, section 4b. Computed BEFORE the stars so the star field can be occluded by the
 * curtain it is behind, added AFTER them so the curtain reads as light in front of the stars.
 *   • `reach` IS THE COST STORY. The display lives in a cone around the magnetic zenith, on the northern
 *     half of the sky, clear of the horizon haze. Look south, look down at the deck, or sail by day, and
 *     the expensive branch is false for every pixel on screen — screen-coherent, the only kind a GPU
 *     genuinely skips.
 *   • `warp` is ONE slow domain warp shared by all three curtains: they are folds of the same sheet of
 *     plasma and must wander together. Its amplitude is bounded under its own slope so the bearing
 *     mapping stays monotonic — a folded bearing pinches the curtain into a knot. The low path skips it
 *     (with one curtain there is nothing for a shared warp to keep in agreement).
 *   • `seg` / `gap0` / `gap1` are THE GAPS: one very low-frequency ride round the field breaks the
 *     display into a couple of bright segments with dark sky between them. Without it the curtain closes
 *     into a continuous ring, and a ring seen from a deck is exactly the stripe this wave exists to kill.
 *     They never reach zero, though: a curtain that switches OFF leaves a hard end, and two curtains
 *     switching off in antiphase leave the sky flipping between two lines. They breathe instead.
 *   • RAY ANTIALIASING WITHOUT A DERIVATIVE. The rays' angular pitch collapses as sin(rho) → 0 toward the
 *     zenith, so their footprint in bearing is (pixel angle)/sin(rho); `uAurPx` is the camera's own pixel
 *     angle — exact, one uniform, and unlike fwidth(atan) it cannot blow up on the ±π meridian (that
 *     blow-up would print a bright line from the zenith to the horizon: the seam the polar construction
 *     exists to avoid). `ray` is clamped because the harmonics sum past ±1 and an unclamped negative ray
 *     SUBTRACTS light, which reads as a black scratch across the curtain.
 *   • THREE LAYERS. (1) the main curtain — nearest, brightest, fully rayed, with the ray contrast eased
 *     out at the very bottom so the incandescent border stays a continuous LINE and not a comb; (2) a
 *     second curtain further off, taller, softer, drifting the other way, living where the first thins —
 *     two distances is what gives the sky depth; (3) a faint high drape, no rays, no gaps, biased up the
 *     colour ramp into the violet, which fills the sky BETWEEN the two sharp curtains. Without (3) they
 *     read as two lines with nothing in between: the postcard-aurora tell.
 *   • LOW PATH: one curtain, no second layer, no water reflection — but it KEEPS striations, which is a
 *     deliberate departure from "no rays on low". They are three sines of the bearing and nothing else
 *     (no noise, no lookup, ~10 ALU), and without them a single soft band photographs as exactly the
 *     stripe above. The cost of the low path is in the noise, not in the sines.
 *   • DITHER, then PEAK ROLL. This is the largest, smoothest gradient in the game and an 8-bit ramp
 *     across a 40° exponential tail prints concentric contour rings you cannot unsee. Then: three
 *     additive curtains can stack on one pixel, and a highlight far past the bloom pass's 0.82 threshold
 *     comes back as a soft-edged BOX with straight sides (see the sun-disc note in buildSkyDome). Below
 *     78% of the ceiling the roll factor is exactly 1.0 — no loss anywhere the aurora is quiet — and
 *     above it the sum asymptotes to the ceiling instead of running off the end of the blur kernel.
 *
 * SEA — `buildOceanMaterial`, right after the moon path. The same curtain function evaluated down the
 * REFLECTED ray, so the green-to-violet band on the water IS the band overhead: one function, two rays.
 * It lands where it should for free — a mirror reflects sky at elevation θ into water at depression θ,
 * so a display hanging 10°–35° up comes back in the water thirty-odd metres out, i.e. right around the
 * hull, instead of pooling at the horizon.
 *   • BAND ONLY, no rays: a striation is finer than the reflected pixel footprint on moving water, so
 *     drawing them there buys sparkle noise, not detail.
 *   • It reflects off a SMOOTHED normal (55% toward vertical). The curtain is a soft, enormous source;
 *     mirrored in the real per-pixel wave normal it came back as a mesh of bright green wires on the
 *     crests — photographed, it read as electric cable, not as light on water.
 *   • The curtain is evaluated with a LONGER, SOFTER tail than the sky's. Not a cheat: a reflection in
 *     moving water is a blurred reflection, and the blur is what carries the road TOWARD the viewer.
 *     With the sky's own tail the road stopped forty metres out and the foreground stayed black.
 *   • …and it is broken by the same `livePatch` field that breaks the moon road. An unbroken reflection
 *     is a decal; a broken one is water.
 */
const AURORA_GLSL = /* glsl */ `
        // fp16-safe cell hash (see the ocean/wake/storm note: fract(sin*43758.5) flattens in mediump)
        float auHash(vec2 p){
          vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
          q += dot(q, q.yzx + 33.33);
          return fract((q.x + q.y) * q.z);
        }
        float auNoise(vec2 p){
          vec2 i = floor(p), f = fract(p);
          f = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);   // quintic: no C1 crease for the long tail to magnify
          return mix(mix(auHash(i), auHash(i + vec2(1.0, 0.0)), f.x),
                     mix(auHash(i + vec2(0.0, 1.0)), auHash(i + vec2(1.0, 1.0)), f.x), f.y);
        }
        // a noise ride around a CIRCLE in the field: exactly 2pi-periodic in the bearing, so no seam
        float auArc1(float ph, float R, float rot){
          return auNoise(vec2(cos(ph + rot), sin(ph + rot)) * R + 11.3);
        }
        // two octaves, lacunarity 2.15 (never 2.0 — that re-registers the lattice and prints a grid)
        float auArc2(float ph, float R, float rot){
          vec2 c = vec2(cos(ph + rot), sin(ph + rot));
          // the fine octave stays QUIET: a loud one on a BORDER reads as a saw (measured: a green
          // mountain range). An aurora's high frequency belongs to the rays, which run ACROSS the border.
          return auNoise(c * R + 11.3) * 0.74 + auNoise(c * (R * 2.15) + 57.1) * 0.26;
        }
        // ONE CURTAIN at bearing ph, given where its lower border sits (b, in rho). h = tail length,
        // edge = border hardness. Returns (density, height 0..1 → the colour, hot-rim → the border line).
        vec3 auCurtain(float rho, float b, float h, float edge){
          float s = b - rho;                     // > 0 = above the border, toward the zenith
          float lo = smoothstep(-edge, 0.0, s);   // SHARP below, LONG above: the border is the one hard line
          return vec3(lo * exp(-max(s, 0.0) / h),
                      clamp(max(s, 0.0) / h, 0.0, 1.0),
                      lo * exp(-max(s, 0.0) / (h * 0.13)));
        }
        // the colour IS the altitude: 557 nm green where the beam stops, nitrogen blue mid, 630 nm top
        vec3 auColour(vec3 lo, vec3 mid, vec3 hi, float h){
          return h < 0.5 ? mix(lo, mid, h * 2.0) : mix(mid, hi, h * 2.0 - 1.0);
        }
        // THE RAYS: striations along the field lines => a function of the BEARING ALONE (any rho term
        // and they stop converging). Integer harmonics => 2pi-periodic => no seam. nerv = the breakup.
        float auRays(float ph, float t, float nerv){
          // five mutually-prime harmonics, fundamental deliberately NOT the loudest: a dominant low
          // harmonic photographed as a picket fence. No single spacing for the eye to lock onto.
          return 0.34 * sin(23.0 * ph + t * 0.17 * nerv + 1.7)
               + 0.30 * sin(43.0 * ph - t * 0.29 * nerv + 4.1)
               + 0.23 * sin(89.0 * ph + t * 0.47 * nerv + 2.3)
               + 0.16 * sin(151.0 * ph - t * 0.71 * nerv + 5.6)
               + 0.11 * sin(233.0 * ph + t * 0.95 * nerv + 0.9);
        }
        // interleaved-gradient dither — the anti-banding for the tail (one fract chain)
        float auDither(vec2 fc){
          return fract(52.9829189 * fract(dot(fc, vec2(0.06711056, 0.00583715)))) - 0.5;
        }
`;

const D2R = Math.PI / 180;
/** AURORA.CURTAIN baked into GLSL literals: the config stays the single source of truth and the shader
 *  spends zero uniforms on numbers that never change at runtime. (border, wander, tail, edge — all in
 *  radians from the magnetic zenith; R = the arc's scale; d = its own drift rate.) */
const AUR_C = AURORA.CURTAIN.map((c) => ({
  b: (c[0] * D2R).toFixed(4), w: (c[1] * D2R).toFixed(4), h: (c[2] * D2R).toFixed(4),
  e: (c[3] * D2R).toFixed(4), R: c[4].toFixed(2), d: c[5].toFixed(5),
}));
/**
 * THE MAGNETIC ZENITH and its tangent frame, in WORLD space (the sim's north, +y, is world +Z). Every
 * ray converges on `z`; `t`/`b` are the two axes the bearing around it is measured in. It is a WORLD
 * direction and never a camera one — which is what turns the display from wallpaper into a landmark:
 * the aurora is in the north, so a captain who can see it can steer by it.
 */
const AUR_FRAME = (() => {
  const el = AURORA.ZENITH_ELEV_DEG * D2R, az = AURORA.ZENITH_BEARING_DEG * D2R;
  const z = new THREE.Vector3(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el)).normalize();
  const t = new THREE.Vector3(0, 1, 0).cross(z).normalize();   // due east of the zenith
  const b = new THREE.Vector3().crossVectors(z, t).normalize();
  return { z, t, b };
})();
/**
 * THE ONE COLOUR-SPACE CORRECTION IN THIS FILE, and it is deliberate.
 *
 * Both shaders that draw the aurora work in an sRGB-ISH space and linearise ONCE at the very end
 * (`pow(col, 2.2)` → the tonemapped composer), while three.js's colour management has ALREADY
 * linearised anything built from a hex. Every other colour in those shaders therefore carries a DOUBLE
 * transform — that is the look the sky and the sea were authored against, and touching it would restyle
 * the whole game. But this palette is a BRAND palette: #14F195 has to arrive on screen as #14F195, not
 * as the fluorescent primary a second gamma makes of it (measured: the green channel survives at 0.75
 * while red collapses to 1e-5, i.e. a pure clipped primary with no mint left in it — photographed, it
 * read as one flat green blob).
 *
 * So these three, and only these three, are PRE-COMPENSATED: raised to 1/2.2 here so the shader's own
 * pow puts them back exactly where the hex says. The rim colour below is NOT — it is handed to a
 * three.js light, which consumes linear directly.
 */
const auroraColour = (hex: number): THREE.Color => {
  const c = new THREE.Color(hex);
  return c.setRGB(Math.pow(c.r, 1 / 2.2), Math.pow(c.g, 1 / 2.2), Math.pow(c.b, 1 / 2.2));
};
const AUR_LO = auroraColour(AURORA.COL_LOW);
const AUR_MID = auroraColour(AURORA.COL_MID);
const AUR_HI = auroraColour(AURORA.COL_HIGH);
/** The colour the hemisphere ambient leans to while the curtains are up (the mint rim on deck/canvas). */
const AUR_HEMI = new THREE.Color(AURORA.RIM_COL);
const HALF_L = SHIP.LENGTH / 2;
const HALF_B = SHIP.BEAM / 2;
/** World up — the axis the masthead pennants yaw about to face the wind. */
const PENNANT_UP = new THREE.Vector3(0, 1, 0);
const TETHER_SEGMENTS = 14; // grapple rope is a multi-segment polyline so it can sag + sway
/**
 * Per-ShipClassId GLB model. Each is normalized to the same base hull length in
 * loadShipModel, then scaled per ship by classScale(cls) — so distinct silhouettes
 * (sloop 1 mast / brigantine 2 / galleon 3) at the correct per-class size.
 *   yaw   — rotate so the model's bow faces +X (per-model: Meshy bow varies)
 *   draft — metres the keel sits BELOW the waterline (absolute; independent of mast height
 *           so tall rigging never lifts the hull out of the water — see loadShipModel)
 */
const SHIP_MODELS: Record<number, { url: string; yaw: number; draft: number }> = {
  // yaw = π: the Meshy concepts were generated bow-toward -X, so without this the hull sails STERN-FIRST
  // (and the baked billowed sails point backward). Rotating 180° puts the bow at +X = the heading/forward dir.
  [ShipClassId.SLOOP]:      { url: '/models/sloop/sloop.opt.glb',           yaw: Math.PI, draft: 1.2 },
  [ShipClassId.BRIGANTINE]: { url: '/models/brigantine/brigantine.opt.glb', yaw: Math.PI, draft: 1.2 },
  [ShipClassId.GALLEON]:    { url: '/models/galleon/galleon.opt.glb',       yaw: Math.PI, draft: 1.2 },
  [ShipClassId.FRIGATE]:    { url: '/models/frigate/frigate.opt.glb',       yaw: Math.PI, draft: 1.2 },
  // Onda Frota (v47): three new hulls (same Meshy bow-toward-−X convention, so yaw π). Sails ship FURLED
  // (static) — the animated-sail rebake is an asset-pipeline follow-up; a missing furlClip just means no trim anim.
  [ShipClassId.CUTTER]:        { url: '/models/cutter/cutter.opt.glb',               yaw: Math.PI, draft: 1.2 },
  [ShipClassId.FLUYT]:         { url: '/models/fluyt/fluyt.opt.glb',                 yaw: Math.PI, draft: 1.2 },
  [ShipClassId.EAST_INDIAMAN]: { url: '/models/east_indiaman/east_indiaman.opt.glb', yaw: Math.PI, draft: 1.2 },
};

/** Boot-diet phase split (Onda First-60s). The CRITICAL hulls are loaded BEFORE the first sea frame: the
 *  newcomer default (SLOOP), the universal GALLEON fallback template, and the two other common classes. The
 *  RARE hulls — CUTTER, FLUYT, and the owner-exclusive EAST_INDIAMAN (~1.6 MB together, which a first-session
 *  captain almost never sees) — are DEFERRED to the world-asset phase (kicked at spawn / idle). A ship of a
 *  not-yet-loaded class is NEVER invisible: getShipTemplate falls back to the GALLEON template scaled to the
 *  class dims (render.ts ~3266/3810), so a deferred hull shows a stand-in silhouette and upgrades in place the
 *  instant its own GLB lands. Captains ride the deferred phase too (cosmetic deck figures). This trims the
 *  first-visit critical path by ~2.5 MB (the 3 rare hulls + the 4 captains) with no gameplay-visible gap. */
const CRITICAL_SHIP_CLASSES: number[] = [ShipClassId.SLOOP, ShipClassId.BRIGANTINE, ShipClassId.GALLEON, ShipClassId.FRIGATE];
const DEFERRED_SHIP_CLASSES: number[] = [ShipClassId.CUTTER, ShipClassId.FLUYT, ShipClassId.EAST_INDIAMAN];

/**
 * Optional GLB props (base buildings + treasure). Each is normalized to a target
 * footprint (max horizontal dim) with its base resting at y=0; a missing/failed
 * load falls back to the procedural mesh.
 */
/** `ember` (Onda Sistema de Luz): a GLB whose baked emissive map should BREATHE with the day/night cycle —
 *  its template materials are registered once with the FakeLight system (lights.ts registerEmissive), so the
 *  glow is a faint hint by day and blazes after dusk. Applies to every clone (they share the template mats). */
const PROP_MODELS: Record<string, { name: string; url: string; size: number; yaw: number; ember?: { day: number; night: number } }> = {
  hq:      { name: 'hq',      url: '/models/hq/hq.opt.glb',           size: 15, yaw: 0 }, // the fort — biggest building
  turret:  { name: 'turret',  url: '/models/turret/turret.opt.glb',   size: 9,  yaw: 0 },
  chest:   { name: 'chest',   url: '/models/chest/chest.opt.glb',     size: 3,  yaw: 0 },
  island:  { name: 'island',  url: '/models/island/island.opt.glb',   size: 64, yaw: 0 }, // the base terrain
  dock:    { name: 'dock',    url: '/models/dock/dock.opt.glb',       size: 22, yaw: 0, ember: { day: 0.25, night: 1.25 } }, // the pier/slip (baked lamp emissive breathes with the night)
  outpost: { name: 'outpost', url: '/models/outpost/outpost.opt.glb', size: 42, yaw: 0 }, // at-sea trading post (sell zone)
  drydock: { name: 'drydock', url: '/models/drydock/drydock.opt.glb', size: 20, yaw: 0 }, // enclosed boathouse (dock lvl>=DRYDOCK_LVL)
  storage: { name: 'storage', url: '/models/storage/storage.opt.glb', size: 12, yaw: 0 },
  farm:    { name: 'farm',    url: '/models/farm/farm.opt.glb',       size: 12, yaw: 0 },
  // wave 2b props
  shipwreck: { name: 'shipwreck', url: '/models/shipwreck/shipwreck.opt.glb', size: 14, yaw: 0 }, // half-submerged, divable
  tavern:    { name: 'tavern',    url: '/models/tavern/tavern.opt.glb',       size: 8,  yaw: 0 }, // production building
  foundry:   { name: 'foundry',   url: '/models/foundry/foundry.opt.glb',     size: 8,  yaw: 0, ember: { day: 0.3, night: 1.9 } }, // the skull forge — its furnace emissive blazes after dusk
  crop_plot: { name: 'crop_plot', url: '/models/crop_plot/crop_plot.opt.glb', size: 4,  yaw: 0 },
  // wave 3 props
  bottle:       { name: 'bottle',       url: '/models/bottle/bottle.opt.glb',             size: 1.6, yaw: 0 }, // drifting Message in a Bottle (3.1)
  cursed_chest: { name: 'cursed_chest', url: '/models/cursed_chest/cursed_chest.opt.glb', size: 4,   yaw: 0 }, // the Cursed Treasure (3.4)
  whale:        { name: 'whale',        url: '/models/whale/whale.opt.glb',               size: 26,  yaw: 0 }, // the White Whale (3.5) — yaw baked by WHALE_MODEL_YAW instead (the animal needs pitch/roll too)
};

/**
 * Per-ship-class CAPTAIN figure that stands at the helm — a scale reference + personality.
 * Added as a child of the ship group (so it inherits the ship's pose/bob), but its own size
 * divides out classScale so a captain is a FIXED real height on every class. Render-only;
 * a missing/failed GLB simply means no captain (the ship still renders).
 */
const CAPTAIN_MODELS: Record<number, string> = {
  [ShipClassId.SLOOP]:      '/models/captain_sloop/captain_sloop.opt.glb',
  [ShipClassId.BRIGANTINE]: '/models/captain_brigantine/captain_brigantine.opt.glb',
  [ShipClassId.GALLEON]:    '/models/captain_galleon/captain_galleon.opt.glb',
  [ShipClassId.FRIGATE]:    '/models/captain_frigate/captain_frigate.opt.glb',
  // Onda Frota (v47): the 3 new hulls REUSE existing captain GLBs (no new art) — cutter→sloop, fluyt→brig, east→galleon.
  [ShipClassId.CUTTER]:        '/models/captain_sloop/captain_sloop.opt.glb',
  [ShipClassId.FLUYT]:         '/models/captain_brigantine/captain_brigantine.opt.glb',
  [ShipClassId.EAST_INDIAMAN]: '/models/captain_galleon/captain_galleon.opt.glb',
};
/**
 * Helm placement, in HULL-TEMPLATE-LOCAL units (the hull is normalized to ~19 long, bow at +X,
 * waterline at y=0). These scale with the ship (×classScale); the captain's own scale divides
 * classScale back out so its real height stays HEIGHT_M. Tune Y per class if a captain floats/sinks.
 */
const HELM = {
  HEIGHT_M: 3.4,                 // captain real height incl. hat (slightly heroic so it reads from the chase cam)
  X: -7.0,                       // on the quarterdeck behind the sternmost mast (open deck on all 4 classes)
  Z: 0,                          // centreline (the wheel)
  Y: 2.6,                        // fallback deck height if the seating ray misses
  YAW: Math.PI / 2,              // face the bow (+X): the model's local front is +Z, so +90° turns it forward (π=abeam, -90°=astern)
  WHEEL_GAP: 0.62,               // real metres the ship's wheel sits forward (+X) of the captain (hands-on-the-wheel)
};
/** Deckhands — the captain GLBs reused as crew (a deliberate "gambiarra"), shorter than the heroic
 *  captain, spread along the deck and animated to bustle (and run/brawl during a grapple). Hull-local. */
const CREW = {
  HEIGHT_M: 2.0,                 // a deckhand, shorter than the 3.4 m heroic captain
  CULL_D2: 170 * 170,            // only show/animate crew on ships within ~170 m (deck detail reads close up)
  COUNT: [2, 3, 4, 3, 1, 2, 10] as number[],  // per ShipClassId (sloop/brig/galleon/frigate/cutter/fluyt/east_indiaman)
};
/** Instanced naval deck guns (GLB) on every ship's broadsides. ONE shared InstancedMesh across the
 *  whole fleet — invariant: +1 draw call regardless of ship count. Guns are anchored in hull-local
 *  space (like the crew) and written from each ship's world matrix each frame. A hard CAP + distance
 *  cull bound the worst-case triangle count in a big brawl. */
const CANNON_RENDER = {
  SCALE: 1.15,                   // hull-template-space scale (renders ~2.2 m long on a brigantine, scales with the hull)
  RAIL_Z: 2.15,                  // hull-local lateral offset — sits at the gunwale so the barrel pokes over the rail
  DIR: 1,                        // muzzle-direction sign (barrel outboard); flip to -1 if the muzzles face inboard
  CAP: 128,                      // hard instance cap → bounds worst-case tris (128 × ~2.4k ≈ 300k) well under 500k
  CULL_D2: 240 * 240,            // only gun ships within ~240 m of the camera (deck props unreadable past that)
  // gun stations/side per class (both sides), hull-template-local X. Onda Frota (v47): cutter 1, fluyt 2, and the
  // East Indiaman's WALL of 10 guns/side spread across the deck (the flagship's "a wall of cannon" identity).
  BX: [[0.5], [-2, 3.5], [-3.5, 0.5, 4.5], [-3.5, 0.5, 4.5],
    [0], [-2.5, 2.5], [-4.5, -3.5, -2.5, -1.5, -0.5, 0.5, 1.5, 2.5, 3.5, 4.5]] as number[][],
};

/** A loaded, DoubleSide-fixed treasure-islet GLB template + its measured footprint (max of x,z), height and
 *  base offset — cloned per island in setWorld and uniformly scaled to the island radius. */
interface IsleTpl { obj: THREE.Object3D; fw: number; fh: number; minY: number; }

export interface LocalShipView { x: number; y: number; heading: number; sail: number; cls: number; }
/** One animated deckhand: its mesh + its hull-local base station (bx,bz,by), idle phase and base y-scale. */
interface CrewMember { mesh: THREE.Object3D; bx: number; bz: number; by: number; ph: number; sy: number; }

/** Per-class uniform render scale relative to the base (brigantine) hull length. */
function classScale(cls: number): number {
  const def = SHIP_CLASS[cls as ShipClassId] ?? SHIP_CLASS[ShipClassId.BRIGANTINE];
  return def.length / SHIP.LENGTH;
}

/**
 * Is this mesh CANVAS? The rigged classes (SLOOP/BRIGANTINE/GALLEON/FRIGATE) ship their sails as a separate
 * Blender node carrying the furl morph; the three later hulls (CUTTER/FLUYT/EAST_INDIAMAN) fused theirs into
 * the hull mesh with furled sails, so they have none to find — and correctly get no cloth sim.
 * The single source of this test (it drives the canvas texture, the cosmetic tint AND the wind rig).
 */
function isSailMesh(m: THREE.Mesh): boolean {
  return (m.morphTargetInfluences?.length ?? 0) > 0 || /sail|canvas|cloth/i.test(m.name);
}

/** Minimal shape of the dynamically-imported GLTFLoader (shared across the prioritized asset load). */
type GltfLoader = { loadAsync(url: string): Promise<{ scene: THREE.Object3D; animations?: THREE.AnimationClip[] }>; setMeshoptDecoder(d: unknown): void };

interface Fx { mesh: THREE.Mesh | THREE.Sprite; life: number; maxLife: number; grow: number; vx: number; vy: number; vz: number; grows: boolean; a0: number; }

/** One sinking-ship cinematic. Two hull chunks (bow/stern, split by clipping planes) + fire/smoke/
 *  bubble sprites + a foam ring, played over DAMAGE_S + SINK_S then disposed. */
interface WreckChunk { group: THREE.Group; localPlane: THREE.Plane; worldPlane: THREE.Plane; mats: THREE.Material[]; sign: number; }
interface Wreck {
  id: number; t0: number; x: number; z: number; heading: number; scale: number;
  bow: WreckChunk; stern: WreckChunk; broke: boolean;
  fire: THREE.Sprite[]; foam: THREE.Mesh; smokeAcc: number; bubbleAcc: number;
}

/** Onda Água v2 — the wake itself lives in wake.ts (ONE merged draw call for every ship + the whale).
 *  What stays here is only the per-owner bookkeeping the renderer already had to do anyway: the smoothed
 *  speed signal (which also drives the ocean displacer) and the distance cull. */
const WAKE_H = 0.1;         // height above the DISPLACED wave surface for the whale/wreck foam discs
const SHADOW_CULL_D2 = 220 * 220; // beyond this a contact shadow is a few pixels — it simply stops being added
const WAKE_CULL_D2 = 280 * 280; // beyond this (sq. metres from the camera) a wake is hidden — a few px, not worth the rebuild (caps wake cost at O(nearby ships))
/** The White Whale's wake owner id in the shared WakeSystem (never collides with a server ship id). */
const WHALE_WAKE_ID = -777;

/** Yaw baked into the whale model so its NOSE points +X, the forward every other hull in the scene uses.
 *  The Meshy whale was authored along Z (its bind box is ~2× longer in z than x), so without this it swims
 *  broadside-on — and v1 compounded that by driving `rotation.y = heading` where every hull uses `-heading`. */
const WHALE_MODEL_YAW = -Math.PI / 2;
/** Hard cap on live flat-foam discs (each is one extra draw call while it lives). */
const FOAM_PATCH_MAX = 12;
interface FoamPatch { mesh: THREE.Mesh; life: number; maxLife: number; size: number; a0: number; }

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  private composer!: EffectComposer;      // linear scene -> bloom -> tonemap+sRGB (one uniform color pipeline)
  private bloomPass?: UnrealBloomPass;     // emissive glow (desktop only; mobile keeps the cheap path)
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private sun: THREE.DirectionalLight;
  private hemi!: THREE.HemisphereLight;
  // ---- atmosphere: the SKY DOME (Onda Céu v1). ONE mesh, ONE draw call — it is the gradient, the sun
  // disc, the moon (with its real phase), the stars and the cloud decks, all at once. It replaced a
  // separate cloud dome + sun sprite + moon sprite + 700-point star cloud that each had their own opinion
  // about where the sun was. Plus the gull flock and the horizon silhouettes, which are their own objects.
  private cloudMat: THREE.ShaderMaterial | null = null;
  private clouds: THREE.Mesh | null = null;
  /** Where the moon is (unit, world), where its own sunlight comes from (→ the phase), and how strongly
   *  it lights the world (0 = no moon up / broad daylight / socked in by a gale). See `updateMoon`. */
  private moonDir = new THREE.Vector3(0, 1, 0);
  private moonLight = new THREE.Vector3(0, 0, -1);
  private moonAmt = 0;
  private _moonAxis = new THREE.Vector3();
  /** 🌌 THE AURORA: how strong the display is RIGHT NOW (schedule × the night we are actually in × the
   *  gale we are actually rendering) and where the substorm breakup beat is. Both 0 by day, so the whole
   *  effect — sky, water and ambient — costs one comparison. `setAurora` pins them for a proof shot. */
  private auroraAmt = 0;
  private auroraBreak = 0;
  private auroraOverride: number | null = null;
  private auroraBreakOverride: number | null = null;
  private _skyLiftA = new THREE.Color();
  private _skyLiftB = new THREE.Color();
  /** Onda Aves — the whole gull flock in ONE instanced draw call, wing beat in the vertex shader (birds.ts). */
  private birds!: BirdFlock;
  /** Onda Polish — the bow-flushed flying-fish shoal (1 instanced draw, vertex-shader glide). */
  private fish!: FlyingFish;
  /** Where the fauna is startled FROM: the bow of the hull nearest the render centre (i.e. yours), its
   *  course and how hard she is driving. Refreshed once per frame in render() — a fish flushed off a hull
   *  you cannot see is CPU spent on nothing. */
  private faunaBowX = 0; private faunaBowZ = 0; private faunaBowH = 0; private faunaSpeed = 0;
  private birdsRoot = new THREE.Group();
  private _birdTop = new THREE.Color(0xf2f6fa);
  private _birdUnder = new THREE.Color(0x9fb0bf);
  private horizon: THREE.Group | null = null;
  private horizonMat: THREE.ShaderMaterial | null = null;
  private _atmoScratch = new THREE.Color();
  private skyGlow = new THREE.Color(0xffffff);    // current horizon-glow colour (lerped from SKY_KEYS)
  private skyGlowAmt = 0;                          // current horizon-glow strength
  private skyZenith = new THREE.Color(0x8ec5e6);   // top-of-sky colour for the gradient dome
  private skyHorizon = new THREE.Color(0x8ec5e6);  // glow-tinted horizon colour (sky bottom + ocean fog)
  private glowTex: THREE.Texture | null = null; // shared warm radial glow for ship/base lanterns
  private nightFactor = 0;                       // 0 day … 1 deep night (drives lanterns + moon)
  private forceSkyPhase: number | null = null;   // menu showcase pins a dramatic time-of-day; null = live cycle
  /** 🎨 the water palette A/B rig (temporary — see WATER_PALETTES). Uniform values only; switching it
   *  mid-frame is legal and free, which is what lets a contact sheet photograph one frame four ways. */
  private waterPaletteName: WaterPaletteName = resolveWaterPaletteName();
  private waterPalette: WaterPalette = WATER_PALETTES[this.waterPaletteName];
  private heroShip?: THREE.Group;                // the menu showcase hero galleon
  /** Onda Sistema de Luz — THE FakeLight registry (lights.ts): every lantern/window/bonfire/beacon in the sea
   *  world is one entry here, drawn as ONE instanced additive glow batch + N ocean-glint shader uniforms.
   *  Public so the Landmark editor + tests can inspect it. */
  readonly lights: LightSystem;
  /** Which lights each placeable landmark kind carries (window panes, lamp heads, the lighthouse beacon) —
   *  derived once from the walkable city's own night tables so the two scenes never drift. */
  private readonly landmarkKindLights = buildLandmarkKindLights(WINDOW_SPECS, LAMP_KINDS);
  private _skyScratch = new THREE.Color();       // reused per frame by the day/night sky update
  // ---- dynamic storm (driven by the server-authoritative intensity via setStorm) ----
  private _storm = 0;                            // smoothed local storm intensity 0..1 (read via the `storm` getter)
  private _dive = 0;                             // smoothed 🤿 wreck-dive presentation intensity 0..1 (cosmetic only)
  private _diveTarget = 0;                       // target set by setDiveFx (main.ts drives it from self.activity)
  private diveX = 0; private diveZ = 0;          // sim-plane anchor of the dive (the local hull over the wreck)
  private diveBubbleAcc = 0;                     // diver bubble-column emitter accumulator (Hz-based)
  private _diveScratch = new THREE.Color();      // reused per frame by the dive tint
  private stormTarget = 0;                       // last authoritative intensity from the snapshot
  private waveScale = 1;                          // ocean swell amp (1 calm) — bob/wake sample with this
  /** Onda Tempestade v2 — ALL storm weather art (rain, marching rain curtains, spindrift, lightning flash +
   *  bolt, the waterspout landmark and the Eye of the Storm) lives in storm.ts. The renderer keeps only what
   *  is renderer-owned: the sky/fog/ocean grade and folding the strike FLASH into the scene lighting. */
  private stormFx!: StormFx;
  private tradeFoam: THREE.Points | null = null;  // foam flecks drifting along the trade-wind lane (1 draw call)
  private tradeFoamPos: Float32Array | null = null;
  private tradeFoamOpacity = 0;                   // eased so the current fades in/out (no pop) as you enter a lane
  // THE LIVE WIND, as the renderer sees it (fed by setWind from the authoritative snapshot). It is a pair
  // of numbers, not an object on screen: the cat's paws, the sails, the pennants, the rain slant, the
  // drifting foam domains and the gulls' crab angle all read it. (The old additive white wind STREAKS that
  // used to draw it were retired in the polish wave — cat's paws say the same thing on the water itself,
  // without a mesh, and the streaks read as scratches on the lens.)
  private windDir = 0;                             // world radians (x east, z = sim-y south)
  private windStrength = 1;
  private _stormScratch = new THREE.Color();      // reused per frame by the storm tint
  private _baseFogNear = 250;                      // calm scene-fog near plane (captured at init)
  private ocean: THREE.Mesh;
  private oceanMat: THREE.ShaderMaterial;
  /** Triangles in the sea clipmap — reported by `stats()` so the budget is provable, not asserted. */
  private oceanTriangles = 0;
  private ships = new Map<number, THREE.Group>();
  /** Per-ship equipped cosmetics (hull/sail/flag cosmetic ids) — applied as tint/texture in syncShips. */
  private shipCos = new Map<number, { hull: number; sail: number; flag: number; trophy: number }>();
  private flagLoader = new THREE.TextureLoader();
  private flagTex = new Map<number, THREE.Texture>(); // cached masthead emblem textures (shared across ships)
  /** Active grapple tethers, keyed by canonical pair (minId*65536+maxId). */
  private tethers = new Map<number, { line: THREE.Line; a: number; b: number }>();
  /** Soft broadside bearing guide (shown while aiming) + smoothed camera look-at point. */
  private aimBeam: THREE.Mesh | null = null;          // vivid-green beam emitted from the cannon muzzle (pitches with elevation)
  private aimBeamMat: THREE.ShaderMaterial | null = null;
  private aimMarks: THREE.Mesh[] = [];                // sea "nav marks" pips along the firing bearing (fade before the landing)
  private leadGhost: THREE.Mesh | null = null;        // ONE reused reticle on the water at the aim-lead intercept point
  private _aimDir = new THREE.Vector3();
  private readonly _unitX = new THREE.Vector3(1, 0, 0);
  private camLook = new THREE.Vector3();
  private _scratch = new THREE.Vector3(); // reused in updateCamera so the hot path allocates nothing
  private _proj = new THREE.Vector3();    // reused by worldToScreen (HUD hit-markers)
  /** Per-ball track: last pos (despawn splash), first-seen (visual-arc age), gen (frame liveness, no per-frame Set). */
  private ballTrack = new Map<number, { x: number; z: number; seen: number; gen: number; puff?: number }>();
  private ballGen = 0;
  /** Per-ShipClassId loaded template + its baked furl clips (missing class -> fallback). */
  private shipTemplates = new Map<number, { template: THREE.Object3D; furlClips: THREE.AnimationClip[]; sailRig?: SailRig; topY?: number }>();
  /** Onda Velas — the sun/glow uniform holders EVERY sail material shares (one object, N materials). */
  private sailShared: SailShared = makeSailShared();
  /** Live luffing of the LOCAL ship (0..1) — main.ts feeds it to the procedural canvas-crack audio. */
  private _localLuff = 0;
  private _sailQ = 1;                            // 1 = full cloth sim, 0 = Low preset (shape only, no flogging)
  private _sailT = -1;                           // last syncShips time (the sail springs' own dt); -1 = first frame
  private _sunView = new THREE.Vector3();        // scratch: the sun direction in VIEW space
  /** Optional prop templates (hq/turret/chest) keyed by name; missing -> procedural. */
  private propTemplates = new Map<string, THREE.Object3D>();
  /** Per-class captain figure (normalized to 1 unit tall, feet at y=0, facing +X); missing -> no captain. */
  private captainTemplates = new Map<number, THREE.Object3D>();
  /** Lazily-built ship's-wheel prop (real metres, base at y=0); cloned per helm so the captain reads as steering. */
  private helmWheelTemplate?: THREE.Group;
  /** Reused for seating base buildings on the real (palm-excluded) island surface via a downward ray. */
  private _seatRay = new THREE.Raycaster();
  private _seatDown = new THREE.Vector3(0, -1, 0);
  /** Per-slot squared distance for the alloc-free nearest-N displacer select (no per-frame array/sort). */
  private _dispD2 = new Array<number>(OCEAN_DISP_MAX).fill(Infinity);
  /** Reused Gerstner-sample scratch for the wake hot path (rebuildWake needs two live at once). */
  private _ws0: OceanSample = { dx: 0, dy: 0, height: 0 };
  private _ws1: OceanSample = { dx: 0, dy: 0, height: 0 };
  /** Last world payload, so props that finish loading after setWorld can re-render it. */
  private lastWorld: WorldMsg | null = null;
  /** Onda Ilhas-Tipo: the archetype set that dresses treasure islets. Defaults to the committed
   *  `ISLAND_ARCHETYPES` (null → the shipped 1/3-sandbar split); the Islands editor swaps in a live set for
   *  preview via `setIslandArchetypes`. Render-only — never touches sim/wire/chest positions. */
  private islandArchetypes: IslandArchetypeSet | null = ISLAND_ARCHETYPES;
  /** Treasure-chest beacon materials — pulsed every frame so gold columns breathe on the horizon. */
  private chestBeaconMats: THREE.MeshBasicMaterial[] = [];
  private beaconTex: THREE.CanvasTexture | null = null;
  /** Onda Carona: per-chest renderables (mesh + its two beacon planes) keyed by sim chestId, so a chest that
   *  another captain hoisted can be hidden (and shown again on respawn) without rebuilding the sea. */
  private chestNodes = new Map<number, THREE.Object3D[]>();
  /** Chest ids currently hoisted-and-gone. Re-applied after every setWorld (incl. the GLB-load rerun) so a
   *  looted chest stays hidden through internal re-dressing. */
  private takenChests = new Set<number>();
  /** The Cursed Treasure — a floating chest + oversized violet beacon on open water (feature 3). */
  private cursedGroup: THREE.Group | null = null;
  private cursedPos: { x: number; y: number } | null = null;
  private cursedBeaconMats: THREE.MeshBasicMaterial[] = [];
  /** Fishing shoals (fishing-polish): a living spot instead of the old flat disc — one instanced
   *  shader sheet (soft stirred-water glow + expanding ripple rings, displaced by the SAME ocean GLSL
   *  so it rides the swell), one instanced school of serpentine fish silhouettes (+2 reserved
   *  leaping-fish slots), and the circling gulls. 4 instanced draw calls for all 24 shoals. Day/night:
   *  gulls go to roost at night while the water turns faintly bioluminescent — the spot always reads. */
  private shoalsRoot = new THREE.Group();
  private gullMesh: THREE.InstancedMesh | null = null;
  private gullMat: THREE.ShaderMaterial | null = null;
  private gullData: { cx: number; cz: number; a: number; r: number; h: number; sp: number; ph: number }[] = [];
  private gullDummy = new THREE.Object3D();
  private shoalCenters: { x: number; z: number }[] = [];
  private shoalWater: THREE.InstancedMesh | null = null;
  private shoalWaterMat: THREE.ShaderMaterial | null = null;
  private shoalFish: THREE.InstancedMesh | null = null;
  private shoalFishMat: THREE.MeshBasicMaterial | null = null;
  private shoalFishData: { a0: number; r0: number; sp: number; ph: number; sc: number; wsp: number; dy: number }[] = [];
  private readonly shoalFishN = 8; // silhouettes per shoal (one instanced mesh for the whole sea)
  private shoalJumpers = [
    { active: false, nextAt: 5, t0: 0, x: 0, z: 0, dx: 0, dz: 0, splashIn: false },
    { active: false, nextAt: 11, t0: 0, x: 0, z: 0, dx: 0, dz: 0, splashIn: false },
  ];
  private jumpDummy = (() => { const o = new THREE.Object3D(); o.rotation.order = 'YZX'; return o; })();
  private shoalFishColDay = new THREE.Color(0xffffff);   // day: the per-instance slate/silver reads true
  private shoalFishColNight = new THREE.Color(0xa2ffe4); // night: the school shifts toward biolum teal
  private shoalColScratch = new THREE.Color();
  // ---- the local captain's fishing line (cast arc → slack wait → taut fight) — pure client theatre ----
  private fishLine: THREE.Line | null = null;
  private fishLinePos: Float32Array | null = null;
  private fishBobber: THREE.Mesh | null = null;
  private fishAct = 0; // mirror of self activity: 0 none, 1 line out, 2 bite
  private fishShip = { x: 0, z: 0, heading: 0 };
  private fishCast = { x: 0, z: 0, t0: -1, landed: false };
  private fishNibT0 = -10;
  private fishNextNib = 0;
  private fishNextThrash = 0;
  private lastRenderT = 0;
  private lastFrameDt = 1 / 60;   // last frame delta (s) — systems driven from updateAtmosphere read it here
  private dynamicWreckRoot = new THREE.Group();
  private dynamicWrecks = new Map<number, THREE.Group>();
  // wave 3.1: drifting Message-in-a-Bottle pickups (bob on the swell)
  private bottleRoot = new THREE.Group();
  private bottles = new Map<number, { g: THREE.Group; x: number; y: number }>();
  // wave 3.5: the White Whale — a hunted GLB creature that surfaces, dives, and spouts
  private whaleRoot = new THREE.Group();
  private whaleMesh: THREE.Group | null = null;
  private whaleTarget: { x: number; y: number; heading: number; surfaced: boolean } | null = null;
  /** The animal's rendered body — dead-reckoning + the breach/sound springs (whale.ts). */
  private whaleBody: WhaleBody | null = null;
  /** Which asset tier the live mesh was built from (0 procedural, 1 static GLB, 2 animated GLB) — the
   *  self-heal in setWhale rebuilds it in place when a better one finishes loading. */
  private whaleMeshTier = -1;
  private whaleWakeLive = false;            // is the whale currently laying into the shared WakeSystem?
  private foamPatches: FoamPatch[] = [];    // flat foam discs on the sea (breach / fluke / death wash)
  private foamPatchPool: THREE.Mesh[] = [];
  private whaleSpeed = 1;                   // eased swim-clip drive (0 dead … 1.5 sounding/rising)
  /** The animated (skeletal "swim" clip) whale template + its clips, and the live mixer for the instance. */
  private whaleTemplate: THREE.Group | null = null;
  private whaleClips: THREE.AnimationClip[] = [];
  private whaleMixer: THREE.AnimationMixer | null = null;
  /** Measured, world-scale hull of the whale model (origin at the BELLY, base y=0) — see loadWhale. */
  private whaleBodyH = 6; private whaleBodyL = 26;
  /** The Kraken (deep-water spectacle, v1 — ZERO WIRE). Its state is a deterministic function of the shared
   *  wall clock (`krakenStateAt` in protocol); main.ts feeds it here every sea frame. The mesh is built ONLY
   *  when the schedule is active AND the camera is within KRAKEN.CULL_D, and disposed otherwise → literally
   *  zero cost whenever the beast is submerged or nobody is near its deep-water zone. */
  private krakenRoot = new THREE.Group();
  private krakenMesh: THREE.Group | null = null;
  private krakenState: KrakenState | null = null;
  private krakenTemplate: THREE.Group | null = null;   // animated (idle+sink) GLB template + its clips
  private krakenClips: THREE.AnimationClip[] = [];
  private krakenMixer: THREE.AnimationMixer | null = null;
  private krakenIdle: THREE.AnimationAction | null = null;   // looping tentacle undulation (weight tracks emergence)
  private krakenSink: THREE.AnimationAction | null = null;   // paused; time = (1−rise)·dur → gather up (also emerge-reversed)
  private krakenSinkDur = 4;
  private krakenFoamAcc = 0;
  /** Last bases payload + viewer's island, so props loading after syncBases can rebuild bases in place. */
  private lastBases: WireBase[] | null = null;
  private lastYourIslandId = 0;
  private shipsRoot = new THREE.Group();
  // instanced broadside deck guns: one shared mesh for the whole fleet (+ reusable transform scratch)
  private cannonInst: THREE.InstancedMesh | null = null;
  /** Onda de-proc: the Capital sea-landmark kit — the lighthouse tower + a seawall segment, baked from the
   *  same GLBs the walkable city uses, so the far-out-at-sea landmark is a real model, not procedural. */
  private landmarkBake: {
    lighthouse?: { geo: THREE.BufferGeometry; mat: THREE.Material }; wall?: { geo: THREE.BufferGeometry; mat: THREE.Material };
    // Onda Entorno landscape kit — each baked to ONE instanceable geometry+material (a decorated port draws
    // one InstancedMesh per kind). A missing bake → a merged procedural fallback covers (same one-mesh cost).
    pine?: { geo: THREE.BufferGeometry; mat: THREE.Material }; pine_tall?: { geo: THREE.BufferGeometry; mat: THREE.Material };
    rock?: { geo: THREE.BufferGeometry; mat: THREE.Material }; cliff?: { geo: THREE.BufferGeometry; mat: THREE.Material };
  } = {};
  /** Landmark Editor: a Save-Online (or in-editor) per-outpost exterior override, keyed by outpost id. When
   *  set, it replaces the shipped `landmarkForOutpost(id)` for that port. Render-only, no wire. */
  private landmarkOverride: Record<number, readonly LandmarkPiece[]> | null = null;
  /** Onda Portos Livres — the editable PortLayout overrides (Save-Online, else the shipped layouts). Only the
   *  EXTERIOR props of each feed the sea view (see exteriorPiecesFor). `capital` overrides the capital city;
   *  `city` overrides every village (mirrors main.ts's resolvePortLayout). null on a slice → the shipped layout. */
  private portExterior: { capital: PortLayout | null; city: PortLayout | null } = { capital: null, city: null };
  /** Item 3 (hideOutpostStructure) — per-outpost render-only flags (Save-Online `ports` slice, else the editor
   *  live). When `hideOutpostStructure` is set for an outpost id, its stock trading-post GLB (and the procedural
   *  fallback) is NOT drawn from the sea, so the owner's own landmark can stand in its place. The sell-zone ring +
   *  the landmark pieces still render. null / absent / false → the shipped structure draws (byte-identical). */
  private portFlags: Record<number, { hideOutpostStructure?: boolean }> | null = null;
  /** Onda Portos Livres — the sea-baked kit GLBs (wharf/citadel/houses/town-dressing/…), lazily loaded ONLY when
   *  some exterior layout references the kind (a heavy 1-6 MB GLB never weighs the boot of a player who never sees
   *  it). value === null = a load that failed (don't retry). Baked like portscene's kit: merged geo + first
   *  material + the ORIGINAL box, so KIT_FOOT/KIT_TALL normalization matches the walkable scene pivot-for-pivot. */
  private kitSeaBake = new Map<string, { geo: THREE.BufferGeometry; mat: THREE.Material | THREE.Material[]; box: THREE.Box3 } | null>();
  private kitSeaLoading = new Set<string>();
  private kitSeaLoader: GltfLoader | null = null;
  /** Onda Portos Livres — the heavy kit-exterior InstancedMeshes + their outpost centre, for a cheap per-interval
   *  distance cull (a hero citadel far out on the horizon costs zero tris). Rebuilt every setWorld. */
  private kitExtNodes: { node: THREE.InstancedMesh; ox: number; oz: number }[] = [];
  /** Hero-building LOD — the decimated `.lod.glb` bake per heavy kind (lazily loaded alongside the full mesh only
   *  when an exterior layout references it). null = a load that failed (don't retry). Baked exactly like the full
   *  kit, but placed with the FULL bake's box so the two meshes are pixel-aligned (no pop on the swap). */
  private kitSeaLodBake = new Map<string, { geo: THREE.BufferGeometry; mat: THREE.Material | THREE.Material[]; box: THREE.Box3 } | null>();
  private kitSeaLodLoading = new Set<string>();
  /** Hero-building LOD — the full+lod InstancedMesh pairs to swap by distance (with hysteresis) + hard-cull, one
   *  entry per heavy kind per outpost. Rebuilt every setWorld (children of the disposed port groups). */
  private kitLodNodes: { full: THREE.InstancedMesh; lod: THREE.InstancedMesh; ox: number; oz: number; far: boolean }[] = [];
  /** Onda de Arte — Ilhas de Tesouro: the two loot-islet GLBs (a leaning-coconut isle + a bare sandbar),
   *  each baked to a single instanceable geometry+material. All ~60 treasure islands render as TWO
   *  InstancedMeshes (one per type), the type chosen deterministically per island id. A missing bake →
   *  the procedural sand cone + palm keeps covering (fallback, same as the Capital landmark). */
  /** Onda de Arte — Ilhas de Tesouro: normalized GLB templates for the two loot-islet types (a leaning-coconut
   *  isle + a bare sandbar). Each treasure island CLONES its template (geometry/material shared) into its own
   *  distance-culled group, uniformly scaled to the island radius — so far islets cost no triangles, exactly
   *  like the retired procedural cones. A missing template → the procedural sand cone + palm keeps covering
   *  (same fallback as the Capital landmark). Baking to one InstancedMesh flipped the palm underneath the sand
   *  (Meshy's islet mesh doesn't survive bakeMerged), so — like every other prop — we clone the loaded scene. */
  private isleTpl: { coconut?: IsleTpl; sand?: IsleTpl } = {};
  private readonly _cnUp = new THREE.Vector3(0, 1, 0);
  private readonly _cnQ = new THREE.Quaternion();
  private readonly _cnPos = new THREE.Vector3();
  private readonly _cnScl = new THREE.Vector3();
  private readonly _cnLM = new THREE.Matrix4();
  private readonly _cnM = new THREE.Matrix4();
  private wakesRoot = new THREE.Group();
  /** Onda Polish — combat impact FX (miss column + rings, hull spray/splinters/sparks): 2 instanced draws
   *  for every impact on screen, replacing up to fourteen pooled single-mesh particles PER HIT. */
  private splashFx!: SplashFx;
  /** Onda Polish — the soft dark ellipse under every hull, one instanced draw for the whole fleet. */
  private shadowFx!: ContactShadows;
  /** Onda Água v2 — EVERY wake in the world (ships + whale) in one merged mesh. See wake.ts. */
  private wakeFx!: WakeSystem;
  /** Smoothed 0..1 speed per wake owner (also feeds the ocean displacer; EMA kills the render-cadence blink). */
  private wakeSpd = new Map<number, number>();
  private foamTex: THREE.Texture | null = null;
  /** Soft PUFF texture for spray/mist: a broad, nearly-flat white core with a long fade, unlike foamTex's
   *  tight bright core (which reads as a tiny dot when used on a big sprite). */
  private mistTex: THREE.Texture | null = null;
  private foamQuad!: THREE.BufferGeometry;   // shared flat (lying-on-water) quad for bow/side foam
  private worldRoot = new THREE.Group();
  private basesRoot = new THREE.Group();
  private bases = new Map<number, THREE.Group>();
  private balls: THREE.InstancedMesh;
  private ballDummy = new THREE.Object3D();
  private fx: Fx[] = [];
  private fxPool: THREE.Mesh[] = [];
  /** Pool for the SOFT variant (a camera-facing sprite on the shared foam glow texture). The hard fx spheres
   *  read as white balloons at close range; spray and mist need something with no edge. */
  private mistPool: THREE.Sprite[] = [];
  private wrecks: Wreck[] = [];                  // active sinking cinematics
  private sinkingIds = new Set<number>();         // ship ids whose wreck owns the visual (skip normal placement)
  private smokeTex: THREE.Texture | null = null;  // soft grey puff for smoke/bubbles
  // free-orbit camera: ship-relative yaw offset (0 = behind), pitch coupled to zoom, distance.
  // each value has a target (set by drag/pinch) + an eased value (damped per frame) -> smooth, snap-free.
  private camYaw = 0; private camYawT = 0;             // 360° orbit offset behind the ship
  private camFollowYaw = NaN;                           // eased world yaw — ALWAYS converges behind (never frozen), just lags hard while turning
  private camFocusY = NaN;                              // eased focus height — rides the MEAN sea, not every wavelet (kills the per-wave bob jitter)
  private orbitIdle = 99;                               // seconds since the last manual orbit; after a delay the camera glides back behind the ship
  private camPitchBias = 0; private camPitchBiasT = 0; // manual vertical-drag tilt added on top of the zoom pitch
  private camDist = 58; private camDistT = 58;         // zoom: small = close third-person, large = tactical top-down
  // --- combat-feel "juice" (all cosmetic / render-only — never touches the sim or netcode) ---
  private shakeAmt = 0;        // camera-shake budget (world units), decays fast each frame
  private shakeSeed = 0;       // advances so successive shakes don't sample the same jitter
  private hitstopT = 0;        // seconds of micro-slowmo left — slows the impact FX a beat (a "hit-stop")
  private camPunch = 0;        // APPLIED dolly-in kick (subtracted from camDist) — ramps toward camPunchT, never jumps
  private camPunchT = 0;       // TARGET kick set by an impact; decays over ~0.5 s so camPunch rises AND falls smoothly
  isMobile: boolean;
  // Settings (render-only): graphics preset + sea-camera drag feel. Defaults reproduce shipped behaviour.
  private graphicsPreset: 'low' | 'medium' | 'high' = 'medium';
  private camSensitivity = 1;   // 0.5..2.0 — scales the drag→orbit rate
  private invertCamX = false;   // flip the horizontal drag sign (default OFF = WoW/L2 drag-right→starboard)
  /** Optional hook so main can play a splash SFX when a cannonball ends its flight. */
  onSplash: ((x: number, z: number, big: boolean) => void) | null = null;

  // ---- Wallet-webview memory survival (Onda wallet-fix) ----
  /** LIGHT MODE: caps the pixel-ratio to 1, drops bloom + the gull flock, and makes the sea→port dispose
   *  unconditional. Set true at boot for a wallet in-app browser / low-RAM phone (see setLowMemoryMode). */
  private lowMem = false;
  /** WebGL context has been lost (WKWebView OOM). Every render early-returns while true so the dead context is
   *  never touched again; main shows a parchment "ran low on memory — reload" card via `onContextLost`. */
  private _contextLost = false;
  /** Fired ONCE the moment the WebGL context is lost — main shows the reload card + stops its loops. */
  onContextLost: (() => void) | null = null;
  /** True between releaseSeaAssets() and restoreSeaAssets() — the heavy sea GLBs are freed while ashore. */
  private _seaReleased = false;
  /** Last shoals payload, so restoreSeaAssets can rebuild the fishing shoals it disposed on the way ashore. */
  private lastShoals: { x: number; y: number }[] = [];

  constructor(private canvas: HTMLCanvasElement) {
    this.isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    this.renderer = new THREE.WebGLRenderer({
      canvas, antialias: !this.isMobile, powerPreference: 'high-performance', stencil: false,
    });
    // Mobile GPUs choke on a full 2× device-pixel-ratio (a dpr-3 phone renders ~4× the pixels of the
    // screen). Cap it lower on mobile — the single biggest FPS lever for "not fluid on mobile". The cap
    // is preset-driven (Settings › Graphics); `medium` is the shipped default.
    this.renderer.setPixelRatio(this.pixelRatioCap());
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.localClippingEnabled = true; // per-chunk clipping planes split a sinking hull in two
    // We render through an EffectComposer (several full-screen passes), and three.js auto-resets info.render
    // at the start of every renderer.render() — so after composer.render() the counters reflect only the LAST
    // pass (a single output quad). Take manual control: reset once per frame in render() so stats() reports the
    // TRUE per-frame draw-call / triangle total across all passes (used by the Settings perf readout + budgets).
    this.renderer.info.autoReset = false;
    // Filmic HDR pipeline: the scene renders linear, OutputPass applies this tone map + sRGB ONCE at the
    // end of the chain (see buildComposer). ACES gives cinematic highlight rolloff, so the sun disc, a
    // muzzle flash and a lantern roll off to warm white instead of clipping to a flat white hole.
    //
    // `?tone=legacy` is the kill-switch (config.ts): back to the pre-filmic linear clamp at exposure 1.0,
    // live, with no rebuild — because this ONE enum grades the sea, the port, the house interior, the
    // Draga ritual, every FX and every 3D nametag, and that is far too much surface to have no escape
    // hatch. `setToneMapping()` flips it at runtime for an A/B.
    this.applyToneMapping(toneMapping());
    this.attachCameraControls();

    this.scene.background = new THREE.Color().copy(SKY); // a private sky colour the day/night cycle animates
    this.scene.fog = new THREE.Fog(SKY.getHex(), 250, OCEAN_SPAN * 0.55);
    this._baseFogNear = 250;

    // Vertical fov is aspect-compensated (camfov.ts "Hor+"): 58° on landscape/desktop, raised on narrow portrait
    // phones so the HORIZONTAL fov doesn't collapse to a ~29° slot (the mobile "I can't see the sea" report).
    const aspect0 = window.innerWidth / Math.max(1, window.innerHeight);
    this.camera = new THREE.PerspectiveCamera(verticalFovForAspect(aspect0), aspect0, 0.5, 4000);
    this.camera.position.set(0, 40, 60);

    this.hemi = new THREE.HemisphereLight(0xbfe3ff, 0x274a5a, 1.05);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(0xfff2d8, 1.5);
    this.sun.position.set(120, 200, 80);
    this.scene.add(this.sun);
    // Onda Tempestade v2: the whole weather kit (rain + curtains + spindrift + lightning + waterspout + eye).
    // NOTE there is deliberately NO THREE.PointLight for lightning any more — a strike is a scene-wide flash
    // value the renderer folds into sun/hemi/sky/ocean/clouds below (the FakeLight law, see lights.ts).
    this.stormFx = new StormFx(this.scene);
    this.tradeFoam = this.buildTradeFoam();
    this.scene.add(this.tradeFoam);
    // atmosphere: a gull flock, hazy horizon islands (the SKY DOME is built below, after the quality
    // probe — it rides the same `?water=` / Settings lever the sea does, so there is one quality opinion).
    this.scene.add(this.birdsRoot); this.birds = new BirdFlock(this.birdsRoot);
    this.fish = new FlyingFish(this.birdsRoot);
    this.horizon = this.buildHorizon(); this.scene.add(this.horizon);
    // warm-orange, soft, NON-saturated core (no white blow-out) — candle glow, not a headlight
    this.glowTex = this.makeGlowTexture('rgba(255,184,95,0.82)', 'rgba(255,135,60,0.26)');
    // FakeLight system: instanced glow batch (1 draw call for every light in view) + ocean glints. The glow
    // texture is a NEUTRAL white radial so the per-instance colour carries each preset/custom tint faithfully.
    this.lights = new LightSystem(this.scene, this.makeGlowTexture('rgba(255,244,230,0.9)', 'rgba(255,214,160,0.3)'));

    // Water kill-switch: probe the GL context, fold in `?water=`, THEN compile. The legacy path is what a
    // failed probe (or a typo in the query string) lands on, so the sea always draws.
    this.waterCapable = this.probeWaterCapable();
    this.waterPref = waterQuality();
    this.waterHigh = this.resolveWaterHigh();
    // ☁️ the sky (Onda Céu v1): gradient + sun + moon + stars + clouds + storm, ONE draw call. It absorbed
    // the old cloud dome, the sun sprite, the moon sprite and the 700-point star cloud.
    this.clouds = this.buildSkyDome(this.waterHigh); this.scene.add(this.clouds);
    this.oceanMat = this.buildOceanMaterial(undefined, true);
    // Onda Água v2 — the sea is a camera-following geometry clipmap (oceanmesh.ts), not a uniform plane:
    // 2 m quads under the bowsprit out to 72 m, doubling to 32 m at the 1152 m rim. Still ONE mesh, one
    // draw call, and 44% FEWER triangles (41 472 vs 73 728) than the uniform 192² grid it replaces.
    const clip = buildOceanClipmap();
    this.oceanTriangles = clip.triangles;
    this.ocean = new THREE.Mesh(clip.geometry, this.oceanMat);
    this.ocean.frustumCulled = false;
    this.scene.add(this.ocean);

    this.foamTex = this.makeGlowTexture('rgba(255,255,255,0.95)', 'rgba(225,240,255,0.45)');
    this.mistTex = this.makeGlowTexture('rgba(255,255,255,0.98)', 'rgba(250,253,255,0.86)');
    this.foamQuad = new THREE.PlaneGeometry(1, 1); this.foamQuad.rotateX(-Math.PI / 2); // unit quad lying flat (normal up)
    this.scene.add(this.worldRoot);
    this.scene.add(this.shoalsRoot);      // fishing shoals (foam patch + circling gulls)
    this.scene.add(this.dynamicWreckRoot);// player-sunk wrecks (divable, 2b.3)
    this.scene.add(this.bottleRoot);      // drifting Messages in a Bottle (wave 3.1)
    this.scene.add(this.whaleRoot);       // the White Whale (wave 3.5)
    this.scene.add(this.krakenRoot);      // the Kraken deep-water spectacle (zero-wire)
    this.scene.add(this.wakesRoot);   // wakes under the ships, on the water
    this.wakeFx = new WakeSystem(this.wakesRoot); // ONE draw call for every Kelvin wake in the world
    this.wakeFx.setQuality(this.waterHigh ? 'high' : 'low');
    // …and the two other instanced sea systems, in the same root so they release/restore with it
    this.shadowFx = new ContactShadows(this.wakesRoot);  // 1 draw for the whole fleet's contact shadows
    this.splashFx = new SplashFx(this.wakesRoot);        // 2 draws for every shot landing on screen
    this.splashFx.setQuality(this.waterHigh ? 'high' : 'low');
    this.scene.add(this.basesRoot);
    this.scene.add(this.shipsRoot);

    // instanced cannonballs (one draw call for the whole battlefield)
    const ballGeo = new THREE.SphereGeometry(CANNON.BALL_VISUAL_RADIUS, 10, 8);
    const ballMat = new THREE.MeshStandardMaterial({ color: 0x14110f, roughness: 0.5, metalness: 0.4 });
    this.balls = new THREE.InstancedMesh(ballGeo, ballMat, 512);
    this.balls.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.balls.frustumCulled = false;
    this.balls.count = 0;
    this.scene.add(this.balls);

    this.buildComposer();
    window.addEventListener('resize', () => this.resize());
    // TELA AZUL NUNCA MAIS: an iOS WKWebView (Phantom/MetaMask/… in-app browser) has a much smaller GPU/JS memory
    // ceiling than Safari. When it runs out — classically on the double-peak of loading the walkable port while the
    // sea is still resident — it fires `webglcontextlost` and, if unhandled, the page dies to a silent blue/white
    // screen. We preventDefault() (so the browser doesn't tear the page down) and hand main a single callback to
    // raise a diegetic "ran low on memory — reload" parchment card. We do NOT try to auto-restore every GPU
    // resource (Three can't reliably re-upload a whole scene mid-flight) — a graceful reload is the honest path.
    canvas.addEventListener('webglcontextlost', (e) => {
      try { e.preventDefault(); } catch { /* non-cancelable synthetic event — the flag+card below still fire */ }
      if (this._contextLost) return;
      this._contextLost = true;
      try { this.onContextLost?.(); } catch { /* the card is best-effort — never rethrow from the GL callback */ }
    }, false);
    canvas.addEventListener('webglcontextrestored', () => { /* handled by a full reload from the card */ }, false);
    void this.loadAssets();
  }

  /** True once the WebGL context has been lost (WKWebView OOM). main's loops check this to stop rendering. */
  get contextLost(): boolean { return this._contextLost; }

  /** GPU-resource census for verification (headless): the live count of geometries + textures Three holds on the
   *  GL context. Drops when releaseSeaAssets() disposes the sea; climbs back after restoreSeaAssets(). */
  memInfo(): { geometries: number; textures: number } {
    const m = this.renderer.info.memory;
    return { geometries: m.geometries, textures: m.textures };
  }

  /* ------------------------------ asset loading (prioritized) ------------------------------ */

  /** Fired as each GLB finishes (loaded, total) — the menu draws a discreet progress bar from it. */
  onAssetProgress: ((loaded: number, total: number) => void) | null = null;
  /** Fired once the CRITICAL set (ships + captains + chest) is in — the sea is playable. */
  onAssetsReady: (() => void) | null = null;
  private _assetLoaded = 0;
  private _assetTotal = 0;
  private bumpAsset(): void { this._assetLoaded++; this.onAssetProgress?.(this._assetLoaded, this._assetTotal); }

  private async makeGltfLoader(): Promise<GltfLoader | null> {
    try {
      // Retry the code-split chunk fetch too: a truncated/aborted GLTFLoader chunk (same deploy window that
      // aborts GLBs) would otherwise strand the WHOLE session on procedural art via the catch below. The
      // deadline turns a STALLED chunk fetch (pending forever, no error) into a rejection the retry can see;
      // the chunks are tiny, so 20s total is generous even on a very slow link.
      const [{ GLTFLoader }, { MeshoptDecoder }] = await retryAsync(() => withDeadline(() => Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]), 20_000, 'GLTF loader chunk import'));
      const loader = new GLTFLoader() as unknown as GltfLoader;
      loader.setMeshoptDecoder(MeshoptDecoder);
      return loader;
    } catch (err) {
      console.warn('[render] GLTF loader unavailable, using procedural art', err);
      return null;
    }
  }

  /**
   * Load the GLBs in PRIORITY order so the sea is playable fast on a cold cache: CRITICAL first
   * (the ships you'll sail + their captains + the treasure chest), then the WORLD props (fort, turret,
   * island terrain, dock, outpost, …) which have procedural fallbacks and can pop in late. A shared
   * loader (one meshopt decoder) + per-asset progress feed the menu's "Loading the sea… n/total" bar.
   */
  private async loadAssets(): Promise<void> {
    const loader = await this.makeGltfLoader();
    if (!loader) { this.onAssetsReady?.(); return; }
    this._worldLoader = loader;
    // The menu's "Charting the seas…" bar tracks ONLY the CRITICAL set (4 common hulls + chest = 5), so it fills
    // to 100% exactly when the sea is playable. The rare hulls, the captains, and the heavy WORLD props all load
    // silently afterwards (deferred) — see the CRITICAL_SHIP_CLASSES / DEFERRED_SHIP_CLASSES note.
    this._assetTotal = CRITICAL_SHIP_CLASSES.length + 1;
    this.onAssetProgress?.(0, this._assetTotal);
    // Phase 1 — critical: the common hulls (incl. the GALLEON fallback template) + the chest (the core loop's props).
    await Promise.all([this.loadShipModel(loader, CRITICAL_SHIP_CLASSES), this.loadProps(loader, ['chest'])]);
    this.onAssetsReady?.();
    // Phase 2 is DEFERRED off the boot path (was ~8 MB of GLBs downloading eagerly on the menu, starving the
    // WebSocket connect + first snapshots on mobile data). It's kicked when the captain spawns (beginWorldAssets)
    // with an idle-timeout safety net so a long menu dwell still warms the cache without hurting boot/connect.
    this.scheduleWorldAssets();
  }

  private _worldLoader: GltfLoader | null = null;
  private _worldAssetsStarted = false;
  private _worldIdleTimer: number | null = null;
  private _worldIdleRic: number | null = null;

  /** Idle/timeout safety net for the deferred world assets: if nobody explicitly kicks the load (e.g. the captain
   *  lingers on the title menu), warm the cache when the main thread is idle — but never during the boot/connect
   *  crunch. Cancelled cleanly the moment beginWorldAssets runs. */
  private scheduleWorldAssets(): void {
    if (this._worldAssetsStarted || this._worldIdleTimer != null || this._worldIdleRic != null) return;
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === 'function') this._worldIdleRic = ric(() => { this._worldIdleRic = null; void this.beginWorldAssets(); }, { timeout: 12000 });
    else this._worldIdleTimer = window.setTimeout(() => { this._worldIdleTimer = null; void this.beginWorldAssets(); }, 8000);
  }

  /**
   * Load the WORLD props (fort/turret/island/dock/outpost/… + the White Whale, the Kraken, deck guns, the Capital
   * sea-landmark and the treasure islets). Every one has a procedural fallback, so they pop in late with no gap.
   * Deferred off boot so the GLB flood never competes with the menu bundle, the connect handshake, or the first
   * snapshots on 4G. Idempotent — call once the captain has spawned (main.ts) or let the idle net kick it.
   */
  async beginWorldAssets(): Promise<void> {
    if (this._worldAssetsStarted) return;
    this._worldAssetsStarted = true;
    if (this._worldIdleTimer != null) { clearTimeout(this._worldIdleTimer); this._worldIdleTimer = null; }
    if (this._worldIdleRic != null) { const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback; cic?.(this._worldIdleRic); this._worldIdleRic = null; }
    const loader = this._worldLoader;
    if (!loader) return;
    // Deferred hulls + captains FIRST (a neighbour in a rare hull upgrades from the GALLEON proxy fastest, and
    // every deck gets its helmsman) — then the heavy world props. All have graceful stand-ins, so nothing is
    // ever invisible while these stream in.
    await Promise.all([this.loadShipModel(loader, DEFERRED_SHIP_CLASSES), this.loadCaptains(loader)]);
    await this.loadProps(loader, Object.keys(PROP_MODELS).filter((n) => n !== 'chest'));
    await this.loadWhale(loader); // the animated (baked "swim" morph clip) White Whale (3.5)
    await this.loadKraken(loader); // the animated (idle+sink) Kraken — deep-water spectacle
    await this.loadCannon(loader); // instanced broadside deck guns for the whole fleet
    await this.loadLandmark(loader); // the Capital's GLB sea-landmark (lighthouse + seawall)
    await this.loadTreasureIsles(loader); // the ~60 loot islets: a leaning-coconut isle + a bare sandbar, instanced
  }

  /** Load + bake the Capital sea-landmark GLBs (the striped lighthouse tower + a crenellated seawall panel),
   *  then rebuild the world so addCapitalLandmark swaps its procedural proxy for the real models. Silent
   *  no-op on failure (the procedural landmark keeps covering the horizon). */
  private async loadLandmark(loader: GltfLoader): Promise<void> {
    try {
      // Bake each landmark GLB to ONE instanceable geometry+material. Meshy bakes single-sided materials;
      // force DoubleSide so a tree's foliage / a thin cliff face reads from every camera angle (the same
      // lesson the treasure-islet loader applies). A missing/failed GLB simply leaves the merged procedural
      // fallback covering that kind — the harbour still reads.
      const bake = async (url: string): Promise<{ geo: THREE.BufferGeometry; mat: THREE.Material } | null> => {
        try {
          const b = this.bakeMerged((await loadWithRetry(loader, url)).scene);
          if (b) { b.geo.computeBoundingBox(); (b.mat as THREE.MeshStandardMaterial).side = THREE.DoubleSide; }
          return b;
        } catch { return null; }
      };
      const [lh, wl, pine, pineTall, rock, cliff] = await Promise.all([
        bake('/models/lighthouse/lighthouse.opt.glb'),
        bake('/models/wall_segment/wall_segment.opt.glb'),
        bake('/models/pine/pine.opt.glb'),
        bake('/models/pine_tall/pine_tall.opt.glb'),
        bake('/models/rock/rock.opt.glb'),
        bake('/models/cliff/cliff.opt.glb'),
      ]);
      if (lh) this.landmarkBake.lighthouse = lh;
      if (wl) this.landmarkBake.wall = wl;
      if (pine) this.landmarkBake.pine = pine;
      if (pineTall) this.landmarkBake.pine_tall = pineTall;
      if (rock) this.landmarkBake.rock = rock;
      if (cliff) this.landmarkBake.cliff = cliff;
      // Rebuild the world so ports swap their procedural landmark proxies for the real models. Any port (not
      // just the Capital) can carry a landscape override, so rerun whenever any GLB loaded and a world exists.
      if ((lh || wl || pine || pineTall || rock || cliff) && this.lastWorld) this.setWorld(this.lastWorld);
    } catch { /* the far landmark is cosmetic — the harbour still reads without the GLBs */ }
  }

  /** Load the two treasure-islet GLBs (a leaning-coconut isle + a bare sandbar) as normalized templates
   *  (DoubleSide, footprint/height measured), then rerun setWorld so the ~60 procedural sand cones + palms
   *  are replaced by cloned GLB islets. Silent no-op on failure → the procedural cones keep covering (same
   *  fallback as the Capital landmark). */
  private async loadTreasureIsles(loader: GltfLoader): Promise<void> {
    const mk = async (url: string): Promise<IsleTpl | null> => {
      try {
        const gltf = await loadWithRetry(loader, url);
        const obj = gltf.scene;
        obj.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = false; m.receiveShadow = false;
          // Meshy bakes single-sided materials → the thin palm fronds vanish when a leaning palm faces away
          // (each islet gets a random yaw). DoubleSide so the crown reads from every angle.
          const mats = Array.isArray(m.material) ? m.material : [m.material];
          for (const mm of mats) (mm as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
        });
        const box = new THREE.Box3().setFromObject(obj);
        const size = box.getSize(new THREE.Vector3());
        return { obj, fw: Math.max(size.x, size.z) || 1, fh: size.y || 1, minY: box.min.y };
      } catch { return null; }
    };
    const [co, sa] = await Promise.all([mk('/models/coconut_isle/coconut_isle.opt.glb'), mk('/models/sand_bar/sand_bar.opt.glb')]);
    if (co) this.isleTpl.coconut = co;
    if (sa) this.isleTpl.sand = sa;
    if ((co || sa) && this.lastWorld) this.setWorld(this.lastWorld);
  }

  /**
   * 🏝️ The sand apron's material — ordinary stylised sand, plus the LAND half of the beach transition.
   *
   * The sea's shore block (see the ocean fragment) shoals the water to almost the sand's colour and lays a
   * breathing swash ring on it. That alone leaves the sand itself bone-dry right down to the waterline,
   * which still reads as a decal: real sand is DARK where the swash keeps reaching it and there is a lace
   * of foam sliding up and down that band.
   *
   * It is bought for nothing. The apron is a CONE, so its local (and world — the island group sits at
   * y = 0) height is a linear function of radius, which means world-Y IS the distance up the beach, for
   * every islet, whatever its radius. One varying, two smoothsteps, one shared uniform for the swash
   * clock, patched into the standard material with onBeforeCompile: no extra mesh, no extra draw call, no
   * per-island material.
   */
  private makeBeachMaterial(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({ color: 0xe3c892, roughness: 1 });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uSwash = this.beachSwash;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n varying float vBeachY;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n vBeachY = (modelMatrix * vec4(transformed, 1.0)).y;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\n varying float vBeachY; uniform float uSwash;')
        .replace('#include <color_fragment>', /* glsl */ `#include <color_fragment>
          // WET SAND. The band the sea keeps reaching is soaked: darker, and it takes a wet sheen.
          float wet = smoothstep(1.9, -0.3, vBeachY);
          diffuseColor.rgb *= mix(1.0, 0.66, wet);
          // …and the LACE of foam sliding up and down it, on the same slow surge clock the sea's swash
          // ring uses, so the water's foam line and the sand's wet line are one shoreline and not two.
          float lace = smoothstep(0.62, 0.0, abs(vBeachY - uSwash));
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.95, 0.97, 0.99), lace * 0.55);
        `);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n roughnessFactor *= mix(1.0, 0.42, smoothstep(1.9, -0.3, vBeachY));');
    };
    mat.customProgramCacheKey = () => 'beach-wet-v1';
    return mat;
  }
  /** The swash line's height on the sand (m), shared by every apron — driven once per frame in render(). */
  private beachSwash = { value: 0 };

  /** Onda Ilhas-Tipo: build one archetype DECORATION (a compact accent islet, a beached wreck, a rock or a
   *  palm) sized relative to a host island of `radius`. Render-only. GLB clones are tagged shared (skipped by
   *  trackOwned; disposed with the cached template); procedural pieces are owned by the island group. Returns
   *  null when a requested GLB is not loaded so the caller can skip it. */
  private makeIsleDecoration(deco: IslandDecoration, radius: number): THREE.Object3D | null {
    const g = new THREE.Group();
    if (deco.kind === 'coconut' || deco.kind === 'sand') {
      const tpl = deco.kind === 'sand' ? (this.isleTpl.sand ?? this.isleTpl.coconut) : (this.isleTpl.coconut ?? this.isleTpl.sand);
      if (!tpl) return null;
      const s = (radius * 0.34 * deco.scale) / tpl.fw; // a compact accent islet (~1/3 of the host)
      const c = tpl.obj.clone(true); c.userData.shared = true;
      c.scale.setScalar(s); c.position.y = ISLE.SINK - tpl.minY * s;
      g.add(c);
    } else if (deco.kind === 'shipwreck') {
      const tpl = this.propTemplates.get('shipwreck');
      if (!tpl) return null;
      const c = tpl.clone(true); c.userData.shared = true; c.scale.multiplyScalar(deco.scale);
      g.add(c);
    } else if (deco.kind === 'rock') {
      const m = new THREE.Mesh(new THREE.ConeGeometry(3.4 * deco.scale, 5 * deco.scale, 6), new THREE.MeshStandardMaterial({ color: 0x6b7b73, roughness: 1 }));
      m.position.y = 1.4 * deco.scale; g.add(m);
    } else { // 'palm'
      const bark = new THREE.MeshStandardMaterial({ color: 0x7a5a34, roughness: 1 });
      const frond = new THREE.MeshStandardMaterial({ color: 0x2f7d4f, roughness: 1, side: THREE.DoubleSide });
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.4 * deco.scale, 0.6 * deco.scale, 8 * deco.scale), bark);
      trunk.position.y = 4 * deco.scale; g.add(trunk);
      const crown = new THREE.Mesh(new THREE.ConeGeometry(3.2 * deco.scale, 2.6 * deco.scale, 6), frond);
      crown.position.y = 8 * deco.scale; g.add(crown);
    }
    return g;
  }

  /** Load the naval deck-gun GLB once, bake it to a single instanceable geometry+material, and build the
   *  ONE fleet-wide InstancedMesh. Distance-culled + capped in updateCannons. Silent no-op on failure
   *  (ships still render, just without the deck guns). */
  private async loadCannon(loader: GltfLoader): Promise<void> {
    try {
      const gltf = await loadWithRetry(loader, '/models/cannon/cannon.inst.opt.glb');
      const bake = this.bakeMerged(gltf.scene);
      if (!bake) return;
      const inst = new THREE.InstancedMesh(bake.geo, bake.mat, CANNON_RENDER.CAP);
      inst.frustumCulled = false;              // one mesh spanning the whole sea; per-instance culling done in updateCannons
      inst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      inst.castShadow = false; inst.receiveShadow = false;
      inst.count = 0;
      this.cannonInst = inst;
      this.shipsRoot.add(inst);
    } catch { /* deck guns are cosmetic — the fleet still sails without them */ }
  }

  /** Merge a small GLB's meshes into one geometry + its material, keeping only pos/normal/uv so multi-mesh
   *  GLBs merge cleanly, then centre it in XZ with its base at y=0 so it rests on the deck at its anchor. */
  private bakeMerged(obj: THREE.Object3D): { geo: THREE.BufferGeometry; mat: THREE.Material } | null {
    obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1);
    obj.updateMatrixWorld(true);
    const geos: THREE.BufferGeometry[] = [];
    let mat: THREE.Material | null = null;
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const g = m.geometry.clone();
      for (const nm of Object.keys(g.attributes)) if (nm !== 'position' && nm !== 'normal' && nm !== 'uv') g.deleteAttribute(nm);
      if (!g.attributes.normal) g.computeVertexNormals();
      if (!g.attributes.uv) { const n = g.attributes.position!.count; g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2)); }
      g.applyMatrix4(m.matrixWorld);
      geos.push(g);
      if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    });
    if (!geos.length || !mat) return null;
    const geo = geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
    geo.computeBoundingBox();
    const bb = geo.boundingBox!;
    geo.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2); // centre XZ, base at y=0
    return { geo, mat };
  }

  /** Load the animated White Whale (whale_swim.opt.glb) keeping its skeletal "swim" clip, normalized
   *  the same way as a prop (footprint → WHALE size, base ~ y=0). Falls back silently to the static whale. */
  private async loadWhale(loader: GltfLoader): Promise<void> {
    try {
      const gltf = await loadWithRetry(loader, '/models/whale/whale_swim.opt.glb');
      const model = gltf.scene;
      // The skinned whale deforms well past its bind-pose bbox as the tail undulates —
      // frustumCulled=false keeps it from vanishing when the bind box leaves the frustum.
      model.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; m.frustumCulled = false; } });
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const footprint = Math.max(size.x, size.z) || 1;
      const scale = 26 / footprint;
      model.scale.setScalar(scale);
      model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
      const template = new THREE.Group();
      template.add(model);
      // Measured hull of the animal, in WORLD metres, with the model origin at its BELLY (base y=0). The
      // presentation needs both: how deep to sit it so the waterline cuts the body correctly (height) and
      // how far fore/aft the head and flukes reach (length, for the blow/fluke anchors and wave-follow pitch).
      this.whaleBodyH = size.y * scale;
      this.whaleBodyL = Math.max(size.x, size.z) * scale;
      this.whaleTemplate = template;
      this.whaleClips = gltf.animations ?? [];
    } catch (err) {
      console.warn('[render] animated whale unavailable, using the static/procedural whale', err);
    }
  }

  /** Load the animated Kraken (kraken_anim.opt.glb, clips `idle`+`sink`), normalized so its tentacle-span
   *  footprint = KRAKEN.SPAN_M (GIGANTIC — mantle apex towers ~26 m over the swell). frustumCulled=false so
   *  the wildly-deforming tentacles never vanish when the bind box leaves the frustum. Silent no-op on
   *  failure (setKraken then falls back to a dark procedural silhouette). */
  private async loadKraken(loader: GltfLoader): Promise<void> {
    try {
      const gltf = await loadWithRetry(loader, '/models/kraken/kraken_anim.opt.glb');
      const model = gltf.scene;
      model.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; m.frustumCulled = false; } });
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const footprint = Math.max(size.x, size.z) || 1;
      const scale = KRAKEN.SPAN_M / footprint;
      model.scale.setScalar(scale);
      // centre horizontally, drop the mantle base to y=0 (the group's y then rides the swell / submersion)
      model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
      const template = new THREE.Group();
      template.add(model);
      this.krakenTemplate = template;
      this.krakenClips = gltf.animations ?? [];
    } catch (err) {
      console.warn('[render] animated kraken unavailable, will use a procedural silhouette', err);
    }
  }

  /** Post-processing chain: RenderPass (linear HDR) -> UnrealBloom (emissive glow,
   *  desktop only) -> OutputPass (tone map + sRGB). One uniform color pipeline for
   *  built-in AND custom shader materials, so nothing is double-graded. */
  private buildComposer(): void {
    const w = window.innerWidth, h = window.innerHeight;
    this.composer = new EffectComposer(this.renderer);
    this.composer.setPixelRatio(this.pixelRatioCap());
    this.composer.setSize(w, h);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // The bloom pass is always built but its `.enabled` is preset-driven (Settings › Graphics): a disabled
    // pass is skipped by EffectComposer at zero cost, so `medium` on mobile stays bloom-free (the shipped
    // default) while `high` can switch it on. resolution, strength, radius, threshold — only bright pixels bloom.
    this.bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), 0.62, 0.4, 0.82);
    this.bloomPass.enabled = !this.isMobile; // shipped default = desktop-only bloom
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new ShaderPass(VignetteShader));
    this.composer.addPass(new OutputPass());
  }

  /* ------------------------------- graphics settings (render-only) ------------------------------- */

  /** Device-pixel-ratio cap for the active preset. `medium` reproduces the shipped cap exactly. LIGHT MODE hard-
   *  caps it at 1 (the single biggest GPU-memory + fragment-load lever) so the composer render-targets shrink to
   *  ~1/2.25 the pixels of a dpr-1.5 phone — the headroom a WKWebView needs to also hold the walkable port. */
  private pixelRatioCap(): number {
    const dpr = window.devicePixelRatio || 1;
    const cap = this.graphicsPreset === 'low' ? 1 : this.graphicsPreset === 'high' ? 2 : (this.isMobile ? 1.5 : 2);
    return Math.min(dpr, this.lowMem ? Math.min(cap, 1) : cap);
  }

  /** Whether emissive bloom should run for the active preset (off on Low, desktop-only on Medium, on for High).
   *  LIGHT MODE forces it off — the bloom mip chain is pure GPU-buffer memory a wallet webview can't spare. */
  private bloomForPreset(): boolean {
    if (this.lowMem) return false;
    return this.graphicsPreset === 'low' ? false : this.graphicsPreset === 'high' ? true : !this.isMobile;
  }

  /**
   * LIGHT MODE — the memory-frugal profile for a wallet in-app browser (Phantom/MetaMask/…) or a low-RAM phone.
   * Caps the pixel-ratio at 1, kills bloom + the decorative gull flock, and (via `lowMem`) makes the sea→port
   * dispose unconditional. Applied ONCE at boot from platform.lightModeDecision(); NEVER on desktop, so the
   * full-fat experience is untouched there. Render-only — zero sim/netcode impact, safe to flip live.
   */
  setLowMemoryMode(on: boolean): void {
    if (this.lowMem === on) return;
    this.lowMem = on;
    const cap = this.pixelRatioCap();
    this.renderer.setPixelRatio(cap);
    this.composer?.setPixelRatio(cap);
    this.composer?.setSize(window.innerWidth, window.innerHeight);
    if (this.bloomPass) { this.bloomPass.enabled = this.bloomForPreset(); this.bloomPass.setSize(window.innerWidth, window.innerHeight); }
    this.birdsRoot.visible = !on && this.graphicsPreset !== 'low';
    this.applyWaterQuality();          // …and the per-pixel sea + the storm LOD (see applyStormQuality)
  }

  /** Whether light mode is active (portScene reads this to trim its own footprint too). */
  get isLowMemory(): boolean { return this.lowMem; }

  /**
   * Apply a graphics quality preset. Render-only levers: the renderer/composer pixel-ratio cap (the single
   * biggest FPS knob), the emissive bloom pass, and cheap decorative density (the gull flock). Never touches
   * the ocean height shader, the world-edge fog, entity positions or anything the sim/netcode observe, so it
   * can be flipped live with zero desync. `medium` == the game's shipped look.
   */
  setGraphicsPreset(preset: 'low' | 'medium' | 'high'): void {
    if (preset !== 'low' && preset !== 'medium' && preset !== 'high') return;
    this.graphicsPreset = preset;
    const cap = this.pixelRatioCap();
    this.renderer.setPixelRatio(cap);
    this.composer?.setPixelRatio(cap);
    this.composer?.setSize(window.innerWidth, window.innerHeight);
    if (this.bloomPass) { this.bloomPass.enabled = this.bloomForPreset(); this.bloomPass.setSize(window.innerWidth, window.innerHeight); }
    // decorative density: the gull flock is pure atmosphere — drop it on Low (or in light mode) to shave draw calls.
    this.birdsRoot.visible = !this.lowMem && preset !== 'low';
    // water + storm LOD: Low falls back to the legacy flat-shaded sea (unless `?water=high` pins it) and
    // drops the storm's fill-rate-heavy layers. Both resolve inside applyWaterQuality, so they cannot
    // disagree — a simplified sea with a full-fury lens spatter on top of it is the worst of both.
    this.applyWaterQuality();
  }

  /* ------------------------------- camera settings (render-only) ------------------------------- */

  /**
   * The filmic grade, and its kill-switch.
   *
   * 'aces' (shipped) — ACES Filmic at exposure 1.06. Highlights ROLL instead of clipping: the sun disc,
   * a cannon's muzzle flash, a lantern on black water and a sunlit whitecap all keep colour right up to
   * white, and the mid-tones sit a touch richer. Every shader in the game already writes LINEAR (they all
   * end in `pow(col, 2.2)`), so the grade is applied exactly once, by OutputPass, for the whole frame.
   *
   * 'legacy' — the pre-filmic look: a plain linear clamp at exposure 1.0. Kept reachable with `?tone=legacy`
   * because this single enum affects the sea, the walkable port, the house interior, the Draga ritual, the
   * FX and the 3D nametags at once; a look that regresses in one of them must be diagnosable in seconds
   * without a rebuild.
   */
  private applyToneMapping(pref: 'legacy' | 'aces' | 'auto'): void {
    this.tonePref = pref;
    if (pref === 'legacy') {
      this.renderer.toneMapping = THREE.LinearToneMapping;
      this.renderer.toneMappingExposure = 1.0;
    } else {
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      // 1.05 is the SHIPPED, owner-approved exposure and it is deliberately left alone. ACES has been
      // live since before this wave, so the port, the house interior and the Draga ritual are already
      // graded by it and already signed off; moving this number would re-grade all of them for the sake
      // of the sea, which is the one place a shader change can be made instead. Anything the new sky
      // needed was recalibrated inside the dome and the ocean, where it can be seen.
      this.renderer.toneMappingExposure = 1.05;
    }
    // every material that was already compiled has the old tone-mapping define baked in
    this.scene.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (!m) return;
      if (Array.isArray(m)) for (const x of m) x.needsUpdate = true; else m.needsUpdate = true;
    });
  }
  private tonePref: 'legacy' | 'aces' | 'auto' = 'auto';
  /** Flip the filmic grade live (Settings / `?tone=` / an A/B harness). */
  setToneMapping(pref: 'legacy' | 'aces' | 'auto'): void { this.applyToneMapping(pref); }
  /** Which grade is actually running — for the Settings readout and a headless render-proof. */
  get toneMapping(): 'legacy' | 'aces' { return this.tonePref === 'legacy' ? 'legacy' : 'aces'; }

  /**
   * Where the sky's two lights actually are, as plain numbers — the headless render-proof for the sky
   * dome. A screenshot can show that a moon is drawn; only this can show that the disc in the sky, the
   * light on the hulls and the road on the water are the SAME vector, which is the one thing about this
   * system that is easy to get silently wrong. Read-only, allocation-light, no debug-only gating (it is
   * three numbers and it is how the QA harness aims a camera at the moon).
   */
  get skyInfo(): { sun: [number, number, number]; moon: [number, number, number]; moonAmt: number; night: number; storm: number; tone: string } {
    const s = this.oceanMat.uniforms.uSun!.value as THREE.Vector3;
    return {
      sun: [s.x, s.y, s.z],
      moon: [this.moonDir.x, this.moonDir.y, this.moonDir.z],
      moonAmt: this.moonAmt,
      night: this.nightFactor,
      storm: this._storm,
      tone: this.toneMapping,
    };
  }

  /** Sea-camera drag sensitivity (0.5×..2.0×). 1.0 == shipped feel. */
  setCameraSensitivity(mult: number): void {
    this.camSensitivity = Math.max(0.5, Math.min(2, Number.isFinite(mult) ? mult : 1));
  }
  /** Invert the horizontal camera-drag direction. false == shipped WoW/L2 convention. */
  setInvertCameraDrag(on: boolean): void { this.invertCamX = !!on; }

  /** Load + normalize every per-class AI ship model (parallel); fall back to procedural per class. */
  /** Load ship-class GLBs into templates. `classes` (optional) restricts the load to a subset — the boot-diet
   *  phase split loads the CRITICAL hulls first and the rare hulls later (see the class-list note). Loading a
   *  subset still rebuilds spawned ships so any stand-in (GALLEON-proxy) hull upgrades to its own mesh in place. */
  private async loadShipModel(loader: GltfLoader, classes?: number[]): Promise<void> {
    const entries = Object.entries(SHIP_MODELS).filter(([clsStr]) => !classes || classes.includes(Number(clsStr)));
    if (entries.length === 0) return;
    await Promise.all(entries.map(([clsStr, cfg]) =>
      this.loadShipClass(loader, Number(clsStr), cfg).finally(() => this.bumpAsset())));

    // rebuild any ships already spawned with a stand-in mesh now that these templates exist
    for (const [id, g] of this.ships) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); }
  }

  private sailTex?: THREE.CanvasTexture;
  /** A tileable woven-canvas texture for the sails (procedural, one shared texture). */
  private makeSailTexture(): THREE.CanvasTexture {
    if (this.sailTex) return this.sailTex;
    const s = 256, c = document.createElement('canvas'); c.width = c.height = s;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#e6ddc8'; ctx.fillRect(0, 0, s, s);                 // warm off-white canvas
    ctx.globalAlpha = 0.06;                                             // fine woven threads
    for (let i = 0; i < s; i += 2) { ctx.fillStyle = (i % 4 === 0) ? '#3a3020' : '#fff'; ctx.fillRect(0, i, s, 1); ctx.fillRect(i, 0, 1, s); }
    ctx.globalAlpha = 0.2; ctx.fillStyle = '#7a6b4c';                   // vertical sail-panel seams (strips of canvas, dense)
    for (let x = 26; x < s; x += 28) { ctx.fillRect(x, 0, 2, s); ctx.globalAlpha = 0.08; ctx.fillRect(x + 2, 0, 2, s); ctx.globalAlpha = 0.2; }
    ctx.globalAlpha = 0.1; ctx.fillStyle = '#6a5a3c';                   // horizontal reef-band stitching
    for (let y = 40; y < s; y += 66) ctx.fillRect(0, y, s, 1.5);
    ctx.globalAlpha = 0.06; ctx.fillStyle = '#4a3c24';                  // weathered stains
    for (let k = 0; k < 40; k++) { ctx.beginPath(); ctx.arc(Math.random() * s, Math.random() * s, 6 + Math.random() * 26, 0, 6.3); ctx.fill(); }
    ctx.globalAlpha = 1;
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(3, 3);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
    this.sailTex = tex;
    return tex;
  }

  /** Project planar UVs onto a UV-less geometry's two widest axes (sails are ~flat). */
  private generatePlanarUV(geo: THREE.BufferGeometry): void {
    const pos = geo.attributes.position as THREE.BufferAttribute;
    geo.computeBoundingBox();
    const bb = geo.boundingBox!;
    const ext = new THREE.Vector3().subVectors(bb.max, bb.min);
    const axes = [0, 1, 2].sort((a, b) => ext.getComponent(b) - ext.getComponent(a));
    const u = axes[0]!, v = axes[1]!; // drop the thin sail-normal axis
    const uMin = bb.min.getComponent(u), uExt = ext.getComponent(u) || 1;
    const vMin = bb.min.getComponent(v), vExt = ext.getComponent(v) || 1;
    const comp = (i: number, ax: number) => ax === 0 ? pos.getX(i) : ax === 1 ? pos.getY(i) : pos.getZ(i);
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      uv[i * 2] = (comp(i, u) - uMin) / uExt;
      uv[i * 2 + 1] = (comp(i, v) - vMin) / vExt;
    }
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  }

  /** Load one class GLB, normalize it to the base hull length, and cache its template + furl clips. */
  private async loadShipClass(
    loader: { loadAsync(url: string): Promise<{ scene: THREE.Object3D; animations?: THREE.AnimationClip[] }> },
    cls: number,
    cfg: { url: string; yaw: number; draft: number },
  ): Promise<void> {
    try {
      const gltf = await loadWithRetry(loader, cfg.url);
      const model = gltf.scene;
      const sailTex = this.makeSailTexture();
      model.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = false; m.receiveShadow = false;
        // Sails are the Blender morph meshes (the furl morph) — separate material from the
        // fused Meshy hull. Give them a woven-canvas texture so they read as cloth, not flat plastic.
        if (!isSailMesh(m)) return;
        // the Blender sail mesh ships WITHOUT UVs — generate planar ones so the map is sampleable
        if (!m.geometry.attributes.uv) this.generatePlanarUV(m.geometry);
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        for (const mm of mats) {
          const mat = mm as THREE.MeshStandardMaterial;
          if (mat && 'map' in mat) { mat.map = sailTex; mat.roughness = Math.min(1, (mat.roughness ?? 0.8) + 0.12); mat.metalness = 0; mat.needsUpdate = true; }
        }
      });

      // normalize: center, scale to the base hull length, orient bow -> +X, sit on the waterline
      const box0 = new THREE.Box3().setFromObject(model);
      const size = box0.getSize(new THREE.Vector3());
      const center = box0.getCenter(new THREE.Vector3());
      const longest = Math.max(size.x, size.z) || 1;
      const scale = (SHIP.LENGTH * 0.95) / longest;
      model.scale.setScalar(scale);
      model.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

      const inner = new THREE.Group();
      inner.add(model);
      const box1 = new THREE.Box3().setFromObject(inner);
      inner.position.y = -box1.min.y - cfg.draft; // seat the keel cfg.draft metres below the waterline (mast height irrelevant)

      const template = new THREE.Group();
      template.rotation.y = cfg.yaw;
      template.add(inner);

      // ---- Onda Velas: bake the cloth rig ONCE per class, on the shared template geometry ----
      // The template is unparented, so `mesh.matrixWorld` after this update is exactly the sail's
      // sail-local -> SHIP-GROUP matrix (bow +X, up +Y, port +Z) — which is what lets sails.ts do the whole
      // panel analysis in the frame a sailor thinks in. Every clone of this class reuses the attribute.
      let sailRig: SailRig | undefined;
      template.updateMatrixWorld(true);
      template.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || !isSailMesh(m)) return;
        const r = bakeSailRig(m.geometry, m.matrixWorld);
        if (r && !sailRig) sailRig = r; // one rig per class (the panels of one mesh share a basis)
      });

      // the masthead, in group-local metres — where the wind pennant flies (see updatePennants)
      const topY = new THREE.Box3().setFromObject(template).max.y;

      // furl clips: baked Blender morph anim (sail trim 0..1 -> clip time 0..duration)
      this.shipTemplates.set(cls, { template, furlClips: gltf.animations ?? [], sailRig, topY });
    } catch (err) {
      // loadWithRetry already rode over the transient deploy-window aborts; reaching here means the asset is
      // genuinely unreachable this session, so we accept the procedural hull. FOLLOW-UP (not done — needs a hook
      // in the spawn/mesh path): a cheap "self-heal" could re-attempt loadShipClass(cls) the next time a remote
      // ship of a template-less class enters the AOI, upgrading late-arriving sessions. Kept out of scope here to
      // stay surgical; the retry covers the incident's transient case.
      console.warn(`[render] ship model for class ${cls} unavailable (${cfg.url}), using procedural`, err);
    }
  }

  /** Load the optional GLB props (hq/turret/chest) in parallel; each falls back to procedural. */
  private async loadProps(loader: GltfLoader, names: string[] = Object.keys(PROP_MODELS)): Promise<void> {
    await Promise.all(names.map((n) => {
      const cfg = PROP_MODELS[n]; if (!cfg) return Promise.resolve();
      return this.loadPropModel(loader, cfg).finally(() => this.bumpAsset());
    }));

    // re-render only what the freshly-loaded props in THIS batch affect, and skip if they failed to load
    // (so a missing GLB never triggers a pointless world clear / bases rebuild)
    if (this.lastWorld && ['chest', 'outpost', 'shipwreck'].some((n) => this.propTemplates.has(n))) this.setWorld(this.lastWorld);
    if (this.lastBases && ['hq', 'turret', 'island', 'dock', 'drydock', 'storage', 'farm', 'tavern', 'foundry', 'crop_plot'].some((n) => this.propTemplates.has(n))) {
      for (const [id, g] of this.bases) { this.basesRoot.remove(g); disposeGroup(g); this.bases.delete(id); }
      this.syncBases(this.lastBases, this.lastYourIslandId);
    }
  }

  /** Load one prop GLB, normalize it to a target footprint with its base at y=0, and cache it. */
  private async loadPropModel(
    loader: { loadAsync(url: string): Promise<{ scene: THREE.Object3D }> },
    cfg: { name: string; url: string; size: number; yaw: number; ember?: { day: number; night: number } },
  ): Promise<void> {
    try {
      const gltf = await loadWithRetry(loader, cfg.url);
      const model = gltf.scene;
      const emberMats = new Set<THREE.MeshStandardMaterial>(); // unique emissive template materials (shared by every clone)
      model.traverse((o) => {
        const m = o as THREE.Mesh; if (!m.isMesh) return;
        m.castShadow = false; m.receiveShadow = false;
        if (cfg.ember) { // Onda Sistema de Luz — collect the baked-emissive materials so the glow can breathe with the night
          for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
            const s = mat as THREE.MeshStandardMaterial;
            if (s.isMeshStandardMaterial && (s.emissiveMap || (s.emissive && (s.emissive.r || s.emissive.g || s.emissive.b)))) emberMats.add(s);
          }
        }
      });
      // register the TEMPLATE materials once, anchored to the scene root (never pruned): clone(true) shares
      // materials, so a single registration drives the furnace/lamp glow of every instance in unison.
      if (cfg.ember) for (const mat of emberMats) this.lights.registerEmissive(mat, this.scene, cfg.ember.day, cfg.ember.night);
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const footprint = Math.max(size.x, size.z) || 1;
      const scale = cfg.size / footprint;
      model.scale.setScalar(scale);
      // center on x/z, rest the base on y=0 (placement code positions the base anchor)
      model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
      const template = new THREE.Group();
      template.rotation.y = cfg.yaw;
      template.add(model);
      this.propTemplates.set(cfg.name, template);
    } catch (err) {
      console.warn(`[render] prop "${cfg.name}" unavailable (${cfg.url}), using procedural`, err);
    }
  }

  /** Load the per-class captain GLBs in parallel; each normalizes to 1 unit tall, feet at y=0,
   *  facing +X. A missing GLB is skipped (no captain for that class). */
  private async loadCaptains(loader: GltfLoader): Promise<void> {
    const results = await Promise.all(Object.entries(CAPTAIN_MODELS).map(async ([clsStr, url]) => {
      try {
        const gltf = await loadWithRetry(loader, url);
        const model = gltf.scene;
        model.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; } });
        // normalize: center x/z, feet at y=0, scale so the figure is exactly 1 unit tall
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const scale = 1 / (size.y || 1);
        model.scale.setScalar(scale);
        model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
        const template = new THREE.Group();
        template.add(model);
        this.captainTemplates.set(Number(clsStr), template);
        return true;
      } catch (err) {
        console.warn(`[render] captain for class ${clsStr} unavailable (${url})`, err);
        return false;
      } finally {
        this.bumpAsset();
      }
    }));

    // captains finished after ships were built -> rebuild so each picks up its helmsman
    if (results.some(Boolean)) {
      for (const [id, g] of this.ships) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); }
    }
  }

  /** Clone this class's captain (if loaded) onto the ship group at the helm, fixed real height,
   *  feet seated on the actual deck (each class's hull has a different deck height). */
  private attachCaptain(g: THREE.Group, cls: number, hull: THREE.Object3D): void {
    const tpl = this.captainTemplates.get(cls);
    if (!tpl) return;
    const cap = tpl.clone(true);
    // the ship group is ×classScale; divide it out so the captain is HEIGHT_M real metres on every class
    cap.scale.setScalar(HELM.HEIGHT_M / classScale(cls));
    // seat the feet on the real deck under the helm (raycast down — hulls differ in deck height per class)
    hull.updateMatrixWorld(true);
    const deckY = this.surfaceYAt(hull, HELM.X, HELM.Z, HELM.Y);
    cap.position.set(HELM.X, deckY, HELM.Z);
    cap.rotation.y = HELM.YAW;
    cap.userData.phase = Math.random() * Math.PI * 2; // per-captain idle offset (cosmetic, not sim)
    g.userData.captain = cap;
    g.add(cap);

    // a ship's wheel right in front of the helmsman so he reads as actively steering the ship.
    // built in real metres → scale by 1/classScale so g's ×classScale renders it real-sized (like the captain);
    // positioned a FIXED real gap forward (+X, toward the bow) of the captain on every class.
    const wheel = this.helmWheel().clone(true);
    wheel.userData.shared = true;                       // shares the template's geom/mats — never disposed per-ship
    const cs = classScale(cls);
    wheel.scale.setScalar(1 / cs);
    wheel.position.set(HELM.X + HELM.WHEEL_GAP / cs, deckY, HELM.Z);
    g.userData.helmWheel = wheel;
    g.userData.helmDisc = wheel.getObjectByName('helmDisc'); // the spinnable rim — turned by the steering code
    g.add(wheel);
  }

  /** Spread deck crew (the captain GLBs reused as shorter deckhands — a deliberate gambiarra) along the
   *  open deck of `g`, each seated on the real deck at its station. Stored in g.userData.crew for the
   *  bustle/grapple animation. Crew share the template geom/mats (clone(true)) so — like the captain —
   *  they are never disposed individually (the ship group removal drops the clones). */
  private attachCrew(g: THREE.Group, cls: number, hull: THREE.Object3D): void {
    const models: THREE.Object3D[] = [];
    for (const c of [ShipClassId.SLOOP, ShipClassId.BRIGANTINE, ShipClassId.GALLEON, ShipClassId.FRIGATE, ShipClassId.CUTTER, ShipClassId.FLUYT, ShipClassId.EAST_INDIAMAN]) {
      const m = this.captainTemplates.get(c); if (m) models.push(m);
    }
    if (!models.length) return;
    const n = CREW.COUNT[cls] ?? 3;
    const cs = classScale(cls), sy = CREW.HEIGHT_M / cs;
    const HB = 2.5; // hull-template-local half-beam of the WALKABLE deck (hull beam ~3.5; the deck is narrower)
    hull.updateMatrixWorld(true);
    const deckFallback = (g.userData.captain as THREE.Object3D | undefined)?.position.y ?? HELM.Y;
    const crew: CrewMember[] = [];
    for (let i = 0; i < n; i++) {
      const side = i % 2 === 0 ? -1 : 1;                                   // alternate port / starboard
      const f = n > 1 ? i / (n - 1) : 0.5;                                 // 0 (mid-aft) … 1 (fore)
      const bx = -4.0 + f * 9.0;                                          // main deck, off the stern helm + the bow tip
      const taper = clamp(1 - Math.max(0, bx) / 8, 0.4, 1);               // the hull narrows toward the bow
      const bz = side * HB * (0.5 + 0.2 * ((i >> 1) % 2)) * taper;
      const mesh = models[(i + cls + 1) % models.length]!.clone(true);     // vary the figure per station/ship
      mesh.scale.setScalar(sy);
      const by = this.surfaceYAt(hull, bx, bz, deckFallback, 5.0);         // DECK only — skip the rigging above 5 m
      mesh.position.set(bx, by, bz);
      mesh.rotation.y = HELM.YAW - side * (Math.PI / 2) * 0.8;             // face mostly outboard (man the rail/guns)
      crew.push({ mesh, bx, bz, by, ph: Math.random() * Math.PI * 2, sy });
      g.add(mesh);
    }
    g.userData.crew = crew;
  }

  /** Pre-compute this ship's broadside gun stations once at build time (hull-local, deck-seated via the
   *  same raycast the crew use). updateCannons writes them into the shared fleet InstancedMesh each frame,
   *  so a full broadside on every ship costs ONE draw call. Barrels point outboard (± the hull's beam). */
  private attachCannons(g: THREE.Group, cls: number, hull: THREE.Object3D): void {
    const bxs = CANNON_RENDER.BX[cls] ?? CANNON_RENDER.BX[1]!;
    hull.updateMatrixWorld(true);
    const deckFallback = (g.userData.captain as THREE.Object3D | undefined)?.position.y ?? HELM.Y;
    const anchors: { x: number; y: number; z: number; yaw: number }[] = [];
    for (const bx of bxs) for (const side of [-1, 1] as const) {
      const bz = side * CANNON_RENDER.RAIL_Z;
      const by = this.surfaceYAt(hull, bx, bz, deckFallback, 5.0); // seat on the real deck (skip rigging above 5 m)
      anchors.push({ x: bx, y: by, z: bz, yaw: -side * (Math.PI / 2) * CANNON_RENDER.DIR });
    }
    g.userData.cannons = anchors;
  }

  /** Deck crew come alive: a constant bustle (bob + sway + shuffle around their station), a quick crouch
   *  when the ship fires a broadside, and frantic running/jostling while grappled. Distance-culled so far
   *  ships pay nothing (crew detail only reads up close). */
  private updateCrew(t: number): void {
    const cam = this.camera.position;
    for (const g of this.ships.values()) {
      const crew = g.userData.crew as CrewMember[] | undefined;
      if (!crew) continue;
      const ddx = g.position.x - cam.x, ddz = g.position.z - cam.z;
      if (ddx * ddx + ddz * ddz > CREW.CULL_D2) { for (const c of crew) c.mesh.visible = false; continue; }
      const r0 = g.userData.recoilT0 as number | undefined;
      const since = r0 !== undefined ? t - r0 : 99;
      const fE = since >= 0 && since < 0.45 ? 1 - since / 0.45 : 0;        // recoil crouch envelope 1→0
      const agi = g.userData.grappled ? 1 : 0;                            // running/brawling while boarded
      // bailing: crew frantically scoop water once the hull floods (but not while
      // boarded — they fight — and not once basically sunk). Derived from hp, so it
      // works for every visible ship with no extra wire data. Free: no new geometry.
      const flood = (g.userData.flood as number) ?? 0;
      const bail = agi ? 0 : clamp((flood - 0.12) / 0.4, 0, 1) * (1 - clamp((flood - 0.9) / 0.1, 0, 1));
      const sp = 1 + agi * 1.8 + bail * 1.1;                              // faster, more frantic when bailing
      for (const c of crew) {
        c.mesh.visible = true;
        const ph = c.ph;
        const scoop = 0.5 + 0.5 * Math.sin(t * 3.6 * (1 + 0.4 * bail) + ph); // 0…1 heave cycle
        const wander = 1 - 0.6 * bail;                                    // stay at the bail station, don't roam
        c.mesh.position.x = c.bx + Math.sin(t * 1.3 * sp + ph) * (0.05 + agi * 0.24) * wander;
        c.mesh.position.z = c.bz + Math.cos(t * 1.1 * sp + ph * 0.7) * (0.05 + agi * 0.18) * wander;
        // Rigid low-poly figures: sell "bailing" as a CROUCH/HEAVE — dip down + squash
        // (scale.y) with only a hint of stoop. A big body hinge reads as tipping over.
        c.mesh.position.y = c.by + (bail > 0.001 ? -bail * 0.13 * scoop : Math.abs(Math.sin(t * 2.2 * sp + ph)) * (0.03 + agi * 0.12));
        c.mesh.rotation.z = Math.sin(t * 1.6 * sp + ph * 1.3) * (0.06 + agi * 0.2);
        c.mesh.rotation.x = -fE * 0.5 + bail * scoop * 0.2;              // duck on fire; a SLIGHT stoop, not a faceplant
        c.mesh.scale.y = c.sy * (1 - fE * 0.12 - bail * scoop * 0.16);   // crouch squash is what sells the scoop
      }
    }
  }

  /** Lazily build (once) the helmsman's ship-wheel prop: a spoked wheel on a binnacle post, in REAL
   *  metres with its base at y=0 and the axle along +X (fore-aft) so a captain aft of it grips the rim.
   *  Two merged, vertex-coloured (wood+brass) meshes that share ONE material → 2 draw calls/ship max:
   *  a STATIC base (post+collar) and a spinnable DISC (rim+hub+spokes+pegs, centred on its axle, named
   *  'helmDisc') that the steering code rotates as the ship turns. Cloned per ship; template lives forever. */
  private helmWheel(): THREE.Group {
    if (this.helmWheelTemplate) return this.helmWheelTemplate;
    const WOOD = new THREE.Color(0x4a2c14), BRASS = new THREE.Color(0xb98a32);
    const paint = (geo: THREE.BufferGeometry, col: THREE.Color): THREE.BufferGeometry => {
      const n = geo.attributes.position!.count, arr = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { arr[i * 3] = col.r; arr[i * 3 + 1] = col.g; arr[i * 3 + 2] = col.b; }
      geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
      return geo;
    };
    const R = 0.92;     // wheel rim radius
    const CY = 1.78;    // wheel-centre height above the deck (≈ a heroic captain's hands)
    const postH = CY - R + 0.12;
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.25 });
    // static base — post + brass collar
    const baseParts = [
      paint(new THREE.CylinderGeometry(0.15, 0.27, postH, 8).translate(0, postH / 2, 0), WOOD),
      paint(new THREE.CylinderGeometry(0.2, 0.22, 0.16, 10).translate(0, postH, 0), BRASS),
    ];
    const base = new THREE.Mesh(mergeGeometries(baseParts, false)!, mat);
    for (const p of baseParts) p.dispose();
    // spinnable disc — rim + hub + spokes + pegs, CENTRED on the origin (axle = X) so it turns about itself
    const discParts: THREE.BufferGeometry[] = [];
    const rim = new THREE.TorusGeometry(R, 0.07, 8, 28); rim.rotateY(Math.PI / 2); discParts.push(paint(rim, WOOD));
    const hub = new THREE.CylinderGeometry(0.17, 0.17, 0.24, 12); hub.rotateZ(Math.PI / 2); discParts.push(paint(hub, BRASS));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2, cy = Math.cos(a), cz = Math.sin(a);
      const spoke = new THREE.CylinderGeometry(0.035, 0.035, R + 0.05, 6);
      spoke.rotateX(a); spoke.translate(0, cy * R * 0.5, cz * R * 0.5);   // align +Y cylinder to (0, cos a, sin a)
      discParts.push(paint(spoke, WOOD));
      const peg = new THREE.CylinderGeometry(0.045, 0.045, 0.34, 6);
      peg.rotateZ(Math.PI / 2); peg.translate(0, cy * (R + 0.07), cz * (R + 0.07)); // poke out along ±X past the rim
      discParts.push(paint(peg, WOOD));
    }
    const disc = new THREE.Mesh(mergeGeometries(discParts, false)!, mat);
    for (const p of discParts) p.dispose();
    const discPivot = new THREE.Group(); discPivot.name = 'helmDisc'; discPivot.position.y = CY; discPivot.add(disc);
    const grp = new THREE.Group(); grp.add(base); grp.add(discPivot);
    this.helmWheelTemplate = grp;
    return grp;
  }

  /** The helm comes alive: the WHEEL spins into the turn (held proportional to the ship's turn rate) and
   *  the CAPTAIN leans into it like he's working it — plus a subtle idle (weight-shift + horizon scan) so
   *  he reads as alive even sailing straight. Feet stay planted; inherits the ship's bob/roll. */
  private updateCaptains(t: number, dt: number): void {
    const k = Math.min(1, dt * 6); // wheel-ease factor (frame-rate independent)
    for (const g of this.ships.values()) {
      const tn = clamp(((g.userData.turn as number) ?? 0) / 0.55, -1, 1); // normalized steer −1(port)…+1(stbd)
      const disc = g.userData.helmDisc as THREE.Object3D | undefined;
      if (disc) disc.rotation.x += (tn * 3.0 - disc.rotation.x) * k;   // hold the wheel turned into the steer (~±172°)
      const cap = g.userData.captain as THREE.Object3D | undefined;
      if (!cap) continue;
      const ph = cap.userData.phase as number;
      cap.rotation.z = Math.sin(t * 0.9 + ph) * 0.05 - tn * 0.16;         // brace + heave on the wheel INTO the turn
      cap.rotation.y = HELM.YAW + Math.sin(t * 0.4 + ph * 1.3) * 0.07 + tn * 0.12; // glance toward the turn
      cap.rotation.x = Math.abs(tn) * 0.07;                              // lean forward a touch while working it
    }
  }

  /* --------------------------- water quality (kill-switch) --------------------------- */

  /** `?water=low|high` — the owner/QA override. 'auto' lets the probe below decide. Read once at boot. */
  private waterPref: 'low' | 'high' | 'auto' = 'auto';
  /** Does this GL context look able to run the per-pixel lit sea without wobbling or crawling? */
  private waterCapable = true;
  /** The RESOLVED path currently compiled into the ocean material. */
  private waterHigh = true;

  /** Honest weak-device probe. The lit path evaluates the wave phase per PIXEL, so it needs (a) real highp
   *  floats in the fragment stage — a mediump-only GPU quantises `k·p − ωt` into visible banding at world
   *  coordinates of a few thousand metres — and (b) fragment derivatives, which the far-field normal already
   *  required. A device that reports a GLES2-minimum uniform budget is also assumed to be a fill-rate potato.
   *  Everything here only ever DOWNGRADES; `?water=high` overrides it. */
  private probeWaterCapable(): boolean {
    try {
      const caps = this.renderer.capabilities;
      if (caps.precision !== 'highp') return false;                       // no highp fragment floats → phase banding
      if (!caps.isWebGL2 && !this.renderer.extensions.get('OES_standard_derivatives')) return false;
      const gl = this.renderer.getContext();
      const maxFrag = gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS) as number;
      if (typeof maxFrag === 'number' && maxFrag > 0 && maxFrag < 256) return false; // GLES2-class budget
      return true;
    } catch { return false; } // any probe failure = take the cheap path, never the broken one
  }

  /** Fold the override, the probe and the quality levers into the single boolean the shader is built from. */
  private resolveWaterHigh(): boolean {
    if (this.waterPref === 'low') return false;
    if (this.waterPref === 'high') return true;      // explicit owner override beats every heuristic
    if (!this.waterCapable) return false;
    if (this.lowMem) return false;                   // wallet webview / low-RAM phone
    if (this.graphicsPreset === 'low') return false; // Settings › Graphics › Low
    return true;
  }

  /** Recompile + hot-swap the ocean material if the resolved quality changed. Render-only and idempotent:
   *  the wave GEOMETRY (and therefore every bit of buoyancy, wake and prediction maths) is identical on both
   *  paths, so this is safe to flip live, mid-battle, with zero desync. */
  private applyWaterQuality(): void {
    this.applyStormQuality();
    const mesh = this.ocean as THREE.Mesh | undefined; // may be called before the constructor builds the sea
    const want = this.resolveWaterHigh();
    if (!mesh) { this.waterHigh = want; return; }      // constructor path: buildOceanMaterial reads the flag
    if (want === this.waterHigh) return;
    this.waterHigh = want;
    const old = this.oceanMat;
    this.oceanMat = this.buildOceanMaterial(want ? 'high' : 'low', true);
    // carry the live grade across so the swap is invisible (colours/time/glints are re-driven next frame anyway)
    for (const key of Object.keys(old.uniforms)) {
      const dst = this.oceanMat.uniforms[key]; const src = old.uniforms[key];
      if (!dst || !src) continue;
      const dv = dst.value as unknown, sv = src.value as unknown;
      if (typeof sv === 'number') dst.value = sv;
      else if (dv instanceof THREE.Color && sv instanceof THREE.Color) dv.copy(sv);
      else if (dv instanceof THREE.Vector3 && sv instanceof THREE.Vector3) dv.copy(sv);
      else if (dv instanceof THREE.Vector2 && sv instanceof THREE.Vector2) dv.copy(sv);
    }
    mesh.material = this.oceanMat;
    old.dispose();
    // the Kelvin wake rides the same quality lever (Low = stem only, no arms) — same hot swap, no reload
    this.wakeFx?.setQuality(want ? 'high' : 'low');
    // …and so does the impact spray: Low halves the particle COUNT but never drops the effect, because a
    // splash is combat feedback (where did that shot land?), not decoration.
    this.splashFx?.setQuality(want ? 'high' : 'low');
    // …and so does the SKY (Onda Céu v1): Low compiles a gradient + sun + moon + stars dome with no FBM
    // cloud decks. Same hot swap — rebuild the mesh in place, next frame's updateAtmosphere re-drives every
    // uniform from scratch, so nothing has to be carried across.
    if (this.clouds) {
      const oldSky = this.clouds;
      this.scene.remove(oldSky);
      (oldSky.material as THREE.ShaderMaterial).dispose();
      oldSky.geometry.dispose();
      this.clouds = this.buildSkyDome(want);
      this.scene.add(this.clouds);
    }
  }

  /**
   * The storm's own LOD, resolved from the SAME levers the sea is (light mode, Settings > Graphics, and
   * `?water=low`) — not from the graphics preset alone.
   *
   * `?water=low` used to simplify the water and leave the weather at full fury on top of it: a flat-shaded
   * legacy sea under a fullscreen lens spatter, mid-distance rain curtains and spindrift, which is both
   * the ugliest combination and the most expensive part of the frame left running on the device that
   * asked for less. Low keeps the thing that carries the storm — the VAGA, which is geometry the cheap
   * path draws just as well — plus a thinner rain, and drops the fill-rate layers (curtains, spindrift,
   * waterspout, lens).
   */
  private applyStormQuality(): void {
    const low = this.lowMem || this.waterPref === 'low' || (this.waterPref !== 'high' && this.graphicsPreset === 'low');
    this.stormFx?.setQuality(low ? 'low' : this.graphicsPreset);
    // Onda Velas rides the SAME lever (never a separate opinion): Low keeps the thing that carries the
    // feature — the SHAPE, which is a handful of vertex ALU either way (a full sail still bellies, a stalled
    // one still collapses and heels) — and drops the per-vertex flogging noise + its normal tilt. Hot-swappable
    // with no recompile: it is a uniform branch, so the whole draw takes the same side of it.
    this._sailQ = low ? 0 : 1;
  }

  /** How hard the LOCAL ship's canvas is luffing (0..1) — main.ts feeds it the procedural canvas-crack audio. */
  get sailLuff(): number { return this._localLuff; }

  /**
   * Hand every sail in the fleet the sun, in VIEW space, once a frame (ONE vec3 shared by N materials).
   * This is what makes cloth read as cloth on the golden hour: a sail with the sun behind it is not a lit
   * surface, it is a lampshade — and the whole effect is two dot products because the direction is prepared
   * here instead of per-pixel. Runs AFTER updateSky/updateWeather, so it reads the sun the storm actually left.
   */
  private updateSailSun(): void {
    this.camera.updateMatrixWorld();  // also refreshes matrixWorldInverse (THREE.Camera overrides this)
    this._sunView.copy(this.sun.position).normalize().transformDirection(this.camera.matrixWorldInverse);
    this.sailShared.sun.value.copy(this._sunView);
    // the bleed follows the sun's OWN colour and strength, so it warms at dusk, greys out in a squall and
    // is simply gone at night (a lamp behind the canvas is not this effect).
    this.sailShared.warm.value.copy(this.sun.color);
    this.sailShared.glow.value = SAIL_ART.GLOW * clamp(this.sun.intensity / 1.5, 0, 1.2) * (1 - this.nightFactor);
  }

  /** Water kill-switch (render-only). 'low' = the legacy flat-shaded sea that always compiles; 'high' = the
   *  per-pixel lit sea; 'auto' = probe + graphics preset. Wired to `?water=` at boot and safe to call live. */
  setWaterQuality(pref: 'low' | 'high' | 'auto'): void {
    this.waterPref = pref === 'low' || pref === 'high' ? pref : 'auto';
    this.applyWaterQuality();
  }

  /** The path actually compiled right now — for the Settings readout and headless render-proof. */
  get waterQuality(): 'low' | 'high' { return this.waterHigh ? 'high' : 'low'; }

  /** Public so the Draga ritual overlay (dragascene.ts) can reuse the EXACT shared-GLSL ocean shader
   *  for its night sea — one ocean, never a divergent copy (invariant #5). That consumer passes NO
   *  `clipmap` flag and keeps its own flat plane, so it compiles byte-for-byte the shipped legacy vertex
   *  path (square edge fade, no stitch attribute) and is untouched by the sea's clipmap.
   *
   *  Two lighting paths over ONE geometry:
   *   - 'low'  — the shipped legacy look: one screen-space-derivative normal per triangle of the 7.8 m sea
   *              grid, a weak fresnel wash toward the fog colour and a single specular lobe.
   *   - 'high' — the same vertices, lit per PIXEL: the analytic Gerstner normal (generated from the very same
   *              wave table as the displacement), micro-ripples that live only in the normal, a Schlick
   *              fresnel driving a two-band sky reflection, subsurface crest glow and a tight sun glitter.
   *  Neither path displaces a single vertex differently — `oceanSample` is untouched in both. */
  buildOceanMaterial(quality?: 'low' | 'high', clipmap = false): THREE.ShaderMaterial {
    const high = quality != null ? quality === 'high' : this.waterHigh;
    const oceanGLSL = `${buildOceanGLSL()}\n${buildOceanSwellGLSL()}`;
    // The coast envelope (shared with the CPU bob — see `ampAt`) shoals the STORM SWELL EXCESS to nothing
    // over the shallows around each nearby island, so a 2.7 m vaga never rolls through a beach. Generated,
    // not hand-written, for the same reason the wave GLSL is: a hand copy drifts the moment it is retuned,
    // and a drifted envelope means the hull bobs on one sea while the eye sees another.
    const coastGLSL = `${buildOceanCoastGLSL(OCEAN_COAST_MAX)}
        // Swell scale AT a point. With no storm running (uWaveScale == 1) this is one float compare and
        // returns exactly 1.0 — the approved calm sea pays literally nothing for the storm machinery.
        float oceanAmp(vec2 p, float scale) {
          if (scale <= 1.001) return 1.0;
          return mix(1.0, scale, oceanCoast(p));
        }`;
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        // the LIVE sea state the generated GLSL reads (uChopDir / uChopAmp) — shipped by @piratas/sim next
        // to the generator that requires them, and mirrored onto the CPU sampler by `updateSeaState`
        ...oceanEnvUniforms(),
        // cat's paws: (driftX, driftZ) metres — the noise domain offset the wind has pushed the field to
        uPawDrift: { value: new THREE.Vector2() },
        // coast shoaling: (worldX, worldZ, innerRadius, outerRadius) per nearby island; count 0 = open sea
        uCoast: { value: Array.from({ length: OCEAN_COAST_MAX }, () => new THREE.Vector4(0, 0, 0, 1)) },
        uCoastCnt: { value: 0 },
        // 🏝️ THE SHORE. (worldX, worldZ, waterlineRadius, 0) per nearby islet — the FRAGMENT's own list,
        // deliberately separate from uCoast above: that one is the storm-swell envelope and is switched off
        // in calm (its loop is vertex-stage and must cost nothing), while the beach has to be drawn on a
        // flat blue day. Count 0 = open sea = one integer compare per pixel.
        uShore: { value: Array.from({ length: SHORE_MAX }, () => new THREE.Vector4(0, 0, -1, 0)) },
        uShoreCnt: { value: 0 },
        // the colour the water takes AT the sand: wet-sand turquoise, graded with the sky by updateSky.
        uShoal: { value: new THREE.Color(0x63c8bd) },
        uCam: { value: new THREE.Vector3() },
        // clipmap only: the UNSNAPPED captain position. The mesh itself jumps in OCEAN_SNAP steps (that
        // is what keeps every level on one world lattice — see oceanmesh.ts), so the horizon fade must be
        // measured from here instead of from the mesh's local corner, or it would step with the snap.
        uCenter: { value: new THREE.Vector2() },
        uDeep: { value: new THREE.Color(0x0a3a54) },
        uShallow: { value: new THREE.Color(0x1f86ad) },
        // 🎨 palette rig (temporary — WATER_PALETTES). Three values, no branches: the fresnel WEIGHT (how
        // much sky the surface mirrors), the subsurface MULTIPLIER on uShallow (the stained-glass term),
        // and the white the day-time foam mixes toward. Every default is the IDENTITY, so a material built
        // outside updateSky's reach (the Draga overlay, which hand-sets its own sea) is bit-for-bit the
        // shipped shader — the rig cannot leak into a scene that never opted in.
        uFresK: { value: 1 },
        uSssK: { value: new THREE.Color(0xffffff) },
        uFoam: { value: new THREE.Color(0xebf5ff) },
        uFog: { value: SKY.clone() },
        // the sky's ZENITH colour (uFog is its horizon band). The lit path mixes the two by the reflected
        // ray's elevation, so a wave face turned skyward mirrors the deep blue overhead and a grazing one
        // mirrors the horizon the sea already fades into — a two-band sky probe for one mix().
        uSkyTop: { value: SKY.clone() },
        uFogDensity: { value: 0.0016 },
        uSun: { value: new THREE.Vector3(120, 200, 80).normalize() },
        // 🌙 the MOON as a second light: xyz = unit direction (the SAME vector the sky dome draws the disc
        // at), w = strength (0 by day, by a new moon, or in a gale). It buys the night's missing landmark
        // on the water — a cold, wide, broken light path running from the horizon to the hull — from the
        // glitter machinery the sun already uses. w = 0 costs one compare per fragment.
        uMoon: { value: new THREE.Vector4(0, 1, 0, 0) },
        // 🌌 THE AURORA, for the water. uAurAmt is 0 by day, outside a display and in a gale — one
        // compare per fragment when it is off. The frame and the three colours are the SAME objects the
        // sky dome carries, so the road on the water is literally the curtain overhead, mirrored: there
        // is no second authoring of the effect that could drift out of agreement with the first.
        uAurAmt: { value: 0 },
        uAurZ: { value: AUR_FRAME.z.clone() }, uAurT: { value: AUR_FRAME.t.clone() }, uAurB: { value: AUR_FRAME.b.clone() },
        uAurLo: { value: AUR_LO.clone() }, uAurMid: { value: AUR_MID.clone() }, uAurHi: { value: AUR_HI.clone() },
        // hull displacement: nearest ships as an ORIENTED ELLIPSE that hugs the real hull shape
        // (a circle floats foam off the long sides). uShips = vec4(x, z, cosH, sinH),
        // uShipsB = vec4(halfLen, halfBeam, strength, 0). Drives a thin foam lip + recessed
        // trough in the fragment shader so the water opens/sinks around each hull (cosmetic only).
        uShips: { value: Array.from({ length: OCEAN_DISP_MAX }, () => new THREE.Vector4(0, 0, 0, 0)) },
        uShipsB: { value: Array.from({ length: OCEAN_DISP_MAX }, () => new THREE.Vector4(0, 0, 0, 0)) },
        uShipCnt: { value: 0 },
        uWaveScale: { value: 1 }, // storm swell: 1 calm … STORM.WAVE_SCALE peak (matches CPU bob/wake amp)
        uStorm: { value: 0 },     // 0 calm … 1 full storm — darkens the water + adds whitecaps
        uFlash: { value: 0 },     // lightning luminance 0..1 — the whole sea lights up for a beat
        uNight: { value: 0 },     // 0 day … 1 deep night — damps the unlit crest sheen so it isn't white at midnight
        // Onda Sistema de Luz — the N nearest water-reflecting FakeLights (lights.ts writeGlints):
        // uGlint = (worldX, worldZ, lampHeightY, strength), uGlintCol = tint. Day → uGlintCnt 0 → the loop
        // costs one comparison per fragment. This is the trembling lantern smear on the waves at night.
        uGlint: { value: Array.from({ length: GLINT_MAX }, () => new THREE.Vector4(0, 0, 0, 0)) },
        uGlintCol: { value: Array.from({ length: GLINT_MAX }, () => new THREE.Color(0)) },
        uGlintCnt: { value: 0 },
      },
      vertexShader: /* glsl */ `
        uniform float uTime, uWaveScale;
        ${clipmap ? 'uniform vec2 uCenter;\n        attribute vec2 aStitch; // != 0 -> this vertex is a LOD-seam midpoint (see oceanmesh.ts)' : ''}
        varying vec3 vWorld;
        varying float vH;
        // The SWELL-ONLY height (normalized the same way vH is). Every THRESHOLDED read of altitude —
        // the depth gradient, the crest sheen, the whitecap mask — takes this instead of vH, because a
        // threshold on the full nine-component sum does not sample "how high is this water", it samples
        // "where do all nine sines peak together", and that is a quasi-periodic INTERFERENCE LATTICE.
        // Photographed from a tactical zoom-out it printed as a regular QUILT of pale cells over the whole
        // ocean (owner QA, pre-existing). Four extra sines on ~22 k vertices; measured cost: free.
        varying float vHS;
        varying float vEdge;   // 0 at the captain … 1 where the sea has become the horizon colour
        varying float vAmp;    // the swell scale THIS vertex was displaced with (coast-shoaled)
        varying float vPaw;    // the CAT'S-PAW field at this vertex, 0 glassy lane … 1 nervous patch
        varying float vFoam;   // the "lively water" patch field the crest sheen and the moon road ride
        uniform vec2 uPawDrift;
        ${high ? 'varying vec2 vP; // UNDISPLACED plane position — the analytic normal must be sampled there' : ''}
        ${oceanGLSL}
        ${coastGLSL}
        ${OCEAN_NOISE_GLSL}
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vec2 p = wp.xz;
          float amp = oceanAmp(p, uWaveScale);
          vec3 disp; float hgt;
${clipmap ? /* glsl */ `
          if (dot(aStitch, aStitch) > 0.0) {
            // LOD SEAM. This vertex sits halfway along a COARSER level's edge. Sampling its own wave
            // height would leave it off that edge by the wave's sagitta — the classic T-junction crack.
            // Instead it renders as the exact MIDPOINT of the coarse edge: average the two neighbours'
            // full Gerstner samples (horizontal pinch included, or the crack reopens sideways). The fine
            // polyline then lies ON the coarse segment, bit-for-bit. Costs 2 wave evaluations on ~2% of
            // the vertices; buys a crack-free seam with no skirt and no degenerate triangles.
            // NOTE each neighbour is sampled at ITS OWN coast-shoaled amp, not this vertex's — that is
            // what keeps the seam crack-free through a shoaling gradient too (the coarse neighbours will
            // each evaluate exactly the same amp when their own turn comes).
            vec2 pa = p - aStitch, pb = p + aStitch;
            vec3 sa = oceanSample(pa, uTime, oceanAmp(pa, uWaveScale));
            vec3 sb = oceanSample(pb, uTime, oceanAmp(pb, uWaveScale));
            disp = 0.5 * vec3(pa.x + sa.x + pb.x + sb.x, sa.y + sb.y, pa.y + sa.z + pb.y + sb.z);
            hgt = 0.5 * (sa.y + sb.y);
          } else {
            vec3 s = oceanSample(p, uTime, amp);
            disp = vec3(p.x + s.x, s.y, p.y + s.z);
            hgt = s.y;
          }
          vEdge = length(p - uCenter) / float(${OCEAN_FADE_R.toFixed(1)});
` : /* glsl */ `
          vec3 s = oceanSample(p, uTime, amp);
          disp = vec3(p.x + s.x, s.y, p.y + s.z);
          hgt = s.y;
          vEdge = max(abs(position.x), abs(position.z)) / float(${(OCEAN_SPAN * 0.5).toFixed(1)});
`}
          ${high ? 'vP = p;' : ''}
          // ---------------------------------- CAT'S PAWS ----------------------------------
          // The reason a real sea in a light breeze never looks like one material. Wind does not arrive
          // evenly: it lands in wide soft patches that rough the surface (which scatters instead of
          // mirroring, so the water goes DARK and matte) and slides downwind, leaving glassy lanes with
          // a hard sun path on them between. Sailors call them cat's paws.
          //
          // NOTHING IS DRAWN. There is no streak mesh, no line texture, no edge that could tile — it is
          // one large slow noise field whose DOMAIN DRIFTS WITH THE WIND (uPawDrift, metres, integrated
          // on the CPU so it never wraps), and in the fragment it modulates two quantities that already
          // exist per pixel: how much of the short chop's slope reaches the normal, and how bright the
          // glitter is. Two octaves on rotated lattices so the value-noise grid cannot survive the sum.
          //
          // EVALUATED PER VERTEX, ON PURPOSE. The patches are ~170 m across; the clipmap's cells are
          // metres. Interpolating this across a triangle is exact to well under a pixel of contrast, and
          // it moves 8 transcendentals off every one of ~1.4 M fragments onto ~22 k vertices.
          vec2 pw = (p + uPawDrift) * ${CATSPAW.SCALE.toFixed(5)};
          vPaw = smoothstep(0.30, 0.72, clamp(oNoise(pw) * 0.68 + oNoise(oRot(pw) * 2.31 + 5.3) * 0.32, 0.0, 1.0));
          // …and the finer "lively water" field the crest sheen keys off (see vFoam in the fragment stage).
          // Same rotated, wind-drifted domain the paws and the storm foam ride, at ~38 m and ~14 m.
          vFoam = oNoise(oRot((p + uPawDrift * 1.4) * 0.026) + 3.7) * 0.62
                + oNoise(oRot((p + uPawDrift * 2.4) * 0.071) + 11.3) * 0.38;
          vAmp = amp;
          vWorld = disp; vH = hgt / max(0.001, amp); // normalize so the colour band ignores swell scale
          // …and the swell-only altitude the thresholded shading reads (see vHS). Sampled at the same
          // undisplaced p as the geometry, so it is the same wave the eye sees rearing up — just without
          // the centimetre chop whose only job is to texture the surface through the NORMAL.
          vHS = oceanSwellHeight(p, uTime, amp) / max(0.001, amp);
          gl_Position = projectionMatrix * viewMatrix * vec4(disp, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform vec3 uDeep, uShallow, uFog, uCam, uSun;
        uniform vec3 uSssK, uFoam;  // 🎨 palette rig: subsurface MULTIPLIER (tint x gain), day foam white
        uniform vec4 uMoon;   // xyz = moon direction (the sky dome's own), w = strength (0 by day)
        uniform float uAurAmt;                                  // 🌌 aurora display strength (0 = off)
        uniform vec3 uAurZ, uAurT, uAurB, uAurLo, uAurMid, uAurHi;
        uniform float uFogDensity, uTime, uStorm, uNight, uFlash, uFresK;
        // How far the wind has pushed every DRIFTING field on this sea (metres). One offset, integrated
        // on the CPU, shared by the cat's paws and the storm foam — so nothing on the water drifts on its
        // own private heading. Declared unconditionally: the storm foam reads it on the cheap path too.
        uniform vec2 uPawDrift;
        uniform vec4 uShips[${OCEAN_DISP_MAX}];
        uniform vec4 uShipsB[${OCEAN_DISP_MAX}];
        uniform int uShipCnt;
        uniform vec4 uGlint[${GLINT_MAX}];
        uniform vec3 uGlintCol[${GLINT_MAX}];
        uniform int uGlintCnt;
        uniform vec4 uShore[${SHORE_MAX}];
        uniform int uShoreCnt;
        uniform vec3 uShoal;
        varying vec3 vWorld;
        varying float vH;
        varying float vHS;    // SWELL-ONLY height — every thresholded read takes this (see the vertex note)
        varying float vEdge;
        varying float vAmp;
        varying float vPaw;   // cat's-paw field, computed per vertex (a 170 m field needs no more)
        varying float vFoam;  // "lively water" patch field, per vertex — the crest sheen + moon road ride it
        ${high ? `uniform vec3 uSkyTop;
        uniform float uWaveScale;
        varying vec2 vP;
        ${buildOceanNormalGLSL()}` : ''}
        ${OCEAN_NOISE_GLSL}
        ${high ? AURORA_GLSL : ''}
        void main() {
          vec3 viewVec = uCam - vWorld;
          float viewD = length(viewVec);
          vec3 viewDir = viewVec / max(viewD, 1e-4);
          // How much SEA this one pixel covers, in metres. Filled by the lit path (which measures it from
          // the undisplaced plane derivative); 0 means "not measured", which is what the cheap path wants.
          // Hoisted out here because the LANTERN GLINTS further down need it too, and they are shared code.
          float footAA = 0.0;
          // The surface normal and the per-pixel wave height. DECLARED HERE, outside the quality branches,
          // because the foam-flattening step further down is SHARED code and reads nrm — and when only
          // the lit path declared it, the cheap path's fragment shader referenced an undeclared identifier
          // and DID NOT COMPILE. A ShaderMaterial that fails to compile simply draws nothing, so the sea
          // vanished on exactly the devices that asked for the cheap sea (?water=low, Settings › Graphics
          // › Low, a wallet in-app browser, or any device the capability probe downgrades). Pre-existing at
          // HEAD; caught by the sky wave's ?water=low verification pass.
          vec3 nrm = vec3(0.0, 1.0, 0.0); float hpx = 0.0;
${high ? /* glsl */ `
          // ============ Onda Água — the LIT path. GEOMETRY UNTOUCHED: not one vertex moves differently, ============
          // ============ every line below only decides what colour the pixel already there should be.    ============
          // The sea mesh is a 7.8 m grid — wider than the ship's beam — so BOTH the derivative normal and the
          // interpolated crest height are one flat facet per quad. That is the whole reason the water read as
          // plastic sheeting. In the near field, re-evaluate the SAME wave table per PIXEL (normal + height in
          // one pass; the height reuses the sines the normal already needed).
          // TWO nested distance gates — this is the whole performance story. Each is a screen-coherent branch
          // (distance varies smoothly down the screen), so far pixels genuinely skip the transcendentals.
          // PIXEL FOOTPRINT on the sea, in metres — how much water this one pixel actually covers.
          // Taken in uniform flow from the undisplaced plane position, and it is a far more honest gate
          // than distance alone: at a GRAZING angle a pixel only 60 m away can still span several metres
          // of sea, and that is precisely where a 1.55 m wavelet stops being resolvable and starts being
          // moire. (Distance can't see that; the derivative can.)
          float foot = max(length(dFdx(vP)), length(dFdy(vP)));
          // 1.0 once the short chop band is under-sampled. 0.62 m is comfortably inside Nyquist for the
          // 1.55 m wavelet at the bottom of the table.
          float aa = clamp(foot / 0.62, 0.0, 1.0);
          footAA = foot;
          float detail = 1.0 - smoothstep(95.0, 280.0, viewD);   // analytic normal+height: past ~280 m a swell
          // crest is a few pixels wide under thick fog — AND the chop must drop out the moment a pixel can
          // no longer resolve it, or the enriched spectrum aliases (see the specular note further down)
          float fine   = (1.0 - smoothstep(24.0, 82.0, viewD)) * (1.0 - aa);
          // ---------------------------------- CAT'S PAWS ----------------------------------
          // The reason a real sea in a light breeze never looks like one material. Wind does not arrive
          // evenly: it lands in wide soft patches that rough the surface (which scatters instead of
          // mirroring, so the water goes DARK and matte) and slides downwind, leaving glassy lanes with a
          // hard sun path on them between. Sailors call them cat's paws.
          //
          // NOTHING IS DRAWN. There is no streak mesh, no line texture, no edge that could tile — it is
          // one large slow noise field whose DOMAIN DRIFTS WITH THE WIND (uPawDrift, metres, advanced on
          // the CPU so it never wraps), modulating two quantities that already exist per pixel: how much
          // of the short chop's slope reaches the normal, and how bright the glitter is. Two octaves on
          // rotated lattices so the value-noise grid cannot survive into the result.
          //
          // Cost: 2 noise lookups on near pixels only (it rides the same 'fine' gate as the chop it
          // modulates — a patch you cannot resolve the chop in has nothing to modulate).
          // CAT'S PAWS — the wide field arrives interpolated from the vertex stage (see vPaw); only the
          // fine GUST octave has to be per-pixel, because it works at ~${(1 / (CATSPAW.SCALE * 13.0)).toFixed(0)} m and the mesh cannot carry it.
          float gust = 1.0;
          if (fine > 0.004) {
            // This is not decoration — it is the fix for the lit plaid. Five pure sines crossing at five
            // fixed angles are not a spectrum, they are a GRATING: their sum has a quasi-periodic
            // interference lattice baked into it, and any specular lobe tight enough to sparkle resolves
            // that lattice into a plaid of bright cells lying across the swell. (Proved by ablation:
            // forcing uChopAmp to 0 removed the plaid and nothing else.) A real wind-sea has no such
            // lattice, because the wind that raises it is gusty at metre scale and the chop is never
            // coherent for more than a few wavelengths. One noise lookup, and there is nowhere for the
            // lattice to build up.
            gust = 0.45 + 1.05 * oNoise(oRot((vP + uPawDrift) * ${CATSPAW.SCALE.toFixed(5)}) * 13.0 + 21.7);
          }
          float pawChop = mix(${CATSPAW.CHOP_MIN.toFixed(3)}, ${CATSPAW.CHOP_MAX.toFixed(3)}, vPaw) * gust;
          float pawSpec = mix(${CATSPAW.SPEC_MIN.toFixed(3)}, ${CATSPAW.SPEC_MAX.toFixed(3)}, vPaw);
          // take the quad derivatives in UNIFORM flow (required by GLSL ES) but defer the cross+normalize into
          // the branch, so a full-detail near pixel never pays for the facet normal it is about to throw away.
          vec3 ddx = dFdx(vWorld), ddy = dFdy(vWorld);
          // the SWELL-ONLY (pinch, height) the storm foam mask needs, filled by the call below at no
          // cost (see buildOceanNormalGLSL). y = -99 means "not evaluated here" (far field).
          vec2 swellNH = vec2(0.0, -99.0);
          if (detail > 0.004) {
            // The fine gate doubles as the SHORT-BAND GATE for the enriched spectrum: the 6.2 → 1.55 m chop
            // components are re-evaluated per pixel only while they are still worth pixels. Past ~130 m a
            // 1.55 m wavelet is sub-pixel, so dropping it is anti-aliasing, not a compromise — and it is
            // the one place the richer table is allowed to cost less than the full sum (the VERTEX stage
            // always evaluates all nine, because that geometry is what the CPU bob and wake agree with).
            // …and the SHORT-BAND GAIN is where the cat's paws land. Feeding fine * pawChop here is one
            // multiply and it is the whole effect: inside a paw the chop's slope reaches the normal at
            // full strength (nervous, matte water), in a glassy lane it is damped to a third and the
            // surface goes back to mirroring the sky. It is a SHADING band gain — the vertex stage still
            // displaces every component everywhere, so the geometry the CPU bob agrees with is untouched
            // and a paw can never move the boat.
            vec4 nh = oceanNormalHeight(vP, uTime, vAmp, fine * pawChop, swellNH);
            hpx = mix(vH, nh.w / max(0.001, vAmp), detail);
            vec3 an = nh.xyz;
            if (fine > 0.004) {
              // two slope-only wavelets (cos = d/dp of a sine), counter-drifting, ~1.04 and ~0.66 m — the
              // capillary texture BELOW the wave table's 1.55 m floor (they used to sit at 3.9/2.3 m, which
              // the enriched spectrum now covers with REAL geometry; leaving them there would double the
              // same band). Amplitudes are A*k, i.e. pure slope: sub-centimetre virtual crests that exist
              // ONLY in the normal, never in the height (CPU buoyancy must keep matching the geometry).
              // Speeds follow the same deep-water dispersion ω = √(g·k) as the table, so they drift at a
              // rate that belongs to their wavelength instead of an arbitrary one.
              // …and they ride the LIVE WIND too (uChopDir rotates them exactly as it rotates the real
              // chop band), so the capillary texture and the geometry it sits on always travel together.
              vec2 a1 = vec2( 0.7431,  0.6691), a2 = vec2(-0.5878,  0.8090);
              vec2 d1 = vec2(a1.x * uChopDir.x - a1.y * uChopDir.y, a1.x * uChopDir.y + a1.y * uChopDir.x);
              vec2 d2 = vec2(a2.x * uChopDir.x - a2.y * uChopDir.y, a2.x * uChopDir.y + a2.y * uChopDir.x);
              float ph1 = dot(vP, d1) * 6.05 - uTime * 7.70;
              float ph2 = dot(vP, d2) * 9.45 - uTime * 9.63;
              // fade out before they alias, and go quiet in a glassy lane (the paws own the whole skin)
              an.xz -= (d1 * (0.026 * cos(ph1)) + d2 * (0.020 * cos(ph2))) * fine * pawChop * uChopAmp;
            }
            if (detail > 0.996) { nrm = normalize(an); }          // pure analytic: no facet normal needed at all
            else {
              vec3 gn = normalize(cross(ddx, ddy));
              if (gn.y < 0.0) gn = -gn;
              nrm = normalize(mix(normalize(mix(vec3(0.0, 1.0, 0.0), gn, 0.65)), normalize(an), detail));
            }
          } else {
            hpx = vH;
            nrm = normalize(cross(ddx, ddy));
            if (nrm.y < 0.0) nrm = -nrm;
            // FAR FIELD: the screen-derivative normal of a 32 m clipmap quad is one flat facet, so a
            // tactical zoom-out came back tiled with diamonds ("facetas em bloco" in the water-v2 report).
            // Damping it toward straight-up by the PIXEL FOOTPRINT is the same anti-aliasing argument the
            // specular lobe and the crest sheen use: once a pixel covers more sea than a facet, the facet
            // is not information any more, it is noise, and the honest answer is the mean surface.
            nrm = normalize(mix(vec3(0.0, 1.0, 0.0), nrm, 0.65 * (1.0 - 0.72 * smoothstep(1.5, 12.0, foot))));
          }
          // THE ALTITUDE CUE IS THE SWELL'S, NOT THE WHOLE SEA'S — and this is the fix for the lit plaid.
          //
          // t drives the depth gradient, the unlit crest sheen and (further down) the crest whitening:
          // three THRESHOLDED reads of height. The enriched chop band moves the full height by +/-4 cm,
          // and five sines crossing at five different angles do not make noise, they make a quasi-periodic
          // INTERFERENCE LATTICE. Threshold that and the lattice prints — photographed as a plaid of
          // bright cells lying across the swell in every sun path, which no amount of specular filtering
          // could remove because it was never the specular.
          //
          // The swell-only height comes back from the same call for free (swellNH — the whole reason it
          // exists), so the fix costs nothing: shading altitude follows the WAVE, and the chop does what
          // chop is for, which is to texture the surface through the NORMAL.
          //
          // …AND THE FAR FIELD GETS IT TOO NOW (the "quilt"/colcha). swellNH is only filled inside the
          // per-pixel gate, so past ~280 m this used to fall back to the FULL nine-component vertex height
          // — and the same lattice the near field had just been cured of printed across the entire far
          // ocean as a regular grid of pale cells (owner QA on a tactical zoom-out; pre-existing at HEAD).
          // vHS is the swell-only altitude carried from the VERTEX stage, so both fields now threshold the
          // same wave, at every distance, on either quality path.
          float hb = swellNH.y > -50.0 ? mix(vHS, swellNH.y / max(0.001, vAmp), detail) : vHS;
          float t = clamp(hb * 0.55 + 0.5, 0.0, 1.0);
` : /* glsl */ `
          float t = clamp(vHS * 0.55 + 0.5, 0.0, 1.0);
`}
          vec3 col = mix(uDeep, uShallow, t);
          // unlit foam sheen, damped at night. On the lit path the crest whitening is mostly done per-pixel
          // further down (from the real slope), so this broad height band is pulled back to keep the sea from
          // going milky — at distance, where the analytic slope has faded out, it is still the only crest cue.
          // The BAND IS WIDE on purpose. It used to be smoothstep(0.8, 1.0), a 0.2-wide ramp — and the
          // enriched chop moves t by about +/-0.04, i.e. a fifth of that ramp, so a narrow band turned
          // every centimetre wavelet into a visible light/dark hatch stamped across the swell. Halving
          // the gradient halves the hatch for free and costs nothing but a softer altitude cue.
          //
          // ============================== THE QUILT (colcha) — the other half ==============================
          // Swapping vH for vHS was necessary and not sufficient, and an ABLATION says exactly why: zeroing
          // THIS ONE TERM cleared the entire far-field quilt, so it was never the specular. Four swell sines
          // crossing at four fixed angles still peak together on a (coarser, prettier) lattice — and the
          // band was authored as a NEAR-MAXIMUM threshold: it opened at t 0.55 and saturated at 1.05, i.e.
          // only where ~76% of all the amplitude the sea HAS turned up at once, at full strength in the far
          // field where nothing was left to hide it. A near-maximum threshold on a sum of sines is a
          // detector for that sum's interference lattice. Three changes, and the lattice has nowhere left:
          //
          // The failed intermediate is worth recording, because it is the obvious fix and it does not work:
          // widening the band and warping its threshold with noise made the cells softer and irregular in
          // brightness — and left them exactly where they were. ANY monotone function of the sea height
          // paints that height's beat pattern, and from 200 m up the beat of four long sines is metres
          // across, i.e. fully RESOLVED, not aliased. (Measured: the pixel footprint in the offending shot
          // is ~0.6 m. There is nothing to filter.) The depth gradient reads the identical height and looks
          // fine only because it grades blue to blue; the sheen mixes toward WHITE, so the same structure
          // arrives as a quilt.
          //
          // So invert which field is in charge. A crest sheen is not "every pixel above altitude X is
          // white" — it is PATCHES OF LIVELY WATER that happen to sit on the crests. The patch field is
          // two octaves of noise on the rotated, wind-drifted domain the foam and the cat's paws already
          // ride (so all the sea's weather travels together), and the swell only BIASES it: a wide, gentle
          // ramp with no threshold anywhere in it. The wave field is now something the sheen prefers, not
          // something it detects — and there is no lattice left for the eye to find.
          // …EVALUATED PER VERTEX (vFoam). The coarse octave spans ~38 m and the fine one ~14 m, while the
          // clipmap's cells are 2 m under the bowsprit — interpolating it is exact to far under a pixel of
          // contrast, and it moves two noise lookups off ~1.4 M fragments onto ~22 k vertices. Exactly the
          // trade the cat's-paw field already makes, for exactly the same reason.
${high ? /* glsl */ `
          float livePatch = vFoam;
          float sheen = smoothstep(0.47, 0.82, livePatch) * smoothstep(0.14, 0.88, t);
` : /* glsl */ `
          // THE CHEAP PATH'S FOAM — the "placas brancas flutuando" of the owner's QA. vFoam is per-VERTEX,
          // and this path's outer clipmap cells are 32 m, so the patches arrive as ~100 m milky plates.
          // Fix (see the TS note above buildOceanMaterial): one per-pixel octave in the near field only,
          // behind the free vEdge varying; a distance fade the lit path got from its pixel footprint and
          // this one never had; and a gain pulled BELOW the lit path's (it has less detail to justify it).
          float lowBreak = 0.5;
          float nearF = 1.0 - smoothstep(0.22, 0.54, vEdge);
          if (nearF > 0.004) lowBreak = oNoise(oRot((vWorld.xz + uPawDrift * 1.6) * 0.078) + 19.7);
          float livePatch = clamp(vFoam * (0.42 + 1.16 * mix(0.5, lowBreak, nearF)), 0.0, 1.0);
          float sheen = smoothstep(0.44, 0.80, livePatch) * smoothstep(0.14, 0.88, t)
                      * (1.0 - 0.70 * smoothstep(0.18, 0.78, vEdge));
`}
          // NIGHT: the sheen used to be damped to 35% after dusk, which is most of why a hull at midnight
          // sat on featureless black (owner QA). Moonlit water is the one thing that IS still bright at
          // night — that is what a moon path is — so the damping is halved and the crest goes cold-blue
          // instead of white. The night is still night; the sea just has a surface again.
          // (uFoam is the palette's DAY white; the night end stays the cold blue for every palette, which
          //  is half of why a saturated green sea does not turn radioactive at midnight.)
          col = mix(col, mix(uFoam, vec3(0.70, 0.80, 1.0), uNight),
                    sheen * (1.0 - 0.34 * uNight)
                    * ${high ? '(0.30 + 0.28 * (1.0 - detail)) * (1.0 - 0.55 * smoothstep(0.8, 7.0, foot))' : '0.30'});
          // How much of this pixel is WHITE WATER (set by the storm block below, 0 in calm). Foam is a
          // diffuse scattering layer of bubbles: it does not mirror the sky and it does not glitter. The
          // sea used to shade it as if it were still polished water, so every sunlit whitecap in a gale
          // came back streaked with the specular corduroy of the wave field underneath it. Carrying the
          // amount out of the block and damping the three specular terms with it is both the fix and the
          // physically honest answer.
          float foam = 0.0;
          // storm: pull the water toward a darker grey-green and TEAR the crests into foam.
          // v2: the old cap was one sine over the whole sea, so a gale painted flat white SLABS on the swell.
          // Now the crest mask is broken by 2 octaves of drifting noise (patchy foam), streaked downwind, and
          // the sea-state read comes from the BREAK (a hard leading edge + a soft dissolving tail), not a wash.
          if (uStorm > 0.001) {
            col = mix(col, col * ${(1 - STORM.OCEAN_DARKEN).toFixed(3)} + vec3(0.055, 0.075, 0.088), uStorm);
            // v3: the foam domain is ROTATED OFF THE WORLD AXES. Value noise lives on an axis-aligned
            // lattice, so ANY threshold taken on it prints that lattice — which at a real gale showed up
            // as a grid of white DASHES lying on the swell (rows of little bricks, not spray). Two causes,
            // both fixed here: the domain now runs at ~34 degrees to the world, and the old per-cell hash
            // brightness multiplier (oHash2 of a floor()ed cell, i.e. literal 12 m squares) is gone entirely.
            // Cost: four multiplies, and there is no grid left to see.
            vec2 fr = oRot(vWorld.xz);
            // SCALE OF THE PATCHES. Whitecaps are patches of white water METRES across; the earlier
            // build ran three octaves down to ~1.4 m cells, and a threshold on a 1.4 m lattice printed a
            // fine CROSS-HATCH over every crest that no amount of specular filtering could remove. Two
            // octaves at ~18 m and ~8.5 m, on their own rotated lattices (see oRot), and a wide
            // smoothstep so the edges dissolve instead of cutting.
            // …and the domain DRIFTS DOWNWIND, not along a hard-coded diagonal. Foam is water the wind
            // tore off a crest and is pushing; a foam field crawling north-east while the gale blows west
            // is the tell that the weather is painted on. uPawDrift is the same wind-integrated offset
            // the cat's paws ride (metres, advanced on the CPU so it never wraps or snaps when the wind
            // veers), so every drifting thing on this sea travels together, for one uniform.
            vec2 fp = (fr + oRot(uPawDrift) * 1.7) * 0.055;
            vec2 q1 = oRot(fp) * 2.13 + 7.1;
            float brk = oNoise(fp) * 0.62 + oNoise(q1) * 0.38;
            // a gently STRETCHED layer: foam torn downwind. 3:1 — past that the stretch stops reading as
            // wind and starts reading as scanlines across the whole sea.
            float streak = oNoise((fr + oRot(uPawDrift) * 2.4) * vec2(0.022, 0.062));
            // WHERE THE WAVE BREAKS — and it is driven by the SWELL ALONE, which is the whole trick.
            // Whitecaps break on the steep face of a big wave, so the mask needs the height AND the
            // slope; but the enriched chop band carries a fifth of the total crest pinch, and feeding
            // that into a thresholded mask stamped a fine CROSS-HATCH of the chop's own wavelengths
            // across every whitecap (a lit corduroy, not spray). Re-evaluating the four LONG components
            // only — the short band's branch is skipped, so this is 4 sin/cos, and only while a storm is
            // actually running — gives a mask that follows the wave. The chop then does what it should:
            // texture the water, not cut the foam.
${high ? /* glsl */ `
            float crest;
            if (swellNH.y > -50.0) {
              float ts = clamp(swellNH.y / max(0.001, vAmp) * 0.55 + 0.5, 0.0, 1.0);
              crest = smoothstep(0.45, 0.90, ts) * smoothstep(0.14, 0.62, clamp(swellNH.x * 6.0, 0.0, 1.0));
            } else {
              crest = smoothstep(0.60, 0.94, t);       // far field: no per-pixel normal, height only
            }
` : /* glsl */ `
            // LOW PATH: no per-pixel normal, so the break has to come from height alone — and a bare
            // threshold on height is a BAND, which in a gale printed the same white plates the calm sheen
            // did, just brighter. Biasing it with the (now broken-up) patch field is the same trick the
            // crest sheen uses and the same argument: a whitecap is a patch of torn water that happens to
            // sit near a crest, not "every pixel above altitude X". Costs one multiply — the patch field
            // was already computed above.
            float crest = smoothstep(0.52, 0.92, t) * (0.30 + 0.90 * livePatch);
`}
            // HOW MUCH OF THE SEA IS WHITE. Photographed at peak fury, the previous threshold
            // (smoothstep(0.28, 0.82, …), whose argument averages ~0.57) passed HALF the noise field, so
            // every crest came back as a continuous white sheet — the sea read as pack ice, not as a
            // gale. Whitecap coverage in a real force-9 sea is roughly a tenth of the surface, and the
            // eye reads fury from the CONTRAST between torn white and near-black water, not from the
            // white area. Sliding the threshold up past the noise's mean is what turns the sheet back
            // into separate breaking crests.
            float cap = crest * smoothstep(0.52, 1.02, brk * 0.82 + streak * 0.32);
            // a dissolving TAIL downwind of each break, so foam looks blown off the crest, not painted on it
            float tail = crest * 0.20 * smoothstep(0.52, 0.95, oNoise((fr + oRot(uPawDrift) * 3.1) * 0.018));
            // NOTE: foam is the BLEND WEIGHT of white water. The specular damping below normalises it, so
            // a fully broken crest really does go matte. Broken water is nearly OPAQUE — but not paper:
            // holding back ~14% lets the sea's own colour tint the foam so it reads as aerated WATER
            // rather than as a white decal laid on top of it.
            foam = clamp(cap + tail, 0.0, 1.0) * 0.86 * uStorm;
            col = mix(col, vec3(0.88, 0.93, 0.97), foam);
            // rain DIMPLING the near water: hashed cells each ping their own tiny expanding ring, and only
            // a THIRD of the cells are ever active at once (a solid grid of rings reads as bubble wrap).
            float camD = length(uCam.xz - vWorld.xz);
            if (camD < 70.0 && uStorm > 0.25) {
              vec2 cp = vWorld.xz * 2.6;
              vec2 cell = floor(cp);
              float ph = oHash2(cell);
              if (ph > 0.62) {
                float age = fract(uTime * 2.3 + ph * 7.31);
                float rr = length(fract(cp) - 0.5) * 2.0;
                float ring = smoothstep(0.10, 0.0, abs(rr - age * 0.8)) * (1.0 - age) * (1.0 - age);
                col += vec3(0.30, 0.36, 0.42) * ring * smoothstep(70.0, 18.0, camD) * uStorm * 0.30;
              }
            }
          }
          // 🏝️ THE SHORE — see the long note on Renderer.updateShoreZones for why this exists and what
          // it costs. Signed distance to the nearest islet's waterline, then: shoaling colour + a
          // breathing swash ring broken by the sea's own wind-drifted foam noise. uShoreCnt 0 = one compare.
          if (uShoreCnt > 0) {
            // SQUARED FIRST, sqrt only inside the band. Every pixel of open sea in sight of an islet was
            // otherwise paying a distance() per shore — and the overwhelming majority of them are hundreds
            // of metres outside a 46 m band, i.e. buying a square root to learn nothing.
            float sd = 1e9;
            for (int i = 0; i < ${SHORE_MAX}; i++) {
              if (i >= uShoreCnt) break;
              vec4 sh = uShore[i];
              vec2 dxz = vWorld.xz - sh.xy;
              float d2 = dot(dxz, dxz);
              float rr = sh.z + ${SHORE_REACH.toFixed(1)};
              if (d2 > rr * rr) continue;
              sd = min(sd, sqrt(d2) - sh.z);                     // signed metres OUTSIDE the waterline
            }
            if (sd < ${SHORE_REACH.toFixed(1)}) {
              // (1) shoaling, squared: deep water untouched, the last few metres carry the whole change
              float shoal = 1.0 - smoothstep(0.0, ${SHORE_REACH.toFixed(1)}, max(sd, 0.0));
              shoal *= shoal;
              col = mix(col, uShoal, shoal * 0.72);
              // (2) the swash: 'run' = where the foam line stands, metres out from the waterline. The
              // phase carries the pixel's own distance, so it is a travelling surge, not one pulsing lamp.
              float run = 4.0 + 2.8 * sin(uTime * 0.44 - sd * 0.06);
              float ring = (1.0 - smoothstep(0.0, run, max(sd, 0.0))) * smoothstep(-5.0, 0.5, sd);
              if (ring > 0.004) {
                // (3) torn along the shore, on the sea's own wind-drifted domain. High contrast on
                // purpose: an EVEN ring reads as a drawn outline (the first tune was a milky pool).
                vec2 fp = oRot(vWorld.xz + uPawDrift * 0.8);
                float lace = oNoise(fp * 0.14) * 0.62 + oNoise(oRot(fp * 0.5) + 4.1) * 0.38;
                ring *= 0.10 + 1.55 * lace;
                // the last two metres are always wet (a shoreline with a hole in it reads as a bug)
                ring += (1.0 - smoothstep(0.0, 2.2, max(sd, 0.0))) * 0.22;
                col = mix(col, vec3(0.93, 0.97, 1.0),
                          clamp(ring, 0.0, 1.0) * 0.44 * (1.0 - 0.42 * uNight) * (1.0 + 0.35 * uStorm));
              }
            }
          }
          // hull displacement: an oriented ELLIPSE hugging each hull. The water sinks (recessed
          // trough) right at the waterline and a thin churned foam lip rides the edge — so the
          // ship looks like it's pushing the sea aside, following the real hull shape (not a halo).
          for (int i = 0; i < ${OCEAN_DISP_MAX}; i++) {
            if (i >= uShipCnt) break;
            vec4 sh = uShips[i];
            vec4 sb = uShipsB[i];
            float k = sb.z;
            if (k <= 0.0) continue;
            vec2 d = vWorld.xz - sh.xy;
            vec2 fwd = vec2(sh.z, sh.w);                                    // hull forward (cosH, sinH)
            vec2 loc = vec2(dot(d, fwd), dot(d, vec2(-fwd.y, fwd.x)));      // local frame: along / across
            float ed = length(loc / sb.xy);                                // 1.0 AT the hull edge, <1 inside
            if (ed > 2.2) continue;
            // ---- Onda Água v2: BOW FOAM, for free. The Kelvin wake (wake.ts) starts at the transom; the
            // water the prow is actually SHOVELING is a short bright V right at the cutwater, and this loop
            // already knows every nearby hull's oriented ellipse — so it costs a few ALU, not a draw call
            // (the old rig spent a whole billboard per ship on it). Speed rides in via k.
            float bt = loc.x / sb.x - 1.02;                                  // ~0 AT the cutwater, >0 ahead of it
            if (bt > -0.50 && bt < 0.80) {
              float bwide = 0.42 + 1.25 * max(0.0, bt + 0.30);               // the V opens ahead of the stem
              float bacross = abs(loc.y) / sb.y;
              float bmask = (1.0 - smoothstep(bwide * 0.30, bwide * 1.10, bacross))
                          * smoothstep(-0.50, -0.02, bt) * (1.0 - smoothstep(0.16, 0.80, bt));
              // 🦠 at night the shoved water GLOWS — same plankton the wake carries (wake.ts). The bow is
              // the most aerated water in the frame, so it is the brightest bio. One mix, no new term.
              vec3 bowCol = mix(vec3(0.90, 0.95, 1.0), vec3(0.24, 1.0, 0.86), uNight * 0.88);
              col = mix(col, bowCol, clamp(bmask, 0.0, 1.0) * 0.34 * k * (1.0 - 0.18 * uNight));
            }
            // only where the hull SIDES peg the water: fade out toward the longitudinal tips so the
            // foam never wraps the raised bow/stern ("o bico"); the bow-cut is handled by its own splash.
            float along = abs(loc.x) / sb.x;                               // 0 amidships … 1 at the tip
            float sideMask = 1.0 - smoothstep(0.55, 0.95, along);
            if (sideMask <= 0.001) continue;
            k *= sideMask;
            // faint trough JUST OUTSIDE the hull (never under it -> no dark "gap" that reads as floating)
            float trough = smoothstep(1.05, 1.4, ed) * smoothstep(1.95, 1.45, ed);
            col = mix(col, uDeep * 0.86, trough * 0.16 * k);
            // contact foam hugging the waterline — glues the sea to the hull (subtle, tight band)
            float lip = 1.0 - clamp(abs(ed - 1.0) / 0.2, 0.0, 1.0);
            float ripple = 0.5 + 0.5 * sin(ed * 9.0 - uTime * 4.5);        // a couple of expanding rings
            float foam = (lip * 0.8 + lip * 0.35 * ripple) * k;
            // the waterline lip picks up the same glow, cooler (less air entrained here)
            col = mix(col, mix(vec3(0.86, 0.93, 1.0), vec3(0.20, 0.92, 0.84), uNight * 0.72),
                      clamp(foam, 0.0, 1.0) * 0.32);
          }
          // --- lighting: surface normal -> fresnel sky reflection (grazing angles pick up the sky/horizon)
          // + a tight sun/moon specular glint. This is what turns the flat gradient into a lit sea. ---
          float fw = min(1.0, foam * 1.163);   // 0..1: is this white water? (foam peaks at 0.86 — normalise)
          // WHITE WATER HAS NO SHAPE. Foam is a scattering layer of bubbles millimetres across: its
          // micro-geometry is randomised, so whatever the wave underneath is doing, the surface the light
          // meets is effectively FLAT. Flattening the normal here (rather than only damping the specular
          // terms one by one) is what stops the wave field's own texture reading straight through every
          // whitecap — the fresnel, the subsurface and the sheen all get the honest normal at once.
          nrm = normalize(mix(nrm, vec3(0.0, 1.0, 0.0), fw * 0.85));
          vec3 L = normalize(uSun);
${high ? /* glsl */ `
          // Schlick fresnel (F0 = 0.02): water seen from above shows its own depth colour, water seen at a
          // grazing angle is a mirror. pow(x,5) written as multiplies — cheaper and exact.
          float ndv = clamp(dot(nrm, viewDir), 0.0, 1.0);
          float f1 = 1.0 - ndv; float f2 = f1 * f1;
          float fres = 0.02 + 0.98 * (f2 * f2 * f1);
          // what the mirror sees: the reflected ray's ELEVATION picks a band of the existing sky gradient.
          float rY = clamp(2.0 * ndv * nrm.y - viewDir.y, -1.0, 1.0);
          vec3 skyRefl = mix(uFog, uSkyTop, smoothstep(0.02, 0.55, rY));
          col = mix(col, skyRefl, fres * 0.72 * uFresK * (1.0 - 0.85 * fw));   // white water is not a mirror
          // subsurface: sunlight travelling THROUGH a crest toward the eye. uSss is the live shallow colour
          // tinted and gained by the palette (classic = the shallow itself at 1.0), so it still grades with
          // the time-of-day for free; killed at night and damped in a gale.
          float bl = clamp(dot(viewDir, -L) * 0.5 + 0.5, 0.0, 1.0); bl = bl * bl * bl;
          col += uShallow * uSssK * (bl * clamp(hpx * 0.9 + 0.3, 0.0, 1.0) * 0.40 * (1.0 - 0.8 * uNight) * (1.0 - 0.5 * uStorm));
          // sun glitter: a very tight core rides the per-pixel ripples, so the specular breaks into a PATH of
          // sparkles toward the sun instead of one smeared blob; a wide, faint lobe keeps the sheet lit.
          vec3 H = normalize(L + viewDir);
          float sp = max(dot(nrm, H), 0.0);
          // GEOMETRIC SPECULAR ANTIALIASING — and the far-field facet fix, because they are the same bug.
          //
          // A pow(.,240) lobe subtends only ~0.09 rad of normal. This wave table turns the normal by
          // roughly SIGMA A k^2 ~ 1.5 rad per metre of sea (the enriched chop band and the micro-ripples
          // dominate that sum). So any pixel spanning more than ~6 cm of water already contains several
          // whole specular cycles — and the framebuffer samples it ONCE. That is textbook specular
          // aliasing, and on a sunlit gale it printed a quilt of bright rectangles across the swell that
          // no amount of foam tuning could remove (proved by ablation: zeroing this one term cleared it).
          // The far-field "facetas em bloco" from the water-v2 report is the same lobe resolving the flat
          // screen-derivative normal of a 32 m clipmap quad.
          //
          // Fix: widen the lobe by the normal variance inside the pixel (Kaplanyan/Toksvig filtering) and
          // dim it to match, which is not a cheat — a surface whose detail has fallen below the sampling
          // rate IS a rougher surface, and that is what a real lens records. Right under the bow the
          // footprint is a centimetre and the lobe stays nearly as tight as before, so the close-up
          // sparkle the sea was tuned for survives; it is only where it could not be represented that it
          // broadens. ~6 ALU.
          // The CEILING matters as much as the filter. 240 was authored against a four-wave normal; with
          // the enriched spectrum the surface turns far faster, and a lobe that tight resolves the wave
          // field itself into a lit grating no matter how the pixel is filtered (ablating this one term
          // was what finally cleared the hatch). 70 is the widest lobe that still breaks the sun into a
          // path of separate sparkles rather than one smeared blob.
          float sigma = 1.55 * foot;                                  // radians of normal turn per pixel
          float shin = clamp(1.0 / (1.0 / 70.0 + 2.0 * sigma * sigma), 16.0, 70.0);
          float gain = 0.35 + 0.65 * (shin / 70.0);                   // a wider lobe must be dimmer
          // …and the cat's paws take their second bite here. A rough patch scatters the sun into a dull
          // sheen; the glassy lane between two paws mirrors it into a hard path. Modulating the TIGHT
          // lobe only (the wide one is the sheet's ambient sheen and must not blink) is what turns the
          // sun track into the broken, moving thing it is on real water instead of one even carpet.
          float glit = (pow(sp, shin) * 1.30 * detail * gain * pawSpec
                     + pow(sp, 34.0) * (0.10 + 0.13 * (1.0 - detail))) * (1.0 - 0.95 * fw);
          col += vec3(1.0, 0.94, 0.82) * glit * (0.85 - 0.5 * uNight);
          // 🌙 THE MOON PATH. The sun's glitter machinery, run a second time down the moon's own vector —
          // which is literally the vector the sky dome draws the moon disc at, so the column of light on
          // the water starts exactly under the moon and no player will ever catch them disagreeing.
          // Colder, WIDER and softer than the sun: a moon is a half-degree source seen through the same
          // roughness, so its path is a broad shimmering road, not a field of hard sparks. uMoon.w is 0
          // by day / under a new moon / in a gale, so this costs one compare per fragment when it is off.
          if (uMoon.w > 0.004) {
            vec3 ML = normalize(uMoon.xyz);
            vec3 MH = normalize(ML + viewDir);
            float msp = max(dot(nrm, MH), 0.0);
            // the SAME footprint-filtered lobe the sun uses (a moon path is not exempt from Nyquist —
            // left unfiltered its wide lobe printed its own quilt across the far field), just wider and
            // dimmer to begin with, because a moon is a dim half-degree source and its road on the water
            // is a broad shimmer rather than a field of hard sparks.
            float mshin = clamp(shin * 0.42, 10.0, 26.0);
            // …and the road is BROKEN by the same patch field the crest sheen rides. An unbroken moon
            // path is a searchlight; a real one shivers, because the water under it is not one material.
            float mroad = 0.45 + 0.75 * livePatch;   // ('patch' is a GLSL reserved word — hence livePatch)
            float mglit = (pow(msp, mshin) * 0.85 * detail * gain * pawSpec
                        + pow(msp, 18.0) * 0.11 * (1.0 - 0.7 * smoothstep(0.8, 7.0, foot))) * (1.0 - 0.95 * fw);
            col += vec3(0.72, 0.82, 1.0) * mglit * mroad * uMoon.w * 0.80;
          }
          // 🌌 THE AURORA ROAD — the sky dome's own curtain function, evaluated down the REFLECTED ray,
          // so the band on the water IS the band overhead. Band only, 2 noise, behind a day-false
          // compare. Every decision here is documented on AURORA_GLSL (§ THE CALL SITES / SEA).
          if (uAurAmt > 0.004) {
            // a SMOOTHED normal: the real per-pixel one mirrors this soft huge source into green wires
            vec3 rn = normalize(mix(nrm, vec3(0.0, 1.0, 0.0), 0.55));
            vec3 rd = reflect(-viewDir, rn);
            rd = normalize(vec3(rd.x, abs(rd.y), rd.z));
            float arho = acos(clamp(dot(rd, uAurZ), -1.0, 1.0));
            float anorth = 0.5 + 0.5 * dot(normalize(rd.xz + vec2(1e-5, 0.0)), normalize(uAurZ.xz + vec2(1e-5, 0.0)));
            float areach = smoothstep(0.08, 0.56, anorth) * smoothstep(1.52, 1.26, arho);
            if (areach > 0.003) {
              float aph = atan(dot(rd, uAurB), dot(rd, uAurT));
              float ab = ${AUR_C[0]!.b} + ${AUR_C[0]!.w} * (auArc2(aph, ${AUR_C[0]!.R}, uTime * ${AUR_C[0]!.d}) - 0.5) * 2.0;
              // longer + softer than the sky's: a reflection in moving water is a BLURRED reflection,
              // and that blur is what carries the road toward the viewer instead of stopping at 40 m
              vec3 ac = auCurtain(arho, ab, ${(Number(AUR_C[0]!.h) * 2.6).toFixed(4)}, ${AUR_C[0]!.e} * 2.4);
              float aroad = 0.34 + 0.90 * livePatch;   // the same broken water the moon road rides
              col += auColour(uAurLo, uAurMid, uAurHi, ac.y) * (ac.x + ac.z * 0.45)
                   * areach * aroad * (0.26 + 0.74 * fres) * uAurAmt * ${AURORA.SEA_GAIN} * (1.0 - 0.85 * fw);
            }
          }
          // crest whitening on the STEEP faces only (the analytic slope knows where they are), on top of the
          // height band above — this is what stops the foam reading as a painted contour line.
          // …and the STEEPNESS this keys off is the swell's too (swellNH.x is the swell-only crest pinch),
          // for exactly the reason the altitude above is: a threshold on the full normal prints the chop's
          // interference lattice instead of finding the face of the wave.
          float steepf = clamp((swellNH.y > -50.0 ? swellNH.x : (1.0 - nrm.y)) * 11.0, 0.0, 1.0);
          col = mix(col, uFoam, smoothstep(0.5, 1.0, t) * smoothstep(0.30, 0.95, steepf) * 0.16 * (1.0 - 0.6 * uNight) * detail * (1.0 - fw));
` : /* glsl */ `
          nrm = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
          if (nrm.y < 0.0) nrm = -nrm;
          nrm = normalize(mix(vec3(0.0, 1.0, 0.0), nrm, 0.65)); // damp low-tessellation faceting, keep wave tilt
          // white water has no shape — same argument as the lit path, applied here because the cheap path
          // only knows its normal now (see the hoisted declaration above)
          nrm = normalize(mix(nrm, vec3(0.0, 1.0, 0.0), fw * 0.85));
          float fres = pow(1.0 - clamp(dot(nrm, viewDir), 0.0, 1.0), 4.0);
          col = mix(col, uFog, fres * 0.30 * uFresK);              // sky/horizon reflection at grazing angles
          vec3 H = normalize(L + viewDir);
          float spec = pow(max(dot(nrm, H), 0.0), 90.0);
          col += vec3(1.0, 0.94, 0.82) * spec * (0.85 - 0.5 * uNight); // sun glint, softened at night
          // 🌙 the moon path on the LOW path too — one wider, colder lobe. The night must be legible on
          // the cheap sea as well; this is 6 ALU behind a compare that is false all day.
          if (uMoon.w > 0.004) {
            vec3 MH = normalize(normalize(uMoon.xyz) + viewDir);
            col += vec3(0.72, 0.82, 1.0) * pow(max(dot(nrm, MH), 0.0), 34.0) * uMoon.w * 1.1;
          }
`}
          // --- Onda Sistema de Luz: lantern glints — a Blinn-Phong smear per nearby FakeLight, using the
          // SAME wave-perturbed normal as the sun glint, so each lamp paints a trembling light-path on the
          // water toward the viewer (the classic "lantern on the harbour" reflection), plus a soft local
          // pool right under the lamp. Strength (uGlint.w) carries the flame flicker → the smear dances. ---
          for (int i = 0; i < ${GLINT_MAX}; i++) {
            if (i >= uGlintCnt) break;
            vec4 gp = uGlint[i];
            vec2 gd = vWorld.xz - gp.xy;
            float gd2 = dot(gd, gd);
            if (gd2 > 6400.0) continue;                                  // 80 m reach per lamp
            vec3 Lg = normalize(vec3(gp.x, gp.z, gp.y) - vWorld);
            vec3 Hg = normalize(Lg + viewDir);
            // FOOTPRINT-FILTERED, exactly like the sun and moon lobes. A fixed pow(.,44) lobe subtends
            // ~0.15 rad of normal, and this wave field turns the normal faster than that inside one pixel
            // as soon as the pixel covers more than a few centimetres — so the lantern's reflection came
            // back as a CHECKERBOARD of orange rectangles (the wave grating, resolved once per pixel).
            // Widening the lobe by the in-pixel normal variance and dimming to match is the honest answer:
            // water too rough to resolve IS rougher, and a real lantern on a real harbour smears.
            float gsh = clamp(44.0 / (1.0 + 5.0 * footAA * footAA), 8.0, 44.0);
            float sg = pow(max(dot(nrm, Hg), 0.0), gsh) * (0.45 + 0.55 * gsh / 44.0);
            float att = gp.w / (1.0 + gd2 * 0.05);                       // tight falloff pool near the hull
            float reach = 1.0 - gd2 / 6400.0;                            // fade the smear out at the rim
            col += uGlintCol[i] * (sg * att * 2.4 + att * 0.09) * reach;
          }
          // --- LIGHTNING on the water: a strike whitens the whole sea for a beat, hardest on the crests ---
          if (uFlash > 0.001) col += vec3(0.50, 0.58, 0.74) * uFlash * (0.55 + 1.1 * smoothstep(0.55, 1.0, t));
          float f = 1.0 - exp(-uFogDensity * viewD);
          col = mix(col, uFog, clamp(f, 0.0, 1.0));
          // THE SEAL, sea side. The sky dome forces its last ~3° to exactly uHorizon and the ocean forces
          // its far field to exactly uFog — and updateSky copies ONE colour into both. So sea and sky meet
          // at the same number and there is nothing to see at the junction.
          //
          // The fade used to start at 0.86 of the 750 m radius (645 m) and finish AT it, leaving 400 m of
          // fully-hazed mesh out to the 1152 m rim. That was already colour-safe, but it wasted the whole
          // outer ring on a constant — so the sea reached its horizon colour 645 m out and then simply
          // STOPPED being interesting, while the rim's 32 m swell silhouette still bit into the sky. Fading
          // over a much longer run (0.55 → 1.30 of the radius, i.e. 412 m → past the rim) puts real
          // atmospheric depth back in the far field and means the outermost drawn row is provably 100%
          // uFog, with the last visible metres of geometry already indistinguishable from the sky.
          col = mix(col, uFog, smoothstep(0.55, 1.30, vEdge));
          gl_FragColor = vec4(pow(col, vec3(2.2)), 1.0); // -> linear for the tonemapped composer
        }
      `,
    });
  }

  /* ------------------------------- world -------------------------------- */

  /** Onda Ilhas-Tipo: swap the island archetype set used to DRESS the islets (Islands editor live preview),
   *  then re-dress the current sea. Render-only — chest positions come from the sim and never move. Pass null
   *  to restore the committed `ISLAND_ARCHETYPES`. */
  setIslandArchetypes(set: IslandArchetypeSet | null): void {
    this.islandArchetypes = set ?? ISLAND_ARCHETYPES;
    if (this.lastWorld) this.setWorld(this.lastWorld);
  }

  /**
   * Free the heavy, sea-EXCLUSIVE GPU resources when the captain steps ashore, so the walkable port's GLB kit
   * doesn't stack on top of a still-resident sea = the double-peak that OOMs a WKWebView (the Phantom-browser
   * blue screen). Disposes the island/outpost/landmark GLBs (worldRoot), the player-base structures (basesRoot),
   * the fishing shoals + gull flock, and every remote-ship clone — all of which rebuild deterministically from
   * renderer-owned state (lastWorld / lastBases / lastShoals) or from the next snapshot (syncShips). The ocean,
   * sky, ship TEMPLATES and the composer are kept (small, shared, or needed the instant we surface). Idempotent.
   *
   * Proven by memInfo(): renderer.info.memory.{geometries,textures} drops on this call and climbs back on restore.
   */
  releaseSeaAssets(): void {
    if (this._seaReleased) return;
    this._seaReleased = true;
    for (const c of this.worldRoot.children) disposeGroup(c); // island/outpost/landmark GLBs — the heaviest sea assets
    this.worldRoot.clear();
    this.kitExtNodes.length = 0; this.kitLodNodes.length = 0; this.chestNodes.clear(); this.chestBeaconMats.length = 0;
    for (const [id, g] of this.bases) { this.basesRoot.remove(g); disposeGroup(g); this.bases.delete(id); } // player bases
    this.setShoals([]);                                        // disposes the shoal sheets + gulls (keeps lastShoals)
    for (const [id, g] of this.ships) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); } // remote hulls
    this.wakeSpd.clear(); this.clearWhaleWake(); this.wakeFx.clear(); // every wake goes with the hulls that laid it
    this.splashFx.clear();                                            // …and every splash still in the air
    this.fish?.clear();                                               // …and the shoal in mid-glide
  }

  /**
   * Rebuild everything releaseSeaAssets() freed, on the way back out to open water. worldRoot + bases + shoals
   * come straight from the renderer-owned last-payloads; remote ships re-materialise from the next snapshot via
   * syncShips (the loop calls it every frame). Idempotent — a no-op if the sea was never released.
   */
  restoreSeaAssets(): void {
    if (!this._seaReleased) return;
    this._seaReleased = false;
    if (this.lastWorld) this.setWorld(this.lastWorld);              // island/outpost/landmark GLBs + chests (taken-state re-applied inside)
    if (this.lastBases) this.syncBases(this.lastBases, this.lastYourIslandId); // player bases
    this.setShoals(this.lastShoals);                               // fishing shoals + gulls
  }

  setWorld(world: WorldMsg): void {
    this.lastWorld = world;
    for (const c of this.worldRoot.children) disposeGroup(c); // free each prior island/outpost group's owned resources
    this.worldRoot.clear();
    this.chestBeaconMats.length = 0; // setWorld can rerun when the chest GLB lands — never double-pulse
    this.chestNodes.clear();        // rebuilt below; taken-state re-applied at the end so a hoisted chest stays hidden
    this.kitExtNodes.length = 0;    // the heavy kit-exterior meshes are children of the disposed groups; rebuilt below
    this.kitLodNodes.length = 0;    // hero-building LOD pairs are children of the disposed groups too; rebuilt below
    // Onda de Arte — Ilhas de Tesouro: draw each loot islet as a CLONE of a GLB template (a leaning-coconut
    // isle, or ~1/3 a bare sandbar) once loaded; until then, the retired procedural sand cone + palm covers.
    // The clone lives in the island's own group, so the existing distance-cull hides far islets for free.
    const coTpl = this.isleTpl.coconut ?? this.isleTpl.sand; // fall back to the other type if a single GLB failed
    const saTpl = this.isleTpl.sand ?? this.isleTpl.coconut;
    const useGlb = !!coTpl;
    const gold = new THREE.MeshStandardMaterial({ color: 0xe8b53a, roughness: 0.4, metalness: 0.6 }); // procedural chest fallback
    const beachMat = this.makeBeachMaterial(); // warm treasure-island sand apron + its wet waterline band
    const sand = new THREE.MeshStandardMaterial({ color: 0xd9c187, roughness: 1 }); // procedural islet (GLB missing OR archetype base='procedural')
    const rock = new THREE.MeshStandardMaterial({ color: 0x6b7b73, roughness: 1 });
    const palm = new THREE.MeshStandardMaterial({ color: 0x2f7d4f, roughness: 1 });
    // 🕊️ hand the flock the land. Gulls work islands; this is what lets a sub-flock adopt one and fly a
    // circuit round it instead of only ever wheeling over the camera (see BirdFlock.setIslands).
    this.birds?.setIslands(world.islands.map((i) => ({ x: i.x, z: i.y, r: i.radius })));
    for (const isl of world.islands) {
      const g = new THREE.Group();
      // The islet + sand apron together form the SEATING TERRAIN. It lives in its own subgroup so the
      // downward chest-seating ray only hits sand — never a chest or a beacon plane we add afterwards.
      const terrain = new THREE.Group();
      g.add(terrain);
      // Onda Ilhas-Tipo: the island's DRESSING is data-driven by its archetype (default = the shipped
      // 1/3-sandbar / 2/3-coconut split, an exact no-op). `base` picks the hero model; extra decorations and
      // per-type apron tuning ride on the archetype too. Chests are NOT moved here — they stay at their sim
      // positions (frente 1) so pickup never desyncs.
      const arch = archetypeForIsland(isl.id, this.islandArchetypes);
      const isBar = arch.base === 'sand';
      const wantProc = arch.base === 'procedural';
      // sand apron: a wide low dome covering the full chest scatter (server scatters to ~50 m, far past the
      // compact ~26 m GLB islet). Rim sunk below the waterline so the sand meets the sea with no hard edge.
      const beachMult = arch.beachMult ?? ISLE.BEACH_MULT;
      const beachRise = arch.beachRise ?? ISLE.BEACH_RISE;
      const beachSink = arch.beachSink ?? ISLE.BEACH_SINK;
      const beach = new THREE.Mesh(new THREE.ConeGeometry(isl.radius * beachMult, beachRise + beachSink, 28), beachMat);
      beach.position.y = (beachRise - beachSink) / 2; // apex at +rise, base rim at −sink
      terrain.add(beach);
      if (useGlb && !wantProc) {
        const tpl = (isBar ? saTpl : coTpl)!;
        const s = (isl.radius * ISLE.FOOT_MULT) / tpl.fw;                  // uniform horizontal fit to the island radius
        const vs = s * (isBar ? ISLE.VS_SAND : ISLE.VS_COCONUT);          // vertical scale (coconut uniform; sandbar flatter)
        const yaw = ((Math.imul(isl.id, 40503) >>> 0) % 3600) * (Math.PI / 1800); // deterministic 0..2π spin per islet
        const clone = tpl.obj.clone(true);
        clone.userData.shared = true;                                     // geometry/material shared with the template → trackOwned skips it
        clone.scale.set(s, vs, s);
        clone.rotation.y = yaw;
        clone.position.y = ISLE.SINK - tpl.minY * vs;                     // rest the base at the (slightly sunk) waterline
        terrain.add(clone);
      } else {
        const cone = new THREE.Mesh(new THREE.ConeGeometry(isl.radius * 1.25, 9, 7), sand);
        cone.position.y = 1.5;
        terrain.add(cone);
        const peak = new THREE.Mesh(new THREE.ConeGeometry(isl.radius * 0.5, 7, 6), rock);
        peak.position.y = 7;
        terrain.add(peak);
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.7, 9), rock);
        trunk.position.set(isl.radius * 0.4, 6, 0);
        terrain.add(trunk);
        const leaves = new THREE.Mesh(new THREE.ConeGeometry(4.5, 3.5, 6), palm);
        leaves.position.set(isl.radius * 0.4, 11, 0);
        terrain.add(leaves);
      }
      terrain.updateMatrixWorld(true); // group still at origin → seating ray sees the apron + islet at local coords
      // archetype decorations (extra accent islets / wrecks / rocks / palms), each placed as a fraction of the
      // island radius and seated on the sand under it. Default archetypes carry none, so this is a no-op.
      for (const deco of arch.decorations ?? []) {
        const node = this.makeIsleDecoration(deco, isl.radius);
        if (!node) continue;
        const dx = deco.fx * isl.radius, dz = deco.fz * isl.radius;
        node.position.set(dx, this.surfaceYAt(terrain, dx, dz, ISLE.WATER_Y, 40), dz);
        node.rotation.y = deco.rot;
        g.add(node);
      }
      const chestTpl = this.propTemplates.get('chest');
      for (const c of isl.chests) {
        // Seat the chest on the ACTUAL sand under it (apron near the rim, islet mound near the centre) by a
        // downward raycast — never a guessed slope. The horizontal (dx, dz) is the EXACT sim position, so the
        // pickup ring (server: dist to chest x,y) always lines up with what the player sees. sim > aesthetics:
        // a chest with no sand under it at all just rests at the waterline (WATER_Y) rather than floating.
        const dx = c.x - isl.x, dz = c.y - isl.y;
        const surf = this.surfaceYAt(terrain, dx, dz, ISLE.WATER_Y, ISLE.SEAT_CAP);
        let chest: THREE.Object3D;
        if (chestTpl) { chest = chestTpl.clone(true); chest.userData.shared = true; chest.position.set(dx, surf + ISLE.SEAT_LIFT, dz); chest.scale.multiplyScalar(1.5); } // GLB clone, base at y=0
        else { chest = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.6, 1.6), gold); chest.position.set(dx, surf + 0.8, dz); } // box origin centred
        g.add(chest);
        const chestNodes: THREE.Object3D[] = [chest]; // Onda Carona: mesh + beacon planes toggled together on hoist/respawn
        // golden beacon column: two crossed additive planes with a vertical fade — a lighthouse for loot,
        // visible from far across the water (and a literal beacon at night). Pulsed in the render loop.
        if (!this.beaconTex) {
          const c = document.createElement('canvas'); c.width = 32; c.height = 128;
          const ctx = c.getContext('2d')!;
          const grad = ctx.createLinearGradient(0, 128, 0, 0);
          grad.addColorStop(0, 'rgba(255,214,110,0.85)'); grad.addColorStop(0.55, 'rgba(255,196,80,0.30)'); grad.addColorStop(1, 'rgba(255,190,70,0)');
          ctx.fillStyle = grad; ctx.fillRect(0, 0, 32, 128);
          this.beaconTex = new THREE.CanvasTexture(c);
        }
        const bMat = new THREE.MeshBasicMaterial({ map: this.beaconTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, color: 0xffcf70 });
        bMat.userData.grp = g; // so the per-frame pulse can skip beacons on distance-culled islands
        const bGeo = new THREE.PlaneGeometry(3.4, 30);
        for (const rot of [0, Math.PI / 2]) {
          const plane = new THREE.Mesh(bGeo, bMat);
          plane.position.set(dx, surf + 15, dz); plane.rotation.y = rot; plane.renderOrder = 9;
          g.add(plane); this.trackOwned(g, plane);
          chestNodes.push(plane);
        }
        this.chestBeaconMats.push(bMat);
        this.chestNodes.set(c.id, chestNodes);
        if (this.takenChests.has(c.id)) for (const n of chestNodes) n.visible = false; // stay hidden through a re-dress
      }
      this.trackOwned(g, g); // procedural island parts (if any) owned; chest GLB clones are tagged shared + skipped
      g.position.set(isl.x, 0, isl.y);
      this.worldRoot.add(g);
    }
    const plat = new THREE.MeshStandardMaterial({ color: 0x7a5a3a, roughness: 0.9 });
    const cloth = new THREE.MeshStandardMaterial({ color: 0x2a9d5a, roughness: 0.8, side: THREE.DoubleSide });
    const outpostTpl = this.propTemplates.get('outpost');
    for (const op of world.outposts) {
      const g = new THREE.Group();
      // Item 3 (hideOutpostStructure) — the owner can hide a port's stock trading-post structure so his own
      // landmark stands in its place. Render-only: the sell-zone ring + landmark below still draw. Absent/false
      // → the shipped structure draws (byte-identical).
      const hideStructure = this.portFlags?.[op.id]?.hideOutpostStructure === true;
      if (hideStructure) {
        /* owner hid the default structure — draw no trading post (nor its fallback) */
      } else if (outpostTpl) {
        // the AI-generated trading post (sandbar + deck + mast/flag + merchant stall); base sits at y=0
        const post = outpostTpl.clone(true);
        post.userData.shared = true; // GLB template — trackOwned skips it (disposed with the template)
        g.add(post);
      } else {
        // procedural fallback (GLB not loaded)
        const dock = new THREE.Mesh(new THREE.CylinderGeometry(18, 20, 4, 12), plat);
        dock.position.y = 1;
        g.add(dock);
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 22), plat);
        pole.position.y = 12;
        g.add(pole);
        const flag = new THREE.Mesh(new THREE.PlaneGeometry(8, 5), cloth);
        flag.position.set(4, 20, 0);
        g.add(flag);
      }
      // sell-zone ring on the water — the Capital berths in gold, the others in trade-green
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(op.radius, op.capital ? 1.7 : 1.2, 6, 40),
        new THREE.MeshBasicMaterial({ color: op.capital ? 0xe6be5a : 0x39d98a, transparent: true, opacity: op.capital ? 0.62 : 0.5 }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.6;
      g.add(ring);
      // The port LANDMARK seen from the sea — lighthouse + breakwater arc + anchored masts — is now DATA per
      // outpost (Landmark Editor micro-wave): a Save-Online override, else the shipped `landmarkForOutpost`.
      // The Capital ships the full breakwater; villages ship nothing (empty) — pixel-identical to before.
      let landmark: readonly LandmarkPiece[] = this.landmarkOverride?.[op.id] ?? landmarkForOutpost(op.id);
      // Onda Portos Livres — every port (capital AND village) also shows the props the owner dropped OUTSIDE its
      // walkable plaza, so a rampart/lighthouse/tree — or a generated harbour building (wharf/citadel) — placed
      // "outside the city" is visible sailing up, not just from inside. Villages carry only what sits outside the
      // plaza box (e.g. Skull Harbor's wharf at port y=-3). Empty for a plaza village → byte-identical to before.
      const ext = this.exteriorPiecesFor(op.capital, op.x, op.y, op.id);
      if (ext.length) landmark = landmark.length ? [...landmark, ...ext] : ext;
      if (landmark.length) this.addLandmark(g, landmark, op.x, op.y);
      this.trackOwned(g, g); // all outpost meshes here are owned
      g.position.set(op.x, 0, op.y);
      this.worldRoot.add(g);
    }
    // static shipwrecks (2b) — half-submerged, divable
    for (const wr of world.wrecks) {
      const g = this.makeWreck();
      g.position.set(wr.x, -0.6, wr.y);
      g.rotation.y = (wr.id * 2.399) % (Math.PI * 2); // deterministic heading spread
      this.worldRoot.add(g);
    }
  }

  /** Onda Carona: set the authoritative set of hoisted-and-gone island chests (by sim chestId). Hides each
   *  taken chest's mesh + beacon and shows the rest — idempotent, so it doubles as the reconnect resync. The
   *  set is remembered so a later internal setWorld rerun (GLB load, archetype re-dress) keeps looted chests
   *  hidden. Cheap: only called by main.ts when the taken-set actually changes. */
  setChestStates(taken: Iterable<number>): void {
    this.takenChests = new Set(taken);
    for (const [id, nodes] of this.chestNodes) {
      const vis = !this.takenChests.has(id);
      for (const n of nodes) n.visible = vis;
    }
  }

  /** One shipwreck group — the GLB (shared clone) if loaded, else a tilted broken-hull fallback. */
  private makeWreck(): THREE.Group {
    const g = new THREE.Group();
    const tpl = this.propTemplates.get('shipwreck');
    if (tpl) { const w = tpl.clone(true); w.userData.shared = true; w.rotation.z = 0.12; g.add(w); }
    else {
      const hull = new THREE.Mesh(new THREE.BoxGeometry(11, 3, 4.5), new THREE.MeshStandardMaterial({ color: 0x33251a, roughness: 1 }));
      hull.rotation.z = 0.28; hull.position.y = 0.4; g.add(hull);
      const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.4, 8), new THREE.MeshStandardMaterial({ color: 0x2a2018, roughness: 1 }));
      mast.rotation.z = 1.0; mast.position.set(0, 2, 0); g.add(mast);
    }
    this.trackOwned(g, g); // the GLB clone is tagged shared (skipped); fallback meshes are owned
    return g;
  }

  /** Landmark Editor (Save Online / in-editor): install a per-outpost exterior override (keyed by outpost id),
   *  or null to clear. Re-renders the world so the change shows immediately. Render-only, no wire. */
  setLandmarkOverride(map: Record<number, readonly LandmarkPiece[]> | null): void {
    this.landmarkOverride = map;
    if (this.lastWorld) this.setWorld(this.lastWorld);
  }

  /** Item 3 (hideOutpostStructure) — install the per-outpost render-only flags (Save-Online `ports` slice at
   *  boot, else the Landmark Editor live). `render` re-renders the world so the change shows immediately; pass
   *  false to only STORE the map (a following setWorld — e.g. setLandmarkOverride — will honour it, avoiding a
   *  double rebuild). null clears back to the shipped structures. Render-only, no wire. */
  setPortFlags(map: Record<number, { hideOutpostStructure?: boolean }> | null, render = true): void {
    this.portFlags = map;
    if (render && this.lastWorld) this.setWorld(this.lastWorld);
  }

  /** Onda Portos Livres — install the Save-Online PortLayout overrides (or null on a slice to fall back to the
   *  shipped layouts). Their EXTERIOR props then render from the sea. `capital` overrides the capital city,
   *  `city` overrides every village (mirrors main.ts's resolvePortLayout). Re-renders so the change shows
   *  immediately. Render-only, no wire. A no-op when a layout has no props outside its plaza. */
  setPortExteriors(city: PortLayout | null, capital: PortLayout | null): void {
    this.portExterior = { city, capital };
    if (this.lastWorld) this.setWorld(this.lastWorld);
  }
  /** Back-compat shim (Onda Entorno Livre) — install only the capital's exterior override, keeping the village
   *  slice untouched. Kept so older callers/tests still compile. */
  setCapitalExterior(layout: PortLayout | null): void {
    this.setPortExteriors(this.portExterior.city, layout);
  }

  /** Convert a port's EXTERIOR walkable-city props (Save-Online override, else the shipped layout) into sea
   *  landmark pieces in the outpost's LOCAL sea frame — only props OUTSIDE the plaza box [0,SIZE]², size-reconciled
   *  to the walkable scale so a prop never jumps between scenes. CLASSIC breakwater kinds (wall/lighthouse/pine/
   *  pine_tall/rock/cliff) carry an EXT_SEA_SCALE ratio; ANY OTHER kit kind (wharf/citadel/houses/town-dressing)
   *  passes through with scale = its `s` (the sea normalizes it directly with KIT_FOOT/KIT_TALL). The port frame
   *  is embedded at the outpost's seaward heading (`harborMouth`), so a village at any world position projects
   *  correctly (the capital, seaward -y, reduces to the old pure px-48/py-46 offset). Render-only — no collision.
   *  Returns [] when the resolved layout has nothing outside its plaza (a plaza village, or the shipped capital). */
  private exteriorPiecesFor(capital: boolean, ox: number, oy: number, _id: number): LandmarkPiece[] {
    const layout = capital ? (this.portExterior.capital ?? CAPITAL_LAYOUT) : (this.portExterior.city ?? layoutForOutpost(_id));
    const props = layout.props;
    if (!props || !props.length) return [];
    const S = layout.size;
    const pinX = S / 2, pinY = capital ? CAPITAL.MOUTH_DIST : VILLAGE_SEA_PIN_Y;
    // the outpost's seaward frame: (sx,sy) toward open sea, (px,py) tangent (port +x). The port frame is embedded
    // rotated by alpha so port -y (the dock/exit) points seaward; a prop's world yaw is alpha - heading.
    const m = harborMouth(ox, oy);
    const alpha = Math.atan2(-m.py, m.px);
    const out: LandmarkPiece[] = [];
    for (const p of props) {
      if (p.x >= 0 && p.y >= 0 && p.x <= S && p.y <= S) continue;         // interior props render only in the walkable city
      const ratio = EXT_SEA_SCALE[p.kind];                                // classic breakwater kind → LANDMARK.* ratio
      const isKit = ratio === undefined && KIT_URL[p.kind] !== undefined; // any other kit GLB kind → KIT_FOOT/KIT_TALL
      if (ratio === undefined && !isKit) continue;                        // shadow decals / stations / unknown → not sea-renderable
      const dx = p.x - pinX, dy = p.y - pinY;
      const piece: LandmarkPiece = {
        kind: p.kind,
        x: dx * m.px - dy * m.sx,                                         // port east·tangent + port north·(-seaward)
        y: dx * m.py - dy * m.sy,
        rot: alpha - p.heading,                                           // frame yaw − the port's -heading render convention
        scale: (ratio ?? 1) * (p.s ?? 1),                                // classic: ratio·s ; kit: s (direct KIT_* normalize)
      };
      if (p.h != null && p.h !== 0) piece.h = p.h;                        // altitude passes through (world-Y over the waterline)
      out.push(piece);
    }
    return out;
  }

  /** The port's far-sea landmark, built from DATA (Landmark Editor micro-wave) in the outpost group's LOCAL
   *  space: `lighthouse` towers (the striped GLB, or a procedural fallback), `wall` seawall panels merged into
   *  ONE InstancedMesh (the GLB stone panel, or a box fallback), and `mast` poles merged into ONE InstancedMesh
   *  + a small cloth sail each. Each piece carries LOCAL (x, y→world z), yaw `rot`, and an optional uniform
   *  `scale`. The shipped Capital data (`LANDMARK_LAYOUTS[1]`) reproduces the old hard-coded breakwater
   *  pixel-for-pixel. Frustum-culled, no shadows — the sea draw-call budget holds (lighthouse + wall inst +
   *  mast inst + a sail per mast).
   *
   *  Onda Portos Livres — any kind that is NOT a classic breakwater piece but HAS a city-kit GLB (`KIT_URL`)
   *  is bucketed by kind and instanced via `instanceKitSeaKind` (one InstancedMesh per kind, lazily loaded). The
   *  outpost world position (`ox`,`oz`) is passed through so those heavy meshes register for the distance cull. */
  private addLandmark(g: THREE.Group, pieces: readonly LandmarkPiece[], ox = 0, oz = 0): void {
    const dark = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 1 });
    const walls: LandmarkPiece[] = [], masts: LandmarkPiece[] = [], islets: LandmarkPiece[] = [];
    const pines: LandmarkPiece[] = [], pinesTall: LandmarkPiece[] = [], rocks: LandmarkPiece[] = [], cliffs: LandmarkPiece[] = [];
    const kitBuckets = new Map<string, LandmarkPiece[]>();

    // ---- lighthouses: the GLB tower (height-normalized) or a procedural fallback ----
    const lhBake = this.landmarkBake.lighthouse;
    for (const p of pieces) {
      if (this.addLandmarkLights(g, p, ox, oz)) continue;   // a pure light_* piece — glow only, no mesh
      if (p.kind === 'wall') { walls.push(p); continue; }
      if (p.kind === 'mast') { masts.push(p); continue; }
      if (p.kind === 'islet' || p.kind === 'sandbar') { islets.push(p); continue; }
      if (p.kind === 'pine') { pines.push(p); continue; }
      if (p.kind === 'pine_tall') { pinesTall.push(p); continue; }
      if (p.kind === 'rock') { rocks.push(p); continue; }
      if (p.kind === 'cliff') { cliffs.push(p); continue; }
      if (p.kind !== 'lighthouse' && KIT_URL[p.kind]) { (kitBuckets.get(p.kind) ?? kitBuckets.set(p.kind, []).get(p.kind)!).push(p); continue; }
      const s = p.scale ?? 1;
      const hh = p.h ?? 0;                                   // ALTITUDE (m above waterline); 0 = on the water
      if (lhBake?.geo.boundingBox) {
        const bb = lhBake.geo.boundingBox; const h = (bb.max.y - bb.min.y) || 1;
        const lh = new THREE.Mesh(lhBake.geo, lhBake.mat);
        lh.castShadow = false; lh.receiveShadow = false;
        lh.scale.setScalar((LANDMARK.LIGHTHOUSE_H / h) * s); // ~30 m tall — reads across the whole AOI
        lh.position.set(p.x, hh, p.y);
        lh.rotation.y = p.rot;                               // turn its door landward
        g.add(lh);
      } else {
        const stone = new THREE.MeshStandardMaterial({ color: 0xd9d2c4, roughness: 0.95 });
        const band = new THREE.MeshStandardMaterial({ color: 0xb5432f, roughness: 0.95 });
        const lh = new THREE.Group();
        const base = new THREE.Mesh(new THREE.CylinderGeometry(5.5, 7, 6, 14), stone); base.position.y = 3; lh.add(base);
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 5, 20, 14), stone); shaft.position.y = 16; lh.add(shaft);
        const stripe = new THREE.Mesh(new THREE.CylinderGeometry(3.9, 4.4, 5, 14), band); stripe.position.y = 12; lh.add(stripe);
        const gallery = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.2, 1.4, 14), dark); gallery.position.y = 26.6; lh.add(gallery);
        const lantern = new THREE.Mesh(new THREE.CylinderGeometry(3, 3, 3.4, 12), new THREE.MeshStandardMaterial({ color: 0xffdf9a, emissive: 0xffb638, emissiveIntensity: 0.9, roughness: 0.5 }));
        lantern.position.y = 29; lh.add(lantern);
        // the lamp room's emissive breathes with the night (dim by day, blazing after dusk)
        this.lights.registerEmissive(lantern.material as THREE.MeshStandardMaterial, lh, 0.25, 1.8);
        const cap = new THREE.Mesh(new THREE.ConeGeometry(3.6, 3, 12), dark); cap.position.y = 32.2; lh.add(cap);
        lh.scale.setScalar(s); lh.position.set(p.x, hh, p.y); lh.rotation.y = p.rot; g.add(lh);
      }
    }

    // ---- seawall: ONE InstancedMesh — the GLB stone panel, or a box fallback — each scaled to world dims ----
    if (walls.length) {
      const wlBake = this.landmarkBake.wall;
      let geo: THREE.BufferGeometry, mat: THREE.Material, segW: number, segH: number, segD: number;
      if (wlBake?.geo.boundingBox) {
        const bb = wlBake.geo.boundingBox;
        geo = wlBake.geo; mat = wlBake.mat;
        segW = (bb.max.x - bb.min.x) || 1; segH = (bb.max.y - bb.min.y) || 1; segD = (bb.max.z - bb.min.z) || 1;
      } else {
        geo = new THREE.BoxGeometry(1, 1, 1); geo.translate(0, 0.5, 0); // unit box centred in XZ, base at y=0
        mat = new THREE.MeshStandardMaterial({ color: 0x8f8474, roughness: 1 });
        segW = 1; segH = 1; segD = 1;
      }
      const inst = new THREE.InstancedMesh(geo, mat, walls.length);
      inst.castShadow = false; inst.receiveShadow = false; inst.frustumCulled = false;
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sv = new THREE.Vector3(), pv = new THREE.Vector3();
      const up = new THREE.Vector3(0, 1, 0);
      walls.forEach((p, i) => {
        const s = p.scale ?? 1;
        pv.set(p.x, p.h ?? 0, p.y);                          // world-Y = altitude above the waterline (default 0)
        q.setFromAxisAngle(up, p.rot);                       // panel width runs tangent to the arc
        sv.set((LANDMARK_WALL_W * s) / segW, (LANDMARK.WALL_H * s) / segH, (LANDMARK.WALL_D * s) / segD);
        m4.compose(pv, q, sv); inst.setMatrixAt(i, m4);
      });
      inst.instanceMatrix.needsUpdate = true; g.add(inst);
    }

    // ---- anchored ship masts poking over the wall — one instanced mast set + a small sail each ----
    if (masts.length) {
      const inst = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.5, 0.6, LANDMARK.MAST_H, 6), dark, masts.length);
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sv = new THREE.Vector3(), pv = new THREE.Vector3();
      masts.forEach((p, i) => {
        const s = p.scale ?? 1;
        const hh = p.h ?? 0;                                 // altitude above the waterline (default 0)
        pv.set(p.x, hh + 1 + (LANDMARK.MAST_H / 2) * s, p.y); q.identity(); sv.setScalar(s);
        m4.compose(pv, q, sv); inst.setMatrixAt(i, m4);
        const sail = new THREE.Mesh(new THREE.PlaneGeometry(9, 12), new THREE.MeshStandardMaterial({ color: 0xe8e0cf, roughness: 0.9, side: THREE.DoubleSide }));
        sail.scale.setScalar(s); sail.position.set(p.x, hh + 1 + 0.65 * LANDMARK.MAST_H * s, p.y); sail.rotation.y = p.rot; g.add(sail);
      });
      inst.instanceMatrix.needsUpdate = true; g.add(inst);
    }

    // ---- shore islets (Onda Fix 5) — the treasure-islet GLBs (a leaning-coconut isle / a bare sandbar)
    // reused to dress a port's approach. Cloned from the shared template (geometry/materials shared → cheap),
    // scaled to LANDMARK.ISLET_R; a small procedural sand cone covers until the GLBs stream in (same fallback
    // pattern as the lighthouse). Render-only, no collision.
    for (const p of islets) {
      const bar = p.kind === 'sandbar';
      const tpl = bar ? (this.isleTpl.sand ?? this.isleTpl.coconut) : (this.isleTpl.coconut ?? this.isleTpl.sand);
      const s0 = p.scale ?? 1;
      const hh = p.h ?? 0;                                                // altitude above the waterline (default 0)
      if (tpl) {
        const s = (LANDMARK.ISLET_R * ISLE.FOOT_MULT * s0) / tpl.fw;
        const vs = s * (bar ? ISLE.VS_SAND : ISLE.VS_COCONUT);
        const clone = tpl.obj.clone(true);
        clone.userData.shared = true;                                     // shared geometry/material → trackOwned skips it
        clone.scale.set(s, vs, s);
        clone.rotation.y = p.rot;
        clone.position.set(p.x, hh + ISLE.SINK - tpl.minY * vs, p.y);
        g.add(clone);
      } else {
        const r = LANDMARK.ISLET_R * ISLE.FOOT_MULT * s0 * 0.6;
        const cone = new THREE.Mesh(new THREE.ConeGeometry(r, r * 0.5, 8), new THREE.MeshStandardMaterial({ color: 0xd9c187, roughness: 1 }));
        cone.position.set(p.x, hh + 1, p.y); g.add(cone);
      }
    }

    // ---- Onda Entorno landscape: dense pines, coastal boulders and stackable cliff chunks that green the
    // bare ground around a port's ramparts. Each kind → ONE InstancedMesh (the GLB bake, or a merged
    // procedural fallback), frustum-culled per port so an off-screen decorated port costs zero draw calls. So
    // "30 pines in a port" = +1 draw call, exactly like the seawall. Trees are height-normalized, rock/cliff
    // footprint-normalized; every instance honours its `rot`, `scale` and altitude `h`.
    this.instanceLandmarkKind(g, pines, this.landmarkBake.pine, LANDMARK.PINE_H, true, () => this.procPine(0x8a5a34, [0x2f7d4a, 0x35895a, 0x3f9a63]));
    this.instanceLandmarkKind(g, pinesTall, this.landmarkBake.pine_tall, LANDMARK.PINE_TALL_H, true, () => this.procPine(0x6f4a2e, [0x2b6f66, 0x2f7a6e, 0x36876f], true));
    this.instanceLandmarkKind(g, rocks, this.landmarkBake.rock, LANDMARK.ROCK_W, false, () => this.procRock());
    this.instanceLandmarkKind(g, cliffs, this.landmarkBake.cliff, LANDMARK.CLIFF_W, false, () => this.procCliff());

    // ---- Onda Portos Livres: the generated harbour kit (wharf/citadel/houses/town-dressing) placed OUTSIDE the
    // walls. Each kind → ONE InstancedMesh from the lazily-baked GLB, normalized with the SAME KIT_FOOT/KIT_TALL
    // the walkable city uses (pivot-for-pivot parity). Heavy hero meshes register for the distance cull.
    for (const [kind, ps] of kitBuckets) this.instanceKitSeaKind(g, kind, ps, ox, oz);
  }

  /** Onda Sistema de Luz — register the FakeLights a landmark piece carries, in the outpost group's local
   *  frame (piece-local offsets rotated by its yaw, scaled by its `scale`, lifted by its altitude `h`):
   *    - a pure `light_*` editor kind → ONE light from its preset (+ the owner's custom `tint`), NO mesh
   *      (returns true so the caller skips mesh building);
   *    - a mesh kind with a night table (windows, lamp heads, the lighthouse beacon — landmarkKindLights)
   *      → its lights ride along with the mesh.
   *  The flicker phase seeds from the piece's WORLD position → deterministic on every client. */
  private addLandmarkLights(g: THREE.Group, p: LandmarkPiece, ox: number, oz: number): boolean {
    const s = p.scale ?? 1, hh = p.h ?? 0;
    const seed = (Math.imul(Math.round((ox + p.x) * 8), 73856093) ^ Math.imul(Math.round((oz + p.y) * 8), 19349663)) >>> 0;
    const pure = presetForLightKind(p.kind);
    if (pure) {
      this.lights.add({ anchor: g, lx: p.x, ly: hh + pure.y * s, lz: p.y, preset: pure, seed, radiusScale: s, tint: p.tint });
      return true;
    }
    const kls = this.landmarkKindLights[p.kind];
    if (kls) {
      const cr = Math.cos(p.rot), sr = Math.sin(p.rot);
      for (let i = 0; i < kls.length; i++) {
        const k = kls[i]!;
        this.lights.add({
          anchor: g,
          lx: p.x + (k.dx * cr + k.dz * sr) * s,
          ly: hh + k.dy * s,
          lz: p.y + (-k.dx * sr + k.dz * cr) * s,
          preset: k.preset, seed: seed + i * 101,
          radiusScale: (k.r ?? 1) * s, intensityScale: k.i ?? 1, glint: k.glint,
        });
      }
    }
    return false;
  }

  /** Onda Portos Livres — lazily load + bake a city-kit GLB for the SEA view. Only fired when an exterior layout
   *  references the kind, so a heavy 1-6 MB harbour building never weighs the boot of a player who never sails to
   *  a port that placed it. On success it reruns setWorld so the port swaps its proxy for the real mesh; a
   *  failure caches `null` so we never retry. DoubleSide (Meshy bakes single-sided; the aggressive decimation
   *  leaves open edges — same fix the walkable kit + the landmark GLBs apply). */
  private ensureKitSeaKind(kind: string): void {
    if (this.kitSeaBake.has(kind) || this.kitSeaLoading.has(kind)) return;
    const url = KIT_URL[kind];
    if (!url) { this.kitSeaBake.set(kind, null); return; }
    this.kitSeaLoading.add(kind);
    void (async () => {
      try {
        const loader = this.kitSeaLoader ?? (this.kitSeaLoader = await this.makeGltfLoader());
        if (!loader) { this.kitSeaBake.set(kind, null); return; }
        const baked = this.bakeKitSea((await loadWithRetry(loader, url)).scene);
        this.kitSeaBake.set(kind, baked);
        if (baked && this.lastWorld) this.setWorld(this.lastWorld); // swap the proxy for the real mesh
      } catch { this.kitSeaBake.set(kind, null); }
      finally { this.kitSeaLoading.delete(kind); }
    })();
  }

  /** Bake a loaded kit GLB into ONE instanceable geometry + its (first) material + the ORIGINAL box — the exact
   *  same recipe as portscene's `bakeTemplate` (NO XZ centering, pivot kept), so KIT_FOOT/KIT_TALL normalization
   *  places the sea prop at the identical spot/scale the walkable city does. DoubleSide forced. */
  private bakeKitSea(obj: THREE.Object3D): { geo: THREE.BufferGeometry; mat: THREE.Material | THREE.Material[]; box: THREE.Box3 } | null {
    obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1);
    obj.updateMatrixWorld(true);
    const geos: THREE.BufferGeometry[] = [];
    let mat: THREE.Material | null = null;
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const gg = m.geometry.clone();
      for (const nm of Object.keys(gg.attributes)) if (nm !== 'position' && nm !== 'normal' && nm !== 'uv') gg.deleteAttribute(nm);
      if (!gg.attributes.normal) gg.computeVertexNormals();
      if (!gg.attributes.uv) { const n = gg.attributes.position!.count; gg.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2)); }
      gg.applyMatrix4(m.matrixWorld);
      geos.push(gg);
      if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    });
    if (!geos.length || !mat) return null;
    const merged = geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
    const box = new THREE.Box3().setFromBufferAttribute(merged.attributes.position as THREE.BufferAttribute);
    const material = mat as THREE.Material;
    (material as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
    return { geo: merged, mat: material, box };
  }

  /** Onda Portos Livres — instance a sea-view kit kind (the generated harbour buildings placed outside the walls)
   *  as ONE InstancedMesh, normalized with the SAME KIT_FOOT/KIT_TALL the walkable city uses (so a wharf/citadel
   *  is identical metres in both scenes). Triggers the lazy load when the bake isn't ready yet (setWorld reruns
   *  on arrival). Each piece honours its `rot`, `scale` and altitude `h`; the base rests at the waterline. The
   *  mesh registers for the distance cull (heavy hero geometry). */
  private instanceKitSeaKind(g: THREE.Group, kind: string, pieces: LandmarkPiece[], ox: number, oz: number): void {
    if (!pieces.length) return;
    this.ensureKitSeaKind(kind);
    const baked = this.kitSeaBake.get(kind);
    if (!baked) return;                                    // not loaded yet (load triggered) or failed → skip; reruns on arrival
    const sz = baked.box.getSize(new THREE.Vector3());
    const tall = KIT_TALL[kind];
    const base = tall ? tall / (sz.y || 1) : (KIT_FOOT[kind] ?? 6) / (Math.max(sz.x, sz.z) || 1);
    const minY = baked.box.min.y;                          // FULL bake's box normalizes BOTH meshes → pixel-aligned LOD
    // one InstancedMesh from a geometry+material with the shared per-piece placement (rot/scale/altitude, grounded).
    const build = (geo: THREE.BufferGeometry, mat: THREE.Material | THREE.Material[]): THREE.InstancedMesh => {
      const inst = new THREE.InstancedMesh(geo, mat, pieces.length);
      inst.castShadow = false; inst.receiveShadow = false;
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sv = new THREE.Vector3(), pv = new THREE.Vector3(), up = this._cnUp;
      pieces.forEach((p, i) => {
        const k = base * (p.scale ?? 1);                   // per-piece uniform size, still grounded at any scale
        q.setFromAxisAngle(up, p.rot); sv.setScalar(k);
        pv.set(p.x, -minY * k + (p.h ?? 0), p.y);
        m4.compose(pv, q, sv); inst.setMatrixAt(i, m4);
      });
      inst.instanceMatrix.needsUpdate = true;
      inst.computeBoundingSphere();                        // instance-aware → off-screen ports frustum-cull
      return inst;
    };
    const full = build(baked.geo, baked.mat);
    g.add(full);
    // Hero-building LOD — a heavy diorama also builds its decimated LOD (same transforms, FULL box), and the pair
    // is swapped by distance in cullWorld. If the LOD isn't loaded yet the full mesh just takes the plain distance
    // cull until it lands (setWorld reruns then, rebuilding the pair).
    if (HEAVY_LOD_KINDS.has(kind)) {
      this.ensureKitSeaLodKind(kind);
      const lodBaked = this.kitSeaLodBake.get(kind);
      if (lodBaked) {
        const lod = build(lodBaked.geo, lodBaked.mat);
        lod.visible = false;                               // start on the full; cullWorld sets the real state next tick
        g.add(lod);
        this.kitLodNodes.push({ full, lod, ox, oz, far: false });
        return;
      }
    }
    this.kitExtNodes.push({ node: full, ox, oz });         // non-LOD (or LOD not loaded yet) → plain distance cull
  }

  /** Hero-building LOD — lazily load + bake the decimated `.lod.glb` for a heavy kind (only when an exterior layout
   *  references it, so a player who never sails to a decorated port never pays for it). On success reruns setWorld
   *  so the port swaps its full-only pair for the full+lod pair; a failure caches null so we never retry. */
  private ensureKitSeaLodKind(kind: string): void {
    if (this.kitSeaLodBake.has(kind) || this.kitSeaLodLoading.has(kind)) return;
    const url = KIT_URL[kind]?.replace('.opt.glb', '.lod.glb');
    if (!url) { this.kitSeaLodBake.set(kind, null); return; }
    this.kitSeaLodLoading.add(kind);
    void (async () => {
      try {
        const loader = this.kitSeaLoader ?? (this.kitSeaLoader = await this.makeGltfLoader());
        if (!loader) { this.kitSeaLodBake.set(kind, null); return; }
        const baked = this.bakeKitSea((await loadWithRetry(loader, url)).scene);
        this.kitSeaLodBake.set(kind, baked);
        if (baked && this.lastWorld) this.setWorld(this.lastWorld); // swap the full-only pair for full+lod
      } catch { this.kitSeaLodBake.set(kind, null); }
      finally { this.kitSeaLodLoading.delete(kind); }
    })();
  }

  /** Build ONE InstancedMesh for a landscape landmark kind (pine/rock/cliff). Uses the baked GLB template when
   *  loaded, else the merged procedural fallback — both are a single geometry+material, so the port draws one
   *  mesh per kind regardless of instance count. `targetDim` is the world size the template normalizes to at
   *  scale 1 (HEIGHT when `byHeight`, else FOOTPRINT); each piece multiplies it by its `scale`, rotates by
   *  `rot`, and rises to altitude `h`. Frustum-culled by an instance-aware bounding sphere. */
  private instanceLandmarkKind(
    g: THREE.Group, pieces: LandmarkPiece[],
    bake: { geo: THREE.BufferGeometry; mat: THREE.Material } | undefined,
    targetDim: number, byHeight: boolean, fallback: () => { geo: THREE.BufferGeometry; mat: THREE.Material },
  ): void {
    if (!pieces.length) return;
    const tpl = bake?.geo.boundingBox ? bake : fallback();
    if (!tpl.geo.boundingBox) tpl.geo.computeBoundingBox();
    const bb = tpl.geo.boundingBox!;
    const dim = byHeight ? ((bb.max.y - bb.min.y) || 1) : (Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) || 1);
    const norm = targetDim / dim;
    const inst = new THREE.InstancedMesh(tpl.geo, tpl.mat, pieces.length);
    inst.castShadow = false; inst.receiveShadow = false;
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sv = new THREE.Vector3(), pv = new THREE.Vector3(), up = this._cnUp;
    pieces.forEach((p, i) => {
      const s = (p.scale ?? 1) * norm;
      pv.set(p.x, (p.h ?? 0) - bb.min.y * s, p.y);         // base rests at the waterline + altitude h
      q.setFromAxisAngle(up, p.rot); sv.setScalar(s);
      m4.compose(pv, q, sv); inst.setMatrixAt(i, m4);
    });
    inst.instanceMatrix.needsUpdate = true;
    inst.computeBoundingSphere();                          // instance-aware → off-screen ports are culled
    g.add(inst);
  }

  /** Merge a list of coloured primitive geometries into ONE vertex-coloured BufferGeometry with its base at
   *  y=0 (so it instances like a bake). Used by the procedural landmark fallbacks. */
  private mergeColored(parts: { geo: THREE.BufferGeometry; color: number }[]): { geo: THREE.BufferGeometry; mat: THREE.Material } {
    const c = new THREE.Color();
    for (const { geo, color } of parts) {
      const n = geo.attributes.position!.count; c.set(color);
      const col = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      for (const nm of Object.keys(geo.attributes)) if (nm !== 'position' && nm !== 'normal' && nm !== 'color') geo.deleteAttribute(nm);
    }
    const geo = parts.length === 1 ? parts[0]!.geo : (mergeGeometries(parts.map((p) => p.geo), false) ?? parts[0]!.geo);
    geo.computeBoundingBox();
    const bb = geo.boundingBox!; geo.translate(0, -bb.min.y, 0); geo.computeBoundingBox(); // rest base at y=0
    return { geo, mat: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1 }) };
  }

  /** Procedural conifer fallback: a fat trunk + stacked solid foliage cones (anti-pancake). `tall` steepens the
   *  taper for the slim variant. Built ~15 m tall; instanceLandmarkKind renormalizes to the target height. */
  private procPine(bark: number, greens: number[], tall = false): { geo: THREE.BufferGeometry; mat: THREE.Material } {
    const parts: { geo: THREE.BufferGeometry; color: number }[] = [];
    const trunkH = tall ? 5 : 4, trunkR = tall ? 0.5 : 0.6;
    const t = new THREE.CylinderGeometry(trunkR, trunkR * 1.3, trunkH, 6); t.translate(0, trunkH / 2, 0);
    parts.push({ geo: t, color: bark });
    const tiers = tall ? 5 : 3;
    const baseR = tall ? 3.0 : 4.0, topY = tall ? 22 : 15, y0 = trunkH * 0.75;
    for (let i = 0; i < tiers; i++) {
      const f = i / tiers, r = baseR * (1 - f * 0.72), ch = (topY - y0) / tiers * 1.7;
      const cone = new THREE.ConeGeometry(r, ch, 7); cone.translate(0, y0 + (topY - y0) * f + ch * 0.35, 0);
      parts.push({ geo: cone, color: greens[i % greens.length]! });
    }
    return this.mergeColored(parts);
  }

  /** Procedural coastal-boulder fallback: a fat rock + two smaller ones, faceted grey stone. ~9 m across. */
  private procRock(): { geo: THREE.BufferGeometry; mat: THREE.Material } {
    const parts: { geo: THREE.BufferGeometry; color: number }[] = [];
    const mk = (r: number, x: number, z: number, y: number, col: number): void => {
      const s = new THREE.IcosahedronGeometry(r, 0); s.scale(1, 0.72, 1); s.translate(x, y, z); parts.push({ geo: s, color: col });
    };
    mk(3.2, 0, 0, 2.0, 0x8a8f96); mk(2.0, 3.0, 0.6, 1.2, 0x7d838b); mk(1.6, -2.6, -1.0, 1.0, 0x969ba1);
    return this.mergeColored(parts);
  }

  /** Procedural cliff-chunk fallback: a strata block with a grassy top, ~26 m across. Base at y=0 → stacks. */
  private procCliff(): { geo: THREE.BufferGeometry; mat: THREE.Material } {
    const parts: { geo: THREE.BufferGeometry; color: number }[] = [];
    const w = 26, d = 18, hRock = 12;
    const rock = new THREE.BoxGeometry(w, hRock, d); rock.translate(0, hRock / 2, 0); parts.push({ geo: rock, color: 0x8a8072 });
    const strata = new THREE.BoxGeometry(w * 1.02, hRock * 0.28, d * 1.02); strata.translate(0, hRock * 0.42, 0); parts.push({ geo: strata, color: 0x776f63 });
    const grass = new THREE.BoxGeometry(w, 1.6, d); grass.translate(0, hRock + 0.6, 0); parts.push({ geo: grass, color: 0x4f8a48 });
    return this.mergeColored(parts);
  }

  /** Reconcile the dynamic (player-sunk) wreck meshes against the authoritative set (net.dynamicWrecks). */
  syncDynamicWrecks(wrecks: Map<number, { x: number; y: number }>): void {
    for (const [id, g] of this.dynamicWrecks) if (!wrecks.has(id)) { this.dynamicWreckRoot.remove(g); disposeGroup(g); this.dynamicWrecks.delete(id); }
    for (const [id, pos] of wrecks) if (!this.dynamicWrecks.has(id)) {
      const g = this.makeWreck();
      g.position.set(pos.x, -0.5, pos.y);
      g.rotation.y = (id * 1.7) % (Math.PI * 2);
      this.dynamicWreckRoot.add(g);
      this.dynamicWrecks.set(id, g);
    }
  }

  /** A drifting bottle: the GLB if loaded, else a small procedural green-glass bottle with a cork. */
  private makeBottle(): THREE.Group {
    const g = new THREE.Group();
    const tpl = this.propTemplates.get('bottle');
    if (tpl) { const b = tpl.clone(true); b.userData.shared = true; g.add(b); }
    else {
      const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.32, 1.5, 8), new THREE.MeshStandardMaterial({ color: 0x2f7d52, roughness: 0.25, metalness: 0.1, transparent: true, opacity: 0.85 }));
      const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.2, 0.5, 8), glass.material); neck.position.y = 0.95;
      const cork = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.22, 6), new THREE.MeshStandardMaterial({ color: 0x8a6a3a, roughness: 1 })); cork.position.y = 1.3;
      g.add(glass); g.add(neck); g.add(cork);
      g.rotation.z = 1.35; // lie on its side, adrift
    }
    this.trackOwned(g, g);
    return g;
  }

  /** Reconcile drifting bottles against the authoritative set (net.bottles). Bobbed each frame in render(). */
  syncBottles(bottles: Map<number, { x: number; y: number }>): void {
    for (const [id, e] of this.bottles) if (!bottles.has(id)) { this.bottleRoot.remove(e.g); disposeGroup(e.g); this.bottles.delete(id); }
    for (const [id, pos] of bottles) if (!this.bottles.has(id)) {
      const g = this.makeBottle();
      g.position.set(pos.x, 0.4, pos.y);
      this.bottleRoot.add(g);
      this.bottles.set(id, { g, x: pos.x, y: pos.y });
    }
  }

  /** Bob + slowly turn every drifting bottle on the swell (allocation-free). */
  private updateBottles(t: number): void {
    for (const e of this.bottles.values()) {
      const oy = oceanHeightAt(e.x, e.y, t, this.ampAt(e.x, e.y));
      e.g.position.y = oy + 0.35;
      e.g.rotation.y = t * 0.4 + e.x * 0.01;
    }
  }

  /* ------------------------------ treasure-map dig X (3.9) ------------------------------ */

  private digGroup: THREE.Group | null = null;
  private digPos: { x: number; y: number } | null = null;
  /** Mark the treasure-map dig site with a red X + a rising crimson beacon (or clear it). */
  setDigMarker(pos: { x: number; y: number } | null): void {
    if (!pos) { if (this.digGroup) { this.scene.remove(this.digGroup); disposeGroup(this.digGroup); this.digGroup = null; } this.digPos = null; return; }
    if (this.digGroup && this.digPos && this.digPos.x === pos.x && this.digPos.y === pos.y) return;
    if (this.digGroup) { this.scene.remove(this.digGroup); disposeGroup(this.digGroup); }
    const g = new THREE.Group();
    const mat = new THREE.MeshBasicMaterial({ color: 0xe23b30, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false });
    for (const rot of [Math.PI / 4, -Math.PI / 4]) { const bar = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 9), mat); bar.rotation.x = -Math.PI / 2; bar.rotation.z = rot; bar.position.y = 2.6; g.add(bar); }
    const bMat = new THREE.MeshBasicMaterial({ map: this.ensureBeaconTex(), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, color: 0xe64a3a });
    for (const rot of [0, Math.PI / 2]) { const plane = new THREE.Mesh(new THREE.PlaneGeometry(5, 48), bMat); plane.position.set(0, 22, 0); plane.rotation.y = rot; plane.renderOrder = 9; g.add(plane); }
    g.position.set(pos.x, 0, pos.y);
    this.trackOwned(g, g);
    this.scene.add(g);
    this.digGroup = g; this.digPos = { x: pos.x, y: pos.y };
  }

  /* ------------------------------ Eye of the Storm (3.8) ------------------------------ */

  /** Show/hide the Eye of the Storm (built + animated in storm.ts): the calm pool with its boiling foam rim,
   *  the towering eyewall curtain around it, and the soft shaft of daylight down the middle. The pre-v2 build
   *  was one open-ended additive cylinder — from the sea it read as a giant hollow CONE hanging in the sky
   *  (the owner's report), which is exactly what the rebuild removes. */
  setEye(pos: { x: number; y: number } | null): void { this.stormFx.setEye(pos); }

  /* ------------------------------ the White Whale (3.5) ------------------------------ */

  /** Update the White Whale marker: build/keep the mesh and hand the marker to the body, or start it leaving.
   *
   *  SELF-HEAL: the sea-world GLBs are DEFERRED to the first sea frame (cold-load diet), and a whale marker can
   *  land before `loadWhale` resolves — a captain joining mid-hunt always does. v1 built the procedural
   *  stand-in in that window and, because creation was guarded by `if (!this.whaleMesh)`, never upgraded it:
   *  the legendary boss stayed a featureless white blob for the whole hunt. The mesh now records WHICH tier it
   *  was built from and is rebuilt in place the moment a better one arrives (the body/pose survives). */
  setWhale(w: { x: number; y: number; heading: number; surfaced: boolean } | null): void {
    if (!w) { this.whaleBody?.leave(); this.whaleTarget = null; return; }
    const tier = this.whaleTemplate ? 2 : this.propTemplates.has('whale') ? 1 : 0;
    if (!this.whaleMesh || (tier > this.whaleMeshTier && this.whaleBody?.phase !== 'dying')) {
      if (this.whaleMesh) { this.whaleRoot.remove(this.whaleMesh); disposeGroup(this.whaleMesh); }
      const g = new THREE.Group();
      // the model's own forward axis is baked into an inner group, so the OUTER group can use the same
      // (roll, -yaw, pitch) convention every other hull in the scene uses.
      const inner = new THREE.Group(); inner.rotation.y = tier === 0 ? 0 : WHALE_MODEL_YAW; g.add(inner);
      // drop the model so the GROUP's origin sits at the animal's core, not at its belly: pitch and roll then
      // pivot through the body (a belly-up carcass stays at the waterline instead of dropping out of sight).
      inner.position.y = -this.whaleBodyH * WHALE_ART.ROLL_PIVOT;
      this.whaleMixer = null;
      if (tier === 2) {
        // SkeletonUtils.clone (NOT Object3D.clone) — a plain clone leaves the cloned SkinnedMesh bound to
        // the TEMPLATE's skeleton, so the mixer would drive dead bones and the whale wouldn't deform. This
        // clone rebinds a fresh skeleton while still SHARING geometry/materials (mark shared so dispose skips them).
        const m = skeletonClone(this.whaleTemplate!); m.userData.shared = true; inner.add(m);
        const swim = this.whaleClips.find((c) => c.name === 'swim') ?? this.whaleClips[0];
        if (swim) { this.whaleMixer = new THREE.AnimationMixer(m); this.whaleMixer.clipAction(swim).play(); }
      } else if (tier === 1) {
        const tpl = this.propTemplates.get('whale')!;
        const m = tpl.clone(true); m.userData.shared = true; inner.add(m);
        // the animated GLB never arrived, so its measurements never happened — take them off the static one
        // (same normalization: footprint 26 m, base at y=0), or the draft/pivot would use the seed defaults.
        const bb = new THREE.Box3().setFromObject(tpl), sz = bb.getSize(new THREE.Vector3());
        this.whaleBodyH = sz.y || this.whaleBodyH; this.whaleBodyL = Math.max(sz.x, sz.z) || this.whaleBodyL;
        inner.position.y = -this.whaleBodyH * WHALE_ART.ROLL_PIVOT;
      } else { // procedural stand-in (GLB not in yet): a pale humped body + fluke, nose toward +X
        const body = new THREE.Mesh(new THREE.SphereGeometry(8, 16, 12), new THREE.MeshStandardMaterial({ color: 0xdfe6ea, roughness: 0.7 }));
        body.scale.set(1.7, 0.7, 0.85); body.position.y = 5.6; inner.add(body);
        const fluke = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.5, 7), body.material); fluke.position.set(-13, 5.6, 0); inner.add(fluke);
      }
      this.trackOwned(g, g);
      this.whaleRoot.add(g);
      this.whaleMesh = g;
      this.whaleMeshTier = tier;
      this.whaleBody ??= new WhaleBody(this.whaleBodyH, this.whaleBodyL);
      this.whaleBody.setBody(tier === 0 ? 11.2 : this.whaleBodyH, tier === 0 ? 27.2 : this.whaleBodyL);
    }
    this.whaleTarget = w;
    this.whaleBody!.sync(w);
  }

  /** The whale has been FELLED (EV_WHALE state DEAD): play the client-only death beat (roll belly-up, drift,
   *  sink with foam) instead of the mesh simply vanishing the instant the marker stops. */
  whaleFelled(): void { this.whaleBody?.kill(); }

  /** Dev/E2E render-proof hook (`__cc.renderer.whaleDebug()`): where the whale is DRAWN vs. where the sea
   *  surface actually is under it. `clear` > 0 means the model's belly is ABOVE the water — i.e. it flies. */
  whaleDebug(t: number = this.lastRenderT): { x: number; y: number; z: number; water: number; clear: number; top: number; depth: number; phase: string; tier: number; yaw: number; pitch: number; roll: number; surf: number; surfaced: boolean } | null {
    const g = this.whaleMesh, b = this.whaleBody; if (!g || !b) return null;
    const water = oceanHeightAt(g.position.x, g.position.z, t, this.ampAt(g.position.x, g.position.z));
    return {
      x: +g.position.x.toFixed(2), y: +g.position.y.toFixed(2), z: +g.position.z.toFixed(2),
      water: +water.toFixed(2), clear: +(g.position.y - water).toFixed(2), top: +this.whaleBodyH.toFixed(2),
      depth: +b.depth.toFixed(2), phase: b.phase, tier: this.whaleMeshTier, yaw: +b.yaw.toFixed(3),
      pitch: +b.pitch.toFixed(3), roll: +b.roll.toFixed(3), surf: +b.surfacing.toFixed(2), surfaced: !!this.whaleTarget?.surfaced,
    };
  }

  /** Advance the whale: step the body against the SAME ocean the sea mesh draws, place the mesh, drive the
   *  swim clip's tempo, lay its wake and cash in whatever beats (breach / fluke-up / blow / death) it asked for. */
  private updateWhale(t: number, dt: number): void {
    const g = this.whaleMesh, b = this.whaleBody; if (!g || !b) return;
    const water = (x: number, z: number): number => oceanHeightAt(x, z, t, this.ampAt(x, z));
    b.step(dt, water);
    if (b.finished) { // the leave/death beat played out — let it go
      this.whaleRoot.remove(g); disposeGroup(g);
      this.whaleMesh = null; this.whaleMixer = null; this.whaleBody = null; this.whaleTarget = null;
      this.clearWhaleWake();
      return;
    }
    const oy = water(b.x, b.z);
    g.position.set(b.x, oy - b.depth + this.whaleBodyH * WHALE_ART.ROLL_PIVOT, b.z); // +pivot: see inner.position.y
    g.rotation.set(b.roll, -b.yaw, b.pitch, 'YXZ'); // same convention as every hull (see syncShips)
    // swim tempo: a surfaced/rising whale beats its flukes hard, a resting one idles. (v1 measured the mesh's
    // own frame-to-frame movement, which BLINKS to 0 on frames between marker updates — a strobing tail.)
    const drive = b.phase === 'dying' ? 0 : b.phase === 'sounding' || b.phase === 'rising' ? 1.5 : 1;
    if (this.whaleMixer) {
      this.whaleSpeed += (drive - this.whaleSpeed) * Math.min(1, dt * 2.2);
      this.whaleMixer.timeScale = clamp(0.55 + this.whaleSpeed * 0.55, 0.2, 1.7);
      this.whaleMixer.update(dt);
    }
    this.updateWhaleWake(b, t);
    for (const f of b.fx) this.whaleFx(f, water(f.x, f.z));
    b.fx.length = 0;
  }

  /** Draw one whale beat, on the two pooled primitives the scene already had to grow for this: soft MIST
   *  sprites (spray hanging in the air) and flat FOAM discs (water lying on the sea). No lights, no new
   *  systems. Scale discipline learned the hard way: the old fx spheres are opaque, so a big `size` reads as
   *  a white balloon parked on the animal — spray is many small, short-lived, widely varied puffs thrown
   *  CLEAR of the silhouette, and everything that should lie on the water actually lies on it. */
  private whaleFx(f: WhaleFx, wy: number): void {
    const R = Math.random;
    /** A ring of thrown droplets around (f.x, f.z) — the sheet of water a body shoulders aside. */
    const spray = (n: number, r0: number, r1: number, up: number, out: number, size: number, life: number): void => {
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + R() * 0.5, rr = r0 + R() * (r1 - r0);
        this.spawnMist(f.x + Math.cos(a) * rr, wy + 0.4 + R() * 0.9, f.z + Math.sin(a) * rr,
          0xffffff, size * (0.6 + R() * 0.8), life * (0.7 + R() * 0.6),
          Math.cos(a) * out * (0.5 + R()), up * (0.5 + R()), Math.sin(a) * out * (0.5 + R()), 0.8, false);
      }
    };
    /** Foam lying FLAT ON the water where the body cut it — a disc that spreads and fades, riding the swell.
     *  (A sprite can't do this: it always faces the camera, so it reads as haze in the air, not as foam.) */
    const foam = (n: number, r0: number, r1: number, size: number, life: number, alpha = 0.45): void => {
      for (let i = 0; i < n; i++) {
        const a = R() * Math.PI * 2, rr = r0 + R() * (r1 - r0);
        this.spawnFoamPatch(f.x + Math.cos(a) * rr, f.z + Math.sin(a) * rr, size * (0.7 + R() * 0.7), life, alpha);
      }
    };
    switch (f.kind) {
      case 'breach': { // the back bursts through: water sheets off the whole flank, not a puff at the nose
        const p = 0.7 + f.power * 0.6;
        const yaw = this.whaleBody?.yaw ?? 0, cx = Math.cos(yaw), cz = Math.sin(yaw);
        for (let i = 0; i < 18; i++) {
          const along = (R() - 0.75) * this.whaleBodyL * 0.75;   // biased toward the head, which broke first
          // thrown CLEAR of the flank (the animal is ~10 m in the beam): spray that lands on the silhouette
          // reads as measles, not as water. Sizes vary widely so it never looks like a row of cotton balls.
          const side = (R() < 0.5 ? -1 : 1) * (5 + R() * 4.5);
          const px = f.x + cx * along - cz * side, pz = f.z + cz * along + cx * side;
          this.spawnMist(px, wy + 0.2 + R() * 0.8, pz, 0xffffff, 0.7 + R() * R() * 3.4, 0.8 + R() * 0.7,
            -cz * side * 0.55, (4 + R() * 6) * p, cx * side * 0.55, 0.45, false);
        }
        for (let i = 0; i < 8; i++) {
          const along = (R() - 0.6) * this.whaleBodyL * 0.7;
          this.spawnFoamPatch(f.x + cx * along, f.z + cz * along, 4 + R() * 4, 1.3, 0.5);
        }
        break;
      }
      case 'settle': // it eases onto its waterline — a low foam collar that spreads and dies
        foam(6, 3, 8, 4.2, 1.5, 0.34);
        break;
      case 'fluke': // the flukes swing clear and the sea SHEETS OFF them — the water falls, it doesn't fly
        for (let i = 0; i < 14; i++) {
          const a = R() * Math.PI * 2, rr = R() * 3;
          this.spawnMist(f.x + Math.cos(a) * rr, wy + 2.2 + R() * 3.4, f.z + Math.sin(a) * rr,
            0xffffff, 1.1 + R() * 1.7, 0.7 + R() * 0.5, Math.cos(a) * 0.8, -1 - R() * 2, Math.sin(a) * 0.8, 0.7, false);
        }
        foam(5, 0.5, 4, 3.6, 1.1, 0.42);
        break;
      case 'slip': // the body slides under and the hole it left closes over
        foam(8, 0, 6, 5.0, 1.5, 0.38);
        break;
      case 'blow': { // THE SPOUT: a narrow column off the blowhole that bushes out and leans away downwind
        const wd = this.windDir, ws = 0.5 + this.windStrength * 1.6;
        const dx = Math.cos(wd) * ws, dz = Math.sin(wd) * ws;
        for (let i = 0; i < 16; i++) {
          const k = i / 15;                                   // 0 = the base of the column, 1 = its bushy top
          this.spawnMist(f.x + (R() - 0.5) * (0.6 + k * 3.4), wy + 0.9 + k * 3.4, f.z + (R() - 0.5) * (0.6 + k * 3.4),
            0xffffff, 2.4 + k * 4.0, 1.5 + k * 1.2, dx * (0.3 + k * 1.6), 9 - k * 5.5, dz * (0.3 + k * 1.6), 0.7 - k * 0.34, true);
        }
        foam(3, 0, 2.2, 2.6, 0.8, 0.4); // the wash round the blowhole
        break;
      }
      case 'death': // the kill: a heavy wash of foam, then the carcass rolls belly-up
        spray(20, 1, 8, 5, 5, 2.4, 1.3);
        foam(10, 2, 9, 5.5, 1.6, 0.5);
        break;
      case 'bubble': // the carcass going down
        this.spawnMist(f.x + (R() - 0.5) * 9, wy + 0.2, f.z + (R() - 0.5) * 9, 0xdceff6, 1.8 + R() * 2 * f.power, 1.3, 0, 0.9, 0, 0.32, true);
        break;
    }
  }

  /** The whale's own wake — laid into the SAME merged WakeSystem the ships use, so a surfaced whale costs
   *  ZERO extra draw calls (it shares the one), and nothing at all the moment it sounds. */
  private updateWhaleWake(b: WhaleBody, t: number): void {
    if (b.surfacing < 0.15 || b.phase === 'dying') { this.clearWhaleWake(); return; }
    const cam = this.camera.position;
    if ((b.x - cam.x) ** 2 + (b.z - cam.z) ** 2 > WAKE_CULL_D2) { this.clearWhaleWake(); return; }
    const c = Math.cos(b.yaw), s = Math.sin(b.yaw), half = this.whaleBodyL * 0.42;
    const sx = b.x - c * half * 0.9, sz = b.z - s * half * 0.9; // laid off the flukes
    // a whale pushes a softer, rounder wake than a hull under sail (wider stem, dimmer arms)
    this.whaleWakeLive = this.wakeFx.lay(WHALE_WAKE_ID, sx, sz, 0.6 * b.surfacing, this.whaleBodyL * 0.19, t);
  }

  /** A disc of foam lying flat on the sea (shared foamQuad + foam glow texture), spreading and fading while it
   *  rides the swell. Pooled and hard-capped, so a violent breach can never open more than FOAM_PATCH_MAX
   *  extra draw calls. Whale-owned for now; the ship wakes have their own persistent billboards. */
  private spawnFoamPatch(x: number, z: number, size: number, life: number, alpha: number): void {
    if (this.foamPatches.length >= FOAM_PATCH_MAX) return;
    const mesh = this.foamPatchPool.pop() ?? (() => {
      const m = new THREE.Mesh(this.foamQuad, new THREE.MeshBasicMaterial({ map: this.foamTex!, transparent: true, opacity: 0, depthWrite: false, fog: false }));
      m.renderOrder = 2; this.wakesRoot.add(m); return m;
    })();
    mesh.visible = true;
    mesh.position.set(x, 0, z);
    mesh.rotation.y = Math.random() * Math.PI;
    this.foamPatches.push({ mesh, life, maxLife: life, size, a0: alpha });
  }

  /** Age the foam discs: spread, fade, and stay glued to the (displaced) wave sheet under them. */
  private stepFoamPatches(t: number, dt: number): void {
    for (let i = this.foamPatches.length - 1; i >= 0; i--) {
      const p = this.foamPatches[i]!;
      p.life -= dt;
      const k = 1 - p.life / p.maxLife;
      const s = oceanSampleInto(this._ws0, p.mesh.position.x, p.mesh.position.z, t, this.ampAt(p.mesh.position.x, p.mesh.position.z));
      p.mesh.position.y = s.height + WAKE_H;
      const sz = p.size * (0.55 + k * 1.15);
      p.mesh.scale.set(sz, 1, sz);
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, p.a0 * (1 - k) * (1 - k));
      if (p.life <= 0) {
        p.mesh.visible = false;
        this.foamPatchPool.push(p.mesh);
        this.foamPatches[i] = this.foamPatches[this.foamPatches.length - 1]!;
        this.foamPatches.pop();
      }
    }
  }

  private clearWhaleWake(): void {
    if (!this.whaleWakeLive) return;
    this.whaleWakeLive = false;
    this.wakeFx.drop(WHALE_WAKE_ID);   // frees the slot; the merged mesh keeps drawing everyone else
  }

  /* ------------------------------ the Kraken (deep-water spectacle, v1) ------------------------------ */

  /** Feed the Kraken its deterministic schedule state (main.ts passes `krakenStateAt(Date.now())` every sea
   *  frame). Cheap: just stashes it — the actual build/cull/animation happens in {@link updateKraken}, which
   *  runs in the render loop where the camera position (for distance culling) is known.
   *
   *  v2 SEAM: when the Kraken becomes a real server boss, this same setter takes the wire-driven marker
   *  instead (position/phase from an EV_KRAKEN like EV_WHALE); the presentation below is unchanged. */
  setKraken(state: KrakenState | null): void {
    this.krakenState = state && state.active ? state : null;
  }

  private buildKrakenMesh(): void {
    const g = new THREE.Group();
    this.krakenMixer = null; this.krakenIdle = null; this.krakenSink = null;
    const anim = this.krakenTemplate;
    if (anim) {
      // SkeletonUtils.clone rebinds a fresh skeleton while sharing geometry/materials (mark shared → dispose skips them).
      const m = skeletonClone(anim); m.userData.shared = true; g.add(m);
      this.trackOwned(g, g);
      this.krakenMixer = new THREE.AnimationMixer(m);
      const idle = this.krakenClips.find((c) => c.name === 'idle') ?? this.krakenClips[0];
      const sink = this.krakenClips.find((c) => c.name === 'sink');
      if (idle) { this.krakenIdle = this.krakenMixer.clipAction(idle); this.krakenIdle.play(); }
      if (sink) {
        this.krakenSink = this.krakenMixer.clipAction(sink);
        this.krakenSink.play(); this.krakenSink.paused = true; // we drive its .time from `rise` (also = emerge-reversed)
        this.krakenSinkDur = sink.duration;
      }
    } else { // procedural fallback: a dark bulbous mantle + a splay of tapered tentacles (no rig, static)
      const skin = new THREE.MeshStandardMaterial({ color: 0x241826, roughness: 0.72, emissive: 0x0a2a12, emissiveIntensity: 0.5 });
      const mantle = new THREE.Mesh(new THREE.SphereGeometry(11, 20, 16), skin); mantle.scale.set(1, 1.25, 1); mantle.position.y = 14; g.add(mantle);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const arm = new THREE.Mesh(new THREE.ConeGeometry(2.6, 24, 8), skin);
        arm.position.set(Math.cos(a) * 9, 5, Math.sin(a) * 9);
        arm.rotation.set(Math.PI * 0.62, 0, -a); g.add(arm);
      }
      this.trackOwned(g, g);
    }
    this.krakenRoot.add(g);
    this.krakenMesh = g;
    this.krakenFoamAcc = 0;
  }

  private disposeKraken(): void {
    if (this.krakenMesh) { this.krakenRoot.remove(this.krakenMesh); disposeGroup(this.krakenMesh); this.krakenMesh = null; }
    this.krakenMixer = null; this.krakenIdle = null; this.krakenSink = null;
  }

  /** Build/cull + drive the Kraken. It is FIXED in place (presence, not locomotion): position is the schedule
   *  spot, body Y rises from `−DEEP_M` (submerged, culled) to the swell over the emerge/sink window, tentacles
   *  undulate (idle clip) and gather up as it sinks (sink clip time = (1−rise)·dur). Hysteresis on the cull
   *  radius avoids build/dispose thrash at the boundary. */
  private updateKraken(t: number, dt: number): void {
    const st = this.krakenState;
    const cam = this.camera.position;
    // cull by distance (with a little hysteresis once built) — and never build while submerged/absent
    const cullR = KRAKEN.CULL_D * (this.krakenMesh ? 1.15 : 1.0);
    const near = !!st && ((cam.x - st.x) ** 2 + (cam.z - st.y) ** 2) < cullR * cullR;
    if (!near) { if (this.krakenMesh) this.disposeKraken(); return; }
    if (!this.krakenMesh) this.buildKrakenMesh();
    const g = this.krakenMesh!;
    const s = st!.rise * st!.rise * (3 - 2 * st!.rise); // smoothstep on emergence
    g.position.x = st!.x; g.position.z = st!.y;
    g.rotation.y = st!.yaw;
    const oy = oceanHeightAt(st!.x, st!.y, t, this.ampAt(st!.x, st!.y));
    g.position.y = oy - KRAKEN.DEEP_M * (1 - s);        // deep under at rise=0 → base at the swell at rise=1
    // ride the swell with a slow, heavy list — scaled by emergence so nothing wobbles while it's submerged
    g.rotation.z = Math.sin(t * 0.33) * 0.06 * s;
    g.rotation.x = Math.sin(t * 0.47 + 0.6) * 0.04 * s;
    // clip blend: idle undulation always alive; the sink clip gathers the tentacles UP as rise→0 (so the arm
    // tips are the last thing under). Driving sink.time from `rise` makes emerge the exact reverse for free.
    if (this.krakenSink) {
      this.krakenSink.time = (1 - st!.rise) * this.krakenSinkDur;
      this.krakenSink.setEffectiveWeight(1 - st!.rise);
    }
    if (this.krakenIdle) this.krakenIdle.setEffectiveWeight(Math.max(0.2, st!.rise));
    this.krakenMixer?.update(dt);
    // churned foam at the waterline while it's crossing the surface (emerging or sinking)
    if (st!.phase === KrakenPhase.EMERGING || st!.phase === KrakenPhase.SINKING) {
      this.krakenFoamAcc += dt;
      if (this.krakenFoamAcc >= 0.12) {
        this.krakenFoamAcc = 0;
        const r = KRAKEN.SPAN_M * 0.42;
        for (let i = 0; i < 5; i++) {
          const a = Math.random() * Math.PI * 2, rr = r * (0.55 + Math.random() * 0.5);
          this.spawnFx(st!.x + Math.cos(a) * rr, oy + 0.6, st!.y + Math.sin(a) * rr, 0xdfeaf0, 4 + Math.random() * 3, 0.7, 0, 1.5 + Math.random() * 2, 0, true);
        }
      }
    }
  }

  /* ------------------------------ fishing shoals ------------------------------ */

  /** Build the fishing shoals once (static per session): one instanced ocean-riding water sheet
   *  (soft glow pool + expanding ripple rings, shader-animated — zero CPU per frame), one instanced
   *  school of fish silhouettes (+2 leaping-fish slots), and the circling gulls. 4 draw calls for the
   *  whole sea. Called from main when the world first lands. */
  setShoals(shoals: { x: number; y: number }[]): void {
    this.lastShoals = shoals; // remembered so restoreSeaAssets() can rebuild the shoals it disposed on the way ashore
    for (const c of this.shoalsRoot.children.slice()) this.shoalsRoot.remove(c);
    this.shoalsRoot.clear();
    this.shoalWater?.geometry.dispose(); this.shoalWaterMat?.dispose(); this.shoalWater?.dispose();
    this.shoalFish?.geometry.dispose(); this.shoalFishMat?.dispose(); this.shoalFish?.dispose();
    this.gullMesh?.geometry.dispose(); this.gullMat?.dispose(); this.gullMesh?.dispose();
    this.shoalWater = null; this.shoalWaterMat = null; this.shoalFish = null; this.shoalFishMat = null;
    this.gullMesh = null; this.gullMat = null;
    this.gullData = []; this.shoalFishData = []; this.shoalCenters = [];
    if (!shoals.length) return;
    const d = new THREE.Object3D();
    // --- the LIVING WATER: one instanced sheet displaced by the SAME ocean GLSL the sea uses, so the
    // glow pool + ripple rings ride the swell instead of lying on it like a decal ---
    const R = FISHING.RING_R * 1.5; // the glow feathers a little past the cast ring — no hard rim
    const wgeo = new THREE.PlaneGeometry(2, 2, 14, 14); wgeo.rotateX(-Math.PI / 2);
    const seeds = new Float32Array(shoals.length);
    for (let i = 0; i < shoals.length; i++) seeds[i] = (i * 0.6180339887) % 1; // golden-ratio stagger, no sync'd rings
    wgeo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    this.shoalWaterMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 }, uAmp: { value: 1 }, uNight: { value: 0 },
        uDay: { value: new THREE.Color(0x9adfe8) }, uBio: { value: new THREE.Color(0x2df0c2) },
        // the generated GLSL below references these; leaving them undeclared would read uChopDir as
        // (0,0), collapsing the chop band into a plane pumping in unison (see oceanEnvUniforms)
        ...oceanEnvUniforms(),
      },
      vertexShader: buildOceanGLSL() + /* glsl */ `
        attribute float aSeed;
        varying vec2 vUv; varying float vSeed;
        uniform float uTime, uAmp;
        void main() {
          vUv = uv; vSeed = aSeed;
          vec4 wp = modelMatrix * instanceMatrix * vec4(position, 1.0);
          vec3 os = oceanSample(wp.xz, uTime, uAmp);
          wp.x += os.x; wp.z += os.z; wp.y = os.y + 0.42;  // sit just above the displaced sheet
          gl_Position = projectionMatrix * viewMatrix * wp;
        }`,
      fragmentShader: /* glsl */ `
        varying vec2 vUv; varying float vSeed;
        uniform float uTime, uNight;
        uniform vec3 uDay, uBio;
        float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        void main() {
          float d = length(vUv - 0.5) * 2.0;
          // a soft pool of stirred water: feathered edge, gently breathing — never a hard-rimmed disc
          float pool = pow(smoothstep(1.0, 0.06, d), 1.8) * (0.30 + 0.08 * sin(uTime * 0.8 + vSeed * 6.2831853));
          // sardine glints: tiny round lights winking across the stirred heart (scales catching the light)
          vec2 gp = vUv * 42.0;
          float g = hash(floor(gp) + floor(uTime * 4.0) * 0.173 + vSeed);
          float glint = smoothstep(0.955, 1.0, g) * smoothstep(0.42, 0.0, length(fract(gp) - 0.5)) * pow(smoothstep(0.72, 0.04, d), 2.0);
          // two staggered ripple fronts spreading outward from the school
          float rings = 0.0;
          for (int i = 0; i < 2; i++) {
            float k = fract(uTime * 0.16 + vSeed + float(i) * 0.5);
            float r = mix(0.10, 0.92, k);
            float w = 0.030 + 0.045 * k;               // the front widens as it spreads
            rings += smoothstep(w, 0.0, abs(d - r)) * (1.0 - k) * (1.0 - k) * 0.55;
          }
          float alpha = (pool * 0.55 + rings + glint * 0.9) * smoothstep(1.0, 0.86, d);
          alpha *= 0.8 + 0.5 * uNight;                 // subtle by day, bioluminescent after dark
          vec3 col = mix(uDay, uBio, uNight);
          gl_FragColor = vec4(col * alpha, alpha);
        }`,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.shoalWater = new THREE.InstancedMesh(wgeo, this.shoalWaterMat, shoals.length);
    for (let i = 0; i < shoals.length; i++) {
      const s = shoals[i]!;
      this.shoalCenters.push({ x: s.x, z: s.y });
      d.position.set(s.x, 0, s.y); d.rotation.set(0, (i * 2.399963) % (Math.PI * 2), 0); d.scale.setScalar(R); d.updateMatrix();
      this.shoalWater.setMatrixAt(i, d.matrix);
    }
    this.shoalWater.instanceMatrix.needsUpdate = true;
    this.shoalWater.frustumCulled = false; this.shoalWater.renderOrder = 2;
    this.shoalsRoot.add(this.shoalWater);
    // --- the SCHOOL: dark fish silhouettes serpentining just under the surface (one instanced mesh,
    // matrices refreshed only within sight — far shoals freeze inside the fog) + 2 leaping-fish slots ---
    const FISH_N = this.shoalFishN, JUMPERS = this.shoalJumpers.length;
    // material colour = the global day/night tint; per-INSTANCE colours give the school its texture.
    // SIZE VARIATION (owner's call — "tem que ter uns peixe maior"): most are small sardines, a couple
    // run medium, and each shoal has 1–2 BIG dark silhouettes cruising a wider, slower, heavier circuit —
    // same mesh, per-instance scale, still one draw call.
    this.shoalFishMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.62, depthWrite: false, side: THREE.DoubleSide, fog: true });
    this.shoalFish = new THREE.InstancedMesh(this.buildFishGeo(), this.shoalFishMat, shoals.length * FISH_N + JUMPERS);
    const slate = new THREE.Color(0x2c5a6a), silver = new THREE.Color(0xa8ccd8), bulk = new THREE.Color(0x152f3b);
    for (let i = 0; i < shoals.length; i++) {
      for (let f = 0; f < FISH_N; f++) {
        const big = f === 1 || (i % 3 === 0 && f === 5); // 1 per shoal, 2 in every third
        const med = !big && (f === 2 || f === 6);
        const v = ((f * 37 + i * 11) % 10) / 10; // deterministic per-fish jitter
        this.shoalFishData.push({
          a0: (f / FISH_N) * Math.PI * 2 + i * 1.7,
          r0: (big ? 7.5 : 4.5) + (f % 4) * 3.4,
          sp: (i % 2 ? 1 : -1) * (0.30 + (f % 3) * 0.12) * (big ? 0.45 : med ? 0.8 : 1), // the big one cruises, heavy and slow
          ph: f * 1.93 + i * 0.71,
          sc: big ? 2.5 + v * 0.9 : med ? 1.55 + v * 0.4 : 1.0 + v * 0.45,
          wsp: big ? 1.5 : 3.2,                        // a slow, weighty tail-swing for the big one
          dy: big ? 0.09 : 0.16 + v * 0.05,            // it rides lower in the water
        });
        this.shoalFish.setColorAt(i * FISH_N + f, big ? bulk : med ? slate : (f % 4 === 0 ? silver : slate));
      }
    }
    this.shoalFish.setColorAt(shoals.length * FISH_N + 0, silver); // small jumper flashes silver
    this.shoalFish.setColorAt(shoals.length * FISH_N + 1, bulk);   // the BIG breach is a dark bulk
    if (this.shoalFish.instanceColor) this.shoalFish.instanceColor.needsUpdate = true;
    d.rotation.set(0, 0, 0); d.position.set(0, -30, 0); d.scale.setScalar(0.0001); d.updateMatrix();
    for (let i = 0; i < shoals.length * FISH_N + JUMPERS; i++) this.shoalFish.setMatrixAt(i, d.matrix);
    this.shoalFish.instanceMatrix.needsUpdate = true;
    this.shoalFish.frustumCulled = false; this.shoalFish.renderOrder = 3;
    this.shoalsRoot.add(this.shoalFish);
    // --- the GULLS: the daylight long-range tell (they go to roost at night). Same gull mesh + vertex-shader
    // wing beat as the sky flock (birds.ts), so a shoal's birds and the ones over your masthead are the same
    // species — one instanced draw call for every shoal in the sea. ---
    const GULLS = 4;
    const N = shoals.length * GULLS;
    this.gullMat = buildGullMaterial();
    (this.gullMat.uniforms.uTop!.value as THREE.Color).setHex(0xf6f9ff);
    (this.gullMat.uniforms.uUnder!.value as THREE.Color).setHex(0x7d909f);
    const gullGeo = buildGullGeometry(2.8);
    const flap = new Float32Array(N * 4);
    const gulls = new THREE.InstancedMesh(gullGeo, this.gullMat, N);
    for (let i = 0; i < shoals.length; i++) {
      const s = shoals[i]!;
      for (let g = 0; g < GULLS; g++) {
        const k = i * GULLS + g;
        this.gullData.push({ cx: s.x, cz: s.y, a: (g / GULLS) * Math.PI * 2 + i, r: 8 + (g % 3) * 4, h: 13 + (g % 2) * 5, sp: 0.45 + (g % 3) * 0.16, ph: g * 1.7 });
        flap[k * 4] = (k * 2.399) % 6.283;         // beat phase
        flap[k * 4 + 1] = 6.4 + (g % 3) * 0.9;     // beat rate
        flap[k * 4 + 2] = g === 1 ? 1 : 0;         // one bird per shoal rides the updraught on held wings
        flap[k * 4 + 3] = ((k * 29) % 100) / 100 * 0.4;
      }
    }
    gullGeo.setAttribute('aFlap', new THREE.InstancedBufferAttribute(flap, 4));
    gulls.frustumCulled = false;
    this.shoalsRoot.add(gulls);
    this.gullMesh = gulls;
  }

  /** A small top-down fish silhouette (nose +X, ~2 m before per-fish scale): body + forked tail, 4 tris. */
  private buildFishGeo(): THREE.BufferGeometry {
    const v = new Float32Array([
      1.0, 0, 0, 0.22, 0, 0.34, 0.22, 0, -0.34,      // nose wedge
      0.22, 0, -0.34, 0.22, 0, 0.34, -0.55, 0, 0,    // body taper
      -0.5, 0, 0, -1.0, 0, 0.30, -0.74, 0, 0.03,     // tail fork (upper lobe)
      -0.5, 0, 0, -0.74, 0, -0.03, -1.0, 0, -0.30,   // tail fork (lower lobe)
    ]);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(v, 3));
    return g;
  }

  /** Animate the shoals (allocation-free): shader uniforms for the water sheet, serpentine fish
   *  matrices for schools within sight, the odd leaping fish, and the circling gulls (who go to
   *  roost at night while the water's biolum glow takes over as the tell). */
  private updateShoals(t: number, cx: number, cz: number): void {
    if (!this.shoalCenters.length) return;
    const nf = this.nightFactor;
    if (this.shoalWaterMat) {
      this.shoalWaterMat.uniforms.uTime!.value = t;
      this.shoalWaterMat.uniforms.uAmp!.value = this.waveScale;
      this.shoalWaterMat.uniforms.uNight!.value = nf;
    }
    // gulls: circle by day, sleep at night. The wing beat is in the shader now (uTime); the matrix only
    // carries the circling course + the BANK into it — a bird on a tight turn holds a steady roll.
    if (this.gullMesh && this.gullMat) {
      this.gullMat.uniforms.uOpacity!.value = 1 - nf * 0.92;
      this.gullMat.uniforms.uTime!.value = t;
      this.gullMesh.visible = nf < 0.97;
      if (this.gullMesh.visible) {
        const d = this.gullDummy;
        for (let i = 0; i < this.gullData.length; i++) {
          const g = this.gullData[i]!;
          const a = g.a + t * g.sp;
          d.position.set(g.cx + Math.cos(a) * g.r, g.h + Math.sin(t * 1.6 + g.ph) * 1.1, g.cz + Math.sin(a) * g.r);
          // heading = the circle's tangent; roll = the bank that circle needs (v²/r, capped) + a slow wobble
          d.rotation.set(0, -(a + Math.PI / 2), -0.55 + Math.sin(t * 0.9 + g.ph) * 0.12, 'YXZ');
          d.scale.setScalar(1); d.updateMatrix();
          this.gullMesh.setMatrixAt(i, d.matrix);
        }
        this.gullMesh.instanceMatrix.needsUpdate = true;
      }
    }
    // fish silhouettes: serpentine around the shoal heart, riding the real ocean height
    const fishMesh = this.shoalFish;
    if (fishMesh && this.shoalFishMat) {
      this.shoalFishMat.color.copy(this.shoalColScratch.lerpColors(this.shoalFishColDay, this.shoalFishColNight, nf * nf));
      this.shoalFishMat.opacity = 0.62 - 0.14 * nf;
      const d = this.gullDummy;
      const N = this.shoalFishN, LOD2 = 620 * 620;
      for (let i = 0; i < this.shoalCenters.length; i++) {
        const c = this.shoalCenters[i]!;
        const ddx = c.x - cx, ddz = c.z - cz;
        if (ddx * ddx + ddz * ddz > LOD2) continue; // hidden in the fog — let the school freeze
        for (let f = 0; f < N; f++) {
          const fd = this.shoalFishData[i * N + f]!;
          const a = fd.a0 + t * fd.sp;
          const r = fd.r0 + Math.sin(t * 0.33 + fd.ph) * 3.0;
          const x = c.x + Math.cos(a) * r, z = c.z + Math.sin(a) * r;
          const wig = Math.sin(t * fd.wsp + fd.ph) * 0.4; // the serpentine tail-swing (slow + heavy on the big one)
          d.position.set(x, oceanHeightAt(x, z, t, this.ampAt(x, z)) + fd.dy + Math.sin(t * 1.4 + fd.ph) * 0.05, z);
          d.rotation.set(0, -(a + (fd.sp > 0 ? Math.PI / 2 : -Math.PI / 2)) - wig, 0);
          d.scale.set(fd.sc, 1, fd.sc); d.updateMatrix();
          fishMesh.setMatrixAt(i * N + f, d.matrix);
        }
      }
      this.stepShoalJumpers(t, cx, cz, fishMesh);
      fishMesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** The occasional fish leaping clear of a nearby shoal — a glint of life you catch from the corner
   *  of your eye. Two reserved instances at the tail of the school mesh (zero extra draw calls):
   *  slot 0 is a quick silver leaper; slot 1 is the rare BIG dark breach (higher, heavier, twice the
   *  splash — the "olha aquilo!" beat). Entry/exit splashes ride the pooled FX (Math.random fine). */
  private stepShoalJumpers(t: number, cx: number, cz: number, mesh: THREE.InstancedMesh): void {
    const base = this.shoalCenters.length * this.shoalFishN;
    const d = this.jumpDummy;
    for (let j = 0; j < this.shoalJumpers.length; j++) {
      const jp = this.shoalJumpers[j]!;
      const big = j === 1;
      const DUR = big ? 1.35 : 1.05, LEN = big ? 9 : 7, H = big ? 3.1 : 2.3, SC = big ? 2.6 : 1.15;
      if (!jp.active) {
        if (t < jp.nextAt) continue;
        let pick = -1, pd = 460 * 460; // only leap where a captain can actually see it
        for (let i = 0; i < this.shoalCenters.length; i++) {
          const c = this.shoalCenters[i]!;
          const dx = c.x - cx, dz = c.z - cz, q = dx * dx + dz * dz;
          if (q < pd) { pd = q; pick = i; }
        }
        if (pick < 0) { jp.nextAt = t + 2.5; continue; }
        const c = this.shoalCenters[pick]!;
        const a = Math.random() * Math.PI * 2, rr = 4 + Math.random() * 10;
        jp.x = c.x + Math.cos(a) * rr; jp.z = c.z + Math.sin(a) * rr;
        const dir = Math.random() * Math.PI * 2;
        jp.dx = Math.cos(dir); jp.dz = Math.sin(dir);
        jp.t0 = t; jp.active = true; jp.splashIn = false;
        this.spawnFx(jp.x, oceanHeightAt(jp.x, jp.z, t, this.ampAt(jp.x, jp.z)) + 0.4, jp.z, 0xdfeef5, big ? 4.2 : 2.4, 0.55); // exit spray
      }
      const k = (t - jp.t0) / DUR;
      if (k >= 1) {
        jp.active = false;
        jp.nextAt = t + (big ? 11 + Math.random() * 15 : 3.5 + Math.random() * 6); // the big breach stays rare
        d.position.set(0, -30, 0); d.rotation.set(0, 0, 0); d.scale.setScalar(0.0001); d.updateMatrix();
        mesh.setMatrixAt(base + j, d.matrix);
        continue;
      }
      const x = jp.x + jp.dx * LEN * k, z = jp.z + jp.dz * LEN * k;
      const oy = oceanHeightAt(x, z, t, this.ampAt(x, z));
      if (!jp.splashIn && k > 0.88) { // re-entry splash just before it slips back under
        jp.splashIn = true;
        this.spawnFx(x, oy + 0.3, z, 0xe8f4fa, big ? 5.5 : 3.0, big ? 0.7 : 0.55);
        this.spawnFx(x, oy + 1.1, z, 0xffffff, big ? 2.6 : 1.5, big ? 0.55 : 0.4, 0, big ? 6 : 4.5, 0, true);
        if (big) this.spawnFx(x, oy + 0.5, z, 0xcfe4ee, 8.5, 0.8); // the heavy crash ring
      }
      // silhouette rolled upright (YZX: roll flat→vertical, pitch along the arc, yaw to the leap line)
      d.position.set(x, oy + Math.sin(Math.PI * k) * H, z);
      d.rotation.set(Math.PI / 2, -Math.atan2(jp.dz, jp.dx), Math.atan2(Math.cos(Math.PI * k) * Math.PI * H / DUR, LEN / DUR));
      d.scale.setScalar(SC); d.updateMatrix();
      mesh.setMatrixAt(base + j, d.matrix);
    }
  }

  /* --------------------------- the local fishing line --------------------------- */

  /** Mirror the local captain's fishing activity into the world (called each frame by main; cheap
   *  state setter — the geometry itself is animated inside render()). act: 0 none, 1 line out, 2 bite.
   *  On the cast edge it aims the throw at the heart of the nearest shoal. Pure presentation. */
  setFishing(act: number, shipX: number, shipZ: number, heading: number, shoalX: number, shoalZ: number): void {
    if (act !== 1 && act !== 2) act = 0;
    this.fishShip.x = shipX; this.fishShip.z = shipZ; this.fishShip.heading = heading;
    if (act >= 1 && this.fishAct === 0) {
      let dx = shoalX - shipX, dz = shoalZ - shipZ;
      const dd = Math.hypot(dx, dz);
      if (dd > 1e-3 && dd < 90) { dx /= dd; dz /= dd; }
      else { dx = Math.cos(heading + Math.PI / 2); dz = Math.sin(heading + Math.PI / 2); } // no shoal? cast off the rail
      const throwD = 9 + Math.min(6, dd * 0.25);
      this.fishCast = { x: shipX + dx * throwD, z: shipZ + dz * throwD, t0: -1, landed: false };
      this.fishNibT0 = -10; this.fishNextNib = 0;
    }
    if (act === 2 && this.fishAct !== 2) this.fishNextThrash = 0;
    this.fishAct = act;
  }

  /** The catch settles (from EV_FISH_CATCH): a splash at the float and — if it's aboard — the fish
   *  arcing to the rail (a golden gleam for a rare haul); an escape darts away as a dark flick. */
  fishingResult(caught: boolean, rare: boolean): void {
    const c = this.fishCast;
    if (c.t0 < 0) return; // no cast was ever shown (e.g. joined mid-activity)
    const t = this.lastRenderT;
    const fy = oceanHeightAt(c.x, c.z, t, this.ampAt(c.x, c.z));
    let dx = this.fishShip.x - c.x, dz = this.fishShip.z - c.z;
    const dd = Math.hypot(dx, dz) || 1;
    if (caught) {
      const T = 0.55, vy = (3.2 + 12 * T * T) / T; // debris gravity is 24 → vy lands the arc on the rail
      this.spawnFx(c.x, fy + 0.3, c.z, rare ? 0x49e6c8 : 0x9fb8c4, 1.2, T, dx / T, vy, dz / T, false); // the fish, hauled aboard
      for (let i = 0; i < 3; i++) this.spawnFx(c.x + (Math.random() - 0.5) * 1.4, fy + 0.35, c.z + (Math.random() - 0.5) * 1.4, 0xeaf6fc, 2.2 + Math.random() * 1.6, 0.5);
      if (rare) this.spawnFx(c.x, fy + 1.2, c.z, 0xffd66e, 3.4, 0.7, 0, 2.5, 0, true); // gold gleam off the scales
    } else {
      dx /= dd; dz /= dd;
      this.spawnFx(c.x, fy + 0.25, c.z, 0xbcd2dc, 2.6, 0.6); // the swirl where it slipped the hook
      this.spawnFx(c.x, fy + 0.35, c.z, 0x123540, 0.9, 0.7, -dx * 7, 1.5, -dz * 7, false); // darting away
    }
  }

  /** Animate the cast arc / floating cork / taut fight each frame (allocation-free; ~2 draw calls
   *  only while a line is out). The float rides the real ocean height; the line is a drooping bezier
   *  that snaps taut and vibrates when the fish takes it. */
  private updateFishingLine(t: number): void {
    if (!this.fishAct) {
      if (this.fishLine) { this.fishLine.visible = false; this.fishBobber!.visible = false; }
      return;
    }
    if (!this.fishLine) {
      const pos = new Float32Array(16 * 3);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
      const line = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xf4ecd8, transparent: true, opacity: 0.85, depthWrite: false }));
      line.frustumCulled = false; line.renderOrder = 4;
      this.fishLinePos = pos; this.fishLine = line; this.scene.add(line);
      const bob = new THREE.Mesh(new THREE.SphereGeometry(0.62, 10, 8), new THREE.MeshBasicMaterial({ map: this.makeBobberTex() }));
      bob.frustumCulled = false; bob.renderOrder = 4;
      this.fishBobber = bob; this.scene.add(bob);
    }
    this.fishLine.visible = true; this.fishBobber!.visible = true;
    const c = this.fishCast;
    if (c.t0 < 0) c.t0 = t;
    const sx = this.fishShip.x, sz = this.fishShip.z;
    let rdx = c.x - sx, rdz = c.z - sz;
    const rd = Math.hypot(rdx, rdz) || 1; rdx /= rd; rdz /= rd;
    const ax = sx + rdx * 2.6, az = sz + rdz * 2.6;               // rod tip on the rail, toward the float
    const ay = oceanHeightAt(sx, sz, t, this.ampAt(sx, sz)) + 4.1;
    const fy = oceanHeightAt(c.x, c.z, t, this.ampAt(c.x, c.z));
    const CAST_S = 0.55;
    const ck = Math.min(1, (t - c.t0) / CAST_S);
    let bx: number, by: number, bz: number, sag: number;
    if (ck < 1) {
      const e = 1 - (1 - ck) * (1 - ck);                           // eased throw
      bx = ax + (c.x - ax) * e; bz = az + (c.z - az) * e;
      by = ay + (fy + 0.12 - ay) * ck + Math.sin(Math.PI * ck) * 3.0; // the arc of the cast
      sag = 0.25;
    } else {
      if (!c.landed) { // touchdown — plip
        c.landed = true;
        this.spawnFx(c.x, fy + 0.25, c.z, 0xdfeef5, 1.8, 0.45);
        this.spawnFx(c.x, fy + 0.7, c.z, 0xffffff, 0.9, 0.35, 0, 3.5, 0, true);
      }
      if (this.fishAct === 1) {
        // waiting: the cork drifts and rocks; the odd NIBBLE dips it (ambience — the real bite is loud)
        bx = c.x + Math.sin(t * 0.4) * 0.5; bz = c.z + Math.cos(t * 0.33) * 0.5;
        if (this.fishNextNib > 0 && t >= this.fishNextNib) {
          this.fishNibT0 = t;
          this.spawnFx(bx, fy + 0.2, bz, 0xbfe4ec, 1.3, 0.5);
          this.fishNextNib = t + 2.0 + Math.random() * 2.8;
        } else if (this.fishNextNib === 0) this.fishNextNib = t + 1.6 + Math.random() * 2.2;
        const dip = t - this.fishNibT0 < 0.4 ? 0.3 * (1 - (t - this.fishNibT0) / 0.4) : 0;
        by = fy + 0.26 - dip;
        this.fishBobber!.rotation.z = Math.sin(t * 2.3) * 0.18;
        sag = 1.7; // slack line
      } else {
        // THE BITE: cork yanked under, water churning, line snapped taut and singing
        bx = c.x + Math.sin(t * 19) * 0.22; bz = c.z + Math.cos(t * 23) * 0.22;
        by = fy - 0.5 + Math.sin(t * 26) * 0.1;
        this.fishBobber!.rotation.z = Math.sin(t * 30) * 0.5;
        if (t >= this.fishNextThrash) {
          this.fishNextThrash = t + 0.26;
          this.spawnFx(bx + (Math.random() - 0.5), fy + 0.3, bz + (Math.random() - 0.5), 0xe6f2f8, 2.0, 0.4);
          this.spawnFx(bx, fy + 0.5, bz, 0x123540, 0.8, 0.5, (Math.random() - 0.5) * 5, 3.5, (Math.random() - 0.5) * 5, false); // a fin flick
        }
        sag = -0.1 + Math.sin(t * 50) * 0.06; // taut + vibrating
      }
    }
    this.fishBobber!.position.set(bx, by, bz);
    // the line: quadratic bezier rod-tip → mid (sagging or taut) → float
    const pos = this.fishLinePos!;
    const mx = (ax + bx) / 2, my = (ay + by) / 2 - sag, mz = (az + bz) / 2;
    for (let i = 0; i < 16; i++) {
      const u = i / 15, iu = 1 - u;
      pos[i * 3] = iu * iu * ax + 2 * u * iu * mx + u * u * bx;
      pos[i * 3 + 1] = iu * iu * ay + 2 * u * iu * my + u * u * by;
      pos[i * 3 + 2] = iu * iu * az + 2 * u * iu * mz + u * u * bz;
    }
    (this.fishLine.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** The classic cork float: crimson cap over a bone band and dark belly (equirect canvas, built once) —
   *  red-first so it reads against both bright foam and deep-blue water. */
  private makeBobberTex(): THREE.CanvasTexture {
    const c = document.createElement('canvas'); c.width = 8; c.height = 32;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#e04326'; ctx.fillRect(0, 0, 8, 16);   // crimson cap (sphere top = v high)
    ctx.fillStyle = '#f2ecdc'; ctx.fillRect(0, 16, 8, 5);   // bone waterline band
    ctx.fillStyle = '#241a12'; ctx.fillRect(0, 21, 8, 11);  // tarred belly
    const tex = new THREE.CanvasTexture(c); tex.needsUpdate = true;
    return tex;
  }

  /** Lazily build the shared vertical beacon gradient texture (a chest lighthouse column). */
  private ensureBeaconTex(): THREE.CanvasTexture {
    if (this.beaconTex) return this.beaconTex;
    const c = document.createElement('canvas'); c.width = 32; c.height = 128;
    const ctx = c.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 128, 0, 0);
    grad.addColorStop(0, 'rgba(255,214,110,0.85)'); grad.addColorStop(0.55, 'rgba(255,196,80,0.30)'); grad.addColorStop(1, 'rgba(255,190,70,0)');
    ctx.fillStyle = grad; ctx.fillRect(0, 0, 32, 128);
    this.beaconTex = new THREE.CanvasTexture(c);
    return this.beaconTex;
  }

  /** Add / move / remove the Cursed Treasure mesh (feature 3). `pos` = world (x,y) or null to clear.
   *  A bigger chest + a tall violet beacon on open water — bobbed on the sea + pulsed in render(). */
  setCursed(pos: { x: number; y: number } | null): void {
    if (!pos) {
      if (this.cursedGroup) { this.scene.remove(this.cursedGroup); disposeGroup(this.cursedGroup); this.cursedGroup = null; }
      this.cursedPos = null; this.cursedBeaconMats.length = 0;
      return;
    }
    if (this.cursedGroup && this.cursedPos && this.cursedPos.x === pos.x && this.cursedPos.y === pos.y) return; // unchanged
    if (this.cursedGroup) { this.scene.remove(this.cursedGroup); disposeGroup(this.cursedGroup); }
    this.cursedBeaconMats.length = 0;
    const g = new THREE.Group();
    // wave 3.4: the dedicated Cursed Treasure GLB; fall back to the plain chest ×2.6, then a procedural box
    const cursedTpl = this.propTemplates.get('cursed_chest') ?? this.propTemplates.get('chest');
    let chest: THREE.Object3D;
    if (cursedTpl) { chest = cursedTpl.clone(true); chest.userData.shared = true; if (!this.propTemplates.get('cursed_chest')) chest.scale.multiplyScalar(2.6); chest.position.y = 0.4; }
    else { chest = new THREE.Mesh(new THREE.BoxGeometry(4, 2.6, 2.6), new THREE.MeshStandardMaterial({ color: 0x2a0f3c, emissive: 0x6a1fb0, emissiveIntensity: 0.6, roughness: 0.5 })); chest.position.y = 1.3; }
    g.add(chest);
    const bMat = new THREE.MeshBasicMaterial({ map: this.ensureBeaconTex(), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, color: 0xb94be6 });
    const bGeo = new THREE.PlaneGeometry(6, 60);
    for (const rot of [0, Math.PI / 2]) {
      const plane = new THREE.Mesh(bGeo, bMat);
      plane.position.set(0, 28, 0); plane.rotation.y = rot; plane.renderOrder = 9;
      g.add(plane); this.trackOwned(g, plane);
    }
    this.cursedBeaconMats.push(bMat);
    this.scene.add(g);
    this.cursedGroup = g; this.cursedPos = { x: pos.x, y: pos.y };
  }

  /** Register a freshly-built (OWNED) subtree's geometry + materials onto the despawn-root `owner`, so
   *  disposeGroup frees exactly them. Skips Sprite geometry (global shared quad) and the shared foamQuad.
   *  NEVER call this on a GLB-template clone — its geometry/materials are shared with the cached template. */
  private trackOwned(owner: THREE.Object3D, root: THREE.Object3D): void {
    const um = (owner.userData.ownMats ??= []) as THREE.Material[];
    const ug = (owner.userData.ownGeom ??= []) as THREE.BufferGeometry[];
    root.traverse((c) => {
      // skip GLB-template-clone subtrees (geometry + materials shared with the cached template)
      for (let p: THREE.Object3D | null = c; p && p !== owner.parent; p = p.parent) if (p.userData.shared) return;
      const isSprite = (c as unknown as { isSprite?: boolean }).isSprite === true;
      const geo = (c as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
      if (geo && !isSprite && geo !== this.foamQuad) ug.push(geo);
      const mat = (c as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (mat) { if (Array.isArray(mat)) um.push(...mat); else um.push(mat); }
    });
  }

  /* ------------------------------- bases -------------------------------- */

  syncBases(bases: WireBase[], yourIslandId: number): void {
    this.lastBases = bases;
    this.lastYourIslandId = yourIslandId;
    const seen = new Set<number>();
    for (const wb of bases) {
      seen.add(wb.islandId);
      let g = this.bases.get(wb.islandId);
      if (!g) { g = this.buildBase(wb, wb.islandId === yourIslandId); this.bases.set(wb.islandId, g); this.basesRoot.add(g); }
      g.position.set(wb.x, 0, wb.y);
      const dome = g.userData.dome as THREE.Mesh | undefined;
      if (dome) dome.visible = wb.shielded;
    }
    for (const [id, g] of this.bases) {
      if (!seen.has(id)) { this.basesRoot.remove(g); disposeGroup(g); this.bases.delete(id); }
    }
  }

  /** Topmost island-surface y at local (lx,lz) via a downward ray — robustly the SAND, not the
   *  palm/rock-inflated bounding box. Falls back when the ray misses the (clone) island mesh. */
  private surfaceYAt(target: THREE.Object3D, lx: number, lz: number, fallback: number, maxY = Infinity): number {
    this._seatRay.set(this._scratch.set(lx, 300, lz), this._seatDown);
    const hits = this._seatRay.intersectObject(target, true);
    // first (highest) hit AT OR BELOW maxY = the deck; a low cap skips the masts/yards/sails above it
    for (const h of hits) if (h.point.y <= maxY) return h.point.y;
    return fallback;
  }

  private buildBase(wb: WireBase, isOwn: boolean): THREE.Group {
    const GRID = BASE.GRID, CELL = BASE.CELL_SIZE;
    const islandR = GRID * CELL * 0.62;
    const g = new THREE.Group();
    const islandTpl = this.propTemplates.get('island');
    let yTop = 7;
    let islObj: THREE.Object3D | undefined;
    if (islandTpl) {
      const isl = islandTpl.clone(true);
      isl.userData.shared = true;       // GLB clone — shares geometry/materials with the template
      g.add(isl);
      isl.updateMatrixWorld(true);      // so the seating raycast sees the cloned island's geometry
      islObj = isl;
      // NOT bbox.max.y — that catches the palm tree / rocks and makes every building float. Sample the
      // sand at the island centre instead, and seat each building on the surface under its own cell.
      const bboxTop = new THREE.Box3().setFromObject(isl).max.y;
      yTop = this.surfaceYAt(isl, 0, 0, bboxTop - 0.6);
    } else {
      const sand = new THREE.MeshStandardMaterial({ color: isOwn ? 0xe9d6a0 : 0xd9c187, roughness: 1 });
      const island = new THREE.Mesh(new THREE.ConeGeometry(islandR, 9, 10), sand);
      island.position.y = 2.5; g.add(island);
      const top = new THREE.Mesh(new THREE.CylinderGeometry(islandR * 0.82, islandR * 0.82, 1.4, 12), sand);
      top.position.y = 6.4; g.add(top);
    }
    // warm FakeLights lit at night (HQ beacon, turret watch-fires, dock) — registry entries, zero extra draws
    let lightSeed = Math.imul(wb.islandId, 2654435761) >>> 8;
    for (const b of wb.buildings) {
      if ((b.type === BT.TAVERN || b.type === BT.FOUNDRY || b.type === BT.CROP) && b.lvl < 1) continue; // 2b: unbuilt production plot
      const col = b.cell % GRID, row = Math.floor(b.cell / GRID);
      const lx = (col - (GRID - 1) / 2) * CELL;
      const lz = (row - (GRID - 1) / 2) * CELL;
      const sy = islObj ? this.surfaceYAt(islObj, lx, lz, yTop) - 0.1 : yTop; // seat on the sand under this cell
      const m = this.makeBuilding(b.type);
      m.position.set(lx, sy, lz);
      g.add(m);
      if (b.type === BT.HQ) this.lights.add({ anchor: g, lx, ly: sy + 11, lz, preset: 'lantern', seed: ++lightSeed, radiusScale: 1.6, intensityScale: 0.95 });
      else if (b.type === BT.TURRET) this.lights.add({ anchor: g, lx, ly: sy + 5, lz, preset: 'torch', seed: ++lightSeed, radiusScale: 0.85, intensityScale: 0.75 });
      // the skull forge's furnace halo — a deep ember flicker over the foundry (its baked emissive breathes too, see PROP_MODELS.ember)
      else if (b.type === BT.FOUNDRY) this.lights.add({ anchor: g, lx, ly: sy + 4.4, lz, preset: { color: 0xff6a26, intensity: 0.7, radius: 3.6, flicker: 'bonfire', glint: false, y: 0 }, seed: ++lightSeed });
      // a warm lit window on the tavern — the "someone's still up" glow
      else if (b.type === BT.TAVERN) this.lights.add({ anchor: g, lx, ly: sy + 3.0, lz, preset: 'window', seed: ++lightSeed, radiusScale: 1.15 });
    }
    // dock jetty / parking slip on the water side (toward the +x,+z spawn/dock offset).
    // Dock lvl >= DRYDOCK_LVL = an ENCLOSED dry-dock (boathouse) — others see the closed shed, never the
    // ship inside (the server omits the parked ship from their snapshots). lvl 1 = open jetty.
    const dockLvl = wb.buildings.find((b) => b.type === BT.DOCK)?.lvl ?? 1;
    const enclosed = dockLvl >= BASE.DRYDOCK_LVL;
    const dryTpl = enclosed ? this.propTemplates.get('drydock') : undefined;
    const dockTpl = dryTpl ?? this.propTemplates.get('dock');
    const jetty = new THREE.Group();
    jetty.rotation.y = -Math.PI / 4; // local +X points along the (+x,+z) dock direction
    if (dockTpl) {
      const d = dockTpl.clone(true);
      d.userData.shared = true;        // GLB clone — shares geometry/materials with the template
      // the boathouse sits further out (a covered slip over the water); the open jetty hugs the edge
      d.position.set(dryTpl ? islandR + 8 : islandR + 1, 0.3, 0);
      jetty.add(d);
      // enclosed, but only the open-dock GLB exists (no drydock asset) -> cap the slip with a boathouse shell
      if (enclosed && !dryTpl) jetty.add(this.makeBoathouseShell(islandR));
    } else {
      const wood = new THREE.MeshStandardMaterial({ color: 0x6b4a2b, roughness: 1 });
      const deck = new THREE.Mesh(new THREE.BoxGeometry(17, 0.6, 4), wood);
      deck.position.set(islandR + 5, 0.7, 0); jetty.add(deck);
      for (let i = 0; i < 5; i++) for (const s of [-1, 1]) {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 3, 6), wood);
        post.position.set(islandR - 2 + i * 4.2, 1, s * 1.9); jetty.add(post);
      }
      for (const s of [-1, 1]) {
        const sp = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 4.6, 6), wood);
        sp.position.set(islandR + 14.5, 1.4, s * 5.5); jetty.add(sp);
      }
      if (enclosed) jetty.add(this.makeBoathouseShell(islandR));
    }
    // dock lantern: low over the water → it competes for an ocean-glint slot (the shimmering reflection)
    this.lights.add({ anchor: jetty, lx: dryTpl ? islandR + 8 : islandR + 6, ly: 4, lz: 0, preset: 'lantern', seed: ++lightSeed, radiusScale: 0.95, intensityScale: 0.8, sway: 0.15, glint: true });
    g.add(jetty);
    if (isOwn) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(islandR + 7, 1.4, 6, 36),
        new THREE.MeshBasicMaterial({ color: 0x39d98a, transparent: true, opacity: 0.6 }),
      );
      ring.rotation.x = -Math.PI / 2; ring.position.y = 0.7; g.add(ring);
    }
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(islandR + 5, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      // depthWrite:false (like every other transparent material here) so the glass dome blends over the
      // lanterns/FX it encloses instead of writing depth and z-culling them.
      new THREE.MeshBasicMaterial({ color: 0x6fd3ff, transparent: true, opacity: 0.16, side: THREE.DoubleSide, depthWrite: false }),
    );
    dome.position.y = 1; dome.visible = false; g.add(dome);
    g.userData.dome = dome;
    // register every OWNED resource (ring/dome + procedural fallbacks); the GLB
    // clones (island/buildings/jetty) are tagged userData.shared and skipped.
    this.trackOwned(g, g);
    return g;
  }

  /** Procedural enclosed-boathouse shell (dry-dock fallback) capping the slip: two side walls + a
   *  back wall + a peaked A-roof, open at the water end. Used only when the drydock GLB hasn't loaded;
   *  built in the jetty's local frame (local +X points out along the slip). */
  private makeBoathouseShell(islandR: number): THREE.Group {
    const grp = new THREE.Group();
    const cx = islandR + 9;                 // centre of the shed along the slip (local +x)
    const L = 15, W = 9, H = 5;             // length (along the slip), width, wall height
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x7a5230, roughness: 1 });
    const roofMat = new THREE.MeshStandardMaterial({ color: 0x5a3320, roughness: 1 });
    for (const s of [-1, 1]) {              // side walls
      const w = new THREE.Mesh(new THREE.BoxGeometry(L, H, 0.4), wallMat);
      w.position.set(cx, H / 2 + 0.6, s * (W / 2)); grp.add(w);
    }
    const back = new THREE.Mesh(new THREE.BoxGeometry(0.4, H, W), wallMat); // island-end wall (water end open)
    back.position.set(cx - L / 2, H / 2 + 0.6, 0); grp.add(back);
    const rise = 2.4, slab = Math.hypot(W / 2, rise) + 0.8, ang = Math.atan2(rise, W / 2);
    for (const s of [-1, 1]) {              // peaked A-roof: two slanted slabs meeting at the ridge
      const r = new THREE.Mesh(new THREE.BoxGeometry(L + 0.8, 0.3, slab), roofMat);
      r.position.set(cx, H + 0.6 + rise / 2, s * (W / 4));
      r.rotation.x = s * ang; grp.add(r);
    }
    return grp;
  }

  private makeBuilding(type: number): THREE.Object3D {
    switch (type) {
      case BT.HQ: {
        const tpl = this.propTemplates.get('hq');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(5, 9, 5), new THREE.MeshStandardMaterial({ color: 0x8a6b3f, roughness: 0.8 }));
        m.position.y = 4.5; return m;
      }
      case BT.STORAGE: {
        const tpl = this.propTemplates.get('storage');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(5, 4, 5), new THREE.MeshStandardMaterial({ color: 0x7a4f2a, roughness: 0.9 }));
        m.position.y = 2; return m;
      }
      case BT.TURRET: {
        const tpl = this.propTemplates.get('turret');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const grp = new THREE.Group();
        const base = new THREE.Mesh(new THREE.CylinderGeometry(2, 2.4, 3, 8), new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.6, metalness: 0.4 }));
        base.position.y = 1.5; grp.add(base);
        const barrel = new THREE.Mesh(new THREE.BoxGeometry(4, 1, 1), new THREE.MeshStandardMaterial({ color: 0x23282e, metalness: 0.5 }));
        barrel.position.set(1.5, 3.2, 0); grp.add(barrel);
        return grp;
      }
      case BT.FARM: {
        const tpl = this.propTemplates.get('farm');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(5, 2.4, 5), new THREE.MeshStandardMaterial({ color: 0x3f8f4f, roughness: 1 }));
        m.position.y = 1.2; return m;
      }
      case BT.TAVERN: {
        const tpl = this.propTemplates.get('tavern');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(5, 4, 5), new THREE.MeshStandardMaterial({ color: 0x8a5a2a, roughness: 0.9 }));
        m.position.y = 2; return m;
      }
      case BT.FOUNDRY: {
        const tpl = this.propTemplates.get('foundry');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(5, 4.5, 5), new THREE.MeshStandardMaterial({ color: 0x4a4038, roughness: 0.85, metalness: 0.3 }));
        m.position.y = 2.25; return m;
      }
      case BT.CROP: {
        const tpl = this.propTemplates.get('crop_plot');
        if (tpl) { const c = tpl.clone(true); c.userData.shared = true; return c; }
        const m = new THREE.Mesh(new THREE.BoxGeometry(4.5, 1, 4.5), new THREE.MeshStandardMaterial({ color: 0x5a7a3a, roughness: 1 }));
        m.position.y = 0.5; return m;
      }
      case BT.DOCK: return new THREE.Group(); // the jetty (built in buildBase) is the visible dock/pier
      default: {
        const m = new THREE.Mesh(new THREE.BoxGeometry(3, 3, 3), new THREE.MeshStandardMaterial({ color: 0x888888 }));
        m.position.y = 1.5; return m;
      }
    }
  }

  /* ------------------------------- ships -------------------------------- */

  /** Floating name label above a ship (billboarded, always faces the camera); prefixed with the [CLAN] tag.
   *  `npc` (a marauder) paints the name red with no clan bracket — a hostile cue. `badge` (NAME_BADGE bits,
   *  Onda Frota v47) paints the name GOLD (owner/gold_name grant) and/or prepends a "DEV" tag. */
  setShipName(id: number, name: string, tag = '', npc = false, badge = 0): void {
    const gold = !npc && (badge & NAME_BADGE.GOLD) !== 0;
    const dev = !npc && (badge & NAME_BADGE.DEV) !== 0;
    const label = npc ? name : (tag ? `[${tag}] ${name}` : name);
    const key = (npc ? '☠' : (gold ? '★' : '') + (dev ? 'D' : '')) + label; // cache key varies with the recolor/badge
    const g = this.ships.get(id); if (!g || g.userData.nameStr === key) return;
    g.userData.nameStr = key;
    const tex = this.makeNameTexture(label, npc ? '#ff5a4d' : (gold ? '#ffd54a' : '#f0f7ff'), dev ? NAME_BADGE.DEV : 0);
    let spr = g.userData.nameSprite as THREE.Sprite | undefined;
    if (!spr) {
      spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
      spr.position.set(0, 33, 0); spr.renderOrder = 12;
      g.add(spr); g.userData.nameSprite = spr; this.trackOwned(g, spr);
    } else {
      const m = spr.material as THREE.SpriteMaterial; m.map?.dispose(); m.map = tex; m.needsUpdate = true;
    }
    const img = tex.image as HTMLCanvasElement;
    const inv = 1 / (g.scale.x || 1); // constant world text size regardless of hull scale
    spr.scale.set((img.width / img.height) * 6 * inv, 6 * inv, 1);
  }

  private makeNameTexture(name: string, fill = '#f0f7ff', badge = 0): THREE.CanvasTexture {
    name = name.slice(0, 20);
    const dev = (badge & NAME_BADGE.DEV) !== 0;
    const tag = dev ? 'DEV ' : '';
    const c = document.createElement('canvas'); const ctx = c.getContext('2d')!;
    const font = 'bold 42px system-ui, Arial, sans-serif'; ctx.font = font;
    const tagW = dev ? Math.ceil(ctx.measureText(tag).width) : 0;
    const nameW = Math.ceil(ctx.measureText(name).width);
    c.width = tagW + nameW + 26; c.height = 60;
    ctx.font = font; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
    const y = c.height / 2; let x = 13;
    ctx.lineWidth = 7; ctx.strokeStyle = 'rgba(0,0,0,.9)';
    if (dev) { // a bright DEV badge in front of the name (distinct colour so it reads as a tag, not part of the name)
      ctx.strokeText(tag, x, y); ctx.fillStyle = '#4fc3f7'; ctx.fillText(tag, x, y); x += tagW;
    }
    ctx.strokeText(name, x, y);
    ctx.fillStyle = fill; ctx.fillText(name, x, y);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; return tex;
  }

  /** Register a ship's equipped cosmetics + figurehead trophy — applied on the next syncShips pass. */
  setShipCosmetics(id: number, hull: number, sail: number, flag: number, trophy = 0): void {
    const cur = this.shipCos.get(id);
    if (!cur || cur.hull !== hull || cur.sail !== sail || cur.flag !== flag || cur.trophy !== trophy) this.shipCos.set(id, { hull, sail, flag, trophy });
  }

  /** A carved figurehead mounted on the bow for an earned trophy (wave 3.3). Hull-local (bow at +X); the
   *  White Whale trophy reuses the whale GLB (tiny); the rest are stylized procedural carvings. */
  private makeFigurehead(trophy: number): THREE.Object3D {
    const g = new THREE.Group();
    g.position.set(9, 1.9, 0); // near the bow tip, above the waterline (hull-template-local)
    if (trophy === 1) { // White Whale — a tiny whale carving lunging forward
      const tpl = this.propTemplates.get('whale');
      if (tpl) { const w = tpl.clone(true); w.userData.shared = true; w.scale.setScalar(0.09); w.rotation.y = Math.PI; g.add(w); }
      else { const m = new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), new THREE.MeshStandardMaterial({ color: 0xe8eef2, roughness: 0.6 })); m.scale.set(1.6, 0.8, 0.7); g.add(m); }
    } else if (trophy === 2) { // Cursed Crown — a gilded ring of spikes, faint violet glow
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.7, 0.16, 8, 14), new THREE.MeshStandardMaterial({ color: 0xd9a441, emissive: 0x6a2fb0, emissiveIntensity: 0.5, metalness: 0.8, roughness: 0.3 }));
      ring.rotation.x = Math.PI / 2; g.add(ring);
      for (let i = 0; i < 5; i++) { const s = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.6, 6), ring.material); const a = (i / 5) * Math.PI * 2; s.position.set(Math.cos(a) * 0.7, 0.35, Math.sin(a) * 0.7); g.add(s); }
    } else if (trophy === 3) { // Corsair's Skull — bone-white with hollow sockets
      const skull = new THREE.Mesh(new THREE.SphereGeometry(0.8, 10, 8), new THREE.MeshStandardMaterial({ color: 0xf0ead6, roughness: 0.85 })); skull.scale.set(1, 1.05, 0.9); g.add(skull);
      const eyeMat = new THREE.MeshStandardMaterial({ color: 0x111111 });
      for (const sx of [-0.32, 0.32]) { const e = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), eyeMat); e.position.set(sx, 0.05, 0.62); e.scale.z = 0.5; g.add(e); }
    } else if (trophy === 4) { // Spectral Lantern — a cold emissive glow-cage
      const cage = new THREE.Mesh(new THREE.BoxGeometry(0.7, 1, 0.7), new THREE.MeshStandardMaterial({ color: 0x8fd8e6, emissive: 0x3fd0ff, emissiveIntensity: 0.9, transparent: true, opacity: 0.75 })); g.add(cage);
    }
    this.trackOwned(g, g);
    return g;
  }

  /** The cached masthead emblem texture for a flag cosmetic id (loaded from the same generated art). */
  private getFlagTexture(id: number): THREE.Texture {
    let tex = this.flagTex.get(id);
    if (!tex) { tex = this.flagLoader.load(`/cosmetics/${id}.webp`); tex.colorSpace = THREE.SRGBColorSpace; this.flagTex.set(id, tex); }
    return tex;
  }

  /** Tint a ship's hull + sails and swap its masthead flag emblem to its equipped cosmetics. Clones the
   *  shared GLB material per instance and remembers the base color, so unequipping (id 0) restores it. */
  private applyCosmetics(g: THREE.Group, hull: number, sail: number, flag: number, trophy = 0): void {
    if (g.userData.cosTrophy !== trophy) {
      g.userData.cosTrophy = trophy;
      const old = g.userData.figurehead as THREE.Object3D | undefined;
      if (old) { g.remove(old); disposeGroup(old as THREE.Group); g.userData.figurehead = undefined; }
      if (trophy > 0) { const f = this.makeFigurehead(trophy); g.add(f); g.userData.figurehead = f; }
    }
    if (!g.userData.npcMarked && g.userData.cosFlag !== flag) { // marauders fly a forced black flag — never overwrite it
      g.userData.cosFlag = flag;
      const flagMesh = g.userData.flag as THREE.Mesh | undefined;
      if (flagMesh) {
        const mat = flagMesh.material as THREE.MeshStandardMaterial;
        if (flag > 0) { mat.map = this.getFlagTexture(flag); mat.color.setHex(0xffffff); flagMesh.visible = true; }
        else { mat.map = null; flagMesh.visible = false; } // no cosmetic → no black plate at the masthead
        mat.needsUpdate = true;
      }
    }
    if (g.userData.cosHull === hull && g.userData.cosSail === sail) return;
    g.userData.cosHull = hull; g.userData.cosSail = sail;
    const inst = g.userData.inst as THREE.Object3D | undefined; if (!inst) return;
    const hullC = hull ? cosmeticById(hull)?.color : undefined;
    const sailC = sail ? cosmeticById(sail)?.color : undefined;
    inst.traverse((o) => {
      const m = o as THREE.Mesh; if (!m.isMesh) return;
      const want = isSailMesh(m) ? sailC : hullC;
      const arr = Array.isArray(m.material);
      const mats = (arr ? m.material : [m.material]) as THREE.MeshStandardMaterial[];
      for (let i = 0; i < mats.length; i++) {
        let mat = mats[i]!;
        if (!mat.userData.cosOwned) { // clone once so we never touch the shared template material
          mat = mat.clone(); mat.userData.cosOwned = true; mat.userData.cosBase = mat.color.clone();
          if (arr) (m.material as THREE.Material[])[i] = mat; else m.material = mat;
        }
        if (want !== undefined) mat.color.setHex(want);
        else mat.color.copy(mat.userData.cosBase as THREE.Color);
      }
    });
  }

  /** Wave 3.13: paint a ship group with a translucent, cold-emissive spectral skin (the night Ghost Fleet).
   *  Clones each material once (never touching the shared template), applied a single time per ghost hull. */
  private applyGhost(g: THREE.Group): void {
    if (g.userData.ghosted) return;
    g.userData.ghosted = true;
    const inst = (g.userData.inst as THREE.Object3D | undefined) ?? g;
    inst.traverse((o) => {
      const m = o as THREE.Mesh; if (!m.isMesh) return;
      const arr = Array.isArray(m.material);
      const mats = (arr ? m.material : [m.material]) as THREE.MeshStandardMaterial[];
      for (let i = 0; i < mats.length; i++) {
        const src = mats[i]!;
        const mat = src.clone() as THREE.MeshStandardMaterial;
        // THREE.Material.copy does NOT carry onBeforeCompile — without this the ghost fleet's canvas would
        // silently drop back to the frozen baked pose. Re-pointing at the SOURCE's hook keeps the closure
        // (and therefore THIS ship's wind uniforms) alive through the spectral re-skin.
        if (src.onBeforeCompile !== THREE.Material.prototype.onBeforeCompile) mat.onBeforeCompile = src.onBeforeCompile;
        mat.transparent = true; mat.opacity = 0.5; mat.depthWrite = false;
        mat.color.setHex(0x8fd8e6); mat.emissive = new THREE.Color(0x3fd0ff); mat.emissiveIntensity = 0.7; mat.roughness = 1;
        if (arr) (m.material as THREE.Material[])[i] = mat; else m.material = mat;
      }
    });
  }

  /** Onda Marauders: fly a black masthead flag on a hostile NPC hull (a cosmetic cue; the server owns all
   *  their behaviour). Reuses the ship's built-in flag plane, forced visible + untextured black. Applied once. */
  private applyNpcMark(g: THREE.Group): void {
    if (g.userData.npcMarked) return;
    g.userData.npcMarked = true;
    const flagMesh = g.userData.flag as THREE.Mesh | undefined;
    if (flagMesh) {
      const mat = flagMesh.material as THREE.MeshStandardMaterial;
      mat.map = null; mat.color.setHex(0x0a0a0a); flagMesh.visible = true; mat.needsUpdate = true;
    }
  }

  private buildShip(id: number, isLocal: boolean, cls: number): THREE.Group {
    const scale = classScale(cls);
    // pick this class's model; fall back to the galleon, then any loaded template — and RECORD which
    // class actually dressed the hull (tplCls) so syncShips' self-heal can upgrade a stand-in later.
    let tplCls = cls;
    let entry = this.shipTemplates.get(cls);
    if (!entry) { tplCls = ShipClassId.GALLEON; entry = this.shipTemplates.get(tplCls); }
    if (!entry) { const first = this.shipTemplates.entries().next().value; if (first) { tplCls = first[0]; entry = first[1]; } }
    if (entry) {
      const g = new THREE.Group();
      g.rotation.order = 'YXZ';
      const inst = entry.template.clone(true);
      g.add(inst);
      g.userData.inst = inst; // the ship-model subtree only (cosmetics tint this, never captain/crew/marker)
      g.userData.tplCls = tplCls;                              // which class's GLB dressed this hull (self-heal)
      g.userData.noCap = !this.captainTemplates.has(cls);      // captain missing at build time (self-heal re-crews)
      g.userData.topY = entry.topY ?? 31;                      // masthead height (group-local) — the wind pennant flies there
      // ---- Onda Velas: give THIS hull its own canvas ----
      // The GLB clone shares the template's material, so the wind uniforms have to be per-ship: clone once here
      // (a material, NOT a draw call — the sail mesh was always its own draw) and patch it. The GLSL is
      // byte-identical for every hull, so three's program cache compiles it once and they all share the program.
      // The clone is stamped cosOwned so applyCosmetics tints THIS material instead of making a second one,
      // and it is tracked as owned so it is disposed with the ship.
      if (entry.sailRig) {
        const uni: SailUniforms[] = [];
        const owned = (g.userData.ownMats ??= []) as THREE.Material[];
        inst.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh || !isSailMesh(m)) return;
          const arr = Array.isArray(m.material);
          const mats = (arr ? m.material : [m.material]) as THREE.MeshStandardMaterial[];
          // each sail MESH carries the rig baked from its own basis (geo.userData.sailRig); the class-level one
          // is the fallback for the single-sail-node hulls every shipped class happens to be.
          const rig = (m.geometry.userData.sailRig as SailRig | undefined) ?? entry!.sailRig!;
          for (let i = 0; i < mats.length; i++) {
            const mine = mats[i]!.clone();
            mine.userData.cosOwned = true; mine.userData.cosBase = mine.color.clone();
            uni.push(patchSailMaterial(mine, rig, this.sailShared));
            owned.push(mine);
            if (arr) (m.material as THREE.Material[])[i] = mine; else m.material = mine;
          }
        });
        if (uni.length) { g.userData.sailUni = uni; g.userData.sailState = new SailState(); }
      }
      // per-ship sail animation: scrub the baked Blender "furl" clips by sail trim
      if (entry.furlClips.length) {
        const mixer = new THREE.AnimationMixer(inst);
        const actions = entry.furlClips.map((c) => { const a = mixer.clipAction(c); a.play(); a.paused = true; return a; });
        g.userData.mixer = mixer;
        g.userData.actions = actions;
        g.userData.clipDur = entry.furlClips[0]!.duration || 1;
      }
      if (isLocal) {
        const marker = new THREE.Mesh(new THREE.ConeGeometry(1.3, 3.4, 8), new THREE.MeshBasicMaterial({ color: 0x39d98a }));
        marker.position.y = 19; marker.rotation.x = Math.PI;
        g.add(marker);
        this.trackOwned(g, marker); // owned (the GLB hull clone + captain clone share the template — never tracked)
      }
      this.lights.addShipLights(g, cls, id, classScale(cls)); // per-class lantern rig (FakeLight registry — zero extra draws)
      this.attachCaptain(g, cls, inst); // helmsman at the wheel (scale reference + personality)
      this.attachCrew(g, cls, inst);    // deckhands bustling along the rails (alive during a grapple)
      this.attachCannons(g, cls, inst); // pre-compute broadside gun stations (drawn from the shared InstancedMesh)
      // masthead flag — hidden until a flag COSMETIC is equipped (a plain untextured plane read as an ugly
      // black plate hovering over the ship; the emblem swaps in + reveals it in applyCosmetics)
      const flagMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 0, side: THREE.DoubleSide });
      const flagMesh = new THREE.Mesh(new THREE.PlaneGeometry(6, 3.6), flagMat);
      flagMesh.position.set(-2, 31, 0); // flown high at the masthead; scaled with the ship group
      flagMesh.visible = false;         // no bare masthead plate — only a real equipped emblem shows
      g.add(flagMesh);
      g.userData.flag = flagMesh;
      this.trackOwned(g, flagMesh); // owned mat+geom → disposed with the ship (texture stays cached)
      g.scale.setScalar(scale);
      g.userData.cls = cls;
      this.shipsRoot.add(g);
      this.ships.set(id, g);
      return g;
    }
    const hue = (Math.imul(id, 2654435761) >>> 8) % 360;
    const hullColor = new THREE.Color(`hsl(${hue}, 45%, 32%)`);
    const sailColor = isLocal ? new THREE.Color(0xeaf4e8) : new THREE.Color(`hsl(${hue}, 35%, 88%)`);
    const g = new THREE.Group();

    const hullMat = new THREE.MeshStandardMaterial({ color: hullColor, roughness: 0.85 });
    const woodMat = new THREE.MeshStandardMaterial({ color: 0x6b4a2b, roughness: 0.9 });
    const sailMat = new THREE.MeshStandardMaterial({ color: sailColor, roughness: 0.95, side: THREE.DoubleSide });

    // hull (bow at +X) — a box plus a wedge nose
    const hull = new THREE.Mesh(new THREE.BoxGeometry(SHIP.LENGTH * 0.8, 3.4, SHIP.BEAM), hullMat);
    hull.position.y = 0.3;
    g.add(hull);
    const bow = new THREE.Mesh(new THREE.ConeGeometry(HALF_B, SHIP.LENGTH * 0.34, 4), hullMat);
    bow.rotation.z = -Math.PI / 2;
    bow.rotation.y = Math.PI / 4;
    bow.position.set(SHIP.LENGTH * 0.46, 0.3, 0);
    g.add(bow);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(SHIP.LENGTH * 0.5, 1.0, SHIP.BEAM * 0.8), woodMat);
    deck.position.set(-1, 2.0, 0);
    g.add(deck);
    const poop = new THREE.Mesh(new THREE.BoxGeometry(4, 2.4, SHIP.BEAM * 0.85), woodMat);
    poop.position.set(-HALF_L * 0.7, 2.6, 0);
    g.add(poop);

    // mast + mainsail (across the hull so it catches the side view)
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.45, 16), woodMat);
    mast.position.set(0, 9, 0);
    g.add(mast);
    const sail = new THREE.Mesh(new THREE.PlaneGeometry(SHIP.BEAM * 1.6, 9), sailMat);
    sail.position.set(0, 10, 0);
    sail.rotation.y = Math.PI / 2; // normal along ship's X (visible broadside)
    g.add(sail);
    const flag = new THREE.Mesh(new THREE.PlaneGeometry(3, 1.6), sailMat);
    flag.position.set(0, 17.2, 0);
    flag.rotation.y = Math.PI / 2;
    g.add(flag);

    g.rotation.order = 'YXZ';
    g.userData.sail = sail;
    this.trackOwned(g, g); // all procedural hull/sail meshes here are owned (no GLB clone in this branch)
    this.lights.addShipLights(g, cls, id, classScale(cls)); // per-class lantern rig (FakeLight registry)
    g.scale.setScalar(classScale(cls));
    g.userData.cls = cls;
    this.shipsRoot.add(g);
    this.ships.set(id, g);
    return g;
  }

  private bob(x: number, z: number, heading: number, t: number, cls: number): { y: number; pitch: number; roll: number } {
    const def = SHIP_CLASS[cls as 0 | 1 | 2] ?? SHIP_CLASS[1];
    const halfL = def.length / 2, halfB = def.beam / 2;
    const c = Math.cos(heading), s = Math.sin(heading);
    const bx = x + halfL * c, bz = z + halfL * s;
    const sx = x - halfL * c, sz = z - halfL * s;
    const px = x - halfB * s, pz = z + halfB * c;
    const tx = x + halfB * s, tz = z - halfB * c;
    // Storm swell — sample the bob at the SAME amplitude the ocean mesh renders AT THIS SPOT, which in a
    // gale is not one global number: the shared coast envelope shoals the swell near land, so a hull in a
    // bay must bob on the calmed water the shader actually drew there (`ampAt` is the CPU twin of the
    // shader's `oceanAmp`, reading the same zones through the same function).
    const amp = this.ampAt(x, z);
    // THE HULL FILTERS THE SEA. A 20 m rigid body does not rise 4 cm because a 1.4 m wavelet crossed the
    // bow — it spans fourteen of them and averages them away. Point-sampling the full spectrum at four
    // corners is what makes a boat jitter, and at a storm crest (centimetre chop riding a 2.7 m vaga) it
    // is what makes the water visibly climb over the deck. `oceanHullHeightAt` applies the hull's own
    // sinc window, so the cutoff is THE SHIP'S LENGTH rather than a tuned constant: the galleon sits
    // steadier than the sloop for the right reason, and both slide along the swell instead of buzzing on
    // it. Render-only — the drawn sea still carries every wavelet, this is only what the boat answers.
    const hull = def.length;
    const hBow = oceanHullHeightAt(bx, bz, t, amp, hull), hStern = oceanHullHeightAt(sx, sz, t, amp, hull);
    const hPort = oceanHullHeightAt(px, pz, t, amp, hull), hStar = oceanHullHeightAt(tx, tz, t, amp, hull);
    const mean = (hBow + hStern + hPort + hStar) / 4;
    // in a storm the hull RIDES the swell: bias toward its highest corner + a touch of freeboard, so a
    // big crest lifts the ship onto it instead of washing over the deck (calm seas: storm=0 -> pure mean).
    const st = this._storm;
    const top = Math.max(hBow, hStern, hPort, hStar);
    return {
      y: mean + (top - mean) * (STORM.SHIP_RIDE * st) + STORM.SHIP_LIFT * st,
      pitch: (hStern - hBow) / def.length * 0.7,
      roll: (hStar - hPort) / def.beam * 0.7,
    };
  }

  /* --------------------------- masthead wind pennants (ONE instanced draw) --------------------------- */

  private pennants: THREE.InstancedMesh | null = null;
  private _pMat = new THREE.Matrix4();
  private _pQuat = new THREE.Quaternion();
  private _pPos = new THREE.Vector3();
  private _pScale = new THREE.Vector3();
  private static readonly PENNANT_MAX = 96;

  /**
   * The little swallow-tail streamer at every masthead — the cheapest possible answer to "where IS the wind?".
   * A captain reading a pennant is reading the SAME number `sailEnv` reads, so the rig and the ribbon can never
   * disagree. ONE InstancedMesh for the whole fleet (+1 draw call, flat), a plain unlit material (no light
   * registration — see lights.ts), and the ripple runs in the vertex shader off an instanced phase so no
   * per-ship CPU work happens beyond writing a matrix.
   */
  private buildPennants(): THREE.InstancedMesh {
    const SEG = 9, LEN = 5.8, W0 = 0.9;
    const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
    for (let i = 0; i <= SEG; i++) {
      const f = i / SEG, w = W0 * (1 - f * 0.82);   // tapers to a point, like a real burgee
      pos.push(f * LEN, w, 0, f * LEN, -w, 0);
      uvs.push(f, 1, f, 0);
      if (i < SEG) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(idx);
    const phase = new Float32Array(Renderer.PENNANT_MAX);
    for (let i = 0; i < phase.length; i++) phase[i] = (i * 2.39996) % 6.28318;
    geo.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phase, 1));

    // deep red, not canvas-cream: at the masthead a cream streamer disappears into the rigging it flies from,
    // and this ribbon's whole job is to be legible in a glance against sky, sail and spar alike.
    const mat = new THREE.MeshBasicMaterial({ color: 0xb8352a, side: THREE.DoubleSide, transparent: true, opacity: 0.96, depthWrite: false });
    mat.onBeforeCompile = (sh) => {
      sh.uniforms.uPenT = this.sailShared.pennantT;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', `#include <common>\nattribute float aPhase;\nuniform float uPenT;\nvarying float vPenF;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
{
  // the streamer ripples harder the further it is from the halyard, and snakes rather than waves
  float f = clamp(transformed.x / ${LEN.toFixed(1)}, 0.0, 1.0);
  vPenF = f;
  float a = uPenT * 6.1 + aPhase - f * 4.2;
  transformed.z += sin(a) * f * f * 0.95;
  transformed.y += cos(a * 0.8 + 1.3) * f * 0.34;
  transformed.x -= f * f * 0.42;             // a fluttering flag never reaches its full length
}`);
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>\nvarying float vPenF;`)
        // the tip catches the light and reads against a bright sky; the root stays in the mast's shade
        .replace('#include <dithering_fragment>', `gl_FragColor.rgb *= 0.72 + 0.45 * vPenF;\n#include <dithering_fragment>`);
    };
    const inst = new THREE.InstancedMesh(geo, mat, Renderer.PENNANT_MAX);
    inst.frustumCulled = false;      // the bounding sphere is meaningless once the matrices move every frame
    inst.count = 0;
    inst.renderOrder = 2;
    this.shipsRoot.add(inst);
    this.pennants = inst;
    return inst;
  }

  /** Point every pennant downwind from its masthead. Allocation-free; one matrix write per visible hull. */
  private updatePennants(t: number): void {
    const inst = this.pennants ?? this.buildPennants();
    if (!inst.parent) this.shipsRoot.add(inst);     // survived a releaseSeaAssets sweep
    this.sailShared.pennantT.value = t;
    // a streamer is pure atmosphere: it goes with the gull flock on Low, and never draws with no fleet on
    // screen (`visible = false`, because an InstancedMesh with count 0 still books a draw call)
    if (this._sailQ < 0.5 || this.ships.size === 0) { inst.count = 0; inst.visible = false; return; }
    inst.visible = true;
    // world yaw of the wind: sim windDir is where it blows TOWARD, and sim (x,y) -> world (x,z), so the
    // three.js Y rotation that lines +X up with it is -windDir (the same mapping every hull heading uses).
    this._pQuat.setFromAxisAngle(PENNANT_UP, -this.windDir);
    let n = 0;
    for (const g of this.ships.values()) {
      if (n >= Renderer.PENNANT_MAX || !g.visible) continue;
      // the pennants live under shipsRoot too, so a hull's LOCAL matrix already lands in the right space —
      // one compose per hull, no updateMatrixWorld recursion through a GLB's node tree.
      g.updateMatrix();
      // +0.7 m so it flies CLEAR of the truck instead of growing out of the spar it is lashed to
      this._pPos.set(0, ((g.userData.topY as number) ?? 31) + 0.7, 0).applyMatrix4(g.matrix);
      this._pScale.setScalar(g.scale.x);
      inst.setMatrixAt(n, this._pMat.compose(this._pPos, this._pQuat, this._pScale));
      n++;
    }
    inst.count = n;
    inst.visible = n > 0;
    inst.instanceMatrix.needsUpdate = true;
  }

  private localBerth = { docked: false, bx: 0, by: 0 };
  /** Tell the renderer the local ship is berthed at (bx,by) so it can moor broadside (cosmetic). */
  setLocalBerth(docked: boolean, bx: number, by: number): void {
    this.localBerth.docked = docked; this.localBerth.bx = bx; this.localBerth.by = by;
  }

  syncShips(local: LocalShipView | null, localId: number, remotes: InterpShip[], t: number, localRigging = 0, localHp = 100, localGrappled = false): void {
    const seen = new Set<number>();
    // sail springs integrate on their own dt (syncShips is handed absolute time, not a delta)
    const dtSail = this._sailT < 0 ? 1 / 60 : Math.max(0.001, Math.min(0.1, t - this._sailT));
    this._sailT = t;
    this._localLuff = 0; // a hull with no riggable canvas (or no hull at all) is silent, not stuck on the last value
    const place = (id: number, x: number, z: number, heading: number, sail: number, isLocal: boolean, sinking: boolean, cls: number, rigging: number, hp: number, grappled: boolean, ghost = false, npc = false) => {
      seen.add(id);
      if (this.sinkingIds.has(id)) return; // the wreck cinematic owns this ship's visual right now
      let g = this.ships.get(id);
      // hull class changed (shipyard swap) -> rebuild with the new model scale
      if (g && g.userData.cls !== cls) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); g = undefined; }
      // self-heal (2026-07-15): a hull born procedural, dressed with a stand-in template, or missing its
      // captain upgrades IN PLACE the moment the asset it lacked arrives — the all-or-nothing Promise.all
      // rebuilds in loadShipModel/loadCaptains never fire if one GLB fetch stalls (pending forever ≠
      // rejection, so retry can't see it). The cheap pre-check short-circuits fully-dressed ships with
      // zero allocation; shouldRedressShip (pure, tested) decides the rest. Post-rebuild the recorded
      // state satisfies the pre-check, so a ship is never rebuilt twice for the same reason (no thrash).
      if (g && (!g.userData.inst || g.userData.tplCls !== cls || g.userData.noCap === true) &&
          shouldRedressShip({
            builtWithGlb: !!g.userData.inst,
            builtTplCls: g.userData.tplCls as number | undefined,
            builtWithoutCaptain: g.userData.noCap === true,
            cls,
            templatesLoaded: this.shipTemplates.size,
            clsTemplateLoaded: this.shipTemplates.has(cls),
            clsCaptainLoaded: this.captainTemplates.has(cls),
          })) {
        this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); g = undefined;
      }
      if (!g) g = this.buildShip(id, isLocal, cls);
      const cos = this.shipCos.get(id); if (cos) this.applyCosmetics(g, cos.hull, cos.sail, cos.flag, cos.trophy); // equipped hull/sail tint + flag emblem + bow trophy
      if (ghost) this.applyGhost(g); // wave 3.13: a spectral, emissive night-fleet skin
      if (npc) this.applyNpcMark(g); // Onda Marauders: a black masthead flag marks a hostile NPC
      g.userData.grappled = grappled; // drives the crew "boarded" running/brawling animation
      g.userData.flood = Math.max(0, Math.min(1, (100 - hp) / 100)); // 0 dry … 1 sunk → crew bails
      const b = this.bob(x, z, heading, t, cls);
      g.position.set(x, b.y - (sinking ? 2.5 : 0), z);
      // berth-ease: while docked at your own base, the local ship swings to lie BROADSIDE to the shore
      // (moor alongside) instead of poking its bow at the dock. Cosmetic (docked = passive safe-harbor);
      // eases back to the true heading on undock, and never lags the heading while sailing.
      let renderHeading = heading;
      let by = g.userData.berthYaw as number | undefined;
      if (isLocal && this.localBerth.docked) {
        const radial = Math.atan2(z - this.localBerth.by, x - this.localBerth.bx);
        const tA = radial + Math.PI / 2, tB = radial - Math.PI / 2;
        const target = Math.abs(normAngle(tA - heading)) < Math.abs(normAngle(tB - heading)) ? tA : tB;
        by = by ?? heading;
        by = normAngle(by + normAngle(target - by) * 0.05);
        g.userData.berthYaw = by; renderHeading = by;
      } else if (by !== undefined) {
        by = normAngle(by + normAngle(heading - by) * 0.14);
        if (Math.abs(normAngle(heading - by)) < 0.02) { g.userData.berthYaw = undefined; renderHeading = heading; }
        else { g.userData.berthYaw = by; renderHeading = by; }
      }
      // ---- Onda Velas: read the wind and lay her over ----
      // `sailEnv` scores the heading with the SAME cos(heading − windDir) the sim's `windFactor` uses, so a
      // sail that LOOKS full is a sail that is actually pushing the boat. The heel is MESH-ONLY: the OBB, the
      // prediction body and the wire never see it (invariants 1, 2, 4).
      const sst = g.userData.sailState as SailState | undefined;
      if (sst) sst.step(sailEnv(heading, this.windDir, this.windStrength, sail, rigging, this._storm), dtSail);
      g.rotation.y = -renderHeading;
      g.rotation.z = b.pitch;
      g.rotation.x = b.roll + (sst ? sst.heel : 0);
      // cosmetic recoil: a quick knockback nudge + tilt-away that eases back (does NOT move the real position)
      const rt0 = g.userData.recoilT0 as number | undefined;
      if (rt0 !== undefined) {
        const age = t - rt0;
        if (age >= 0 && age < RECOIL_DUR) {
          const s = age / RECOIL_DUR;
          const env = s < 0.15 ? s / 0.15 : 1 - (s - 0.15) / 0.85; // snap out, ease back (triangle peak)
          const off = (g.userData.recoilPeak as number) * env;
          const rx = g.userData.recoilX as number, rz = g.userData.recoilZ as number;
          g.position.x += rx * off; g.position.z += rz * off;
          const lateral = Math.cos(heading) * rz - Math.sin(heading) * rx; // tilt away from the impact side
          g.rotation.x += Math.sign(lateral) * env * 0.1;
        } else if (age >= RECOIL_DUR) {
          g.userData.recoilT0 = undefined;
        }
      }
      // distance LOD (remote hulls only — the local ship is always full detail): past ~300 m from the camera
      // freeze the sail-furl mixer + hide the name sprite; hysteresis restores both ~20 m before re-entry.
      let lodFar = false;
      if (!isLocal) {
        const dcx = g.position.x - this.camera.position.x, dcz = g.position.z - this.camera.position.z;
        const dcam2 = dcx * dcx + dcz * dcz;
        const wasFar = g.userData.lodFar === true;
        lodFar = wasFar ? dcam2 > SHIP_LOD_NEAR_D2 : dcam2 > SHIP_LOD_FAR_D2;
        g.userData.lodFar = lodFar;
        const spr = g.userData.nameSprite as THREE.Sprite | undefined;
        if (spr) spr.visible = !lodFar;
      }
      // chain-damaged rigging visibly droops the canvas (sails can't draw full)
      const trim = rigging > 0 ? sail * (1 - 0.6 * Math.min(1, rigging)) : sail;
      const sailObj = g.userData.sail as THREE.Mesh | undefined;
      if (sailObj) sailObj.scale.y = 0.35 + trim * 0.65;
      const actions = g.userData.actions as THREE.AnimationAction[] | undefined;
      if (actions && !lodFar) { // LOD: distant hulls freeze the furl mixer (imperceptible ripple, saved cost)
        const ct = Math.max(0, Math.min(1, trim)) * (g.userData.clipDur as number);
        for (const a of actions) a.time = ct;
        (g.userData.mixer as THREE.AnimationMixer).update(0);
      }
      // …and hand the same trim to the cloth shader. LOD: a hull past ~300 m freezes its flap clock with the
      // furl mixer (the beat is sub-pixel out there) — the SHAPE terms still ride, so a distant sail still
      // reads as full or as collapsed, it just stops paying for the flutter.
      const sUni = g.userData.sailUni as SailUniforms[] | undefined;
      if (sUni && sst) for (const u of sUni) {
        u.drive.value.set(sst.fill, sst.luff, sst.lateral, Math.max(0, Math.min(1, trim)));
        u.fx.value.set(t, this._storm, rigging, lodFar ? 0 : this._sailQ);
      }
      if (isLocal && sst) this._localLuff = sst.luff;
      g.visible = true;
      // damage smoke: a hurt hull billows dark smoke, denser the lower the hp (jitter from t+id, no Math.random)
      if (!sinking && hp < 55) {
        const dmg = Math.min(1, (55 - hp) / 55);
        const last = (g.userData.smokeAt as number) ?? 0;
        if (t - last > 0.5 - dmg * 0.34) {
          g.userData.smokeAt = t;
          const jx = Math.sin(t * 7 + id) * 2.4, jz = Math.cos(t * 5 + id * 2.3) * 2.4;
          this.spawnFx(x + jx, b.y + 4 + (id % 3), z + jz, dmg > 0.6 ? 0x1b1b1b : 0x57514a, 3 + dmg * 3.5, 0.8 + dmg * 0.6);
        }
      }
    };
    if (local) place(localId, local.x, local.y, local.heading, local.sail, true, false, local.cls, localRigging, localHp, localGrappled);
    for (const r of remotes) place(r.id, r.x, r.y, r.heading, r.sail, false, (r.flags & ShipFlag.SINKING) !== 0, r.cls, (r.flags & ShipFlag.RIGGING) !== 0 ? 1 : 0, r.hp, (r.flags & ShipFlag.GRAPPLED) !== 0, (r.flags & ShipFlag.GHOST) !== 0, (r.flags & ShipFlag.NPC) !== 0);

    for (const [id, g] of this.ships) {
      if (!seen.has(id)) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); }
    }
    this.updateCannons();
    this.updateTethers(t);
    this.updatePennants(t);
  }

  /** Write every visible, nearby ship's broadside guns into the ONE shared InstancedMesh (whole fleet =
   *  +1 draw call). Ships nearest the camera win the capped slots; distant / sinking ships are skipped so
   *  the triangle budget holds even in a dense brawl. Guns ride each hull's full attitude (heading+bob). */
  private updateCannons(): void {
    const inst = this.cannonInst; if (!inst) return;
    const cx = this.camera.position.x, cz = this.camera.position.z;
    let k = 0;
    for (const [id, g] of this.ships) {
      if (k >= CANNON_RENDER.CAP) break;
      const anchors = g.userData.cannons as { x: number; y: number; z: number; yaw: number }[] | undefined;
      if (!anchors || !g.visible || this.sinkingIds.has(id)) continue;
      const dx = g.position.x - cx, dz = g.position.z - cz;
      if (dx * dx + dz * dz > CANNON_RENDER.CULL_D2) continue;
      g.updateMatrixWorld(true); // finalise this hull's pose (set in place()) before deriving gun transforms
      for (const a of anchors) {
        if (k >= CANNON_RENDER.CAP) break;
        this._cnQ.setFromAxisAngle(this._cnUp, a.yaw);
        this._cnPos.set(a.x, a.y, a.z);
        this._cnScl.setScalar(CANNON_RENDER.SCALE);
        this._cnLM.compose(this._cnPos, this._cnQ, this._cnScl);
        this._cnM.multiplyMatrices(g.matrixWorld, this._cnLM); // hull world matrix ∘ local gun transform
        inst.setMatrixAt(k++, this._cnM);
      }
    }
    inst.count = k;
    if (k > 0) inst.instanceMatrix.needsUpdate = true;
  }

  /* ------------------------------ grapple tether ------------------------------ */

  private static tetherKey(a: number, b: number): number { return a < b ? a * 65536 + b : b * 65536 + a; }

  /** Start a persistent rope between two grappled hulls (no-op if already drawn). */
  spawnTether(idA: number, idB: number): void {
    const key = Renderer.tetherKey(idA, idB);
    if (this.tethers.has(key)) return;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array((TETHER_SEGMENTS + 1) * 3), 3));
    const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x4a3520 }));
    line.frustumCulled = false;
    this.scene.add(line);
    this.tethers.set(key, { line, a: idA, b: idB });
  }

  removeTether(idA: number, idB: number): void {
    const key = Renderer.tetherKey(idA, idB);
    const t = this.tethers.get(key);
    if (!t) return;
    this.scene.remove(t.line);
    t.line.geometry.dispose();
    (t.line.material as THREE.Material).dispose();
    this.tethers.delete(key);
  }

  /** Re-anchor each tether between its two ships as a sagging, gently swaying rope (hidden if an endpoint is absent). */
  private updateTethers(t: number): void {
    for (const tt of this.tethers.values()) {
      const ga = this.ships.get(tt.a), gb = this.ships.get(tt.b);
      if (!ga || !gb) { tt.line.visible = false; continue; }
      tt.line.visible = true;
      const ax = ga.position.x, ay = ga.position.y + 5, az = ga.position.z;
      const bx = gb.position.x, by = gb.position.y + 5, bz = gb.position.z;
      const sag = Math.min(Math.hypot(bx - ax, bz - az) * 0.18, 4); // dip toward the water, capped
      const pos = tt.line.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i <= TETHER_SEGMENTS; i++) {
        const f = i / TETHER_SEGMENTS;
        const slack = f * (1 - f); // 0 at the hulls, peaks mid-span
        const px = ax + (bx - ax) * f;
        const pz = az + (bz - az) * f;
        const py = ay + (by - ay) * f - sag * 4 * slack + Math.sin(t * 2.6 + f * 6.2) * 0.5 * slack;
        pos.setXYZ(i, px, py, pz);
      }
      pos.needsUpdate = true;
    }
  }

  /* ------------------------------ aim guide ----------------------------- */

  private buildAimBeam(): void {
    // a SHORT soft cone stub at the muzzle (two crossed quads) — shows the barrel's direction + elevation
    // pitch right where the cannon is, NOT a long laser. position.x = length frac 0..1 (scaled by uLen);
    // the cross offset TAPERS to a point along +X so it reads as a cone, and the alpha fades out fast.
    const v = [
      0, -1, 0, 1, -1, 0, 1, 1, 0,  0, -1, 0, 1, 1, 0, 0, 1, 0,   // quad in the XY plane
      0, 0, -1, 1, 0, -1, 1, 0, 1,  0, 0, -1, 1, 0, 1, 0, 0, 1,   // quad in the XZ plane
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
    const mat = new THREE.ShaderMaterial({
      uniforms: { uLen: { value: 7.5 }, uWidth: { value: 0.8 }, uAlpha: { value: 0 }, uColor: { value: new THREE.Color(0x6cf0c4) } },
      // soft NORMAL blend (not additive) so it reads as a painted gunnery wedge, not an energy beam.
      // depthTest ON so hulls/your own mast occlude it; depthWrite off so it never pollutes depth.
      transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        uniform float uLen, uWidth; varying float vX;
        void main() {
          vX = position.x;
          float w = uWidth * (1.0 - position.x * 0.82);   // taper to a near-point at the tip -> cone
          vec3 p = vec3(position.x * uLen, position.y * w, position.z * w); // local +X = firing dir
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        precision mediump float; uniform float uAlpha; uniform vec3 uColor; varying float vX;
        void main() {
          float a = uAlpha * pow(max(0.0, 1.0 - vX), 2.2); // strongest at the muzzle, gone well before the tip
          gl_FragColor = vec4(pow(uColor, vec3(2.2)), a); // -> linear for the tonemapped composer
        }`,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false; mesh.renderOrder = 3; mesh.visible = false;
    this.scene.add(mesh);
    this.aimBeam = mesh; this.aimBeamMat = mat;
    // sea "nav marks": hollow ring markers on the water (chart-style), repositioned along the bearing each
    // frame — a dotted bearing line that fades OUT before the landing point (never the exact impact).
    const ring = new THREE.RingGeometry(0.62, 1, 20); ring.rotateX(-Math.PI / 2);
    for (let i = 0; i < 8; i++) {
      const m = new THREE.Mesh(ring, new THREE.MeshBasicMaterial({ color: 0x6cf0c4, transparent: true, depthWrite: false, blending: THREE.NormalBlending }));
      m.frustumCulled = false; m.renderOrder = 3; m.visible = false;
      this.scene.add(m); this.aimMarks.push(m);
    }
  }

  /**
   * Aiming indicator: a vivid-green beam emitted from the cannon MUZZLE on `side`, pitched up by
   * `elevation` (0..1), plus sea "nav marks" along the bearing that fade out BEFORE the landing point —
   * shows the initial firing direction + roughly how far, never the exact impact or full trajectory.
   */
  showAimGuide(side: number, x: number, z: number, heading: number, elevation: number, range: number, t: number, ramp: number): void {
    if (!this.aimBeam) this.buildAimBeam();
    const perp = heading + (side === 1 ? Math.PI / 2 : -Math.PI / 2);
    const sx = Math.cos(perp), sz = Math.sin(perp);          // horizontal bearing (world x,z)
    const pitch = elevation * CANNON.ELEV_MAX_ANGLE, cp = Math.cos(pitch);
    const mx = x + sx * (HALF_B + 0.5), mz = z + sz * (HALF_B + 0.5); // the muzzle, at the ship's side
    const my = oceanHeightAt(mx, mz, t, this.ampAt(mx, mz)) + 3.0; // cannon/deck height (storm swell)
    const beam = this.aimBeam!, bmat = this.aimBeamMat!;
    beam.position.set(mx, my, mz);
    beam.quaternion.setFromUnitVectors(this._unitX, this._aimDir.set(sx * cp, Math.sin(pitch), sz * cp).normalize());
    bmat.uniforms.uAlpha!.value = 0.58 * ramp;              // soft muzzle wedge, not a bright laser
    beam.visible = ramp > 0.01;
    // nav marks on the water along the bearing — a dotted bearing line, gaps widening with distance,
    // fading out BEFORE the landing point (shows direction + roughly how far, never the exact impact).
    const reach = range * 0.62, n = this.aimMarks.length;
    for (let i = 0; i < n; i++) {
      const f = Math.pow((i + 1) / n, 1.5);                   // quadratic-ish spacing -> gaps widen with distance
      const d = HALF_B + 4 + reach * f;
      const px = mx + sx * d, pz = mz + sz * d, m = this.aimMarks[i]!;
      // ride the displaced Gerstner sheet (dx/dy/height), not the undisplaced mean — else crests
      // depth-occlude the pip and it blinks (the same rule the wake foam follows).
      const s = oceanSampleAt(px, pz, t, this.ampAt(px, pz));
      m.position.set(px + s.dx, s.height + 0.3, pz + s.dy);
      m.scale.setScalar(0.55 + f * 1.15);
      (m.material as THREE.MeshBasicMaterial).opacity = ramp * 0.62 * (1 - f * 0.82); // fainter with distance
      m.visible = ramp > 0.01;
    }
  }

  hideAimGuide(): void {
    if (this.aimBeam) this.aimBeam.visible = false;
    for (const m of this.aimMarks) m.visible = false;
  }

  /* ------------------------------ aim-lead ghost ----------------------------- */

  private buildLeadGhost(): void {
    // ONE reused mesh (1 draw call): a hollow reticle ring lying flat on the water. Amber-gold so it reads as
    // a distinct "lead here" cue vs the green aim guide. Only ever visible while a valid target is aimed.
    const ring = new THREE.RingGeometry(2.0, 2.7, 28); ring.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffcf5a, transparent: true, opacity: 0, depthWrite: false, blending: THREE.NormalBlending, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(ring, mat);
    mesh.frustumCulled = false; mesh.renderOrder = 4; mesh.visible = false;
    this.scene.add(mesh);
    this.leadGhost = mesh;
  }

  /** Paint the aim-lead ghost on the water at the intercept point (x, z), fading by `alpha` (0 hides it).
   *  Rides the displaced Gerstner sheet like the nav pips so crests never depth-occlude it into a blink. */
  showLeadGhost(x: number, z: number, t: number, alpha: number): void {
    if (alpha <= 0.01) { this.hideLeadGhost(); return; }
    if (!this.leadGhost) this.buildLeadGhost();
    const g = this.leadGhost!;
    const s = oceanSampleAt(x, z, t, this.ampAt(x, z));
    g.position.set(x + s.dx, s.height + 0.35, z + s.dy);
    const pulse = 1 + Math.sin(t * 4.5) * 0.06;     // a slow breathing pulse so the eye catches it
    g.scale.setScalar(pulse);
    (g.material as THREE.MeshBasicMaterial).opacity = alpha;
    g.visible = true;
  }

  hideLeadGhost(): void { if (this.leadGhost) this.leadGhost.visible = false; }

  /** Near-miss geyser: a small vertical spray column where a shot whistled past the local hull. Reuses the
   *  pooled fx meshes (no new draw calls) and, unlike spawnSplash, does NOT fire onSplash — the near-miss
   *  whistle owns the audio so the two never double up. */
  spawnGeyser(x: number, z: number, t: number): void {
    this.splashFx.geyser(x, oceanHeightAt(x, z, t, this.ampAt(x, z)), z);
  }

  /**
   * Water splash where a cannonball ends its flight (miss/landing feedback); bigger if it lands near a hull.
   *
   * Onda Polish: this used to be two expanding SPHERES — a pale ball and a white ball — which from the deck
   * read as a bubble popping, not as a shot hitting the sea. A shot into water throws a COLUMN and the
   * column throws RINGS, and both of those are now instanced (see seafx.ts), so the whole thing costs two
   * draw calls no matter how many balls are in the air.
   */
  spawnSplash(x: number, z: number, t: number, big = false): void {
    this.splashFx.miss(x, oceanHeightAt(x, z, t, this.ampAt(x, z)), z, big);
    this.onSplash?.(x, z, big);
  }

  syncBalls(balls: InterpBall[], t: number): void {
    let i = 0;
    const gen = ++this.ballGen;
    for (const b of balls) {
      let tr = this.ballTrack.get(b.id);
      if (tr) { tr.x = b.x; tr.z = b.y; tr.gen = gen; }                 // mutate in place — no per-frame alloc
      else { tr = { x: b.x, z: b.y, seen: t, gen }; this.ballTrack.set(b.id, tr); }
      if (i < 512) {
        // elevation-driven arc (approx — the client lacks the ball's ammo type, so it uses bare
        // CANNON.SPEED/range: exact for round, slightly off for chain/grape; ball x/y is server-authoritative)
        const age = Math.max(0, t - tr.seen);
        const range = lerp(CANNON.ELEV_RANGE_MIN, CANNON.ELEV_RANGE_MAX, b.elev);
        const progress = Math.min(1, age * CANNON.SPEED / range);
        const arc = lerp(CANNON.ELEV_PEAK_MIN, CANNON.ELEV_PEAK_MAX, b.elev) * Math.sin(Math.PI * progress);
        const by = oceanHeightAt(b.x, b.y, t, this.ampAt(b.x, b.y)) + 0.9 + arc;
        // small iron ball, slightly stretched along its travel direction (a fast-projectile streak)
        this.ballDummy.position.set(b.x, by, b.y);
        this.ballDummy.rotation.set(0, -b.heading, 0);
        this.ballDummy.scale.set(CANNON.BALL_STRETCH, 1, 1);
        this.ballDummy.updateMatrix();
        this.ballDummy.scale.set(1, 1, 1); this.ballDummy.rotation.set(0, 0, 0); // reset shared dummy
        this.balls.setMatrixAt(i, this.ballDummy.matrix);
        i++;
        // faint smoke trail so the small ball stays readable in flight (throttled per ball)
        if (age > 0.04 && t - (tr.puff ?? 0) > 0.05) {
          tr.puff = t;
          this.spawnFx(b.x, by, b.y, 0x9a9a96, 1.1, 0.34, 0, 1.5, 0, true);
        }
      }
    }
    this.balls.count = i;
    this.balls.instanceMatrix.needsUpdate = true;
    // a tracked ball not touched this frame is gone; splash only if it plausibly ENDED its flight
    // (old enough to have landed, or near a hull) so AOI-edge exits don't spawn phantom splashes
    for (const [id, tr] of this.ballTrack) {
      if (tr.gen === gen) continue;
      let near = false;
      for (const g of this.ships.values()) {
        const dx = g.position.x - tr.x, dz = g.position.z - tr.z;
        if (dx * dx + dz * dz < 18 * 18) { near = true; break; }
      }
      if (near || t - tr.seen >= CANNON.LIFETIME * 0.5) this.spawnSplash(tr.x, tr.z, t, near);
      this.ballTrack.delete(id);
    }
  }

  /* --------------------------------- fx --------------------------------- */

  /** Cannonball strikes a hull. Layered, fast & readable: a 1-2 frame WHITE flash, a brief amber
   * core, wood splinters + sparks spraying DIRECTIONALLY along the ball's travel (a cone, not a
   * sphere), then a subtle trailing smoke. `confirmed` (you landed it) makes it punchier.
   * (dirX,dirZ) = impact direction (ball travel); zero falls back to a radial burst. */
  spawnHit(x: number, z: number, t: number, confirmed = false, dirX = 0, dirZ = 0): void {
    const seaY = oceanHeightAt(x, z, t, this.ampAt(x, z));
    const y = seaY + 1.4;
    // ALL OF IT IS INSTANCED NOW — flash, splinter cone, sparks, waterline spray, the ring and the powder
    // smoke all live in seafx.ts's two buffers. A confirmed hit costs the system's TWO constant draw calls
    // instead of FOURTEEN pooled single-mesh particles (forty for a three-ball broadside), and the two
    // faceted low-poly spheres it used to hang off the hull (an amber ball and a grey one) are gone.
    this.splashFx.hit(x, y, z, seaY, dirX, dirZ, confirmed);
  }
  spawnSink(x: number, z: number, t: number): void { this.spawnFx(x, oceanHeightAt(x, z, t, this.ampAt(x, z)), z, 0x335566, 16, 1.1); }

  /* ---------------------------- sinking cinematic ----------------------------
   * A readable 3-phase wreck (DAMAGE -> BREAK -> SINK). The hull splits into a bow + stern chunk via
   * per-chunk clipping planes on a clone of the live model (no physics, no new meshes). All cosmetic. */

  /** Begin the sinking cinematic for ship `id` at (x,z). The wreck owns the visual until it disposes. `clsHint`
   *  lets a caller (the Kill-Cam, driving the sink on a scrubbed clock after the victim already despawned) name
   *  the victim's real hull class so the wreck isn't a generic galleon. */
  spawnWreck(id: number, x: number, z: number, t: number, clsHint?: number): void {
    const g = this.ships.get(id);
    const cls = clsHint ?? (g?.userData.cls as number) ?? ShipClassId.GALLEON;
    const heading = g ? -g.rotation.y : 0;
    const scale = g ? (g.scale.x || 1) : classScale(cls);
    if (g) { this.shipsRoot.remove(g); disposeGroup(g); this.ships.delete(id); } // hand the visual to the wreck
    this.sinkingIds.add(id);
    const bow = this.buildWreckChunk(cls, +1, x, z, heading, scale);
    const stern = this.buildWreckChunk(cls, -1, x, z, heading, scale);
    const fire: THREE.Sprite[] = [];
    for (let i = 0; i < SINK_FX.FIRE_N; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex!, color: 0xff7a20, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, fog: false }));
      s.visible = false; this.scene.add(s); fire.push(s);
    }
    const foam = new THREE.Mesh(this.foamQuad, new THREE.MeshBasicMaterial({ map: this.foamTex!, transparent: true, opacity: 0, depthWrite: false, fog: false }));
    foam.visible = false; foam.renderOrder = 2; this.wakesRoot.add(foam);
    this.wrecks.push({ id, t0: t, x, z, heading, scale, bow, stern, broke: false, fire, foam, smokeAcc: 0, bubbleAcc: 0 });
  }

  /** A clipped hull chunk: a clone of the class template whose cloned materials are cut by a plane at
   *  midship (group-local x=0). sign +1 keeps the bow (x>=0), -1 keeps the stern. */
  private buildWreckChunk(cls: number, sign: number, x: number, z: number, heading: number, scale: number): WreckChunk {
    const entry = this.shipTemplates.get(cls) ?? this.shipTemplates.get(ShipClassId.GALLEON) ?? this.shipTemplates.values().next().value;
    const group = new THREE.Group(); group.rotation.order = 'YXZ';
    const worldPlane = new THREE.Plane();
    const localPlane = new THREE.Plane(new THREE.Vector3(sign, 0, 0), SINK_FX.SEAM_OVERLAP); // overlap -> no seam gap
    const mats: THREE.Material[] = [];
    if (entry) {
      const inst = entry.template.clone(true);
      inst.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || !m.material) return;
        const cl = (mt: THREE.Material): THREE.Material => { const c = mt.clone(); c.clippingPlanes = [worldPlane]; c.transparent = true; mats.push(c); return c; };
        m.material = Array.isArray(m.material) ? m.material.map(cl) : cl(m.material);
      });
      group.add(inst);
    }
    group.scale.setScalar(scale);
    group.rotation.set(0, -heading, 0, 'YXZ');
    group.position.set(x, 0, z);
    this.shipsRoot.add(group);
    return { group, localPlane, worldPlane, mats, sign };
  }

  private updateWrecks(t: number, dt: number): void {
    const total = SINK_FX.DAMAGE_S + SINK_FX.SINK_S;
    for (let i = this.wrecks.length - 1; i >= 0; i--) {
      const w = this.wrecks[i]!;
      const age = t - w.t0;
      const surfY = oceanHeightAt(w.x, w.z, t, this.ampAt(w.x, w.z));
      const fwx = Math.cos(w.heading), fwz = Math.sin(w.heading);
      if (age < SINK_FX.DAMAGE_S) {
        // PHASE 1 — DAMAGE: the stricken hull lists + dips, burns, smokes (both clipped halves overlap = whole ship)
        const p = age / SINK_FX.DAMAGE_S;
        for (const ch of [w.bow, w.stern]) {
          ch.group.position.set(w.x, surfY, w.z);
          ch.group.rotation.set(SINK_FX.LIST_ANGLE * p, -w.heading, -SINK_FX.BOW_DIP * p, 'YXZ'); // x=roll, z=pitch
          for (const m of ch.mats) m.opacity = 1;
        }
        this.updateWreckFire(w, surfY, p, t);
        w.smokeAcc += dt * SINK_FX.SMOKE_HZ;
        while (w.smokeAcc >= 1) { w.smokeAcc -= 1; this.spawnSmoke(w.x + Math.sin(t * 7 + w.id) * 3, surfY + 4, w.z + Math.cos(t * 5 + w.id) * 3); }
      } else {
        // BREAK (once) then PHASE 3 — SINK
        if (!w.broke) { w.broke = true; this.doWreckBreak(w, surfY); }
        const sp = Math.min(1, (age - SINK_FX.DAMAGE_S) / SINK_FX.SINK_S);
        const sepEase = Math.min(1, sp * 1.9);        // chunks snap apart EARLY -> the break reads at the surface
        const descEase = Math.pow(sp, 1.7);           // then a heavy, delayed plunge (partial float before going under)
        const descend = SINK_FX.DEPTH * descEase;
        const place = (ch: WreckChunk, pitchSign: number) => {
          const sep = SINK_FX.SEPARATE * sepEase * ch.sign;
          ch.group.position.set(w.x + fwx * sep, surfY - descend, w.z + fwz * sep);
          ch.group.rotation.set(SINK_FX.LIST_ANGLE + SINK_FX.BREAK_ROLL * sepEase * ch.sign, -w.heading, pitchSign * SINK_FX.BREAK_PITCH * sepEase, 'YXZ');
          const op = 1 - Math.max(0, (descend - 6) / (SINK_FX.DEPTH - 6)); // fade out only once it's deep under
          for (const m of ch.mats) m.opacity = Math.max(0, op);
        };
        place(w.bow, -1);   // bow end lifts (broken stern-side drops)
        place(w.stern, +1); // stern end lifts (broken bow-side drops)
        this.updateWreckFire(w, surfY, Math.max(0, 1 - sp * 2.2), t); // fire dies early in the sink
        w.bubbleAcc += dt * SINK_FX.BUBBLE_HZ * (1 - sp);
        while (w.bubbleAcc >= 1) { w.bubbleAcc -= 1; this.spawnBubble(w.x + (Math.sin(t * 31 + w.bubbleAcc * 9)) * 6, surfY, w.z + (Math.cos(t * 27 + w.bubbleAcc * 7)) * 6); }
        const fr = (8 + sepEase * 22) * w.scale;
        const sf = oceanSampleAt(w.x, w.z, t, this.ampAt(w.x, w.z)); // ride the displaced sheet (no crest depth-fight)
        w.foam.position.set(w.x + sf.dx, sf.height + 0.12, w.z + sf.dy); w.foam.scale.set(fr, 1, fr);
        (w.foam.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.5 * (1 - sp));
      }
      for (const ch of [w.bow, w.stern]) { ch.group.updateMatrixWorld(true); ch.worldPlane.copy(ch.localPlane).applyMatrix4(ch.group.matrixWorld); }
      if (age >= total) { this.disposeWreck(w); this.wrecks.splice(i, 1); this.sinkingIds.delete(w.id); }
    }
  }

  private updateWreckFire(w: Wreck, surfY: number, intensity: number, t: number): void {
    const fwx = Math.cos(w.heading), fwz = Math.sin(w.heading), n = w.fire.length;
    for (let i = 0; i < n; i++) {
      const s = w.fire[i]!;
      if (intensity <= 0.02) { s.visible = false; continue; }
      const along = (i / (n - 1) - 0.5) * HALF_L * 1.3 * w.scale;
      const fl = 0.7 + 0.3 * Math.sin(t * 18 + i * 2.1);
      s.visible = true;
      s.position.set(w.x + fwx * along + Math.sin(t * 9 + i) * 0.6, surfY + 3 + Math.sin(t * 12 + i) * 0.5, w.z + fwz * along + Math.cos(t * 7 + i) * 0.6);
      s.scale.setScalar((4 + 2 * Math.sin(t * 15 + i)) * w.scale * (0.6 + 0.4 * intensity));
      (s.material as THREE.SpriteMaterial).opacity = intensity * fl * 0.9;
    }
  }

  private doWreckBreak(w: Wreck, surfY: number): void {
    for (let i = 0; i < SINK_FX.SPLASH_N; i++) { const a = (i / SINK_FX.SPLASH_N) * Math.PI * 2, r = (i % 5) * 1.6; this.spawnFx(w.x + Math.cos(a) * r, surfY + 1, w.z + Math.sin(a) * r, 0xbcd6e8, 5 + (i % 4), 0.6 + (i % 3) * 0.15, Math.cos(a) * 6, 4 + (i % 4), Math.sin(a) * 6, true); }
    for (let i = 0; i < SINK_FX.DEBRIS_N; i++) { const a = (i / SINK_FX.DEBRIS_N) * Math.PI * 2, sp = 8 + (i % 5) * 2; this.spawnFx(w.x, surfY + 2, w.z, 0x5a3d22, 1.2 + (i % 3) * 0.4, 1.4, Math.cos(a) * sp, 9 + (i % 4) * 2, Math.sin(a) * sp, false); }
    w.foam.visible = true;
    // camera punch/shake/slow-mo, scaled by how close the wreck is (a far-off sink doesn't jolt your view)
    const dist = Math.hypot(w.x - this.camera.position.x, w.z - this.camera.position.z), near = Math.max(0, 1 - dist / 130);
    if (near > 0.02) { this.addShake(SINK_FX.SHAKE * near); this.cameraPunch(2.4 * near); if (near > 0.55) this.hitStop(SINK_FX.SLOWMO_S); }
  }

  private spawnSmoke(x: number, y: number, z: number): void { this.spawnFx(x, y, z, 0x1c1c1c, 5.5, 1.5, (x % 2 ? 1 : -1) * 1.5, 6, 0, true); }
  private spawnBubble(x: number, y: number, z: number): void { this.spawnFx(x, y, z, 0xdceff6, 0.5, 0.9, 0, 4.5, 0, true); }

  private disposeWreck(w: Wreck): void {
    for (const ch of [w.bow, w.stern]) { this.shipsRoot.remove(ch.group); for (const m of ch.mats) m.dispose(); } // geometries are shared with the template — do NOT dispose
    for (const s of w.fire) { this.scene.remove(s); (s.material as THREE.Material).dispose(); }
    this.wakesRoot.remove(w.foam); (w.foam.material as THREE.Material).dispose();
  }

  /** Dispose EVERY active sinking cinematic and clear the sinking-id claims. The Kill-Cam calls this so a replay
   *  starts from a clean slate (no stale wreck aged by the scrubbed clock) and drives its OWN wreck via spawnWreck
   *  at the exact sink moment — so the clip can film the naufrágio to the end. Cheap; wrecks are few. */
  clearWrecks(): void {
    for (const w of this.wrecks) this.disposeWreck(w);
    this.wrecks.length = 0;
    this.sinkingIds.clear();
  }
  /** Is a given ship id currently owned by a sinking cinematic? (the Kill-Cam checks before spawning its own). */
  isWreck(id: number): boolean { return this.sinkingIds.has(id); }
  /** Kill-Cam: hide the persistent divable (player-sunk) wrecks for the duration of a replay. A real sink spawns a
   *  death-spot salvage wreck (server EV_WRECK_SPAWN → dynamicWreckRoot) that lingers for minutes; the replay rewinds
   *  ~12 s and plays forward, so without this that half-submerged hull sits at the kill site through the WHOLE clip —
   *  visible BEFORE the ship sinks. The clip's sink is driven solely by the wreck CINEMATIC (spawnWreck), so the
   *  divable hull must stay hidden until the sea resumes (restored on replay close). Cosmetic, reversible, cheap. */
  setDynamicWrecksVisible(visible: boolean): void { this.dynamicWreckRoot.visible = visible; }
  /** Muzzle blast, tinted by ammo: round=amber, chain=cold grey, grape=bright. Hot core + flash + drifting smoke. */
  spawnFire(x: number, z: number, t: number, ammo = 0): void {
    const color = ammo === AmmoType.CHAIN ? 0xbcd0dc : ammo === AmmoType.GRAPE ? 0xffcf6a : 0xffe0a0;
    const size = ammo === AmmoType.GRAPE ? 5.5 : 4;
    const y = oceanHeightAt(x, z, t, this.ampAt(x, z)) + 3;
    this.spawnFx(x, y, z, 0xffffff, size * 0.65, 0.1);        // hot white core (brief)
    this.spawnFx(x, y, z, color, size, 0.28);                 // the flash
    this.spawnFx(x, y + 0.7, z, 0x707880, size * 1.25, 0.75); // lingering powder smoke
  }
  /** Hull-vs-hull ram impact: a foamy splash scaled by the ram magnitude (0-255). */
  spawnRam(x: number, z: number, t: number, mag = 128): void {
    const k = Math.max(0.25, mag / 255);
    this.spawnFx(x, oceanHeightAt(x, z, t, this.ampAt(x, z)) + 1.5, z, 0xdfeffa, 9 + 12 * k, 0.6);
  }

  /** Raid drama (feature 5): a burst of gold coins arcing from a plundered storehouse at (x,z) to the
   *  thief ship, plus smoke/embers on the storehouse (a stand-in for "this building is taking a beating").
   *  Called ~3×/s per active raid from the PLUNDER event. Alloc-light — reuses the shared FX pool. */
  plunderStream(thiefId: number, x: number, z: number, t: number): void {
    const surf = oceanHeightAt(x, z, t, this.ampAt(x, z));
    // storehouse under fire: a wisp of dark smoke + a couple of embers
    this.spawnFx(x + (Math.random() - 0.5) * 3, surf + 2.5 + Math.random() * 2, z + (Math.random() - 0.5) * 3, 0x1a1a1a, 4.5, 1.1, 0, 3.2, 0, true);
    this.spawnFx(x + (Math.random() - 0.5) * 2, surf + 1.6, z + (Math.random() - 0.5) * 2, 0xff7a2a, 1.1, 0.5, (Math.random() - 0.5) * 3, 4, (Math.random() - 0.5) * 3, false);
    const ship = this.ships.get(thiefId);
    if (!ship) return;
    const tx = ship.position.x, tz = ship.position.z, ty = ship.position.y + 1.5;
    const dx = tx - x, dz = tz - z, dist = Math.hypot(dx, dz) || 1;
    const life = Math.max(0.4, Math.min(1.6, dist / 24));
    const startY = surf + 2;
    for (let i = 0; i < 3; i++) {
      const jx = (Math.random() - 0.5) * 3, jz = (Math.random() - 0.5) * 3;
      const vy = (ty - startY) / life + 0.5 * 24 * life; // ballistic arc landing at the ship after `life`s
      this.spawnFx(x + jx, startY, z + jz, i === 0 ? 0xfff0b0 : 0xffcf5a, 0.7, life, dx / life, vy, dz / life, false);
    }
  }

  /* --------------------- combat-feel "juice" (cosmetic) --------------------- */

  /** Kick the camera with a short, fast-decaying shake (world units of jitter). */
  addShake(amt: number): void { this.shakeAmt = Math.min(2.6, this.shakeAmt + amt); }

  /* ---- riding the vaga: a shudder when a big crest passes under the hull ---- */
  private _rideY = NaN;      // sea height at the hull last frame
  private _rideVy = 0;       // its vertical rate (m/s), smoothed
  private _rideCd = 0;       // cooldown so one crest kicks once, not every frame

  /**
   * Feel the swell. When a storm crest rolls under the hull the sea's vertical rate REVERSES hard at the
   * top; that is the instant a real deck lurches. Trigger a very short shake on that reversal, scaled by
   * how violent it was and by the storm.
   *
   * Deliberately tiny and cooldown-gated: motion sickness is the enemy here, and a shudder that fires on
   * every wavelet stops reading as a wave at all. It is also derived from the SEA, not from a timer, so
   * it lands exactly when the picture shows the crest — which is what sells it.
   */
  private rideCrest(seaY: number, dt: number): void {
    if (this._storm < 0.35 || dt <= 0) { this._rideY = seaY; this._rideCd = 0; return; }
    if (Number.isNaN(this._rideY)) { this._rideY = seaY; return; }
    const vy = (seaY - this._rideY) / dt;
    this._rideY = seaY;
    const prev = this._rideVy;
    this._rideVy = lerp(this._rideVy, vy, Math.min(1, dt * 14));
    this._rideCd -= dt;
    // crest = the rate crossing from rising to falling, and doing it FAST (a lazy swell must not fire)
    if (this._rideCd > 0 || !(prev > 0.55 && this._rideVy <= 0)) return;
    this._rideCd = 1.1;
    this.addShake(Math.min(0.32, 0.09 + prev * 0.05) * this._storm);
  }
  /** Micro hit-stop: slows the impact FX for a beat so a hit reads as a punch. */
  hitStop(sec: number): void { this.hitstopT = Math.max(this.hitstopT, sec); }
  /** Impact dolly-in punch. Sets the TARGET only — the applied kick RAMPS toward it (updateCamera), so the
   *  camera never jumps a full frame on the entry the way the old instant `camPunch = amt` did ("zoom weird" on
   *  every shot/hit). Scaled down a touch on mobile, where the small viewport makes the same kick read harsher. */
  cameraPunch(amt: number): void {
    this.camPunchT = Math.max(this.camPunchT, amt * (this.isMobile ? CAM_PUNCH.MOBILE_SCALE : 1));
  }

  /** Stamp a transient cosmetic recoil on a ship mesh (knockback nudge + tilt that eases back).
   *  Pure render: the authoritative position is untouched; this only offsets the drawn hull. */
  shipRecoil(id: number, dirX: number, dirZ: number, mag: number, t: number): void {
    const g = this.ships.get(id);
    if (!g) return;
    const len = Math.hypot(dirX, dirZ) || 1;
    g.userData.recoilX = dirX / len;
    g.userData.recoilZ = dirZ / len;
    g.userData.recoilPeak = Math.min(1.1, 0.4 + mag * 0.5); // metres of knockback
    g.userData.recoilT0 = t;
  }

  // grows=true: a puff that expands+fades (smoke/splash). grows=false: a debris bit that flies on (vx,vy,vz)
  // under gravity and keeps its size (wood splinters).
  private spawnFx(x: number, y: number, z: number, color: number, size: number, life: number, vx = 0, vy = 0, vz = 0, grows = true): void {
    let mesh = this.fxPool.pop();
    if (!mesh) {
      mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 8, 6), new THREE.MeshBasicMaterial({ transparent: true }));
      this.scene.add(mesh);
    }
    const mat = mesh.material as THREE.MeshBasicMaterial;
    mat.color.setHex(color);
    mat.opacity = 0.85;
    mesh.scale.setScalar(size * 0.3);
    mesh.position.set(x, y, z);
    mesh.visible = true;
    this.fx.push({ mesh, life, maxLife: life, grow: size, vx, vy, vz, grows, a0: 0.85 });
  }

  /** SOFT particle: a camera-facing sprite on the shared foam glow (no hard silhouette), for spray and mist.
   *  Same pooled lifecycle as spawnFx — it just lands in its own pool. */
  private spawnMist(x: number, y: number, z: number, color: number, size: number, life: number, vx = 0, vy = 0, vz = 0, alpha = 0.5, grows = true): void {
    let sp = this.mistPool.pop();
    if (!sp) {
      sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.mistTex!, transparent: true, depthWrite: false, fog: true }));
      this.scene.add(sp);
    }
    const mat = sp.material as THREE.SpriteMaterial;
    mat.color.setHex(color);
    mat.opacity = alpha;
    sp.scale.set(size, size, 1);
    sp.position.set(x, y, z);
    sp.visible = true;
    this.fx.push({ mesh: sp, life, maxLife: life, grow: size, vx, vy, vz, grows, a0: alpha });
  }

  private stepFx(dt: number): void {
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i]!;
      f.life -= dt;
      const k = 1 - f.life / f.maxLife;
      const soft = (f.mesh as THREE.Sprite).isSprite === true;
      const setScale = (v: number): void => { if (soft) f.mesh.scale.set(v, v, 1); else f.mesh.scale.setScalar(v); };
      if (!f.grows) { // debris: fly + fall, constant size
        f.vy -= 24 * dt;
        f.mesh.position.x += f.vx * dt; f.mesh.position.y += f.vy * dt; f.mesh.position.z += f.vz * dt;
        setScale(soft ? f.grow : f.grow * 0.3);
      } else { // puff: grow + fade, drift along its (optional) velocity (e.g. smoke/bubbles rising)
        f.mesh.position.x += f.vx * dt; f.mesh.position.y += f.vy * dt; f.mesh.position.z += f.vz * dt;
        setScale(soft ? f.grow * (0.55 + k * 1.4) : f.grow * (0.3 + k));
      }
      (f.mesh.material as THREE.Material & { opacity: number }).opacity = Math.max(0, f.a0 * (1 - k));
      if (f.life <= 0) {
        f.mesh.visible = false;
        if (soft) this.mistPool.push(f.mesh as THREE.Sprite); else this.fxPool.push(f.mesh as THREE.Mesh);
        this.fx[i] = this.fx[this.fx.length - 1]!;
        this.fx.pop();
      }
    }
  }

  /* ------------------------------- camera ------------------------------- */

  /**
   * Free-orbit camera input bound to the canvas (so the HUD pads/buttons, which sit on top with
   * pointer-events, still work — the canvas only receives events on empty areas):
   *   1-pointer drag / right-mouse drag → 360° yaw orbit + vertical tilt
   *   2-pointer pinch / mouse wheel     → zoom (pinch-together / wheel-down = tactical zoom-out)
   */
  private attachCameraControls(): void {
    const el = this.canvas;
    el.style.touchAction = 'none';
    const pts = new Map<number, { x: number; y: number }>();
    let dragId = -1, lastX = 0, lastY = 0, pinch = 0;
    el.addEventListener('pointerdown', (e) => {
      el.setPointerCapture(e.pointerId); // keep drag/pinch events even if the cursor slides over a HUD element
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 1) { dragId = e.pointerId; lastX = e.clientX; lastY = e.clientY; }
      else if (pts.size === 2) { const v = [...pts.values()], a = v[0]!, b = v[1]!; pinch = Math.hypot(a.x - b.x, a.y - b.y); dragId = -1; }
    });
    el.addEventListener('pointermove', (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size >= 2) {
        const v = [...pts.values()], a = v[0]!, b = v[1]!; const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch > 0 && d > 0) this.zoomBy(pinch / d); // fingers apart → zoom in, together → tactical out
        pinch = d;
      } else if (e.pointerId === dragId) {
        // MMO convention (WoW / Lineage 2): drag RIGHT orbits the camera right, drag DOWN tilts to a top-down
        // view. Sensitivity + horizontal invert come from Settings › Controls (default 1.0× / not inverted =
        // the shipped feel). Vertical tilt is never inverted.
        const sx = 0.005 * this.camSensitivity * (this.invertCamX ? -1 : 1);
        const sy = 0.004 * this.camSensitivity;
        this.orbitBy((e.clientX - lastX) * sx, (e.clientY - lastY) * sy);
        lastX = e.clientX; lastY = e.clientY;
      }
    });
    const up = (e: PointerEvent) => {
      pts.delete(e.pointerId);
      if (e.pointerId === dragId) dragId = -1;
      if (pts.size < 2) pinch = 0;
      if (pts.size === 1) { const [id, p] = [...pts.entries()][0]!; dragId = id; lastX = p.x; lastY = p.y; } // pinch→1 finger: resume orbit cleanly
    };
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    el.addEventListener('wheel', (e) => { e.preventDefault(); this.zoomBy(e.deltaY > 0 ? 1.12 : 0.89); }, { passive: false });
    el.addEventListener('contextmenu', (e) => e.preventDefault()); // allow right-drag orbit without the context menu
  }

  /** Orbit around the ship (yaw is 360°-free; vertical tilt clamped). Damped toward these targets each frame. */
  orbitBy(dYaw: number, dPitch: number): void {
    this.camYawT = normAngle(this.camYawT + dYaw);
    this.camPitchBiasT = clamp(this.camPitchBiasT + dPitch, -0.32, 0.5);
    this.orbitIdle = 0; // you're driving the camera — hold off the auto-recenter
  }
  /** Zoom (factor > 1 = out toward a tactical top-down, < 1 = in). Distance clamped. */
  zoomBy(factor: number): void { this.camDistT = clamp(this.camDistT * factor, 26, 200); }

  /** Aim helper (both platforms): while holding a fire button, swing the camera to look DOWN that broadside
   *  (from the opposite side) so you see the target with no extra input; on release the idle auto-recenter
   *  glides it back behind the ship. side: 0=port, 1=stbd, <0 = not aiming. */
  setAimOrbit(side: number): void {
    if (side < 0) return;
    this.camYawT = side === 0 ? -Math.PI / 2 : Math.PI / 2; // view the firing broadside (opposite of where it fires)
    this.orbitIdle = 0; // hold off the recenter while aiming
  }

  updateCamera(x: number, z: number, heading: number, t: number, dt: number, turning = false): void {
    // auto-recenter: after you stop dragging the camera, glide the orbit target back to "behind the
    // ship" so it never sits stuck at an odd angle (the "camera won't stay put" report).
    this.orbitIdle += dt;
    if (this.orbitIdle > 1.5) {
      const r = 1 - Math.pow(0.2, dt);
      this.camYawT = normAngle(this.camYawT * (1 - r)); // toward 0 = directly behind
      this.camPitchBiasT *= 1 - r;
    }
    this.camYaw = normAngle(this.camYaw + normAngle(this.camYawT - this.camYaw) * (1 - Math.pow(0.0006, dt)));
    this.camPitchBias = lerp(this.camPitchBias, this.camPitchBiasT, 1 - Math.pow(0.0016, dt));
    this.camDist = lerp(this.camDist, this.camDistT, 1 - Math.pow(0.0025, dt));
    // the camera's world yaw eases toward "behind the ship". While TURNING it eases SLOWLY (heavy lag),
    // so the target barely drifts while you rotate to bring the broadside to bear; otherwise it snaps behind.
    // Crucially the rate is ALWAYS > 0 -> it can never get stuck (the old freeze-at-0 version did).
    const behind = normAngle(heading + Math.PI + this.camYaw);
    if (Number.isNaN(this.camFollowYaw)) this.camFollowYaw = behind;
    const k = 1 - Math.pow(turning ? 0.25 : 0.0006, dt);
    this.camFollowYaw = normAngle(this.camFollowYaw + normAngle(behind - this.camFollowYaw) * k);
    // pitch: zoomed-out tilts toward a near-top-down tactical view; the vertical-drag bias adds on top
    const zf = clamp((this.camDist - 26) / (200 - 26), 0, 1);
    const pitch = clamp(lerp(0.52, 1.34, zf * zf) + this.camPitchBias, 0.16, 1.46);
    // an impact "dolly punch" pulls the camera in a touch — the APPLIED kick ramps toward the (decaying) target
    // so it rises AND falls smoothly (no 1-frame snap on entry, the old "punchy zoom" report).
    this.camPunch += (this.camPunchT - this.camPunch) * (1 - Math.pow(CAM_PUNCH.RAMP, dt));
    this.camPunchT *= Math.pow(CAM_PUNCH.DECAY, dt);
    const dist = Math.max(18, this.camDist - this.camPunch);
    // spherical position around the ship, keeping it centered
    const yaw = this.camFollowYaw, cp = Math.cos(pitch);
    // ride the MEAN sea level, not every passing wavelet — otherwise the camera bobs with each wave
    // and never feels "still". Ease the focus height so high-frequency chop is filtered out.
    const seaAtShip = oceanHeightAt(x, z, t, this.ampAt(x, z));
    this.camFocusY = Number.isNaN(this.camFocusY) ? seaAtShip : lerp(this.camFocusY, seaAtShip, 1 - Math.pow(0.02, dt));
    const targetY = this.camFocusY;
    this.rideCrest(seaAtShip, dt);
    let camX = x + Math.cos(yaw) * cp * dist, camZ = z + Math.sin(yaw) * cp * dist;
    let camY = targetY + Math.sin(pitch) * dist;
    const seaY = oceanHeightAt(camX, camZ, t, this.ampAt(camX, camZ)) + 4; // anti-clip: never dip below the sea
    if (camY < seaY) camY = seaY;
    // keep the camera clear of SOLID base islands — don't let it clip through the
    // terrain/buildings (mirrors the ship's island collision). Push it out of the
    // footprint cylinder and lift it a touch so it looks OVER the shore, not into it.
    if (this.lastBases) {
      const rr = BASE_ISLAND_R + CAM_ISLAND_MARGIN;
      for (let i = 0; i < this.lastBases.length; i++) {
        const b = this.lastBases[i]!;
        const dx = camX - b.x, dz = camZ - b.y;
        const d2 = dx * dx + dz * dz;
        if (d2 >= rr * rr) continue;
        const d = Math.sqrt(d2) || 0.001;
        camX = b.x + (dx / d) * rr;
        camZ = b.y + (dz / d) * rr;
        const overLand = oceanHeightAt(camX, camZ, t, this.ampAt(camX, camZ)) + 16; // clear the beach/buildings
        if (camY < overLand) camY = overLand;
      }
    }
    this._scratch.set(camX, camY, camZ);
    this.camera.position.lerp(this._scratch, 1 - Math.pow(0.0016, dt));
    // micro camera-shake on impact: a fast-decaying time-based jitter added on top of the smooth follow
    if (this.shakeAmt > 0.001) {
      this.shakeSeed += dt * 57;
      const s = this.shakeAmt * clamp(dist / 58, 0.5, 2); // scale with zoom so it reads at any distance
      this.camera.position.x += Math.sin(this.shakeSeed * 1.7) * Math.cos(this.shakeSeed * 0.9) * s;
      this.camera.position.y += Math.sin(this.shakeSeed * 2.3 + 1.3) * s * 0.6;
      this.camera.position.z += Math.cos(this.shakeSeed * 1.9 + 0.5) * s;
      this.shakeAmt *= Math.pow(0.00012, dt);
    }
    // storm camera sway: a slow, gentle drift on top of the smooth follow — you FEEL the swell,
    // never enough to fight (scaled by intensity + zoom, no rotational roll so it can't nauseate).
    if (this._storm > 0.01) {
      const sw = this._storm, zs = clamp(dist / 58, 0.5, 1.6);
      this.camera.position.x += Math.sin(t * 1.25) * STORM.CAM_SWAY_POS * sw * zs;
      this.camera.position.y += Math.sin(t * 0.85 + 1.0) * STORM.CAM_SWAY_POS * sw;
      this.camera.position.z += Math.cos(t * 1.05 + 0.4) * STORM.CAM_SWAY_POS * sw * zs;
    }
    const lookSway = this._storm > 0.01 ? Math.sin(t * 0.7) * STORM.CAM_SWAY_ROT * 60 * this._storm : 0;
    this._scratch.set(x + lookSway, targetY + 5, z);
    if (this.camLook.lengthSq() === 0) this.camLook.copy(this._scratch);
    this.camLook.lerp(this._scratch, 1 - Math.pow(0.0011, dt));
    this.camera.lookAt(this.camLook);
  }

  /** Seat the follow camera DIRECTLY BEHIND a freshly (re)spawned ship so the very first frame is already
   *  framed for sailing — hull centred, open sea ahead — instead of gliding in from the stale previous view.
   *  Spawning at your base, that glide meant the first ~3s stared into your own mill/cannon (the "spawn camera
   *  buried in the base" report). We reset orbit state to a settled neutral, snap position + look-at with no
   *  lerp, and seat it a touch further out so updateCamera's easing gives a gentle dolly-in "settle" (no later
   *  jump). Camera is pushed clear of the base-island footprint, same as the per-frame follow. */
  resetCameraBehind(x: number, z: number, heading: number, t: number): void {
    // orbit → settled neutral: directly behind, resting zoom, no leftover drag-tilt or mid-recenter glide.
    this.camYaw = 0; this.camYawT = 0;
    this.camPitchBias = 0; this.camPitchBiasT = 0;
    this.camDist = 58; this.camDistT = 58;
    this.camPunch = 0; this.camPunchT = 0;
    this.orbitIdle = 99;                                 // already behind — skip the auto-recenter glide
    this.camFollowYaw = normAngle(heading + Math.PI);    // world yaw straight behind the bow-forward ship
    // reproduce updateCamera's placement for a settled, behind view (kept in lockstep with that math).
    const zf = clamp((this.camDist - 26) / (200 - 26), 0, 1);
    const pitch = clamp(lerp(0.52, 1.34, zf * zf), 0.16, 1.46);
    const cp = Math.cos(pitch);
    const seaAtShip = oceanHeightAt(x, z, t, this.ampAt(x, z));
    this.camFocusY = seaAtShip;
    const dist = this.camDist * 1.1;                     // seat slightly back → a brief, gentle settle dolly-in
    let camX = x + Math.cos(this.camFollowYaw) * cp * dist;
    let camZ = z + Math.sin(this.camFollowYaw) * cp * dist;
    let camY = seaAtShip + Math.sin(pitch) * dist;
    const seaY = oceanHeightAt(camX, camZ, t, this.ampAt(camX, camZ)) + 4;
    if (camY < seaY) camY = seaY;
    if (this.lastBases) {                                // keep clear of the base island just launched from
      const rr = BASE_ISLAND_R + CAM_ISLAND_MARGIN;
      for (let i = 0; i < this.lastBases.length; i++) {
        const b = this.lastBases[i]!;
        const dx = camX - b.x, dz = camZ - b.y;
        const d2 = dx * dx + dz * dz;
        if (d2 >= rr * rr) continue;
        const d = Math.sqrt(d2) || 0.001;
        camX = b.x + (dx / d) * rr;
        camZ = b.y + (dz / d) * rr;
        const overLand = oceanHeightAt(camX, camZ, t, this.ampAt(camX, camZ)) + 16;
        if (camY < overLand) camY = overLand;
      }
    }
    this.camera.position.set(camX, camY, camZ);          // hard snap — no lerp-in from the old view
    this.camLook.set(x, seaAtShip + 5, z);
    this.camera.lookAt(this.camLook);
  }

  /* -------------------------------- frame ------------------------------- */

  /** A faint star dome (1 draw call) re-centred on the camera each frame so it reads as infinitely
   *  far; the day/night cycle fades its opacity in at night. */
  private buildStars(): THREE.Points {
    const N = 700, pos = new Float32Array(N * 3), r = 900;
    for (let i = 0; i < N; i++) {
      const u = Math.random(), a = Math.random() * Math.PI * 2, h = Math.sqrt(Math.max(0, 1 - u * u));
      pos[i * 3] = Math.cos(a) * h * r;
      pos[i * 3 + 1] = u * r * 0.85 + 50;      // upper dome only (above the horizon)
      pos[i * 3 + 2] = Math.sin(a) * h * r;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const m = new THREE.PointsMaterial({ color: 0xffffff, size: 2.4, sizeAttenuation: false, transparent: true, opacity: 0, depthWrite: false, fog: false });
    const pts = new THREE.Points(g, m);
    pts.frustumCulled = false; pts.renderOrder = -1; pts.visible = false;
    return pts;
  }

  /** A handful of foam flecks that drift along the trade-wind current — the visible, ambushable route.
   *  World-anchored Points (1 draw call); shown only when the ship sits in a lane. Subtle, not neon. */
  private buildTradeFoam(): THREE.Points {
    const N = 90, pos = new Float32Array(N * 3); // positions filled in on the first update (around the ship)
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    const m = new THREE.PointsMaterial({ color: 0xdce8f0, map: this.makeGlowTexture('rgba(255,255,255,0.85)', 'rgba(210,235,250,0)'), size: 5.5, sizeAttenuation: true, transparent: true, opacity: 0, depthWrite: false, fog: true, blending: THREE.NormalBlending });
    const pts = new THREE.Points(g, m);
    pts.frustumCulled = false; pts.renderOrder = 2; pts.visible = false;
    this.tradeFoamPos = pos;
    return pts;
  }

  /** Advect the foam along the local lane current (client-FX; Math.random ok). Hidden outside a lane. */
  private updateTradeFoam(cx: number, cz: number, t: number, dt: number): void {
    const foam = this.tradeFoam, pos = this.tradeFoamPos;
    if (!foam || !pos) return;
    const dir = tradeLaneDirAt(cx, cz);           // shares the sim's lane geometry — foam flows with the real boost
    const target = dir ? 0.24 : 0;
    this.tradeFoamOpacity += (target - this.tradeFoamOpacity) * Math.min(1, dt * 3);
    const mat = foam.material as THREE.PointsMaterial;
    mat.opacity = this.tradeFoamOpacity * (1 - this._storm * 0.6); // storm chop hides the delicate streaks
    if (this.tradeFoamOpacity < 0.01) { foam.visible = false; return; }
    foam.visible = true;
    const R = 150, R2 = R * R, drift = 9; // m: local disk radius + current drift speed
    const dvx = (dir ? dir.dx : 0) * drift * dt, dvz = (dir ? dir.dy : 0) * drift * dt;
    for (let i = 0; i < pos.length; i += 3) {
      let wx = pos[i]!, wz = pos[i + 2]!;
      // (re)seed uninitialised or out-of-range flecks somewhere in the disk around the ship
      const ddx = wx - cx, ddz = wz - cz;
      if (!(ddx * ddx + ddz * ddz < R2) || (wx === 0 && wz === 0)) {
        const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * R;
        wx = cx + Math.cos(a) * r; wz = cz + Math.sin(a) * r;
      } else { wx += dvx; wz += dvz; }
      pos[i] = wx; pos[i + 2] = wz;
      pos[i + 1] = oceanHeightAt(wx, wz, t, this.ampAt(wx, wz)) + 0.35; // ride the surface
    }
    (foam.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Frente 3b: set the wind the streaks stream with (from the snapshot header, via main). Cheap; per-frame. */
  /** The authoritative wind, from the snapshot. Nothing is DRAWN for it: it drives the cat's paws, the
   *  sails, the pennants, the rain slant, every drifting noise domain and the gulls' crab angle. */
  setWind(dir: number, strength: number): void { this.windDir = dir; this.windStrength = strength; }

  /* ------------------------------ atmosphere ----------------------------- */

  /**
   * ☁️ THE SKY DOME — one draw call for the entire sky (Onda Céu v1).
   *
   * The sky is half the screen and the sea MIRRORS it (`uSkyTop`/`uFog` on the ocean are fed from the very
   * same two colours this shader gradients between), so every improvement here is paid for twice. It
   * replaces four separate objects that used to disagree with each other:
   *   the cloud/gradient dome, a sun SPRITE, a moon SPRITE and a 700-point star cloud → ONE mesh, ONE
   *   draw call. (Net −3 draws, and the sun in the sky is now literally the same vector as the sun in the
   *   water's specular — before, the sprite followed `uSun` while the disc had no size, phase or limb.)
   *
   * WHAT IS IN HERE
   *   1. an atmospheric gradient anchored EXACTLY at `uHorizon` on the skyline (see THE SEAL below),
   *   2. a sun-anchored horizon lobe — a real dawn is brightest where the sun IS, not evenly all round,
   *   3. the sun: a disc with a limb, a tight Mie halo and a wide forward-scatter bloom,
   *   4. 3 cloud layers (cumulus / mid / cirrus veil) of cheap FBM, each DRIFTING WITH THE LIVE WIND and
   *      streaked along it, sun-shaded so a cumulus has a lit side,
   *   5. night: a procedural star field (hashed cells, per-star magnitude + twinkle), a faint galactic
   *      band, and a MOON with a real phase — a sphere lit by `uMoonLight`, so the terminator is the
   *      correct ellipse and the crescent tilts,
   *   6. the storm layers (scud deck, squall shelf, under-lighting, rain haze) and the lightning glow,
   *      unchanged in behaviour — they now sit on top of a sky worth darkening.
   *
   * THE SEAL (the owner's "vertical bands on the horizon, like transparent walls around the map").
   * The old dome projected its clouds through `vDir.xz / max(0.22, elev + 0.06)`. That divide runs away as
   * the ray flattens, so a band of sky a few degrees above the skyline sampled the noise field at enormous
   * rates — and the wisp layer was stretched 3.4× ALONG WORLD Z, i.e. axis-aligned. The two together
   * printed a ring of tall translucent curtains standing on the horizon, once per screen-width, in every
   * direction: exactly the "fenced mesh" read. Three fixes, all here:
   *   (a) the projection floor is now large enough that the cloud plane never runs away (`gy + 0.16`),
   *   (b) the streak axis is the WIND, not world Z, and the stretch is 2.1 not 3.4,
   *   (c) every cloud layer is faded to nothing BELOW `elev ≈ 0.20`, and the last 3° of sky is forced to
   *       exactly `uHorizon` — which is the exact colour the far sea is forced to (`uFog`). Sky and sea
   *       meet at one number, so there is no step, no curtain and no band to see at any camera height.
   *
   * COST. One full-screen-ish backdrop, ALU only, zero textures. The `high` flag drops the three FBM cloud
   * layers, the galactic band and the storm's noise layers for a gradient + sun + moon + stars dome on
   * `?water=low` / Settings › Graphics › Low (they ride the same lever as the sea — one quality opinion).
   */
  private buildSkyDome(high: boolean): THREE.Mesh {
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSky: { value: new THREE.Color(0x9ec7e6) },       // ZENITH — the ocean's uSkyTop reads the same colour
        uHorizon: { value: new THREE.Color(0xffffff) },   // SKYLINE — the ocean's uFog reads the same colour
        uCloud: { value: new THREE.Color(0xf2f6ff) },
        uCover: { value: 0.5 }, uOpacity: { value: 0.55 },
        uDrift: { value: new THREE.Vector2() },           // wind-integrated metres (shared with the sea's foam/paws)
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },   // THE sun — the same vector the water glitters on
        uSunCol: { value: new THREE.Color(0xfff2d8) },
        uSunAmt: { value: 1 },
        uMoonDir: { value: new THREE.Vector3(0, 1, 0) },
        uMoonLight: { value: new THREE.Vector3(0, 0, -1) }, // where the moon's own sunlight comes from → its phase
        uMoonCol: { value: new THREE.Color(0xf4f7ff) },
        uMoonAmt: { value: 0 },
        uNight: { value: 0 },
        // 🌌 THE DISPLAY. uAuroraAmt 0 ⇒ the whole aurora is one compare per fragment; uAurBreak is the
        // substorm beat (spent on motion, never on brightness); uAurZ/T/B is the magnetic-zenith frame
        // every ray converges on; uAurLo/Mid/Hi are the three altitude colours; uAurPx is the camera's
        // own pixel angle, which is how the rays antialias without a derivative (see the shader).
        uAuroraAmt: { value: 0 }, uAurBreak: { value: 0 }, uAurPx: { value: 0.001 },
        uAurZ: { value: AUR_FRAME.z.clone() }, uAurT: { value: AUR_FRAME.t.clone() }, uAurB: { value: AUR_FRAME.b.clone() },
        uAurLo: { value: AUR_LO.clone() }, uAurMid: { value: AUR_MID.clone() }, uAurHi: { value: AUR_HI.clone() },
        uStorm: { value: 0 },
        uFlash: { value: 0 },
        uFlashDir: { value: new THREE.Vector2(1, 0) },
      },
      vertexShader: 'varying vec3 vDir; void main(){ vDir = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime, uCover, uOpacity, uAuroraAmt, uAurBreak, uAurPx, uStorm, uFlash, uNight, uSunAmt, uMoonAmt;
        uniform vec2 uFlashDir, uDrift;
        uniform vec3 uSky, uCloud, uHorizon, uSunDir, uSunCol, uMoonDir, uMoonLight, uMoonCol;
        uniform vec3 uAurZ, uAurT, uAurB, uAurLo, uAurMid, uAurHi;
        varying vec3 vDir;

        float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
        float hash3(vec3 p){ return fract(sin(dot(p, vec3(127.1,311.7,74.7)))*43758.5453); }
        float noise(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f);
          float a=hash(i), b=hash(i+vec2(1,0)), c=hash(i+vec2(0,1)), d=hash(i+vec2(1,1));
          return mix(mix(a,b,f.x), mix(c,d,f.x), f.y); }
        float fbm(vec2 p){ float v=0.0, a=0.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.03; a*=0.5; } return v; }
        float fbm3(vec2 p){ float v=0.0, a=0.5; for(int i=0;i<3;i++){ v+=a*noise(p); p*=2.11; a*=0.5; } return v; }
${AURORA_GLSL}

        void main(){
          vec3 dir = normalize(vDir);
          float elev = dir.y;
          if (elev < -0.14) discard;                 // the ocean owns below; a little overlap for a clean seam
          float gy = max(elev, 0.0);                 // the gradient is anchored AT the skyline (see THE SEAL)

          // ---------------------------- 1. THE ATMOSPHERIC GRADIENT ----------------------------
          // uHorizon at elev 0 exactly (the number the far sea is forced to), climbing to the zenith.
          // The extra deepening at the very top is what a real sky does — the air column is thinnest
          // straight up, so it scatters least and reads darker and more saturated than the mid sky.
          vec3 col = mix(uHorizon, uSky, smoothstep(0.0, 0.44, gy));
          col = mix(col, uSky * 0.82, smoothstep(0.42, 1.0, gy) * 0.42);

          // ------------------------ 2. THE SUN-ANCHORED HORIZON LOBE ---------------------------
          // A dawn is not a ring: the fire is where the sun is, and the sky opposite it stays cold. The
          // lobe is faded to nothing by elev 0 so the skyline colour stays EXACTLY uHorizon (the seal).
          vec2 az = normalize(dir.xz + 1e-5);
          vec2 saz = normalize(uSunDir.xz + 1e-5);
          float toSun = 0.5 + 0.5 * dot(az, saz);
          float lowSun = 1.0 - smoothstep(0.02, 0.42, uSunDir.y);      // strongest when the sun is on the deck
          float lobe = pow(toSun, 3.0) * smoothstep(0.34, 0.02, gy) * smoothstep(0.0, 0.10, gy) * lowSun;
          col = mix(col, mix(uHorizon, uSunCol, 0.55), lobe * 0.55 * (1.0 - uStorm * 0.8));

          // ---------------------------------- 3. THE SUN ---------------------------------------
          float sd = dot(dir, uSunDir);
          float sunUp = smoothstep(-0.09, 0.05, uSunDir.y);            // below the horizon → gone
          float disc = smoothstep(0.99972, 0.99990, sd);               // ~1.3° — a legible sun, not an astronomer's
          float halo = pow(max(sd, 0.0), 150.0) * 0.34;                // the tight Mie ring around the disc
          float glare = pow(max(sd, 0.0), 6.0) * 0.14 + pow(max(sd, 0.0), 26.0) * 0.26;
          float sunK = sunUp * uSunAmt * (1.0 - uStorm * 0.88);
          // THE PEAK IS BUDGETED AGAINST THE BLOOM PASS, and that is not a taste call — it was measured.
          // UnrealBloom blurs a mip chain with finite separable kernels; feed it a near-delta highlight far
          // above its 0.82 threshold and the falloff runs off the end of the kernel, so the glow comes back
          // as a soft-edged BOX with straight vertical sides standing in the sky — the exact artefact this
          // wave exists to remove, reintroduced by the sun. (Proved by ablation: disabling the bloom pass
          // removed the box and nothing else.) So the disc stays bright enough to read as "you cannot look
          // at it" and the ENERGY lives in the wide glare, which the bloom can actually resolve.
          col += uSunCol * (disc * 0.72 + halo + glare) * sunK;

          // ---------------------------------- 4. THE MOON --------------------------------------
          // A real disc with a real phase: the moon is a sphere and this pixel is a point on it, so the
          // terminator falls out of one dot product instead of being drawn as a shape that can never
          // tilt right. uMoonLight is where the moon's own sunlight arrives from (CPU-side, from the
          // synodic month), which is also why a waxing crescent leans the other way from a waning one.
          if (uMoonAmt > 0.004) {
            float md = dot(dir, uMoonDir);
            float mr = 0.00035;                                        // (1 - cos) at the limb → ~3.0° across
            float rr = clamp((1.0 - md) / mr, 0.0, 1.0);               // 0 centre … 1 limb
            if (rr < 1.0) {
              // tangent frame on the disc, so we can rebuild the sphere normal at this pixel
              vec3 mt = normalize(cross(uMoonDir, vec3(0.0, 1.0, 0.0)) + vec3(1e-4, 0.0, 0.0));
              vec3 mb = cross(mt, uMoonDir);
              float scale = sqrt(2.0 * mr);
              vec2 uv = vec2(dot(dir, mt), dot(dir, mb)) / scale;      // -1..1 across the disc
              float r2 = clamp(dot(uv, uv), 0.0, 1.0);
              vec3 n = normalize(mt * uv.x + mb * uv.y - uMoonDir * sqrt(max(0.0, 1.0 - r2)));
              float lam = max(dot(n, uMoonLight), 0.0);
              // maria + a soft limb, and EARTHSHINE so the dark side is ashen, never a bite out of the sky
              float maria = 0.84 + 0.16 * noise(uv * 3.1 + 4.7) + 0.10 * noise(uv * 7.3);
              float lit = (0.055 + 0.945 * pow(lam, 0.72)) * maria * smoothstep(1.0, 0.86, r2);
              float edge = smoothstep(1.0, 0.90, rr);
              col = mix(col, uMoonCol * lit * 1.15, edge * uMoonAmt);
            }
            float mhalo = pow(max(md, 0.0), 2600.0) * 0.26 + pow(max(md, 0.0), 110.0) * 0.09;
            col += uMoonCol * mhalo * uMoonAmt * (1.0 - uStorm * 0.9);
          }
          // ------------------------------- 4b. THE AURORA --------------------------------------
          // Every decision below is documented on AURORA_GLSL (§ THE CALL SITES). Before the stars so
          // they can be occluded; added after them so it reads as light in front. Day = one compare.
          vec3 aurCol = vec3(0.0); float aurDen = 0.0;
          if (uAuroraAmt > 0.004) {
            float rho = acos(clamp(dot(dir, uAurZ), -1.0, 1.0));
            vec2 mz = normalize(uAurZ.xz + vec2(1e-5, 0.0));
            // reach = THE EARLY OUT (the whole cost story): a screen-coherent northern-sky cone test
            float reach = smoothstep(0.30, 0.72, 0.5 + 0.5 * dot(az, mz))
                        * smoothstep(0.012, 0.085, gy)
                        * smoothstep(1.52, 1.26, rho);
            if (reach > 0.003) {
              float ph = atan(dot(dir, uAurB), dot(dir, uAurT));
              float nerv = 1.0 + uAurBreak * 2.4;                    // the substorm: everything speeds up
              // one slow domain warp, shared: three folds of the SAME sheet must wander together
              float warp = ${high ? '(auArc1(ph, 0.60, uTime * 0.0041) - 0.5) * 0.35' : '0.0'};
              float phw = ph + warp;
              // THE GAPS: without this the curtain closes into a ring, and a ring from a deck is a stripe
              float seg = auArc1(phw, 1.35, uTime * 0.0035);
              // …never to zero, though: a curtain that switches OFF leaves a hard end. They breathe.
              float gap0 = 0.16 + 0.84 * smoothstep(0.26, 0.70, seg);
              float gap1 = 0.14 + 0.86 * smoothstep(0.74, 0.30, seg);
              vec3 c0 = auCurtain(rho, ${AUR_C[0]!.b} + ${AUR_C[0]!.w} * (${high ? 'auArc2' : 'auArc1'}(phw, ${AUR_C[0]!.R}, uTime * ${AUR_C[0]!.d} * nerv) - 0.5) * 2.0, ${AUR_C[0]!.h}, ${AUR_C[0]!.e});
${high ? /* glsl */ `
              vec3 c1 = auCurtain(rho, ${AUR_C[1]!.b} + ${AUR_C[1]!.w} * (auArc2(phw + 2.71, ${AUR_C[1]!.R}, uTime * ${AUR_C[1]!.d} * nerv + 21.0) - 0.5) * 2.0, ${AUR_C[1]!.h}, ${AUR_C[1]!.e});
              vec3 c2 = auCurtain(rho, ${AUR_C[2]!.b} + ${AUR_C[2]!.w} * (auArc1(phw - 1.93, ${AUR_C[2]!.R}, uTime * ${AUR_C[2]!.d} * nerv + 47.0) - 0.5) * 2.0, ${AUR_C[2]!.h}, ${AUR_C[2]!.e});
              // ray AA with no derivative: the pitch collapses as sin(rho)→0, so the bearing footprint
              // is (pixel angle)/sin(rho). fwidth(atan) would tear on the ±π meridian. Clamped: a
              // negative ray SUBTRACTS light and reads as a black scratch.
              float phFoot = uAurPx / max(sin(rho), 0.02);
              float ray = clamp(0.48 + 0.52 * auRays(ph + warp * 0.4, uTime, nerv) * smoothstep(1.7, 0.55, phFoot * 233.0), 0.0, 1.3);
              vec3 acc = vec3(0.0); float den = 0.0;
              // (1) the main curtain — ray contrast eased out at the border so it stays a LINE, not a comb
              float rk0 = mix(1.0, ray, 0.40 + 0.60 * smoothstep(0.02, 0.30, c0.y));
              float d0 = c0.x * gap0 * rk0;
              acc += auColour(uAurLo, uAurMid, uAurHi, c0.y) * d0 + uAurLo * c0.z * gap0 * (0.20 + 0.40 * ray);
              den += d0;
              // (2) a second curtain further off — two distances is what gives the sky depth
              float rk1 = mix(1.0, ray, 0.30 * smoothstep(0.04, 0.40, c1.y));
              float d1 = c1.x * gap1 * rk1 * 0.72;
              acc += auColour(uAurLo, uAurMid, uAurHi, c1.y) * d1 + uAurLo * c1.z * gap1 * 0.14;
              den += d1;
              // (3) a faint high drape — the glow BETWEEN the two sharp curtains (without it: two lines)
              float d2 = c2.x * 0.20;
              acc += auColour(uAurLo, uAurMid, uAurHi, 0.35 + 0.65 * c2.y) * d2;
              den += d2 * 0.5;
` : /* glsl */ `
              // LOW PATH: one curtain, no second layer, no reflection — but it KEEPS striations, from
              // three sines and nothing else (~10 ALU). Without them a lone band photographs as a stripe.
              float ray = clamp(0.52 + 0.48 * (0.42 * sin(23.0 * ph + uTime * 0.17 * nerv + 1.7)
                                             + 0.34 * sin(43.0 * ph - uTime * 0.29 * nerv + 4.1)
                                             + 0.24 * sin(89.0 * ph + uTime * 0.47 * nerv + 2.3)), 0.0, 1.3);
              float rk0 = mix(1.0, ray, 0.35 + 0.65 * smoothstep(0.02, 0.30, c0.y));
              float d0 = c0.x * gap0 * rk0;
              vec3 acc = auColour(uAurLo, uAurMid, uAurHi, c0.y) * d0 + uAurLo * c0.z * gap0 * (0.14 + 0.26 * ray);
              float den = d0;
`}
              float gain = uAuroraAmt * ${AURORA.SKY_GAIN} * reach;
              acc *= gain; den *= gain;
              // dither (the tail is the biggest gradient in the game; 8-bit ramps it into contour rings)
              acc *= 1.0 + auDither(gl_FragCoord.xy) * 0.06;
              // PEAK ROLL — the bloom budget (same argument as the sun disc above): identity below 78%
              // of the ceiling, asymptotic above it, so a stack of three can never print a bloom BOX.
              float mx = max(max(acc.r, acc.g), acc.b);
              acc *= ${AURORA.SKY_PEAK} / (${AURORA.SKY_PEAK} + max(0.0, mx - ${(AURORA.SKY_PEAK * 0.78).toFixed(3)}));
              aurCol = acc;
              aurDen = clamp(den * 1.6, 0.0, 1.0);
            }
          }
${high ? /* glsl */ `
          // ---------------------------------- 5. THE STARS -------------------------------------
          // Hashed cells on the direction sphere: present/absent, a magnitude, a colour temperature and a
          // slow twinkle. No texture, no geometry, and (unlike the 700-point cloud it replaces) the
          // density is uniform in ANGLE, so there is no crowding overhead and no gap at the skyline.
          if (uNight > 0.02) {
            vec3 sp = dir * 132.0;
            vec3 si = floor(sp); vec3 sf = fract(sp) - 0.5;
            float sh = hash3(si);
            float present = step(0.963, sh);
            float rad = length(sf);
            float mag = 0.26 + 0.74 * fract(sh * 91.7);
            mag *= mag;                                   // a real sky is a FEW bright stars and a lot of faint ones
            float tw = 0.62 + 0.38 * sin(uTime * (1.1 + fract(sh * 37.3) * 2.4) + sh * 47.0);
            float star = present * smoothstep(0.32, 0.02, rad) * (0.22 + 2.4 * mag) * tw;
            // hot blue-white to cool amber, and thinner near the skyline (there is more air to look through)
            vec3 scol = mix(vec3(1.0, 0.86, 0.68), vec3(0.80, 0.88, 1.0), fract(sh * 13.7));
            // …and the curtain in front of them takes them — the cue that says it is NEARER than the sky
            star *= smoothstep(0.02, 0.24, gy) * uNight * (1.0 - uStorm) * (1.0 - aurDen * 0.72);
            // the galactic band: one soft great-circle arch, textured with 3 cheap octaves
            float gal = smoothstep(0.42, 0.0, abs(dot(dir, normalize(vec3(0.62, 0.35, -0.70)))));
            float galn = fbm3(dir.xz * 2.6 + dir.y * 3.1);
            col += vec3(0.42, 0.46, 0.62) * gal * (0.10 + 0.16 * galn) * smoothstep(0.04, 0.30, gy) * uNight * (1.0 - uStorm) * 0.42 * (1.0 - aurDen * 0.62);
            col += scol * star * 1.15;
          }
` : ''}
          // …and NOW the curtain goes on, over the stars it just occluded and under the cloud deck below.
          col += aurCol;
${high ? /* glsl */ `
          // ---------------------------------- 6. THE CLOUDS ------------------------------------
          // PROJECTION. A cloud deck is a plane, so the honest projection is dir.xz / dir.y — and that is
          // exactly what used to run away at the skyline and print curtains. The floor below (0.16) caps
          // the plane's reach at ~6× the zenith rate, which is still plenty of perspective for the eye and
          // is bounded everywhere. The layers are then faded out BELOW elev 0.20 regardless, so nothing
          // cloud-shaped can ever touch the sea.
          vec2 cuv = dir.xz / (gy + 0.16);
          // DRIFT. uDrift is the same wind-integrated offset (metres) the sea's foam and cat's paws ride,
          // so the clouds, the whitecaps and the sails all answer the same breeze. Higher deck = faster.
          vec2 wdir = normalize(uDrift + vec2(1e-4, 0.0));
          vec2 wperp = vec2(-wdir.y, wdir.x);
          vec2 d0 = uDrift * 0.0016, d1 = uDrift * 0.0031, d2 = uDrift * 0.0052;
          // STREAKED ALONG THE WIND, not along world Z. Torn cloud runs downwind; an axis-aligned stretch
          // is a scanline that ignores the weather (and, at the horizon, a wall).
          mat2 W = mat2(wdir.x, wperp.x, wdir.y, wperp.y);
          float big  = fbm((cuv + d0) * 1.55);
          float wisp = fbm(W * (cuv + d1) * vec2(0.62, 1.30));          // 2.1:1 downwind, not 4.9:1 axis-aligned
          float f = max(big, wisp * 0.84);
          float cover = smoothstep(uCover, uCover + 0.20, f);
          // SUN SHADING: sample the field a step TOWARD the sun; where it thins, light gets through, so
          // that flank of the puff is bright and the far flank is in its own shadow. One extra fbm3.
          vec2 toS = normalize(saz - az * dot(saz, az) + 1e-5);
          // …only where there IS cloud. Most of a partly-cloudy sky has cover 0, and cloud comes in
          // patches, so the branch is screen-coherent and those pixels genuinely skip the extra fbm.
          float shade = 1.0;
          if (cover > 0.002) shade = mix(0.62, 1.06, clamp((f - fbm3((cuv + d0) * 1.55 + toS * 0.30)) * 2.4 + 0.5, 0.0, 1.0));
          vec3 cloudCol = mix(uCloud * shade, uSky, 0.14);
          // the sunlit RIM: a cloud edge with the sun behind it lights up. Cheap and it sells golden hour.
          cloudCol += uSunCol * pow(toSun, 6.0) * (1.0 - cover) * cover * 1.4 * sunUp * (1.0 - uStorm);
          float deckFade = smoothstep(0.03, 0.20, gy);                  // never reaches the skyline (the seal)
          col = mix(col, cloudCol, cover * deckFade * uOpacity);
          // a thin CIRRUS veil much higher up, drifting faster and stretched harder — depth for free
          float cir = fbm3(W * (cuv * 0.62 + d2) * vec2(0.34, 1.15));
          col = mix(col, mix(uCloud, uSky, 0.30), smoothstep(0.52, 0.86, cir) * smoothstep(0.10, 0.42, gy) * uOpacity * 0.30 * (1.0 - uStorm));
          // ---- STORM SKY — a gale is LAYERED, not one grey wash ----
          if (uStorm > 0.01) {
            // (a) a low, fast SCUD deck racing under the main cloud: torn, dark, and much closer to the eye
            vec2 sv = dir.xz / (gy + 0.22);
            float scud = fbm(W * (sv + d2 * 2.2) * vec2(1.9, 1.2));
            float scudA = smoothstep(0.42, 0.72, scud) * smoothstep(0.02, 0.18, gy) * smoothstep(0.85, 0.30, gy);
            col = mix(col, uCloud * (0.52 + 0.22 * shade), scudA * uStorm * 0.85);
            // (b) the SHELF: a hard, dark ceiling overhead that lifts to a pale bruise right at the skyline
            float shelf = smoothstep(0.16, 0.62, gy);
            col *= mix(1.0, mix(1.06, 0.52, shelf), uStorm);
            float underlight = smoothstep(0.16, 0.0, gy) * uStorm;
            col = mix(col, mix(uHorizon, vec3(0.74, 0.76, 0.72), 0.35), underlight * 0.30);
            // (c) rain HAZE swallowing the horizon so the sea fades into the storm instead of ending at a line
            col = mix(col, uHorizon * 0.92, smoothstep(0.22, 0.0, gy) * uStorm * 0.55);
          }
` : /* glsl */ `
          // LOW PATH: no FBM decks at all — a gradient, the sun, the moon and a flat storm darkening.
          // Same sky, same seal, none of the noise; this is what ?water=low / Settings › Low compiles.
          col *= mix(1.0, mix(1.04, 0.54, smoothstep(0.10, 0.60, gy)), uStorm);
          col = mix(col, uHorizon * 0.94, smoothstep(0.24, 0.0, gy) * uStorm * 0.5);
`}
          // ---- LIGHTNING: the deck glows from INSIDE, brightest on the strike's side of the sky ----
          if (uFlash > 0.001) {
            float side = 0.5 + 0.5 * dot(az, uFlashDir);
            float glow = uFlash * (0.25 + 0.75 * pow(side, 3.0)) * (0.45 + 0.55 * uStorm);
            col += vec3(0.72, 0.78, 0.95) * glow * 0.9;
          }
          // ------------------------------ 7. THE SEAL ------------------------------------------
          // The last ~3° of sky is forced to EXACTLY uHorizon — which is exactly what the far sea is
          // forced to (uFog, same colour, same frame). Sea and sky therefore meet at one number: no
          // step, no curtain, no band, at any camera height or heading. Everything above (clouds,
          // stars, storm layers, the sun lobe) has already been faded out before it can reach here.
          col = mix(col, uHorizon, smoothstep(0.060, -0.010, elev));
          gl_FragColor = vec4(pow(max(col, 0.0), vec3(2.2)), 1.0); // -> linear for the tonemapped composer
        }
      `,
      // OPAQUE backdrop (alpha is always 1.0): drawn FIRST in the opaque pass (renderOrder -3) so the
      // ocean/ships/islands paint OVER it. If this were `transparent:true` it would land in the post-opaque
      // transparent pass and, with depthTest off, overpaint the sea at the horizon when the camera drops to
      // sea level (the far ocean rises to elev~0 and the dome's lower band covered it = "sky in front of sea").
      transparent: false, depthWrite: false, depthTest: false, side: THREE.BackSide, fog: false,
    });
    this.cloudMat = mat;
    // 48×32 segments (was 24×16): the dome is now the sun, the moon and the stars too, and vDir is
    // interpolated across the face — a coarse dome bends a 1.3° sun disc into a polygon near a seam.
    const m = new THREE.Mesh(new THREE.SphereGeometry(1600, 48, 32), mat);
    m.frustumCulled = false; m.renderOrder = -3; // backdrop, behind everything
    return m;
  }

  /**
   * Distant land on the skyline — ONE merged mesh (was 14 separate ones), hazed into the horizon colour
   * by the shader rather than by the scene fog.
   *
   * IT WAS PART OF THE "WALLS AROUND THE MAP" (owner QA). Nine identical-ish silhouettes at 720 m, spaced
   * at EXACTLY 40°, all the same distance, all standing the same height. Regularity is the tell: the eye
   * reads a periodic ring of slabs as a fence long before it reads it as a coastline. Worse, they were
   * shaded by the scene fog, whose colour is the SKY HORIZON — so at 82% fog they came back BRIGHTER than
   * the sea in front of them, i.e. pale panels floating on the waterline.
   *
   * Now: irregular in angle, distance, size and height (a deterministic hash, so it is the same coast for
   * every captain and every session), pushed out past where the ocean has finished fading, and shaded by
   * a purpose-built material that mixes toward `uFog` — the same colour the sea and the sky both meet at —
   * with the amount driven by each vertex's own distance, so the land dissolves INTO the seam instead of
   * hanging in front of it. Land also reads DARKER than the haze it sits in, which is what distance does
   * to a landmass seen across water.
   */
  private buildHorizon(): THREE.Group {
    const g = new THREE.Group();
    // deterministic pseudo-random — the same coastline for everybody, forever (never Math.random here:
    // a horizon that reshuffles between sessions is a horizon players stop trusting as a landmark)
    let seed = 0x5f3a71;
    const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    const parts: THREE.BufferGeometry[] = [];
    const N = 11;
    for (let i = 0; i < N; i++) {
      // irregular bearing (a real coast is not a protractor) and an irregular offing
      const a = ((i + rnd() * 0.86 - 0.43) / N) * Math.PI * 2 + 0.3;
      const R = 1010 + rnd() * 320;
      const w = 90 + rnd() * 210;
      const h = 13 + rnd() * 26;
      const m = new THREE.Matrix4();
      const base = new THREE.SphereGeometry(w, 12, 6);
      base.scale(1, h / w, 0.42);
      base.translate(0, -h * 0.34, 0);
      m.makeRotationY(-a).setPosition(Math.cos(a) * R, 0, Math.sin(a) * R);
      parts.push(base.applyMatrix4(m));
      if (rnd() > 0.45) { // a headland/peak on some of them, off-centre
        const pk = new THREE.ConeGeometry(w * (0.22 + rnd() * 0.2), h * (1.3 + rnd() * 0.9), 6);
        pk.translate((rnd() - 0.5) * w * 0.9, h * 0.35, 0);
        parts.push(pk.applyMatrix4(m));
      }
    }
    const merged = mergeGeometries(parts, false)!;
    for (const p of parts) p.dispose();
    const mat = new THREE.ShaderMaterial({
      uniforms: { uFog: { value: SKY.clone() }, uLand: { value: new THREE.Color(0x22333f) }, uCam: { value: new THREE.Vector3() } },
      vertexShader: 'varying vec3 vW; void main(){ vec4 w = modelMatrix * vec4(position,1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }',
      fragmentShader: /* glsl */ `
        precision mediump float;
        uniform vec3 uFog, uLand, uCam; varying vec3 vW;
        void main(){
          // land is a DARKER note inside the horizon haze, never a brighter one — and the further out it
          // sits the less of it is left, so the ring dissolves at its own rim instead of ending.
          float d = length(vW.xz - uCam.xz);
          float haze = clamp((d - 700.0) / 620.0, 0.0, 1.0);
          vec3 col = mix(uLand, uFog * 0.94, 0.55 + 0.45 * haze);
          gl_FragColor = vec4(pow(mix(col, uFog, haze * haze), vec3(2.2)), 1.0);
        }
      `,
      fog: false, depthWrite: true,
    });
    this.horizonMat = mat;
    const mesh = new THREE.Mesh(merged, mat);
    mesh.frustumCulled = false; mesh.renderOrder = -2; // after the dome, before the sea
    g.add(mesh);
    return g;
  }

  /** Shared ocean height at the CURRENT render time — handed to the flock so low gulls skim the real swell. */
  private _seaAt = (x: number, z: number): number => oceanHeightAt(x, z, this.lastRenderT, this.ampAt(x, z));

  private updateAtmosphere(t: number, dt: number): void {
    const sky = this.scene.background as THREE.Color, cam = this.camera.position, st = this._storm, nf = this.nightFactor;
    this.lastFrameDt = dt;
    if (this.clouds && this.cloudMat) {
      this.clouds.position.copy(cam);
      const u = this.cloudMat.uniforms;
      u.uTime!.value = t;
      // THE SINGLE SOURCE. These two colours are the SAME objects the ocean's uSkyTop/uFog were copied
      // from one call earlier in the frame (updateSky), and the far sea is forced to exactly uFog — which
      // is why the sea can dissolve into the sky with no seam. Never re-derive them here.
      (u.uSky!.value as THREE.Color).copy(this.skyZenith).lerp(this._atmoScratch.setRGB(0.28, 0.33, 0.38), st * 0.85);
      (u.uHorizon!.value as THREE.Color).copy(this.skyHorizon).lerp(this._atmoScratch.setRGB(0.30, 0.36, 0.4), st * 0.7);
      // clouds: bright white by day, dark grey in storm; thicker (lower cover) + denser in storm
      (u.uCloud!.value as THREE.Color).setRGB(0.99, 0.99, 1.0).lerp(this._atmoScratch.setRGB(0.30, 0.33, 0.39), st);
      // cover: HIGHER = less cloud. Night thins the deck a touch so the star field has sky to live in —
      // an overcast midnight with no stars is the one night that reads as a bug rather than as weather.
      u.uCover!.value = lerp(0.46, 0.15, st) + nf * 0.10 * (1 - st);
      u.uOpacity!.value = lerp(0.66, 0.97, st) * lerp(1, 0.58, nf);
      u.uStorm!.value = st;
      u.uNight!.value = nf;
      // THE SUN IS ONE SUN: the disc in the sky and the glitter on the water read the same vector.
      (u.uSunDir!.value as THREE.Vector3).copy(this.oceanMat.uniforms.uSun!.value as THREE.Vector3);
      (u.uSunCol!.value as THREE.Color).copy(this.sun.color);
      u.uSunAmt!.value = 1 - nf * 0.85;
      // …and so is the moon: uMoonDir/uMoonLight/uMoonAmt were written by updateSky, and the ocean's
      // uMoon reads the identical direction, which is the whole moon-path-on-the-water effect.
      (u.uMoonDir!.value as THREE.Vector3).copy(this.moonDir);
      (u.uMoonLight!.value as THREE.Vector3).copy(this.moonLight);
      u.uMoonAmt!.value = this.moonAmt;
      // the clouds drift on the SAME wind-integrated offset the sea's foam and cat's paws ride
      (u.uDrift!.value as THREE.Vector2).copy(this.pawDrift);
      // 🌌 the display, already resolved in updateSky (schedule × the night × the gale). uAurPx is the
      // camera's own pixel angle in radians: it is how the rays antialias as they converge on the
      // magnetic zenith WITHOUT a screen-space derivative (fwidth(atan) tears on the ±π meridian, and
      // that tear is a bright line running from the zenith to the horizon).
      u.uAuroraAmt!.value = this.auroraAmt;
      u.uAurBreak!.value = this.auroraBreak;
      u.uAurPx!.value = (this.camera.fov * Math.PI / 180) / Math.max(1, this.renderer.domElement.height);
    }
    // gulls: out by day in calm/light weather, gone at night and once a gale really blows. One instanced
    // draw call; the flock steers itself (boids-lite) and the wing beat runs in the vertex shader.
    const birdOp = (1 - nf) * (1 - Math.min(1, st * 1.25));
    if (this.birdsRoot.visible) {
      this.birds.opacity = birdOp;
      if (birdOp > 0.02) {
        // plumage picks up the sky it flies in (dusk gulls go warm, storm gulls go grey)
        // sunlit back nearly white, underside in its OWN shadow (a gull seen from the deck is a dark-bellied
        // silhouette against the sky — matching it to the sky would make the flock invisible)
        this._birdTop.copy(this.skyHorizon).lerp(this._atmoScratch.setRGB(0.98, 0.99, 1.0), 0.80);
        this._birdUnder.copy(this.skyZenith).lerp(this._atmoScratch.setRGB(0.20, 0.23, 0.28), 0.72);
        this.birds.setPalette(this._birdTop, this._birdUnder);
        // the camera LOOKS opposite to where it sits relative to the ship (camFollowYaw) — hand that to the
        // flock so most of it stays in frame …and the LIVE WIND, which is what the gulls now fly against
        this.birds.update(t, this.lastFrameDt, cam.x, cam.z, this.camFollowYaw + Math.PI, this._seaAt, st, this.windDir, this.windStrength);
      }
    }
    // 🐟 the flying-fish flush: startled by the local BOW, so it only ever happens where you are looking.
    // Same fauna lever as the gulls (gone at night, gone in a gale), plus the Low preset — a shoal of fish
    // is the definition of a thing a potato does not need to draw.
    this.fish.opacity = birdOp;
    this.fish.update(
      t, this.faunaBowX, this.faunaBowZ, this.faunaBowH, this.faunaSpeed, this._seaAt,
      this.birdsRoot.visible && this.waterHigh && birdOp > 0.35,
    );
    if (this.horizon) {
      this.horizon.position.set(cam.x, 0, cam.z);
      if (this.horizonMat) {
        // the distant land hazes into the SAME uFog the sea and the sky meet at (one colour, three consumers)
        (this.horizonMat.uniforms.uFog!.value as THREE.Color).copy(this.oceanMat.uniforms.uFog!.value as THREE.Color);
        (this.horizonMat.uniforms.uCam!.value as THREE.Vector3).copy(cam);
        (this.horizonMat.uniforms.uLand!.value as THREE.Color).setRGB(0.13, 0.19, 0.24).lerp(this._atmoScratch.setRGB(0.05, 0.07, 0.11), nf);
      }
    }
  }

  /**
   * The hull the fauna answers to: the one nearest the render centre, which is yours (and, when you are
   * dead or ashore, whoever is closest to the camera — so the sea still lives). Its BOW, not its centre:
   * a fish is flushed by the cutwater, and spawning at the waist puts the shoal under the hull.
   */
  private updateFaunaAnchor(centerX: number, centerZ: number): void {
    let best: THREE.Group | undefined; let bestD = 60 * 60;
    for (const g of this.ships.values()) {
      const dx = g.position.x - centerX, dz = g.position.z - centerZ, d2 = dx * dx + dz * dz;
      if (d2 < bestD) { bestD = d2; best = g; }
    }
    if (!best) { this.faunaSpeed = 0; return; }
    const h = -best.rotation.y, scale = best.scale.x || 1;
    this.faunaBowX = best.position.x + Math.cos(h) * HALF_L * scale;
    this.faunaBowZ = best.position.z + Math.sin(h) * HALF_L * scale;
    this.faunaBowH = h;
    this.faunaSpeed = (best.userData.spd as number) ?? 0;
  }

  /** Ease the local storm intensity toward the server-authoritative value and derive the wave swell.
   *  Called BEFORE syncShips so the ship bob (storm-scaled ocean) matches the rendered sea this frame. */
  setStorm(target: number, dt: number): void {
    this.stormTarget = clamp(target, 0, 1);
    const a = Math.min(1, dt * STORM.EASE_RATE);
    this._storm += (this.stormTarget - this._storm) * a;
    this.waveScale = lerp(1, STORM.WAVE_SCALE, this._storm);
  }

  /** 0 (calm) … 1 (full storm) — the current smoothed storm intensity, for storm-aware systems (audio). */
  get storm(): number { return this._storm; }

  /** A soft radial glow texture (warm lantern / cool moon), built once on a tiny canvas. */
  private makeGlowTexture(inner: string, mid: string): THREE.CanvasTexture {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    // small bright core + a long soft fade so the light "dies" naturally (no hard edge)
    grad.addColorStop(0, inner); grad.addColorStop(0.18, mid); grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad; g.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(c); tex.needsUpdate = true;
    return tex;
  }

  /** Drive the whole sky (background, fog, ocean tint, sun arc + colour/intensity, hemi light, stars)
   *  from the time-of-day phase. Phase = the synced server clock, so every client matches. Allocation-free. */
  private updateSky(t: number): void {
    // the menu showcase can pin a dramatic time-of-day while everything else animates on real time.
    // Phase is anchored to WALL-CLOCK epoch (render-only, never the sim): it survives server restarts
    // (server sim time starts at 0 = midnight, so every deploy used to reset the world to night) and
    // stays in sync across clients since everyone's clock is NTP-close vs an 8-minute cycle.
    const phase = this.forceSkyPhase != null ? this.forceSkyPhase : ((Date.now() / 1000 / DAY_NIGHT_CYCLE_S) % 1 + 1) % 1;
    let a = SKY_KEYS[0]!, b = SKY_KEYS[SKY_KEYS.length - 1]!;
    let ia = 0, ib = SKY_KEYS.length - 1;   // 🎨 the same span, indexed — the water palette is keyed to it
    for (let i = 0; i < SKY_KEYS.length - 1; i++) {
      if (phase >= SKY_KEYS[i]!.p && phase <= SKY_KEYS[i + 1]!.p) { a = SKY_KEYS[i]!; b = SKY_KEYS[i + 1]!; ia = i; ib = i + 1; break; }
    }
    const k = (phase - a.p) / Math.max(1e-6, b.p - a.p);
    const sky = this._skyScratch.lerpColors(a.sky, b.sky, k);
    this.skyGlow.lerpColors(a.glow, b.glow, k);    // horizon-glow colour + strength (computed first)
    this.skyGlowAmt = lerp(a.glowAmt, b.glowAmt, k);
    // sky is the ZENITH (top) colour; the horizon = zenith tinted toward the glow, used for the
    // scene bg + fog + ocean fog so the sea fades into the same colour the sky meets it with.
    this.skyZenith.copy(sky);
    this.skyHorizon.copy(sky).lerp(this.skyGlow, this.skyGlowAmt * 0.72);
    (this.scene.background as THREE.Color).copy(this.skyHorizon);
    this.scene.fog!.color.copy(this.skyHorizon);
    (this.oceanMat.uniforms.uFog!.value as THREE.Color).copy(this.skyHorizon);
    (this.oceanMat.uniforms.uSkyTop!.value as THREE.Color).copy(this.skyZenith); // upper band of the sea's sky reflection
    // 🎨 THE WATER PALETTE. The body gradient no longer comes from SKY_KEYS directly — it comes from the
    // selected palette's table, which is keyed to the SAME indices, so the sea still grades with the sky
    // key-for-key. `classic` IS SKY_KEYS' own pairs, so the shipped look is unchanged by construction.
    const wp = this.waterPalette;
    const wa = wp.keys[ia]!, wb = wp.keys[ib]!;
    (this.oceanMat.uniforms.uDeep!.value as THREE.Color).lerpColors(wa[0], wb[0], k);
    (this.oceanMat.uniforms.uShallow!.value as THREE.Color).lerpColors(wa[1], wb[1], k);
    // 🏝️ the colour the water reaches AT the sand: this palette's own shallow, pulled most of the way to
    // wet sand. Graded here (rather than pinned) so the beach turns gold at dusk and slate at midnight with
    // everything else — and so the water arrives at the shoreline already almost the sand's colour, which
    // is what makes the seam disappear instead of merely being decorated.
    (this.oceanMat.uniforms.uShoal!.value as THREE.Color)
      .lerpColors(wa[1], wb[1], k).lerp(SHOAL_SAND, wp.sandMix);
    // …and the rest of the palette chain. uSss is the LIVE shallow (so it keeps grading with the day)
    // tinted and gained by the variant — for `classic` that is the shallow itself, unchanged.
    (this.oceanMat.uniforms.uSssK!.value as THREE.Color).copy(wp.sssK);
    (this.oceanMat.uniforms.uFoam!.value as THREE.Color).copy(wp.foam);
    this.oceanMat.uniforms.uFresK!.value = wp.fresK;
    this.sun.color.lerpColors(a.sun, b.sun, k);
    this.sun.intensity = lerp(a.sunI, b.sunI, k);
    this.hemi.color.lerpColors(a.hemiSky, b.hemiSky, k);
    this.hemi.groundColor.lerpColors(a.hemiGround, b.hemiGround, k);
    this.hemi.intensity = lerp(a.hemiI, b.hemiI, k);
    // sun arcs E→W: overhead at noon (phase .5), below the horizon at night
    const ang = phase * Math.PI * 2, elev = -Math.cos(ang);
    this.sun.position.set(Math.sin(ang) * 160, elev * 220, Math.cos(ang) * 120 + 40);
    (this.oceanMat.uniforms.uSun!.value as THREE.Vector3).copy(this.sun.position).normalize();
    const nf = lerp(a.star, b.star, k); // reuse the star curve as the "nightness" 0..1
    this.nightFactor = nf;
    this.oceanMat.uniforms.uNight!.value = nf; // damp the unlit crest sheen at night
    this.updateMoon(phase, ang, nf);
    // 🌌 THE AURORA. The SCHEDULE is the shared pure function of the epoch (`auroraIntensityAt`, zero
    // wire: every captain at sea is under the same curtains on the same second, and it survives a deploy
    // for the same reason the storm does). The client then gates it on the night it is ACTUALLY
    // rendering and the gale it is ACTUALLY rendering, so no retune of the schedule can ever bleed a
    // display into a lit sky, and a `?storm=` preview kills it exactly like real weather does.
    const nowS = Date.now() / 1000;
    const sched = this.auroraOverride != null ? this.auroraOverride : auroraIntensityAt(nowS);
    const ng = clamp((nf - AURORA.NIGHT_IN) / (AURORA.NIGHT_FULL - AURORA.NIGHT_IN), 0, 1);
    this.auroraAmt = sched * (ng * ng * (3 - 2 * ng)) * clamp(1 - this._storm / AURORA.STORM_KILL, 0, 1);
    this.auroraBreak = this.auroraAmt > 0.01
      ? (this.auroraBreakOverride != null ? this.auroraBreakOverride : auroraBreakupAt(nowS)) : 0;
    // …and the SEA gets the same display, the same way it gets the same moon: one number, and the road on
    // the water is the curtain overhead reflected rather than a second thing that has to be kept in step.
    this.oceanMat.uniforms.uAurAmt!.value = this.auroraAmt;
    // 🌙 NIGHT LEGIBILITY (owner QA: "o casco praticamente some no escuro"). The night is allowed to be
    // night — this does NOT turn it into day. It lifts the AMBIENT only, and only in the moonlight's own
    // cold blue, so a hull reads as a shape against the water instead of dissolving into it. Three levers,
    // all of them the sky's own light: the hemisphere sky term (what fills unlit faces), the hemisphere
    // GROUND term (the sea bouncing moonlight back up under the hull, which is what actually separates a
    // black boat from black water), and a floor under the key light so the moon casts SOMETHING.
    if (nf > 0.01) {
      const lift = nf * this.moonAmt;                       // no moon up ⇒ no lift; a dark night stays dark
      this.hemi.color.lerp(this._skyLiftA.setRGB(0.46, 0.58, 0.86), lift * 0.55);
      this.hemi.groundColor.lerp(this._skyLiftB.setRGB(0.20, 0.28, 0.44), lift * 0.62);
      this.hemi.intensity = lerp(this.hemi.intensity, 1.22, lift * 0.7);
      // the key light becomes MOONLIGHT: it comes from the moon, not from a sun under the world.
      this.sun.position.copy(this.moonDir).multiplyScalar(320);
      this.sun.color.lerp(this._skyLiftA.setRGB(0.62, 0.72, 1.0), nf * 0.85);
      this.sun.intensity = lerp(this.sun.intensity, 0.42 * this.moonAmt, nf);
    }
    // 🌌 …and the aurora is IN THE WORLD, not painted on the backdrop. The hemisphere SKY term — the
    // light that fills every unlit face — leans to the display's own mint-green while it is up, so the
    // deck, the canvas and the crew pick up a cold rim that agrees with the sky above them. This is the
    // detail that decides whether the curtains read as weather or as a poster: light that exists only on
    // the dome is a poster. One lerp on a light that already exists — zero draws, zero shader work, and
    // it layers ON TOP of the moonlight lift above rather than replacing it.
    if (this.auroraAmt > 0.01) {
      const rim = this.auroraAmt * AURORA.RIM_GAIN;
      this.hemi.color.lerp(AUR_HEMI, rim);
      this.hemi.intensity *= 1 + rim * 0.30;
    }
    // night haze: pull the fog in + thicken the ocean's fog so the sea fades into a misty moonlit horizon
    (this.scene.fog as THREE.Fog).near = lerp(250, 165, nf);
    this.oceanMat.uniforms.uFogDensity!.value = lerp(0.0016, 0.0031, nf);
    // Onda Sistema de Luz — every lantern/window/bonfire/beacon lights up across the dusk (staggered
    // ignition per lamp), flickers like a flame, and the nearest reflecting ones smear on the water:
    // ONE instanced glow draw call + shader uniforms, driven from the FakeLight registry (lights.ts).
    this.lights.setCameraQuaternion(this.camera.quaternion);
    this.lights.update(t, nf, this.camera.position);
    this.lights.writeGlints(this.oceanMat.uniforms);
  }

  /**
   * 🌙 THE MOON — direction, phase and how hard it lights the world. Render-only, allocation-free.
   *
   * ORBIT. The moon rides the sun's own arc, half a cycle behind: it rises as the sun sets and is highest
   * at midnight. That is a deliberate simplification of a real lunar orbit and it is the RIGHT one here —
   * the moon is this game's only night landmark and the source of the light path on the water, and a
   * faithful orbit would routinely put it below the horizon for a whole session's worth of nights.
   *
   * PHASE. The illuminated fraction comes from the REAL synodic month against the wall clock, so the moon
   * over the Cove is the moon over the player, on the correct day, waxing or waning the correct way round.
   * Because the direction is pinned opposite the sun rather than orbiting for real, the phase cannot be
   * read off the sun/moon geometry any more — so it is handed to the shader as an explicit light vector
   * (`uMoonLight`, the direction the moon's own sunlight arrives from), rotated off the view axis by the
   * synodic angle in the plane that contains the world's up. That one vector gives the shader a correct
   * terminator ELLIPSE and the right crescent TILT for free, from one dot product per pixel.
   *
   * A FLOOR ON THE LIGHT. A true new moon is invisible and would leave a night with no moon path at all
   * every ~29 days; the lit fraction is therefore floored at 0.16 (a thin, real-looking crescent) — the
   * one cinematic liberty here, and the reason night legibility never depends on the calendar.
   */
  private updateMoon(phase: number, sunAng: number, nf: number): void {
    // Opposite the sun (it rises as the sun sets), but on a LOWER, off-axis arc: it tops out around 38°
    // rather than at the zenith, and its bearing is swung off the sun's track. Both on purpose. A moon
    // straight overhead at midnight is a moon the sea camera — which looks at the HORIZON — never sees,
    // and its reflection is a puddle under the bowsprit instead of a road running out to the skyline.
    // …and it stays VERY LOW — 4° to 6° across the whole night. That number is MEASURED, not chosen for
    // looks. The sea camera sits above the ship and looks DOWN at it, so the band of sky it frames above
    // the horizon is only about 10°-20° tall depending on zoom and tilt. Two earlier builds put the moon
    // at 20° and then at 12°, and a projected-screen-position probe said the same thing both times: off
    // the top of the frame, in the right sky and the wrong game. 5° is inside the band at every zoom, it
    // is where the light road runs all the way out to the skyline instead of pooling under the bowsprit,
    // and a big low moon over the sea is the better picture anyway.
    const mAng = sunAng + Math.PI;
    const el = -Math.cos(mAng);
    this.moonDir.set(Math.sin(mAng + 0.55), el * 0.058 + 0.010, Math.cos(mAng + 0.55) * 0.8 + 0.22).normalize();
    // synodic month against the wall clock (2000-01-06 18:14 UTC was a new moon → the epoch offset below)
    const days = Date.now() / 86400000 - 10970.76;
    const syn = ((days / 29.530588) % 1 + 1) % 1;             // 0 = new, 0.5 = full
    let lit = (1 - Math.cos(syn * Math.PI * 2)) * 0.5;        // illuminated fraction, 0..1
    lit = 0.16 + 0.84 * lit;                                   // …never a black new moon (see the note above)
    const pa = Math.acos(clamp(1 - 2 * lit, -1, 1));           // 0 = new … π = full
    const waxing = syn < 0.5 ? 1 : -1;                         // which limb is lit
    // the terminator axis: perpendicular to the view direction, in the plane containing world up, so the
    // crescent's horns point away from where the sun actually is. moonLight = cos·(toward us) + sin·(axis).
    this._moonAxis.set(0, 1, 0).cross(this.moonDir);
    if (this._moonAxis.lengthSq() < 1e-6) this._moonAxis.set(1, 0, 0);
    this._moonAxis.normalize().multiplyScalar(waxing);
    this.moonLight.copy(this.moonDir).multiplyScalar(Math.cos(pa))
      .addScaledVector(this._moonAxis, Math.sin(pa)).normalize();
    // visible only at night, and only while it is actually up. The ramp is tight (it clears by ~7° of
    // altitude) because this arc deliberately never climbs high — a 0.30 ramp authored for a high moon
    // would have held the light at two-thirds all night.
    const up = clamp((this.moonDir.y + 0.015) / 0.09, 0, 1);
    this.moonAmt = nf * up * (0.45 + 0.55 * lit) * (1 - this._storm * 0.85);
    // …and the SEA gets the same moon: a second, colder, wider specular lobe = the light path on the water.
    const um = this.oceanMat.uniforms.uMoon;
    if (um) (um.value as THREE.Vector4).set(this.moonDir.x, this.moonDir.y, this.moonDir.z, this.moonAmt);
  }

  /** 🤿 Wreck-dive presentation (Onda diving-polish; render-only, zero sim/wire): while the captain is below,
   *  the sea "closes over" the hull — both ocean tints sink toward one abyssal teal, the horizon fog draws in,
   *  and a diver's bubble column boils around the ship (pooled puffs — zero new draw calls, no lights). main.ts
   *  drives it each frame from the authoritative `self.activity`; intensity eases in/out so surfacing feels like
   *  breaking back into daylight. Runs AFTER updateSky/updateWeather (which re-set the ocean colours fresh every
   *  frame), exactly how the storm layers its own tint. */
  setDiveFx(on: boolean, x = 0, y = 0): void {
    this._diveTarget = on ? 1 : 0;
    if (on) { this.diveX = x; this.diveZ = y; }
  }
  private updateDive(t: number, dt: number): void {
    this._dive += (this._diveTarget - this._dive) * Math.min(1, dt * 2.6);
    const d = this._dive;
    if (d <= 0.004) return;
    const abyss = this._diveScratch.setRGB(0.015, 0.10, 0.13); // deep teal — the light fades with the diver
    (this.oceanMat.uniforms.uDeep!.value as THREE.Color).lerp(abyss, d * 0.85);
    (this.oceanMat.uniforms.uShallow!.value as THREE.Color).lerp(abyss, d * 0.65);
    (this.oceanMat.uniforms.uSkyTop!.value as THREE.Color).lerp(abyss, d * 0.7); // the sky above closes over too
    const fog = this.scene.fog as THREE.Fog;
    fog.near = lerp(fog.near, 95, d * 0.75);
    this.oceanMat.uniforms.uFogDensity!.value = lerp(this.oceanMat.uniforms.uFogDensity!.value as number, 0.0042, d * 0.7);
    // the diver's exhaust: a boiling ring of bubbles bursting around the hull (recycled fx pool)
    this.diveBubbleAcc += dt * 7.5 * d;
    while (this.diveBubbleAcc >= 1) {
      this.diveBubbleAcc -= 1;
      const a = Math.random() * Math.PI * 2, r = 0.8 + Math.random() * 3.4; // render-only jitter (never the sim)
      const bx = this.diveX + Math.cos(a) * r, bz = this.diveZ + Math.sin(a) * r;
      this.spawnBubble(bx, oceanHeightAt(bx, bz, t, this.ampAt(bx, bz)) + 0.25, bz);
    }
  }

  /** Storm visuals (runs after updateSky each frame, so it pushes the day/night look toward the storm).
   *  The renderer owns the GRADE — swell, water/fog/sky tint, the cool desaturated key light, and folding a
   *  lightning FLASH into every one of them in a single place. The weather ELEMENTS (rain, curtains,
   *  spindrift, the bolt, the waterspout, the eye) are drawn by storm.ts. All cosmetic. */
  private updateWeather(t: number, dt: number): void {
    const s = this._storm;
    this.oceanMat.uniforms.uWaveScale!.value = this.waveScale;
    this.oceanMat.uniforms.uStorm!.value = s;
    this.updateCoastZones();
    this.updateShoreZones();   // the beach transition — calm or gale, it is not weather
    this.updateSeaState(t, dt);
    const fog = this.scene.fog as THREE.Fog;
    if (s > 0.002) {
      // A storm sky is not one flat grey: the near air goes cool green-grey while the ceiling turns to slate,
      // so the horizon keeps a sliver of luminance under the cloud deck. Grading both ends separately is what
      // stops the "everything is the same mud" look.
      const grey = this._stormScratch.setRGB(0.29, 0.35, 0.38);
      const tint = s * 0.7;
      (this.scene.background as THREE.Color).lerp(grey, tint);
      fog.color.lerp(grey, tint);
      (this.oceanMat.uniforms.uFog!.value as THREE.Color).lerp(grey, tint);
      (this.oceanMat.uniforms.uSkyTop!.value as THREE.Color).lerp(grey, s * 0.85); // the sea mirrors the slate ceiling
      // 🎨 PALETTE CONVERGENCE. A gale is a gale in every palette: the storm grade was authored against a
      // sea that was already dark and cold, and dropping a saturated jade into it gives a cheerful
      // emerald tempest — pretty, and completely unthreatening. So a storm drags the body colour, the
      // stained-glass crest and the fresnel weight back toward the common storm sea, by an amount the
      // palette itself declares. `classic` declares 0 and is untouched (it IS the storm sea).
      const conv = s * this.waterPalette.stormConverge;
      if (conv > 0.001) {
        (this.oceanMat.uniforms.uDeep!.value as THREE.Color).lerp(STORM_SEA_DEEP, conv);
        (this.oceanMat.uniforms.uShallow!.value as THREE.Color).lerp(STORM_SEA_SHALLOW, conv);
        (this.oceanMat.uniforms.uSssK!.value as THREE.Color).lerp(SSS_IDENTITY, conv);
        this.oceanMat.uniforms.uFresK!.value = lerp(this.waterPalette.fresK, 1, conv);
      }
      fog.near = lerp(fog.near, STORM.FOG_NEAR_STORM, s);
      this.oceanMat.uniforms.uFogDensity!.value = lerp(this.oceanMat.uniforms.uFogDensity!.value as number, STORM.FOG_DENSITY_STORM, s);
      // global light: dimmer + desaturated/cool (the day/night values are re-set fresh each frame)
      this.sun.intensity *= 1 - s * 0.5;
      this.sun.color.lerp(grey, s * STORM.DESATURATE);
      this.hemi.color.lerp(grey, s * STORM.DESATURATE);
      this.hemi.intensity *= 1 - s * 0.25;
      // (the starfield now lives in the sky dome's own shader and is already gated on `1 - uStorm` there —
      //  no starlight through a gale, for free, without a second opinion out here)
    }
    // the weather itself (one call — every element LODs off the same intensity)
    this._stormEnv.camera = this.camera;
    this._stormEnv.t = t; this._stormEnv.dt = dt; this._stormEnv.storm = s; this._stormEnv.night = this.nightFactor;
    this._stormEnv.windDir = this.windDir; this._stormEnv.windStrength = this.windStrength;
    this._stormEnv.waveScale = this.waveScale; this._stormEnv.quality = this.graphicsPreset;
    this._stormEnv.fog.copy(fog.color);
    this.stormFx.update(this._stormEnv);
    // LIGHTNING as LIGHT: one flash value drives the whole scene at once — the key light stabs, the sky and
    // the sea light up, the cloud deck glows from inside on the strike's side. No THREE.PointLight anywhere.
    const flash = this.stormFx.flash;
    this.oceanMat.uniforms.uFlash!.value = flash;
    if (this.cloudMat) {
      this.cloudMat.uniforms.uFlash!.value = flash;
      const az = this.stormFx.flashAzimuth;
      (this.cloudMat.uniforms.uFlashDir!.value as THREE.Vector2).set(Math.cos(az), Math.sin(az));
    }
    if (flash > 0.002) {
      this.sun.intensity += flash * 2.2 * s;
      this.hemi.intensity += flash * 1.8 * s;
      this.hemi.color.lerp(this._stormScratch.setRGB(0.72, 0.80, 0.95), flash * 0.5);
      (this.scene.background as THREE.Color).lerp(this._stormScratch.setRGB(0.74, 0.80, 0.92), flash * 0.55 * s);
      fog.color.lerp(this._stormScratch.setRGB(0.70, 0.76, 0.88), flash * 0.4 * s);
    }
  }

  /* --------------------------- coast shoaling (storm swell vs. the beach) --------------------------- */

  /**
   * The N nearest islands, as the flat [x, y, rInner, rOuter, …] array both the shader and the CPU bob
   * read (stride 4 — the layout `oceanCoastFactor` and `buildOceanCoastGLSL` agree on).
   */
  private coastZones = new Float32Array(OCEAN_COAST_MAX * 4);
  private coastCount = 0;
  /** Rebuilt only every few frames — the island set barely changes between them. */
  private coastNextRefresh = 0;

  /**
   * Pick the nearest islands and hand them to the sea.
   *
   * WHY IT EXISTS. The sea mesh knows nothing about land. Scale the swell to a 2.7 m storm vaga and it
   * rolls straight through a sand spit — a wall of water standing ON the beach, with the hull bobbing
   * inside it. Real swell shoals: it feels the bottom and spends its energy well before the sand. So the
   * storm EXCESS (never the approved calm sea) is faded to nothing across a band around each island.
   *
   * Cost is zero when it cannot show: with no storm running the count is forced to 0 and the shader loop
   * breaks on its first integer compare.
   */
  private updateCoastZones(): void {
    const world = this.lastWorld;
    if (this.waveScale <= 1.001 || !world || world.islands.length === 0) {
      if (this.coastCount !== 0) { this.coastCount = 0; this.oceanMat.uniforms.uCoastCnt!.value = 0; }
      return;
    }
    if (this.lastRenderT < this.coastNextRefresh) return;
    this.coastNextRefresh = this.lastRenderT + 0.4;   // ~2.5 Hz: a ship covers 5 m in that time
    const cx = this.camera.position.x, cz = this.camera.position.z;
    // partial selection: keep the OCEAN_COAST_MAX nearest by squared distance, no sort, no allocation
    const z = this.coastZones;
    let n = 0, worst = -1, worstD = -1;
    const d2s = this._coastD2;
    for (const isl of world.islands) {
      const dx = isl.x - cx, dy = isl.y - cz;
      const d2 = dx * dx + dy * dy;
      const outer = isl.radius * STORM.COAST_OUTER_MUL + STORM.COAST_OUTER_ADD;
      if (d2 > (outer + OCEAN_FADE_R) * (outer + OCEAN_FADE_R)) continue;  // cannot touch any drawn water
      let slot = -1;
      if (n < OCEAN_COAST_MAX) slot = n++;
      else if (d2 < worstD) slot = worst;
      if (slot < 0) continue;
      d2s[slot] = d2;
      z[slot * 4] = isl.x; z[slot * 4 + 1] = isl.y;
      z[slot * 4 + 2] = isl.radius * STORM.COAST_INNER_MUL; z[slot * 4 + 3] = outer;
      worstD = -1;
      for (let i = 0; i < n; i++) if (d2s[i]! > worstD) { worstD = d2s[i]!; worst = i; }
    }
    this.coastCount = n;
    const u = this.oceanMat.uniforms.uCoast!.value as THREE.Vector4[];
    for (let i = 0; i < n; i++) u[i]!.set(z[i * 4]!, z[i * 4 + 1]!, z[i * 4 + 2]!, z[i * 4 + 3]!);
    this.oceanMat.uniforms.uCoastCnt!.value = n;
  }
  private _coastD2 = new Float32Array(OCEAN_COAST_MAX);

  /* ------------------------------ 🏝️ the shore (beach transition) ----------------------------- */

  /**
   * The WATERLINE RADIUS of an islet, in closed form.
   *
   * The sand apron is a cone of base radius `r × beachMult`, apex at `+beachRise`, rim at `−beachSink`
   * (see ISLE + the archetype). Its height is linear in radius, so it crosses y = 0 at exactly
   * `R × rise / (rise + sink)` — one divide, no raycast, no mesh query, and it tracks any archetype the
   * Islands editor invents because it reads the same three numbers `setWorld` builds the cone from.
   * (For the shipped defaults this lands at ~1.006 × the island radius, i.e. the sand really does end
   * about where the sim says the island is.)
   */
  private shoreRadiusFor(isl: WorldIsland): number {
    const cached = this._shoreR.get(isl.id);
    if (cached !== undefined) return cached;
    const arch = archetypeForIsland(isl.id, this.islandArchetypes);
    const mult = arch.beachMult ?? ISLE.BEACH_MULT;
    const rise = arch.beachRise ?? ISLE.BEACH_RISE;
    const sink = arch.beachSink ?? ISLE.BEACH_SINK;
    const r = isl.radius * mult * (rise / Math.max(1e-3, rise + sink));
    this._shoreR.set(isl.id, r);
    return r;
  }
  private _shoreR = new Map<number, number>();
  private shoreNextRefresh = 0;
  private _shoreD2 = new Float32Array(SHORE_MAX);

  /**
   * Hand the sea the nearest shorelines — the CPU half of the beach transition.
   *
   * THE BUG. "Areia recortada por cima do mar" (owner QA): the sand apron is an opaque cone and the sea is
   * an opaque sheet, so where they cross you get a MESH INTERSECTION — a razor edge with the island's
   * silhouette stamped on it. Nothing about it reads as a beach.
   *
   * WHY IT IS FIXED HERE AND NOT WITH A PASS. The honest fixes all cost one: a depth texture to measure
   * the water's thickness, or a render target for a soft-particle fade. Neither is on this budget. But the
   * shoreline of one of these islets is not an arbitrary curve — the apron is a CONE, so its waterline is a
   * CIRCLE at a radius computable in closed form from the archetype (see shoreRadiusFor). That makes the
   * distance from any pixel of sea to the sand a subtraction, and everything a beach needs falls out of it:
   *   1. SHOALING COLOUR — deep blue to pale wet-sand turquoise as the bottom comes up. This is what
   *      actually kills the razor edge: the sea arrives AT the sand already nearly the sand's own colour,
   *      so the seam has almost no contrast left to show.
   *   2. THE SWASH RING — a band of foam at the waterline that BREATHES, running up the beach and drawing
   *      back. It is the one cue that says "this water is moving against land" instead of "this texture
   *      ends here", and it is shared in antiphase with the wet band on the sand (see makeBeachMaterial).
   *   3. Broken along the shore by the same rotated, wind-drifted noise the rest of the sea's foam rides.
   *
   * COST. Unlike the storm's coast envelope this runs in CALM too (a beach is not weather), but it is just
   * as cheap when it cannot show: with nothing inside the reach the count goes to 0 and the fragment's
   * whole shore block is one integer compare. Nothing is drawn — zero draw calls, zero geometry — and the
   * LOW path gets exactly the same beach.
   */
  private updateShoreZones(): void {
    const world = this.lastWorld;
    const u = this.oceanMat.uniforms;
    if (!world || world.islands.length === 0) {
      if (u.uShoreCnt!.value !== 0) u.uShoreCnt!.value = 0;
      return;
    }
    if (this.lastRenderT < this.shoreNextRefresh) return;
    this.shoreNextRefresh = this.lastRenderT + 0.4;    // ~2.5 Hz: a hull covers 5 m in that time
    const cx = this.camera.position.x, cz = this.camera.position.z;
    const slots = u.uShore!.value as THREE.Vector4[];
    const d2s = this._shoreD2;
    let n = 0, worst = -1, worstD = -1;
    for (const isl of world.islands) {
      const dx = isl.x - cx, dy = isl.y - cz;
      const d2 = dx * dx + dy * dy;
      // only islets whose shore treatment can reach drawn water near the camera are worth a slot
      const reach = this.shoreRadiusFor(isl) + SHORE_REACH + OCEAN_FADE_R;
      if (d2 > reach * reach) continue;
      let slot = -1;
      if (n < SHORE_MAX) slot = n++;
      else if (d2 < worstD) slot = worst;
      if (slot < 0) continue;
      d2s[slot] = d2;
      slots[slot]!.set(isl.x, isl.y, this.shoreRadiusFor(isl), 0);
      worstD = -1;
      for (let i = 0; i < n; i++) if (d2s[i]! > worstD) { worstD = d2s[i]!; worst = i; }
    }
    u.uShoreCnt!.value = n;
  }

  /**
   * The swell scale AT a world point — the CPU twin of the shader's `oceanAmp`, reading the very same
   * zone array through the very same shared function. This is what keeps a hull bobbing on exactly the
   * water it is floating in as it sails into a bay during a gale.
   */
  ampAt(x: number, z: number): number {
    if (this.waveScale <= 1.001 || this.coastCount === 0) return this.waveScale;
    return lerp(1, this.waveScale, oceanCoastFactor(x, z, this.coastZones, this.coastCount));
  }

  /* ------------------------- the LIVE sea state (ambient breeze + wind frame) ------------------------ */

  /** How far the wind has pushed the drifting noise fields, in metres. Integrated rather than derived
   *  from `t · windDir` so that a veering wind bends the drift instead of teleporting it. */
  private pawDrift = new THREE.Vector2();
  /** The chop gain currently in force — eased, so a sea-state change is never a step. */
  private chopAmp = 1;

  /**
   * Hand the ocean the weather it is actually in, once per frame, in ONE place.
   *
   * Three things ship out of here and they must not disagree: the CPU sampler (`setOceanEnv`, read by
   * every bob/wake/FX anchor), the vertex+fragment uniforms (`uChopDir`/`uChopAmp`, read by the generated
   * GLSL), and the drift offset the cat's paws and the storm foam ride. They are written adjacent, from
   * the same two numbers, for the same reason the coast zones are: the failure mode of any of them
   * lagging a frame behind the others is foam floating above the sea.
   *
   * The ambient state is a pure function of the epoch (`chopGainAt`) — same zero-wire synchronisation
   * story as the storm schedule, so two captains on the same second are on the same water without a byte
   * crossing the wire.
   */
  private updateSeaState(t: number, dt: number): void {
    const target = chopGainAt(Date.now() / 1000, this._storm);
    // ease hard-limited to ~4%/s: the ambient state already moves in minutes, and this only smooths the
    // step a storm's arrival would otherwise put in the chop
    this.chopAmp += clamp(target - this.chopAmp, -0.04 * dt, 0.04 * dt);
    setOceanEnv(this.windDir, this.chopAmp);
    const u = this.oceanMat.uniforms;
    const cd = u.uChopDir!.value as number[];
    cd[0] = Math.cos(this.windDir); cd[1] = Math.sin(this.windDir);
    u.uChopAmp!.value = this.chopAmp;
    // integrate the drift (never `t * dir`, which would snap the whole field the instant the wind veers)
    const spd = CATSPAW.DRIFT * (0.55 + 0.45 * Math.min(1.6, this.windStrength));
    this.pawDrift.x += Math.cos(this.windDir) * spd * dt;
    this.pawDrift.y += Math.sin(this.windDir) * spd * dt;
    (u.uPawDrift!.value as THREE.Vector2).copy(this.pawDrift);
    if (this.shoalWaterMat) {
      const su = this.shoalWaterMat.uniforms;
      const sd = su.uChopDir!.value as number[];
      sd[0] = cd[0]!; sd[1] = cd[1]!;
      su.uChopAmp!.value = this.chopAmp;
    }
    void t;
  }
  /** Bound reference so per-frame consumers (the wake) can hold it without allocating a closure. */
  private _ampAt = (x: number, z: number): number => this.ampAt(x, z);

  /** Scratch env handed to the storm system every frame (allocation-free). */
  private _stormEnv: StormEnv = {
    t: 0, dt: 0, storm: 0, night: 0, camera: undefined as unknown as THREE.PerspectiveCamera,
    windDir: 0, windStrength: 1, waveScale: 1, fog: new THREE.Color(), quality: 'medium',
  };

  /** True once per lightning strike, when the THUNDER has travelled to the listener (storm.ts delays it by
   *  the strike's distance), so main can fire the SFX. */
  consumeThunder(): boolean {
    if (!this.stormFx.consumeThunder()) return false;
    // A CLOSE thunderclap is felt, not just heard: the pressure wave rattles the rigging. Kick the camera
    // for a beat, hard for a strike a couple of hundred metres off and nothing at all for a distant roll —
    // the same distance that already delayed the sound now decides whether you feel it. Kept short and
    // small on purpose (the sway/crest shudder is the continuous motion; this is a single punch).
    const near = clamp(1 - (this.stormFx.thunderDist - 220) / 420, 0, 1);
    if (near > 0.02) this.addShake(0.55 * near * near * this._storm);
    return true;
  }
  /** Distance (m) of the strike whose thunder just landed — main scales the clap's loudness with it. */
  get thunderDist(): number { return this.stormFx.thunderDist; }

  /** Dev/E2E hook (`__cc.renderer.devStrike()`): fire a lightning strike on the next frame, so a headless
   *  render-proof can actually photograph the flash + bolt instead of waiting on the random schedule. */
  devStrike(az?: number): void { this.stormFx.forceStrike(az); }

  /* -------------------------------- wakes ------------------------------- */

  /**
   * Lay + age every ship's Kelvin wake from its RENDERED motion (works identically for the predicted local
   * hull and for interpolated remotes) into the ONE merged WakeSystem, then rebuild + upload it once.
   *
   * This also produces the smoothed speed signal the ocean displacer, the helm wheel and the captain lean
   * read, which is why the bookkeeping half runs for EVERY ship while only nearby ships lay foam.
   */
  private updateWakes(t: number, dt: number): void {
    const MAXSP = SHIP.MAX_SPEED;
    const cam = this.camera.position;
    for (const [id, g] of this.ships) {
      const px = g.position.x, pz = g.position.z, heading = -g.rotation.y;
      const fx = Math.cos(heading), fz = Math.sin(heading), scale = g.scale.x || 1;
      const lx = (g.userData.wakeX as number | undefined) ?? px;
      const lz = (g.userData.wakeZ as number | undefined) ?? pz;
      const moved = Math.hypot(px - lx, pz - lz);
      g.userData.wakeX = px; g.userData.wakeZ = pz;
      if (moved > 30) this.wakeFx.reset(id); // teleport (respawn) -> start a fresh trail
      // The rendered position only advances when a new predicted/interp frame lands, so a raw
      // per-frame delta BLINKS to 0 on the frames in between (render fps > position-update rate) —
      // feeding that into foam opacity / displacer strength makes them flicker ("piscando").
      // Low-pass it (frame-rate-independent EMA) so the speed signal is smooth & stable.
      const inst = Math.min(1.4, moved / Math.max(1e-3, dt) / MAXSP);
      const prev = this.wakeSpd.get(id) ?? inst;
      const ema = Math.min(1, dt * 4); // ~250 ms time constant — damps the position-cadence ripple
      const speed = prev + (inst - prev) * ema;
      this.wakeSpd.set(id, speed);
      g.userData.spd = speed; // stash for the ocean displacer (runs right after, same frame) — kept current even when culled
      // smoothed turn rate (rad/s) → drives the helm wheel + captain lean (same EMA: the heading also only
      // advances on sim frames, so a raw per-frame delta would strobe). normAngle handles the ±π wrap.
      const ph0 = g.userData.prevHeading as number | undefined;
      const turnInst = (ph0 !== undefined && moved <= 30) ? normAngle(heading - ph0) / Math.max(1e-3, dt) : 0;
      g.userData.prevHeading = heading;
      const tp = (g.userData.turn as number) ?? 0;
      g.userData.turn = tp + (turnInst - tp) * ema;
      // distance-cull the wake itself: at 280 m+ it is a few pixels, so it simply stops being laid and its
      // slot returns to the pool for a hull that IS on screen. The speed signal above is kept regardless.
      const cdx = px - cam.x, cdz = pz - cam.z;
      if (cdx * cdx + cdz * cdz > WAKE_CULL_D2) continue;
      // foam is born at the TRANSOM, not the hull centre — that is where the Kelvin stem starts
      const sx = px - fx * HALF_L * scale * 0.85, sz = pz - fz * HALF_L * scale * 0.85;
      this.wakeFx.lay(id, sx, sz, speed, HALF_B * scale, t);
    }
    this.updateContactShadows(t);
    // ONE rebuild, ONE upload, ONE draw call for every wake in the world (the whale laid its own above)
    this.wakeFx.setEnv(t, this.nightFactor, this._storm);
    // the wake reads the swell PER POINT (a trail laid across a bay must shoal with the water under it,
    // or the foam floats a metre above the calmed sea near the beach) — see Renderer.ampAt
    this.wakeFx.flush(t, this._ampAt);
    for (const id of this.wakeSpd.keys()) if (!this.ships.has(id)) this.wakeSpd.delete(id);
  }

  /**
   * 🌑 CONTACT SHADOWS — one instanced draw for the whole fleet.
   *
   * The strength is not a constant: it is the sky the hull actually blocks, so it fades out at night (there
   * is no sky to block), thins in a gale (an overcast sky is its own diffuse dome — hard shadows are the
   * FIRST thing that goes when the sun is behind cloud), and rides the swell. The whole thing is skipped
   * when there is nothing to darken, so a night battle pays zero draw calls for it.
   */
  private updateContactShadows(t: number): void {
    const sh = this.shadowFx;
    sh.begin();
    // sky occlusion, not a cast shadow: gone at night, damped under a storm deck
    const strength = (1 - this.nightFactor * 0.92) * (1 - this._storm * 0.55);
    if (strength > 0.02) {
      const cam = this.camera.position;
      for (const g of this.ships.values()) {
        const x = g.position.x, z = g.position.z;
        const dx = x - cam.x, dz = z - cam.z;
        if (dx * dx + dz * dz > SHADOW_CULL_D2) continue;   // a few pixels at 220 m — not worth a slot
        const cls = (g.userData.cls as number) ?? ShipClassId.BRIGANTINE;
        const def = SHIP_CLASS[cls as 0 | 1 | 2] ?? SHIP_CLASS[1];
        const scale = g.scale.x || 1;
        const amp = this.ampAt(x, z);
        const seaY = oceanHeightAt(x, z, t, amp);
        // how high this stretch of water is standing, normalised — a hull on a crest has more light
        // getting under it than one down in a trough, and the patch spreads and pales to match
        const lift = seaY / Math.max(0.35, amp);
        sh.add(x, seaY, z, -g.rotation.y, def.length * 0.5 * scale, def.beam * 0.5 * scale, lift, strength * 0.72);
      }
    }
    sh.end();
  }

  /** Feed the ocean shader the nearest hulls so it can open/sink the water around them. */
  private updateOceanDisplacers(): void {
    const ships = this.oceanMat.uniforms.uShips!.value as THREE.Vector4[];
    const shipsB = this.oceanMat.uniforms.uShipsB!.value as THREE.Vector4[];
    const cam = this.camera.position;
    // alloc-free nearest-N: insertion-sort each candidate directly into the fixed uniform slots
    // (no per-frame list/object/closure — keeps the render hot path GC-free).
    const d2arr = this._dispD2;
    for (let i = 0; i < OCEAN_DISP_MAX; i++) d2arr[i] = Infinity;
    let count = 0;
    for (const g of this.ships.values()) {
      const dx = g.position.x - cam.x, dz = g.position.z - cam.z, d2 = dx * dx + dz * dz;
      if (d2 > 360 * 360) continue; // far -> fogged out, not worth a displacer slot
      if (count >= OCEAN_DISP_MAX && d2 >= d2arr[OCEAN_DISP_MAX - 1]!) continue; // not nearer than the worst slot
      // gate by speed: a parked/slow hull barely displaces water (no ring around a still ship)
      const spd = (g.userData.spd as number) ?? 0;
      const k = Math.max(0, Math.min(1, (spd - 0.04) * 1.6));
      if (k <= 0.001) continue;
      const heading = -g.rotation.y, scale = g.scale.x || 1;
      const ch = Math.cos(heading), sh = Math.sin(heading);
      // WETTED waterline only — shorter than the hull silhouette so the foam never rings the raised
      // bow/stern tips ("o bico"); it hugs the mid-hull where the hull actually sits in the water.
      const hl = HALF_L * scale * 0.82, hb = HALF_B * scale * 1.32;
      let ins = Math.min(count, OCEAN_DISP_MAX - 1);
      while (ins > 0 && d2arr[ins - 1]! > d2) { // shift worse slots down to make room (in-place, no alloc)
        d2arr[ins] = d2arr[ins - 1]!;
        ships[ins]!.copy(ships[ins - 1]!); shipsB[ins]!.copy(shipsB[ins - 1]!);
        ins--;
      }
      d2arr[ins] = d2;
      ships[ins]!.set(g.position.x, g.position.z, ch, sh);
      shipsB[ins]!.set(hl, hb, k, 0);
      if (count < OCEAN_DISP_MAX) count++;
    }
    for (let i = count; i < OCEAN_DISP_MAX; i++) { ships[i]!.set(0, 0, 0, 0); shipsB[i]!.set(0, 0, 0, 0); }
    this.oceanMat.uniforms.uShipCnt!.value = count;
  }

  /** 0 (day) … 1 (deep night) — the current time-of-day darkness, for night-aware systems (e.g. music). */
  get night(): number { return this.nightFactor; }

  /** Distance-cull the static world groups (islands + outposts) around the camera. Cheap O(n) over a
   *  handful of groups; called on an interval, not every frame. A hidden group skips frustum-culling,
   *  matrix updates AND its beacon pulse below — the win that keeps 60 islands inside the draw budget. */
  private _cullAcc = 0;
  private cullWorld(): void {
    const cx = this.camera.position.x, cz = this.camera.position.z;
    for (const c of this.worldRoot.children) {
      const dx = c.position.x - cx, dz = c.position.z - cz;
      c.visible = dx * dx + dz * dz < WORLD_CULL_D2;
    }
    // Onda Portos Livres — the HEAVY exterior kit meshes (hero citadel = 100-400k tris) get a tighter cull than
    // the port group so a far-but-in-frustum port drops them near the horizon (the small dock/ring still reads).
    for (const e of this.kitExtNodes) {
      const dx = e.ox - cx, dz = e.oz - cz;
      e.node.visible = dx * dx + dz * dz < KIT_EXT_CULL_D2;
    }
    // Hero-building LOD — swap each heavy diorama's full/lod pair by distance (30 m hysteresis) and hard-cull the
    // pair beyond the kit-ext range. Exactly one of full/lod is visible inside the cull, neither beyond it.
    for (const e of this.kitLodNodes) {
      const dx = e.ox - cx, dz = e.oz - cz, d2 = dx * dx + dz * dz;
      if (d2 >= KIT_EXT_CULL_D2) { e.full.visible = false; e.lod.visible = false; continue; }
      e.far = e.far ? d2 > KIT_LOD_NEAR_D2 : d2 > KIT_LOD_FAR_D2; // hysteresis: only fall back to full within NEAR
      e.full.visible = !e.far;
      e.lod.visible = e.far;
    }
  }

  render(t: number, dt: number, centerX: number, centerZ: number): void {
    if (this._contextLost) return; // the GL context is gone (WKWebView OOM) — never touch it; the reload card is up
    this.renderer.info.reset(); // manual per-frame reset (autoReset off) so stats() sums all composer passes
    this.lastRenderT = t;       // set FIRST: the render-time closures (ocean height for the flock) read it
    this.updateFaunaAnchor(centerX, centerZ);
    this.updateSky(t);
    this.updateWeather(t, dt);
    this.updateDive(t, dt); // 🤿 layers the abyssal dive tint over the fresh sky/storm colours (same pattern as storm)
    // distance-cull the far islands/outposts (throttled — the set barely changes between frames)
    this._cullAcc += dt;
    if (this._cullAcc >= 0.2) { this._cullAcc = 0; this.cullWorld(); }
    // treasure beacons breathe (allocation-free; a handful of materials) — skip beacons on culled islands
    for (let i = 0; i < this.chestBeaconMats.length; i++) {
      const m = this.chestBeaconMats[i]!;
      const grp = m.userData.grp as THREE.Object3D | undefined;
      if (grp && !grp.visible) continue;
      m.opacity = 0.55 + 0.3 * Math.sin(t * 1.7 + i * 1.3);
    }
    // Cursed Treasure: ride the swell + a faster, hungrier violet pulse
    if (this.cursedGroup && this.cursedPos) {
      const cy = oceanHeightAt(this.cursedPos.x, this.cursedPos.y, t, this.ampAt(this.cursedPos.x, this.cursedPos.y));
      this.cursedGroup.position.set(this.cursedPos.x, cy, this.cursedPos.y);
      this.cursedGroup.rotation.y = t * 0.25;
      for (const m of this.cursedBeaconMats) m.opacity = 0.6 + 0.4 * Math.sin(t * 3.4);
    }
    this.updateAtmosphere(t, dt);
    // 🏝️ the swash clock, shared by BOTH halves of the shoreline: the sea's foam ring runs OUT from the
    // waterline while the sand's wet lace runs UP the beach, so they are driven in antiphase from one
    // number and can never disagree about where the water currently is.
    this.beachSwash.value = 0.28 + 0.28 * Math.sin(t * 0.44 + Math.PI);
    this.updateTradeFoam(centerX, centerZ, t, dt);
    this.updateShoals(t, centerX, centerZ);
    this.updateFishingLine(t);
    if (this.bottles.size) this.updateBottles(t);
    if (this.whaleMesh) this.updateWhale(t, dt);
    if (this.krakenState || this.krakenMesh) this.updateKraken(t, dt);
    this.updateWakes(t, dt);
    this.updateCaptains(t, dt);
    this.updateCrew(t);
    if (this.wrecks.length) this.updateWrecks(t, dt);
    this.updateOceanDisplacers();
    this.oceanMat.uniforms.uTime!.value = t;
    (this.oceanMat.uniforms.uCam!.value as THREE.Vector3).copy(this.camera.position);
    // Onda Água v2 — the clipmap follows the captain by TRANSLATION ONLY, snapped to the COARSEST cell so
    // every level keeps sampling the same fixed world lattice (no vertex swimming, at any level). The
    // horizon fade reads uCenter (unsnapped) so it never steps with the snap.
    this.ocean.position.set(Math.round(centerX / OCEAN_SNAP) * OCEAN_SNAP, 0, Math.round(centerZ / OCEAN_SNAP) * OCEAN_SNAP);
    const uc = this.oceanMat.uniforms.uCenter?.value as THREE.Vector2 | undefined;
    if (uc) uc.set(centerX, centerZ);
    // micro hit-stop: the impact FX play at ~0.22x for a beat (~40ms) so a hit lands as a punch
    const fxDt = this.hitstopT > 0 ? dt * 0.22 : dt;
    if (this.hitstopT > 0) this.hitstopT -= dt;
    this.stepFx(fxDt);
    this.stepFoamPatches(t, fxDt);
    // the instanced impact FX animate entirely from uTime — the CPU only advances the clock and uploads
    // whatever was spawned this frame (both are no-ops when nothing landed)
    this.splashFx.setEnv(t, this.nightFactor);
    this.splashFx.flush();
    this.updateSailSun();
    this.composer.render();
  }

  /** Cinematic main-menu backdrop: a hero galleon (full sails + captain) riding a stormy twilight
   *  swell, lanterns lit, a slow orbiting camera. Returns true once the hero ship is built. */
  showcase(t: number, dt: number): boolean {
    // a moody, sinister mood: deep twilight + a gentle storm (rain + the odd lightning flash on the hull)
    this.forceSkyPhase = 0.9;            // twilight (deep-blue → purple, stars, aurora)
    this.setStorm(0.5, dt);             // gentle storm: rain, darker sea, occasional thunder/lightning
    // (re)build the hero if absent OR if it was orphaned — loadCaptains/loadShips clear all
    // ships when their GLBs finish loading async, which un-parents (and disposes) the hero too.
    if (!this.heroShip || !this.heroShip.parent) {
      if (!this.shipTemplates.has(ShipClassId.GALLEON)) { // templates still loading
        this.render(t, dt, 0, 0);
        return false;
      }
      this.heroShip = this.buildShip(-999, false, ShipClassId.GALLEON);
    }
    const hx = 0, hz = 0, heading = 0.5;
    const hero = this.heroShip;
    const b = this.bob(hx, hz, heading, t, ShipClassId.GALLEON);
    hero.position.set(hx, b.y, hz);
    hero.rotation.set(b.roll, -heading, b.pitch, 'YXZ');
    // full sails for the hero shot
    const actions = hero.userData.actions as THREE.AnimationAction[] | undefined;
    if (actions) { for (const a of actions) a.time = hero.userData.clipDur as number; (hero.userData.mixer as THREE.AnimationMixer).update(0); }
    // …and the canvas ALIVE in it. The menu is the first thing a captain ever sees; a hero galleon whose sails
    // sat frozen while the rain slanted past them was the tell that this was a poster, not a sea. She runs with
    // the gale abaft the beam, so she draws hard and leans — the same code every hull at sea uses.
    const hUni = hero.userData.sailUni as SailUniforms[] | undefined;
    const hSt = hero.userData.sailState as SailState | undefined;
    if (hUni && hSt) {
      const windDir = heading + 0.7;   // over the port quarter: full canvas AND a visible heel
      hSt.step(sailEnv(heading, windDir, 1.1, 1, 0, this._storm), dt);
      hero.rotation.x = b.roll + hSt.heel;
      for (const u of hUni) { u.drive.value.set(hSt.fill, hSt.luff, hSt.lateral, 1); u.fx.value.set(t, this._storm, 0, this._sailQ); }
    }
    // slow orbit at a low, looming hero angle with a gentle rise/fall; aim low so the galleon
    // rides high in frame (full hull + sails clear of the menu panel below it)
    const ang = 0.55 + t * 0.05;
    const dist = 52, cy = (this.bob(0, 0, 0, t, 2).y) + 10 + Math.sin(t * 0.35) * 2.0;
    this.camera.position.set(hx + Math.cos(ang) * dist, cy, hz + Math.sin(ang) * dist);
    this.camera.lookAt(hx, 5, hz);
    this.render(t, dt, hx, hz);
    return true;
  }

  /** Dev/E2E hook (via __cc.setCityPhase): pin the sea sky's time-of-day phase 0..1, or null to resume the
   *  live wall-clock cycle. Lets a headless run force midnight/dawn/noon/dusk to verify the day/night look. */
  setSkyPhase(p: number | null): void { this.forceSkyPhase = p; }

  /**
   * 🌌 Dev/owner hook (`?aurora=…`, `__cc.setAurora`): pin the aurora display 0..1 and its breakup beat,
   * or pass null to resume the live schedule.
   *
   * The NIGHT gate and the STORM gate still apply on top — an aurora at noon is not a thing this can
   * force, and that is deliberate: "zero effect by day" is a property of the renderer, not of the
   * schedule, so it must survive every override. To photograph one, pair it with `setSkyPhase(0)`.
   */
  setAurora(amt: number | null, breakup: number | null = null): void {
    this.auroraOverride = amt == null ? null : clamp(amt, 0, 1);
    this.auroraBreakOverride = breakup == null ? null : clamp(breakup, 0, 1);
  }
  /** What the sky is actually flying right now — for the harness log / a headless render-proof. */
  get aurora(): { amt: number; breakup: number } { return { amt: this.auroraAmt, breakup: this.auroraBreak }; }

  /** 🎨 Water-palette A/B rig (temporary — see WATER_PALETTES). Swaps the whole sea colour chain for the
   *  named variant. Uniform values only: no shader rebuild, no reallocation, no change to draw calls — so
   *  a harness can photograph ONE frozen frame in all four palettes and the only difference on the contact
   *  sheet is the colour. Unknown names are ignored (the sea keeps whatever it is flying). */
  setWaterPalette(name: WaterPaletteName): void {
    if (!(name in WATER_PALETTES)) return;
    this.waterPaletteName = name;
    this.waterPalette = WATER_PALETTES[name];
  }
  /** Which variant the sea is flying right now (for the harness log / a headless render-proof). */
  get waterPaletteId(): WaterPaletteName { return this.waterPaletteName; }

  /** Tear down the showcase when the real game starts (the hero ship is rebuilt by syncShips anyway). */
  endShowcase(): void {
    this.forceSkyPhase = null;
    this.setStorm(0, 0.016);
    if (this.heroShip) { this.shipsRoot.remove(this.heroShip); disposeGroup(this.heroShip); this.ships.delete(-999); this.heroShip = undefined; }
  }

  resize(): void {
    const w = window.innerWidth, h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.fov = verticalFovForAspect(this.camera.aspect); // re-compensate the lens on rotate/resize (Hor+)
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(this.pixelRatioCap());
    this.composer?.setSize(w, h);
    this.bloomPass?.setSize(w, h);
  }

  /** HUD 2.0: screen-space azimuth of a sim-plane point relative to the camera's view axis —
   *  0 = dead ahead (top of screen), positive = clockwise/right. Computed in camera space so it stays
   *  correct even when the point is behind the camera (a raw NDC projection mirrors there), which is
   *  exactly where a directional damage arc matters most. */
  private _bearing = new THREE.Vector3();
  bearingTo(simX: number, simY: number): number {
    const v = this._bearing.set(simX, this.camera.position.y, simY).applyMatrix4(this.camera.matrixWorldInverse);
    return Math.atan2(v.x, -v.z);
  }

  /** Project a sim (x, y) point (lifted to just above the sea) to CSS-pixel screen
   *  coords for a HUD overlay marker. onScreen=false when behind the camera / off-frame. */
  worldToScreen(simX: number, simY: number, t: number): { x: number; y: number; onScreen: boolean } {
    const y = oceanHeightAt(simX, simY, t, this.ampAt(simX, simY)) + 4;
    this._proj.set(simX, y, simY).project(this.camera);
    const onScreen = this._proj.z < 1 && Math.abs(this._proj.x) <= 1.1 && Math.abs(this._proj.y) <= 1.1;
    return { x: (this._proj.x * 0.5 + 0.5) * window.innerWidth, y: (-this._proj.y * 0.5 + 0.5) * window.innerHeight, onScreen };
  }

  /** Headless render-proof. `oceanTriangles` / `wake` make the Onda Água v2 budget checkable from a script:
   *  the sea must never cost more triangles than the uniform grid it replaced, and every wake in the world
   *  must fit in ONE draw call. */
  stats(): { drawCalls: number; triangles: number; ships: number; oceanTriangles: number; wake: WakeStats; splash: SeaFxStats; shadows: number } {
    return {
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      ships: this.ships.size,
      oceanTriangles: this.oceanTriangles,
      wake: this.wakeFx.stats(),
      splash: this.splashFx.stats(),
      shadows: this.shadowFx.count,
    };
  }

  /** Kill-cam: sea-surface world-Y at a sim (x, y) point, using the LIVE storm swell scale — the director camera
   *  seats itself relative to the water (anti-clip) with the same height function the follow camera uses. Render-only. */
  oceanY(simX: number, simY: number, t: number): number { return oceanHeightAt(simX, simY, t, this.ampAt(simX, simY)); }
}

// Dispose ONLY resources the build path registered as owned (userData.ownMats / ownGeom). We must never
// blanket-dispose by traversal: GLB template clones share geometry AND materials by reference, and all
// Sprites share one global geometry — disposing those frees resources still used by the template/other
// clones (GPU re-upload) while leaking the per-build materials. Registering owned-only fixes both.
function disposeGroup(g: THREE.Object3D): void {
  const um = g.userData.ownMats as THREE.Material[] | undefined;
  const ug = g.userData.ownGeom as THREE.BufferGeometry[] | undefined;
  if (um) for (const m of um) m.dispose();
  if (ug) for (const geo of ug) geo.dispose();
  g.userData.ownMats = undefined;
  g.userData.ownGeom = undefined;
}
